import React, { useEffect, useState, useRef, useCallback } from "react";
import { safeLocalStorage } from "./utils/safeLocalStorage";
import { normalizeTags } from "./utils/tagUtils";
import { supabase } from "./supabaseClient";
import { clearApiTokenCache, apiRequest } from "./utils/apiClient";
import { fetchWithRetry, fetchJsonWithRetry } from "./lib/fetchWithRetry";
import { getErrorMessage } from "./utils/errorUtils";
import { setDynamicGoogleMapsConfig, googleMapsConfig } from "./lib/googleMapsConfig";
import { 
  APIProvider, 
  Map as GoogleMap, 
  AdvancedMarker, 
  Pin, 
  Marker,
  InfoWindow, 
  useMap, 
  useMapsLibrary,
  useAdvancedMarkerRef
} from "@vis.gl/react-google-maps";
import { 
  MapPin, 
  Search, 
  User, 
  Mail, 
  Phone, 
  Globe, 
  CheckCircle, 
  AlertCircle, 
  AlertTriangle,
  Loader2, 
  Building2, 
  Star, 
  ExternalLink,
  Sparkles,
  SearchCode,
  ShieldCheck,
  BookOpen,
  Compass,
  ZoomIn,
  Copy,
  Check,
  RotateCcw,
  Info,
  SlidersHorizontal,
  X,
  Database,
  Save,
  Bookmark,
  Trash2,
  Menu,
  History,
  LogOut,
  Lock,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Bell,
  BellRing,
  Download,
  CreditCard,
  Briefcase,
  Megaphone,
  Settings,
  Palette,
  Activity,
  Navigation,
  Maximize2,
  Minimize2,
  Flame,
  Bug,
  Lightbulb,
  Share2,
  Eye,
  ShieldAlert,
  DollarSign,
  Users,
  Ban,
  UserPlus,
  PlusCircle,
  UserMinus,
  Github,
  RefreshCw,
  Ruler,
  Layers,
  HelpCircle,
  List,
  MoreHorizontal,
  Map as MapIcon
} from "lucide-react";
import { BusinessBusiness, ExecutiveContact, LookupHistoryItem, WebsiteReviewState } from "./types";
import { analyzeBusinessProspect, getTagExplanation } from "./utils/opportunityFinder";
import { getUpgradedOrganicScore, UpgradedOrganicScoreResult } from "./utils/discoveryScoring";
import { EXPANDED_CATEGORY_GROUPS, parseBusinessQuery, calculateQueryRelevanceScore, ParsedBusinessQuery, removeTermFromQuery } from "./utils/businessSearchEngine";
import { calculateAudienceScore, runAudienceModeComparisonTest, logAudienceRankingDiagnostics } from "./utils/audienceScoring";
import { calculateBusinessPopularity, determinePopularityLevel, getPopularityWarning, getBusinessExplorerPopularitySignal, popularitySignalLabels, getTopTagsForAudience, getBusinessExplorerPrimaryPopularityLabel, getBusinessExplorerMatchLabel } from "./utils/popularity";
import { rankSearchResultsOnce, RankedBusinessSnapshot, verifyOrderStabilityDev } from "./utils/stableRanking";
import { getAllIntelligenceMatrixTags } from "./config/signalRegistry";
import { getAudienceRankingFactors } from "./config/audienceModes";
import { getAudienceDetailConfig } from "./config/audienceDetailConfig";
import { seoConfig, getSeoConfig } from "./utils/seoConfig";
import { parseRouteLanguage, buildLanguageUrl } from "./utils/languageRegistry";
import { getStandardizedLocationRedirect, normalizeCitySlug, resolveLocationSlug } from "./utils/locationRoutes";
import { generateJobEnrichment } from "./utils/jobEnrichment";
import BizNearYouLogo from "./components/BizNearYouLogo";
import ProToolsPanel from "./components/ProToolsPanel";
import { detectCrisisSearch, CrisisDetectionResult } from "./utils/safetySearch";
import { detectProhibitedUse, PolicySafetyResult } from "./utils/prohibitedUse";
import { detectEmploymentComplianceRisk } from "./utils/employmentSafety";
import { verifyTranslationKeys } from "./utils/i18nCheck";
import { validateSingleLocation } from "./utils/locationValidation";
import { CrisisSupportCard } from "./components/CrisisSupportCard";
import { LegalModal, LegalTab } from "./components/LegalModal";
import { LegalFooter } from "./components/LegalFooter";
import DocumentationPage from "./components/DocumentationPage";
import { SettingsDashboard } from "./components/SettingsDashboard";
import { LoginSuccessPage } from "./components/LoginSuccessPage";
import { ForgotPasswordPage } from "./components/ForgotPasswordPage";
import { ResetPasswordPage } from "./components/ResetPasswordPage";
import { LocationPage } from "./components/LocationPage";
import { LocationDirectoryPage } from "./components/LocationDirectoryPage";
import { StateHubPage } from "./components/seo/StateHubPage";
import { getNicheIntentBySlug } from "./utils/nicheIntentRegistry";
import { getSeoEntryByRoute } from "./utils/seoRegistry";
import { IndustryDirectoryPage } from "./components/seo/IndustryDirectoryPage";
import { IndustryPage } from "./components/seo/IndustryPage";
import { GuideHubPage } from "./components/seo/GuideHubPage";
import { GuidePage } from "./components/seo/GuidePage";
import { FeatureHubPage } from "./components/seo/FeatureHubPage";
import { FeaturePage } from "./components/seo/FeaturePage";
import { ReportHubPage } from "./components/seo/ReportHubPage";
import { ReportPage } from "./components/seo/ReportPage";
import { MethodologyPage } from "./components/seo/MethodologyPage";
import { CompareHubPage } from "./components/seo/CompareHubPage";
import { ComparePage } from "./components/seo/ComparePage";
import { ExploreHubPage } from "./components/seo/ExploreHubPage";
import { JobDirectoryHubPage } from "./components/seo/JobDirectoryHubPage";
import { JobCategoryPage } from "./components/seo/JobCategoryPage";
import { JobCategoryLocationPage } from "./components/seo/JobCategoryLocationPage";
import { getJobCategoryBySlug } from "./utils/jobCategoryRegistry";
import { AI_FEATURES_ENABLED } from "./config/featureFlags";
import { PricingComparisonTable } from "./components/PricingComparisonTable";
import { BusinessDetailsView } from "./components/BusinessDetailsView";
import { getOrCreateSignalFreshness, calculateSignalDecay, formatFreshnessAge } from "./utils/signalFreshness";
import { formatSignalLastUpdated, getSignalDateISO, formatCompactDate } from "./utils/signalNormalization";

export type ThemePreference = "auto" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

export function resolveTheme(preference: string): ResolvedTheme {
  if (preference === "dark") return "dark";
  if (preference === "light") return "light";
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  }
  return "light";
}
import { ThemeSelector } from "./components/ThemeSelector";
import { LanguageSelector } from "./components/LanguageSelector";
import { AuthCallback } from "./components/AuthCallback";
import DashboardView from "./components/DashboardView";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { logger, bizDebug, bizError } from "./utils/debugLogger";
import { BillingSuccessPage, BillingCancelPage } from "./components/BillingPages";
import SignupSuccessPage from "./components/SignupSuccessPage";
import { VerifyEmailPage } from "./components/VerifyEmailPage";
import { 
  trackInteraction, 
  getPersonalizedScore, 
  getHiringLikelihood,
  refineUnconfirmedHiringSignal,
  formatDateString,
  HiringSignalType,
  trackSearch,
  trackPhoneCallClick,
  trackDirectionsRequest,
  trackCopyClick,
  getUserStats,
  saveUserStats
} from "./utils/opportunityHelpers";
import { useTranslation } from "./utils/i18n";
import HowSignalsWorkPage from "./components/HowSignalsWorkPage";
import { 
  PublicHeader, PublicFooter, PublicHomepage, FindJobsPage, 
  HiddenJobsPage, LocalInternshipsPage, FirstJobsPage, 
  BusinessesHiringNearMePage, HowItWorksPage, HowToContactBusinessesPage,
  PrivacyPage, TermsPage, ContactPage, AboutPage,
  HowToPrioritizeBusinessesPage, HowToOrganizeLocalJobSearchPage,
  HowToAskIfHiringPage, HiringSignalsExplainedPage, PageNotFound,
  PublicWhyBizNearYou, PublicFaq, PublicHowDataIsCollected,
  PublicHowScoresAreCalculated, PublicHowHiringSignalsWork,
  PublicWhyBusinessesBeforeJobListings, PublicPricingPage
} from "./components/PublicPages";
import { AudienceLandingPage } from "./components/AudienceLandingPages";
import { AudienceDirectoryPage } from "./components/AudienceDirectoryPage";
import { AudienceSelector } from "./components/AudienceSelector";
import { AudienceActionButtonsBar } from "./components/AudienceActionButtonsBar";
import { AUDIENCE_MODES, AudienceModeId, getAudienceModeConfig, normalizeAudienceModeId, isAudienceMode } from "./config/audienceModes";

export const AUDIENCE_ACTION_LABELS: Record<AudienceModeId, {
  primaryAction: string;
  saveAction: string;
  viewDetails: string;
}> = {
  job_seeker: {
    primaryAction: "Contact Employer",
    saveAction: "Save Opportunity",
    viewDetails: "View Job Details",
  },
  business_explorer: {
    primaryAction: "Call Business",
    saveAction: "Save Business",
    viewDetails: "View Business Details",
  },
  recruiter: {
    primaryAction: "Add to Recruiting Pipeline",
    saveAction: "Save Employer Prospect",
    viewDetails: "View Recruiting Details",
  },
  staffing_agency: {
    primaryAction: "Add to Staffing Pipeline",
    saveAction: "Save Employer",
    viewDetails: "View Staffing Details",
  },
  career_coach: {
    primaryAction: "Add to Client Workspace",
    saveAction: "Save Opportunity",
    viewDetails: "View Client Match",
  },
  workforce_organization: {
    primaryAction: "Add to Employer Outreach",
    saveAction: "Save Employer",
    viewDetails: "View Regional Details",
  },
  college_career_center: {
    primaryAction: "Add to Employer List",
    saveAction: "Save Student Opportunity",
    viewDetails: "View Student Details",
  },
  sales_professional: {
    primaryAction: "Add to Sales Pipeline",
    saveAction: "Save Prospect",
    viewDetails: "View Sales Details",
  },
  business_development: {
    primaryAction: "Add to Partnership Pipeline",
    saveAction: "Save Partner Prospect",
    viewDetails: "View Partnership Details",
  },
  agency_freelancer: {
    primaryAction: "Add to Agency Pipeline",
    saveAction: "Save Prospect",
    viewDetails: "View Client Details",
  },
};
import { LanguageCode } from "./utils/translations";
import { 
  translateTag, 
  translateCategory, 
  translateBullet, 
  translateExplanation, 
  translateBreakdown 
} from "./utils/translateBusinessDetails";

/**
 * Safely extracts latitude and longitude from any Google Places or coordinate object representation,
 * preventing pins from defaulting to (0,0) in the ocean.
 */
function extractCoordinates(locationObj: any): { lat: number; lng: number } {
  if (!locationObj) return { lat: 0, lng: 0 };
  
  let latVal = 0;
  let lngVal = 0;

  if (typeof locationObj.lat === "function") {
    latVal = locationObj.lat();
  } else if (typeof locationObj.latitude === "number") {
    latVal = locationObj.latitude;
  } else if (typeof locationObj.lat === "number") {
    latVal = locationObj.lat;
  } else if (typeof locationObj.latitude === "string") {
    latVal = parseFloat(locationObj.latitude);
  } else if (typeof locationObj.lat === "string") {
    latVal = parseFloat(locationObj.lat);
  }

  if (typeof locationObj.lng === "function") {
    lngVal = locationObj.lng();
  } else if (typeof locationObj.longitude === "number") {
    lngVal = locationObj.longitude;
  } else if (typeof locationObj.lng === "number") {
    lngVal = locationObj.lng;
  } else if (typeof locationObj.longitude === "string") {
    lngVal = parseFloat(locationObj.longitude);
  } else if (typeof locationObj.lng === "string") {
    lngVal = parseFloat(locationObj.lng);
  }

  return {
    lat: isNaN(latVal) ? 0 : latVal,
    lng: isNaN(lngVal) ? 0 : lngVal
  };
}

const CATEGORY_GROUPS = EXPANDED_CATEGORY_GROUPS;

const AVAILABLE_CATEGORIES = Array.from(
  new Set(CATEGORY_GROUPS.reduce<string[]>((acc, g) => [...acc, ...g.items], []))
);

const isAiStudioRuntime =
  typeof window !== "undefined" &&
  (
    window.location.hostname.includes("aistudio") ||
    window.location.hostname.includes("run.app")
  );

const isAiStudioVisualEditMode =
  typeof window !== "undefined" &&
  (
    window.location.search.includes("editMode=true") ||
    window.location.search.includes("visualEditor=true") ||
    window.location.search.includes("aiStudioEdit=true") ||
    localStorage.getItem("BIZ_AI_STUDIO_EDIT_MODE") === "true"
  );

const isEditorSafeMode = isAiStudioVisualEditMode;

const guardedClick = (handler: (e: any) => void) => (e: any) => {
  if (isAiStudioVisualEditMode) {
    e.preventDefault();
    e.stopPropagation();
    logger.debug("[EDIT_MODE_CLICK_SUPPRESSED]");
    return;
  }
  handler(e);
};

interface PlaceAutocompleteInputProps {
  value: string;
  onChange: (val: string) => void;
  onSelectCoords: (coords: { lat: number; lng: number } | null) => void;
  isLocating: boolean;
  handleGetCurrentLocation: () => void;
  theme: string;
}

const PlaceAutocompleteInput = ({
  value,
  onChange,
  onSelectCoords,
  isLocating,
  handleGetCurrentLocation,
  theme
}: PlaceAutocompleteInputProps) => {
  const { t } = useTranslation();
  const places = useMapsLibrary("places");
  const [predictions, setPredictions] = useState<any[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [errorState, setErrorState] = useState<string | null>(null);
  const [activeQuery, setActiveQuery] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const sessionTokenRef = useRef<any>(null);

  const getSessionToken = () => {
    if (!sessionTokenRef.current && window.google && window.google.maps && window.google.maps.places) {
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    }
    return sessionTokenRef.current;
  };

  const resetSessionToken = () => {
    if (window.google && window.google.maps && window.google.maps.places) {
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    } else {
      sessionTokenRef.current = null;
    }
  };

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // Debounced input query matching
  useEffect(() => {
    const timer = setTimeout(() => {
      setActiveQuery(value);
    }, 300);
    return () => clearTimeout(timer);
  }, [value]);

  useEffect(() => {
    if (isEditorSafeMode) return;
    if (!places || !window.google || !window.google.maps || !window.google.maps.places) return;
    if (!activeQuery || activeQuery.trim().length < 2) {
      setPredictions([]);
      return;
    }

    setIsLoading(true);
    setErrorState(null);

    const token = getSessionToken();

    window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input: activeQuery,
      sessionToken: token,
      includedPrimaryTypes: ["geocode"]
    })
      .then(({ suggestions }) => {
        setPredictions(suggestions || []);
        setFocusedIndex(-1);
      })
      .catch((err: any) => {
        console.warn("Autocomplete fetch failed:", err);
        setErrorState(err.message || "Failed to fetch suggestions");
        setPredictions([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [activeQuery, places]);

  const handleSelectPrediction = async (pred: any) => {
    const placeId = pred.placePrediction?.placeId || pred.place_id;
    const text = pred.placePrediction?.text?.toString() || pred.description || "";

    onChange(text);
    setIsOpen(false);
    setFocusedIndex(-1);

    if (!window.google || !window.google.maps || !window.google.maps.places) return;

    try {
      const place = new window.google.maps.places.Place({ id: placeId });
      await place.fetchFields({ fields: ["location"] });

      if (place.location) {
        const coords = extractCoordinates(place.location);
        onSelectCoords(coords);
      }
      resetSessionToken();
    } catch (err) {
      console.error("fetchFields failed for place ID:", placeId, err);
      // Fallback to Geocoder
      try {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ placeId: placeId }, (results, status) => {
          if (status === "OK" && results && results[0]) {
            const coords = extractCoordinates(results[0].geometry.location);
            onSelectCoords(coords);
          }
        });
      } catch (geoErr) {
        console.error("Geocoding fallback also failed:", geoErr);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setIsOpen(false);
      setFocusedIndex(-1);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setIsOpen(true);
      setFocusedIndex((prev) => (prev + 1) % (predictions.length || 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setIsOpen(true);
      setFocusedIndex((prev) => (prev - 1 + (predictions.length || 1)) % (predictions.length || 1));
    } else if (e.key === "Enter") {
      if (isOpen && focusedIndex >= 0 && focusedIndex < predictions.length) {
        e.preventDefault();
        handleSelectPrediction(predictions[focusedIndex]);
      }
    }
  };

  const isDark = theme === "dark";
  const bgValue = isDark ? "#0f172a" : "#ffffff";
  const actualTextValue = isDark ? "#ffffff" : "#0f172a";
  const borderValue = isDark ? "#334155" : "#e2e8f0";

  return (
    <div ref={containerRef} className="relative flex flex-col w-full" data-ai-editable="location-input">
      <div className="relative flex items-center w-full">
        <MapPin className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400 z-10 pointer-events-none" />
        <input
          type="text"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={t("zipcode_placeholder") || "Search locations..."}
          className="w-full h-[38px] border rounded-md pl-9 pr-10 text-base lg:text-sm font-medium outline-none transition-all duration-200"
          style={{
            color: actualTextValue,
            backgroundColor: bgValue,
            borderColor: borderValue,
          }}
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 bg-transparent z-10">
          <button 
            type="button"
            onClick={guardedClick(handleGetCurrentLocation)}
            disabled={isLocating}
            className="p-1 text-slate-400 hover:text-blue-600 hover:bg-slate-50 transition flex items-center justify-center rounded relative group cursor-pointer"
            title="Use current GPS location" // i18n-ignore
          >
            {isLocating ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-500" />
            ) : (
              <Compass className="w-3.5 h-3.5 text-slate-500 hover:text-blue-600 animate-pulse" />
            )}
            <span className="absolute bottom-full right-0 mb-1.5 opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity bg-slate-800 text-white text-[9px] px-1.5 py-0.5 rounded font-sans whitespace-nowrap shadow-lg z-50">
              {isLocating ? "Retrieving GPS..." : "Get Location"}
            </span>
          </button>
        </div>
      </div>

      {isOpen && (predictions.length > 0 || isLoading || errorState) && (
        <div 
          className="absolute left-0 right-0 top-full mt-1 max-h-60 overflow-y-auto rounded-md border shadow-lg z-50"
          style={{
            backgroundColor: bgValue,
            borderColor: borderValue,
          }}
        >
          {isLoading && (
            <div className="px-3.5 py-3 text-xs font-semibold text-slate-500 flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-500" />
              <span>Loading predictions...</span>
            </div>
          )}
          {errorState && (
            <div className="px-3.5 py-3 text-xs font-semibold text-red-500">
              {errorState}
            </div>
          )}
          {!isLoading && !errorState && predictions.length === 0 && activeQuery.trim().length >= 2 && (
            <div className="px-3.5 py-3 text-xs font-semibold text-slate-500">
              No places found
            </div>
          )}
          {!isLoading && !errorState && predictions.map((pred, idx) => {
            const placeId = pred.placePrediction?.placeId || pred.place_id;
            const text = pred.placePrediction?.text?.toString() || pred.description || "";
            return (
              <div
                key={placeId}
                onClick={() => handleSelectPrediction(pred)}
                className={`px-3.5 py-2 text-xs font-semibold cursor-pointer flex items-center gap-2 transition ${
                  focusedIndex === idx 
                    ? "bg-slate-100 dark:bg-slate-800" 
                    : "hover:bg-slate-100 dark:hover:bg-slate-800"
                }`}
                style={{ color: actualTextValue }}
              >
                <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <span className="truncate">{text}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const AdminCostPanel = ({ apiFetch, currentUser, settingsAppTheme = "dark" }: { apiFetch: any; currentUser: any; settingsAppTheme?: string }) => {
  const [activeTab, setActiveTab] = useState<"diagnostics" | "earnings" | "moderation" | "developers" | "contact_removals">("diagnostics");
  const [stats, setStats] = useState<any>(null);
  const [loadingStats, setLoadingStats] = useState<boolean>(true);
  const [message, setMessage] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Diagnostics states
  const [ipInput, setIpInput] = useState<string>("");
  const [banReason, setBanReason] = useState<string>("Cost exceeding limit / Script abuse");
  const [banDuration, setBanDuration] = useState<number>(60);

  // Moderation states
  const [Businesses, setBusinesses] = useState<any[]>([]);
  const [loadingBusinesses, setLoadingBusinesses] = useState<boolean>(false);
  const [BusinessesearchQuery, setBusinessesearchQuery] = useState<string>("");

  // Developers states
  const [devs, setDevs] = useState<string[]>([]);
  const [loadingDevs, setLoadingDevs] = useState<boolean>(false);
  const [newDevEmail, setNewDevEmail] = useState<string>("");

  // Contact removals states
  const [removals, setRemovals] = useState<any[]>([]);
  const [loadingRemovals, setLoadingRemovals] = useState<boolean>(false);
  const [selectedRemovalId, setSelectedRemovalId] = useState<string | null>(null);

  const fetchStats = async () => {
    try {
      setLoadingStats(true);
      setErrorMsg(null);
      const res = await apiFetch("/api/admin/usage-summary");
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      } else {
        const err = await res.json();
        setErrorMsg(err.error || "Failed to load usage diagnostics.");
      }
    } catch (e: any) {
      setErrorMsg(e.message || "Failed to contact diagnostics service.");
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchBusinesses = async (searchQuery?: string) => {
    try {
      setLoadingBusinesses(true);
      setErrorMsg(null);
      const url = searchQuery?.trim()
        ? `/api/admin/Businesses?search=${encodeURIComponent(searchQuery.trim())}`
        : "/api/admin/Businesses";
      const res = await apiFetch(url);
      if (res.ok) {
        const data = await res.json();
        setBusinesses(data.Businesses || []);
      } else {
        const err = await res.json();
        setErrorMsg(err.error || "Failed to load CRM Businesses.");
      }
    } catch (e: any) {
      setErrorMsg(e.message || "Failed to contact moderation services.");
    } finally {
      setLoadingBusinesses(false);
    }
  };

  const fetchDevelopers = async () => {
    try {
      setLoadingDevs(true);
      setErrorMsg(null);
      const res = await apiFetch("/api/admin/developers");
      if (res.ok) {
        const data = await res.json();
        setDevs(data.developers || []);
      } else {
        const err = await res.json();
        setErrorMsg(err.error || "Failed to load developer access list.");
      }
    } catch (e: any) {
      setErrorMsg(e.message || "Failed to contact access control server.");
    } finally {
      setLoadingDevs(false);
    }
  };

  const fetchRemovals = async () => {
    try {
      setLoadingRemovals(true);
      setErrorMsg(null);
      const res = await apiFetch("/api/admin/removals");
      if (res.ok) {
        const data = await res.json();
        setRemovals(data.removals || []);
      } else {
        const err = await res.json();
        setErrorMsg(err.error || "Failed to load removal requests.");
      }
    } catch (e: any) {
      setErrorMsg(e.message || "Failed to contact removal service.");
    } finally {
      setLoadingRemovals(false);
    }
  };

  const handleChangeRemovalStatus = async (id: string, newStatus: string) => {
    try {
      setErrorMsg(null);
      const res = await apiFetch("/api/admin/removals/status", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, status: newStatus }),
      });
      if (res.ok) {
        setMessage(`Updated request status to ${newStatus}`);
        fetchRemovals();
      } else {
        const err = await res.json();
        setErrorMsg(err.error || "Failed to update status.");
      }
    } catch (e: any) {
      setErrorMsg(e.message || "Failed to submit status update.");
    }
  };

  useEffect(() => {
    setMessage(null);
    setErrorMsg(null);
    if (activeTab === "moderation") {
      fetchBusinesses();
    } else if (activeTab === "developers") {
      fetchDevelopers();
    } else if (activeTab === "contact_removals") {
      fetchRemovals();
    } else if (activeTab === "earnings") {
      fetchStats();
    }
  }, [activeTab]);

  const toggleFlag = async (featureKey: string, enabled: boolean) => {
    try {
      const res = await apiFetch("/api/admin/toggle-feature", {
        method: "POST",
        body: JSON.stringify({ featureKey, enabled })
      });
      if (res.ok) {
        setMessage(`Feature safety switch modified.`);
        fetchStats();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const liftBan = async (ip: string) => {
    try {
      const res = await apiFetch("/api/admin/ip-unban", {
        method: "POST",
        body: JSON.stringify({ ip })
      });
      if (res.ok) {
        setMessage(`Suspension lifted for IP ${ip}.`);
        fetchStats();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const applyBan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ipInput.trim()) return;
    try {
      const res = await apiFetch("/api/admin/ip-ban", {
        method: "POST",
        body: JSON.stringify({ ip: ipInput.trim(), durationMinutes: banDuration, reason: banReason })
      });
      if (res.ok) {
        setMessage(`Suspension applied for IP ${ipInput.trim()}.`);
        setIpInput("");
        fetchStats();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleBlockBusiness = async (Business: any) => {
    try {
      const res = await apiFetch("/api/admin/Businesses/block", {
        method: "POST",
        body: JSON.stringify({ BusinessId: Business.id, BusinessDetails: Business })
      });
      if (res.ok) {
        setMessage(`Business successfully blocked and excluded.`);
        fetchBusinesses();
      } else {
        const err = await res.json();
        setErrorMsg(err.error || "Failed to block Business.");
      }
    } catch (e: any) {
      setErrorMsg(e.message || "Failed to block Business.");
    }
  };

  const handleUnblockBusiness = async (BusinessId: string) => {
    try {
      const res = await apiFetch("/api/admin/Businesses/unblock", {
        method: "POST",
        body: JSON.stringify({ BusinessId })
      });
      if (res.ok) {
        setMessage(`Business unblocked successfully.`);
        fetchBusinesses();
      } else {
        const err = await res.json();
        setErrorMsg(err.error || "Failed to unblock Business.");
      }
    } catch (e: any) {
      setErrorMsg(e.message || "Failed to unblock Business.");
    }
  };

  const handBusinessdDeveloper = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDevEmail.trim()) return;
    try {
      const res = await apiFetch("/api/admin/developers/add", {
        method: "POST",
        body: JSON.stringify({ email: newDevEmail.trim() })
      });
      if (res.ok) {
        setMessage(`Developer registration successful.`);
        setNewDevEmail("");
        fetchDevelopers();
      } else {
        const err = await res.json();
        setErrorMsg(err.error || "Failed to register developer.");
      }
    } catch (e: any) {
      setErrorMsg(e.message || "Connection failure registering developer.");
    }
  };

  const handleRemoveDeveloper = async (email: string) => {
    try {
      const res = await apiFetch("/api/admin/developers/remove", {
        method: "POST",
        body: JSON.stringify({ email })
      });
      if (res.ok) {
        setMessage(`Developer access revoked.`);
        fetchDevelopers();
      } else {
        const err = await res.json();
        setErrorMsg(err.error || "Failed to revoke developer.");
      }
    } catch (e: any) {
      setErrorMsg(e.message || "Connection failure revoking developer.");
    }
  };

  const handleResetSandbox = async () => {
    if (!window.confirm("Are you sure you want to reset all sandbox simulation logs? This action is irreversible.")) return;
    try {
      const res = await apiFetch("/api/admin/reset-sandbox", { method: "POST" });
      if (res.ok) {
        setMessage("Sandbox simulation reset successfully.");
        fetchStats();
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="space-y-6 font-sans">
      {/* Dashboard Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-slate-200 pb-5 gap-4">
        <div>
          <h2 className="text-xl font-black text-slate-900 inline-flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-rose-600" /> Platform Admin Command Center
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Dynamic database role provisioning, live Stripe transaction ledgers, and business moderation controls.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={handleResetSandbox} 
            className="px-3 py-1.5 hover:bg-slate-100 text-slate-600 text-xs font-semibold rounded border border-slate-200 transition cursor-pointer"
          >
            Reset Logs
          </button>
          <button 
            onClick={() => {
              if (activeTab === "diagnostics" || activeTab === "earnings") fetchStats();
              else if (activeTab === "moderation") fetchBusinesses();
              else if (activeTab === "developers") fetchDevelopers();
              else if (activeTab === "contact_removals") fetchRemovals();
            }} 
            className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold rounded transition cursor-pointer shadow-xs"
          >
            Refresh View
          </button>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="flex flex-wrap gap-1 bg-slate-100 p-1 rounded-xl">
        <button
          onClick={() => {
            setActiveTab("diagnostics");
            fetchStats();
          }}
          className={`flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-lg transition-all cursor-pointer ${
            activeTab === "diagnostics"
              ? "bg-white text-slate-900 shadow-sm"
              : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
          }`}
        >
          <Activity className="w-4 h-4 text-rose-500" /> Unit Economy & Safety
        </button>
        <button
          onClick={() => {
            setActiveTab("earnings");
            fetchStats();
          }}
          className={`flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-lg transition-all cursor-pointer ${
            activeTab === "earnings"
              ? "bg-white text-slate-900 shadow-sm"
              : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
          }`}
        >
          <DollarSign className="w-4 h-4 text-emerald-500" /> Earned Revenue
        </button>
        <button
          onClick={() => {
            setActiveTab("moderation");
            fetchBusinesses();
          }}
          className={`flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-lg transition-all cursor-pointer ${
            activeTab === "moderation"
              ? "bg-white text-slate-900 shadow-sm"
              : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
          }`}
        >
          <Ban className="w-4 h-4 text-indigo-500" /> Business Moderation
        </button>
        <button
          onClick={() => {
            setActiveTab("developers");
            fetchDevelopers();
          }}
          className={`flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-lg transition-all cursor-pointer ${
            activeTab === "developers"
              ? "bg-white text-slate-900 shadow-sm"
              : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
          }`}
        >
          <Users className="w-4 h-4 text-amber-500" /> Dev Access Control
        </button>
        <button
          onClick={() => {
            setActiveTab("contact_removals");
            fetchRemovals();
          }}
          className={`flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-lg transition-all cursor-pointer ${
            activeTab === "contact_removals"
              ? "bg-white text-slate-900 shadow-sm"
              : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
          }`}
        >
          <UserMinus className="w-4 h-4 text-rose-500" /> Contact Support
        </button>
      </div>

      {/* Notifications banner */}
      {message && (
        <div className={`border text-xs px-4 py-3 rounded-lg flex items-center justify-between transition-all ${
          settingsAppTheme === "sunset"
            ? "bg-[#faf6f0] border-emerald-300 text-[#2d251e]"
            : settingsAppTheme === "ocean"
            ? "bg-[#f0f7f9] border-emerald-300 text-[#0b2533]"
            : settingsAppTheme === "light"
            ? "bg-emerald-50 border-emerald-200 text-emerald-900"
            : "bg-slate-900 border-emerald-800 text-emerald-200"
        }`}>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
            <span>{message}</span>
          </div>
          <button onClick={() => setMessage(null)} className="hover:opacity-100 opacity-60 font-extrabold text-sm cursor-pointer">×</button>
        </div>
      )}

      {errorMsg && (
        <div className={`border text-xs px-4 py-3 rounded-lg flex items-center justify-between transition-all ${
          settingsAppTheme === "sunset"
            ? "bg-[#faf6f0] border-rose-300 text-[#2d251e]"
            : settingsAppTheme === "ocean"
            ? "bg-[#f0f7f9] border-rose-300 text-[#0b2533]"
            : settingsAppTheme === "light"
            ? "bg-rose-50 border-rose-200 text-rose-900"
            : "bg-slate-900 border-rose-800 text-rose-200"
        }`}>
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-rose-600 dark:text-rose-400 shrink-0" />
            <span>{errorMsg}</span>
          </div>
          <button onClick={() => setErrorMsg(null)} className="hover:opacity-100 opacity-60 font-extrabold text-sm cursor-pointer">×</button>
        </div>
      )}

      {/* DIAGNOSTICS TAB CONTENT */}
      {activeTab === "diagnostics" && (
        <div className="space-y-6">
          {loadingStats ? (
            <div className="flex flex-col items-center justify-center p-12 bg-white rounded-lg border border-slate-200">
              <Loader2 className="w-8 h-8 text-rose-500 animate-spin mb-2" />
              <p className="text-slate-500 text-sm">Querying cost center analytics...</p>
            </div>
          ) : (
            <>
              {/* Gross Stats */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
                  <div className="text-xs text-slate-500 font-medium uppercase tracking-wider">Gross Cost Incurred</div>
                  <div className="text-2xl font-black text-slate-900 mt-1">${(stats?.stats?.totalCost || 0).toFixed(4)}</div>
                  <p className="text-[10px] text-slate-400 mt-1">Live model query billings</p>
                </div>
                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
                  <div className="text-xs text-emerald-600 font-medium uppercase tracking-wider">Estimated Cost Saved</div>
                  <div className="text-2xl font-black text-emerald-600 mt-1">${(stats?.stats?.costSaved || 0).toFixed(4)}</div>
                  <p className="text-[10px] text-slate-400 mt-1">Via cached duplicate checks</p>
                </div>
                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
                  <div className="text-xs text-indigo-600 font-medium uppercase tracking-wider">API Cache Hit Rate</div>
                  <div className="text-2xl font-black text-indigo-600 mt-1">{(stats?.stats?.cacheHitRate || 0).toFixed(1)}%</div>
                  <p className="text-[10px] text-slate-400 mt-1">
                    {stats?.stats?.cacheHits || 0} Hits / {stats?.stats?.totalCalls || 0} Calls
                  </p>
                </div>
                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
                  <div className="text-xs text-slate-500 font-medium uppercase tracking-wider">Net ROI Multiplier</div>
                  <div className="text-2xl font-black text-slate-900 mt-1">
                    {stats?.stats?.totalCost > 0 ? `${(stats.stats.costSaved / stats.stats.totalCost).toFixed(1)}x` : "0.0x"}
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1">Efficiency metric ratio</p>
                </div>
              </div>

              {/* Switches & IP blocklist */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Active API Switches */}
                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs">
                  <h3 className="text-sm font-bold text-slate-800 mb-3 border-b pb-2">Global Feature Safety Switches</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between text-xs">
                      <div className="pr-4">
                        <div className="font-semibold text-slate-700">AI Model Generation (Gemini API)</div>
                        <div className="text-slate-400 text-[11px] mt-0.5">Enables outreaches, website audits, and matchmaking insights.</div>
                      </div>
                      <button
                        onClick={() => toggleFlag("isGeminiEnabled", !stats?.featureFlags?.isGeminiEnabled)}
                        className={`px-3 py-1 rounded text-xs font-bold cursor-pointer transition shrink-0 ${
                          stats?.featureFlags?.isGeminiEnabled ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200" : "bg-rose-100 text-rose-800 hover:bg-rose-200"
                        }`}
                      >
                        {stats?.featureFlags?.isGeminiEnabled ? "ACTIVE" : "SUSPENDED"}
                      </button>
                    </div>
                    <div className="flex items-center justify-between text-xs pt-3 border-t">
                      <div className="pr-4">
                        <div className="font-semibold text-slate-700">Google Search Tools / Grounded Crawl</div>
                        <div className="text-slate-400 text-[11px] mt-0.5">Allows crawler to search live maps to locate contact emails.</div>
                      </div>
                      <button
                        onClick={() => toggleFlag("isGoogleSearchEnabled", !stats?.featureFlags?.isGoogleSearchEnabled)}
                        className={`px-3 py-1 rounded text-xs font-bold cursor-pointer transition shrink-0 ${
                          stats?.featureFlags?.isGoogleSearchEnabled ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200" : "bg-rose-100 text-rose-800 hover:bg-rose-200"
                        }`}
                      >
                        {stats?.featureFlags?.isGoogleSearchEnabled ? "ACTIVE" : "SUSPENDED"}
                      </button>
                    </div>
                    <div className="flex items-center justify-between text-xs pt-3 border-t">
                      <div className="pr-4">
                        <div className="font-semibold text-slate-700">Web scraper / Auditor Crawler</div>
                        <div className="text-slate-400 text-[11px] mt-0.5">Direct outbound HTTP scans to parse SEO metadata configurations.</div>
                      </div>
                      <button
                        onClick={() => toggleFlag("isWebsiteAuditEnabled", !stats?.featureFlags?.isWebsiteAuditEnabled)}
                        className={`px-3 py-1 rounded text-xs font-bold cursor-pointer transition shrink-0 ${
                          stats?.featureFlags?.isWebsiteAuditEnabled ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200" : "bg-rose-100 text-rose-800 hover:bg-rose-200"
                        }`}
                      >
                        {stats?.featureFlags?.isWebsiteAuditEnabled ? "ACTIVE" : "SUSPENDED"}
                      </button>
                    </div>
                  </div>
                </div>

                {/* IP Block Controls */}
                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs">
                  <h3 className="text-sm font-bold text-slate-800 mb-3 border-b pb-2">IP Block & Scraper Prevention</h3>
                  <form onSubmit={applyBan} className="space-y-3">
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <label className="block text-slate-500 mb-1">Target IP</label>
                        <input
                          type="text"
                          placeholder="e.g. 192.168.1.1"
                          value={ipInput}
                          onChange={(e) => setIpInput(e.target.value)}
                          className="w-full px-2 py-1.5 border border-slate-300 rounded text-slate-800 font-mono"
                        />
                      </div>
                      <div>
                        <label className="block text-slate-500 mb-1">Duration (min)</label>
                        <select
                          value={banDuration}
                          onChange={(e) => setBanDuration(Number(e.target.value))}
                          className="w-full px-2 py-1.5 border border-slate-300 rounded text-slate-800 bg-white"
                        >
                          <option value={15}>15 Minutes {/* i18n-ignore */}</option>
                          <option value={60}>1 Hour {/* i18n-ignore */}</option>
                          <option value={1440}>24 Hours {/* i18n-ignore */}</option>
                        </select>
                      </div>
                    </div>
                    <div className="text-xs">
                      <label className="block text-slate-500 mb-1">Suspension Trigger/Reason</label>
                      <input
                        type="text"
                        placeholder="Reason description..." // i18n-ignore
                        value={banReason}
                        onChange={(e) => setBanReason(e.target.value)}
                        className="w-full px-2 py-1.5 border border-slate-300 rounded text-slate-800"
                      />
                    </div>
                    <button type="submit" className="w-full py-1.5 bg-rose-600 hover:bg-rose-700 text-white font-semibold text-xs rounded transition cursor-pointer">
                      Apply Suspension Block
                    </button>
                  </form>

                  {/* Suspended list */}
                  <div className="mt-4">
                    <div className="text-xs font-semibold text-slate-600 mb-1.5">Suspended IP Nodes ({stats?.suspendedIps?.length || 0})</div>
                    {(!stats?.suspendedIps || stats.suspendedIps.length === 0) ? (
                      <div className="text-[10px] text-slate-400 italic">No active IP network blocks.</div>
                    ) : (
                      <div className="max-h-24 overflow-y-auto border border-slate-100 rounded p-1 space-y-1 font-mono text-[10px]">
                        {stats.suspendedIps.map((b: any) => (
                          <div key={b.ip} className="flex items-center justify-between bg-slate-50 p-1.5 rounded font-sans">
                            <span className="text-rose-700 font-semibold">{b.ip} <span className="text-slate-400 font-normal">({b.reason})</span></span>
                            <button onClick={() => liftBan(b.ip)} className="text-[9px] px-2 text-rose-600 hover:text-indigo-600 font-bold transition">LIFT</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Audit Trail */}
              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs">
                <h3 className="text-sm font-bold text-slate-800 mb-3 border-b pb-2">Real-Time Cost Audit Trail</h3>
                <div className="overflow-x-auto max-h-72 overflow-y-auto">
                  <table className="w-full text-xs font-sans text-slate-600 border-collapse">
                    <thead>
                      <tr className="bg-slate-50 text-slate-500 font-semibold border-b text-left">
                        <th className="p-2">Timestamp</th>
                        <th className="p-2">Feature</th>
                        <th className="p-2">Provider/Source</th>
                        <th className="p-2 text-center">Cache</th>
                        <th className="p-2 text-right">Est. Cost</th>
                        <th className="p-2">Plan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(!stats?.recentEvents || stats.recentEvents.length === 0) ? (
                        <tr>
                          <td colSpan={6} className="p-4 text-center text-slate-400 italic">No usage events registered in this session.</td>
                        </tr>
                      ) : (
                        stats.recentEvents.map((e: any) => (
                          <tr key={e.id} className="border-b hover:bg-slate-50">
                            <td className="p-2 text-slate-400 text-[11px] font-mono">{new Date(e.timestamp).toLocaleTimeString()}</td>
                            <td className="p-2 font-medium text-slate-800">{e.feature_name}</td>
                            <td className="p-2">{e.api_provider}</td>
                            <td className="p-2 text-center">
                              {e.cache_hit ? (
                                <span className="bg-emerald-50 text-emerald-800 px-1.5 py-0.5 rounded font-bold text-[9px] uppercase">HIT</span>
                              ) : (
                                <span className="bg-amber-50 text-amber-800 px-1.5 py-0.5 rounded font-bold text-[9px] uppercase">MISS</span>
                              )}
                            </td>
                            <td className="p-2 text-right font-mono font-semibold text-slate-800">
                              {e.estimated_cost === 0 ? "$0.0000" : `$${e.estimated_cost.toFixed(4)}`}
                            </td>
                            <td className="p-2">
                              <span className="text-[10px] font-bold uppercase">{e.subscription_plan}</span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* EARNINGS TAB CONTENT */}
      {activeTab === "earnings" && (
        <div className="space-y-6">
          {loadingStats ? (
            <div className="flex flex-col items-center justify-center p-12 bg-white rounded-lg border border-slate-200">
              <Loader2 className="w-8 h-8 text-emerald-500 animate-spin mb-2" />
              <p className="text-slate-500 text-sm">Querying financial statements...</p>
            </div>
          ) : (
            <>
              {/* Financial Dashboard Widgets */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs flex items-center justify-between">
                  <div>
                    <div className="text-xs text-slate-500 font-medium uppercase tracking-wider">Total Website Earnings</div>
                    <div className="text-3xl font-black text-emerald-600 mt-1">${stats?.finances?.totalEarnings || 0}</div>
                    <p className="text-[10px] text-slate-400 mt-1">Sponsor ads + Pro upgrades</p>
                  </div>
                  <div className="bg-emerald-50 p-3 rounded-full text-emerald-600">
                    <DollarSign className="w-6 h-6" />
                  </div>
                </div>

                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs flex items-center justify-between">
                  <div>
                    <div className="text-xs text-slate-500 font-medium uppercase tracking-wider">Active Pro Subscribers</div>
                    <div className="text-3xl font-black text-indigo-600 mt-1">{stats?.finances?.proSubscribersCount || 0} Users</div>
                    <p className="text-[10px] text-slate-400 mt-1">Billed monthly at $49/mo</p>
                  </div>
                  <div className="bg-indigo-50 p-3 rounded-full text-indigo-600">
                    <Users className="w-6 h-6" />
                  </div>
                </div>

                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs flex items-center justify-between">
                  <div>
                    <div className="text-xs text-slate-500 font-medium uppercase tracking-wider">Est. Monthly Recurring (MRR)</div>
                    <div className="text-3xl font-black text-slate-900 mt-1">${stats?.finances?.mrrEstimate || 0}</div>
                    <p className="text-[10px] text-slate-400 mt-1">Stable pipeline value prediction</p>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-full text-slate-600">
                    <CreditCard className="w-6 h-6" />
                  </div>
                </div>
              </div>

              {/* Transactions list */}
              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs">
                <div className="flex items-center justify-between border-b pb-3 mb-4">
                  <h3 className="text-sm font-bold text-slate-800">Financial Revenue ledger (Live Sandbox + Real Stripe)</h3>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-bold uppercase">SECURED</span>
                </div>

                <div className="overflow-x-auto max-h-96">
                  <table className="w-full text-xs font-sans text-slate-600 border-collapse">
                    <thead>
                      <tr className="bg-slate-50 text-slate-500 font-semibold border-b text-left">
                        <th className="p-2.5">User Email / Payer ID</th>
                        <th className="p-2.5">Service Product</th>
                        <th className="p-2.5">Date Paid</th>
                        <th className="p-2.5 text-center">Status</th>
                        <th className="p-2.5 text-right">Amount Received</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!stats?.finances?.transactions || stats.finances.transactions.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="p-4 text-center text-slate-400 italic">No revenue transactions recorded.</td>
                        </tr>
                      ) : (
                        stats.finances.transactions.map((tx: any) => (
                          <tr key={tx.id} className="border-b hover:bg-slate-50">
                            <td className="p-2.5 font-medium text-slate-900">{tx.user}</td>
                            <td className="p-2.5 text-slate-500">{tx.type}</td>
                            <td className="p-2.5 text-slate-400">{new Date(tx.timestamp).toLocaleString()}</td>
                            <td className="p-2.5 text-center">
                              <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase ${
                                tx.status === "active" || tx.status === "succeeded" || tx.status === "paid"
                                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                  : "bg-amber-50 text-amber-700 border border-amber-200"
                              }`}>
                                {tx.status || "succeeded"}
                              </span>
                            </td>
                            <td className="p-2.5 text-right font-bold text-slate-950">${Number(tx.amount).toFixed(2)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Business MODERATION TAB CONTENT */}
      {activeTab === "moderation" && (
        <div className="space-y-6">
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b pb-4 mb-4">
              <div>
                <h3 className="text-sm font-bold text-slate-800">Scraped Businesses Moderation</h3>
                <p className="text-[11px] text-slate-400 mt-0.5">Ban or unban specific businesses globally to keep crawled CRM repositories safe.</p>
              </div>
              <div className="flex items-center gap-2 max-w-md w-full">
                <div className="relative flex-1">
                  <input
                    type="text"
                    placeholder="Search name, category, or address..." // i18n-ignore
                    value={BusinessesearchQuery}
                    onChange={(e) => setBusinessesearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        fetchBusinesses(BusinessesearchQuery);
                      }
                    }}
                    className="w-full px-3 py-1.5 border border-slate-300 rounded text-xs text-slate-800 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-hidden"
                  />
                  {BusinessesearchQuery && (
                    <button
                      onClick={() => {
                        setBusinessesearchQuery("");
                        fetchBusinesses("");
                      }}
                      className="absolute right-2 top-1.5 text-slate-400 hover:text-slate-600 text-[10px] font-bold"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => fetchBusinesses(BusinessesearchQuery)}
                  className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded text-xs font-bold cursor-pointer transition whitespace-nowrap shadow-3xs"
                  title="Query the database for all matching businesses" // i18n-ignore
                >
                  Search DB
                </button>
              </div>
            </div>

            {loadingBusinesses ? (
              <div className="flex flex-col items-center justify-center p-12">
                <Loader2 className="w-8 h-8 text-indigo-500 animate-spin mb-2" />
                <p className="text-slate-500 text-xs">Querying database business collections...</p>
              </div>
            ) : (
              <div className="overflow-x-auto max-h-[480px] border border-slate-100 rounded-lg">
                <table className="w-full min-w-[950px] text-xs font-sans text-slate-600 border-collapse">
                  <thead>
                    <tr className="bg-slate-50 text-slate-500 font-semibold border-b text-left">
                      <th className="p-3">Company/Business Name</th>
                      <th className="p-3">Category / Industry</th>
                      <th className="p-3">Location / Address</th>
                      <th className="p-3 text-center">Stage</th>
                      <th className="p-3 text-center">Safety State</th>
                      <th className="p-3 text-right">Moderator Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Businesses.filter((l: any) => {
                      const isBlocked = l.isBlocked || l.pipeline_stage === "Blocked" || l.pipelineStage === "Blocked";
                      if (!BusinessesearchQuery && !isBlocked) return false;
                      if (!BusinessesearchQuery) return true;
                      const q = BusinessesearchQuery.toLowerCase();
                      return (
                        l.name?.toLowerCase().includes(q) ||
                        l.address?.toLowerCase().includes(q) ||
                        l.category?.toLowerCase().includes(q)
                      );
                    }).length === 0 ? (
                      <tr>
                        <td colSpan={6} className="p-6 text-center text-slate-400 italic">No businesses matching your search criteria.</td>
                      </tr>
                    ) : (
                      Businesses
                        .filter((l: any) => {
                          const isBlocked = l.isBlocked || l.pipeline_stage === "Blocked" || l.pipelineStage === "Blocked";
                          if (!BusinessesearchQuery && !isBlocked) return false;
                          if (!BusinessesearchQuery) return true;
                          const q = BusinessesearchQuery.toLowerCase();
                          return (
                            l.name?.toLowerCase().includes(q) ||
                            l.address?.toLowerCase().includes(q) ||
                            l.category?.toLowerCase().includes(q)
                          );
                        })
                        .map((Business: any) => (
                          <tr key={Business.id} className="border-b hover:bg-slate-50">
                            <td className="p-3 font-semibold text-slate-900">
                              <div>{Business.name}</div>
                              {Business.website && (
                                <a href={Business.website} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-500 hover:underline">
                                  {Business.website}
                                </a>
                              )}
                            </td>
                            <td className="p-3 text-slate-500 text-[11px]">{Business.category || "General Business"}</td>
                            <td className="p-3 text-slate-400 text-[11px] max-w-xs truncate">{Business.address || "Unknown"}</td>
                            <td className="p-3 text-center font-bold">{Business.pipelineStage || Business.pipeline_stage || "New"}</td>
                            <td className="p-3 text-center">
                              {Business.isBlocked || Business.pipeline_stage === "Blocked" || Business.pipelineStage === "Blocked" ? (
                                <span className="bg-rose-100 text-rose-800 border border-rose-200 px-2 py-0.5 rounded text-[9px] font-black uppercase">
                                  BLOCKED
                                </span>
                              ) : (
                                <span className="bg-emerald-50 text-emerald-800 border border-emerald-100 px-2 py-0.5 rounded text-[9px] font-bold uppercase">
                                  COMPLIANT
                                </span>
                              )}
                            </td>
                            <td className="p-3 text-right">
                              {Business.isBlocked || Business.pipeline_stage === "Blocked" || Business.pipelineStage === "Blocked" ? (
                                <button
                                  onClick={() => handleUnblockBusiness(Business.id)}
                                  className="px-2 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-[10px] font-extrabold rounded transition cursor-pointer"
                                >
                                  UNBAN Business
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleBlockBusiness(Business)}
                                  className="px-2 py-1 bg-rose-50 hover:bg-rose-100 text-rose-700 text-[10px] font-extrabold rounded transition cursor-pointer"
                                >
                                  BLOCK Business
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* DEVELOPER ACCESS CONTROL CONTENT */}
      {activeTab === "developers" && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Left side: Add form */}
            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs h-fit">
              <h3 className="text-sm font-bold text-slate-800 border-b pb-2 mb-3">Grant Developer Access</h3>
              <p className="text-xs text-slate-500 mb-4">Adding an account email will immediately grant full administrative rights, bypassing hardcoding checks.</p>
              
              <form onSubmit={handBusinessdDeveloper} className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Developer User Email</label>
                  <input
                    type="email"
                    required
                    placeholder="e.g. partner@biznearyou.com"
                    value={newDevEmail}
                    onChange={(e) => setNewDevEmail(e.target.value)}
                    className="w-full px-3 py-1.5 border border-slate-300 rounded text-xs text-slate-800"
                  />
                </div>
                <button type="submit" className="w-full py-1.5 bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs rounded transition flex items-center justify-center gap-1.5 cursor-pointer">
                  <UserPlus className="w-3.5 h-3.5" /> Authorize Developer
                </button>
              </form>
            </div>

            {/* Right side: Developer List */}
            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs md:col-span-2">
              <h3 className="text-sm font-bold text-slate-800 border-b pb-2 mb-3">Authorized Developer Accounts</h3>
              
              {loadingDevs ? (
                <div className="flex flex-col items-center justify-center p-12">
                  <Loader2 className="w-8 h-8 text-amber-500 animate-spin mb-2" />
                  <p className="text-slate-500 text-xs">Querying authorized accounts...</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {devs.length === 0 ? (
                    <div className="text-xs text-slate-400 italic">No developer accounts in database. Default email has absolute bypass rights.</div>
                  ) : (
                    devs.map((dev: any) => {
                      const emailStr = typeof dev === "string" ? dev : dev.email;
                      const roleStr = typeof dev === "string" ? "admin" : (dev.role || "dev");
                      const userIdStr = typeof dev === "string" ? "" : (dev.userId || "");
                      const isSelf = emailStr?.toLowerCase() === currentUser?.email?.toLowerCase();
                      
                      return (
                        <div key={emailStr} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                              <span className="text-xs font-bold text-slate-800">{emailStr}</span>
                              <span className="text-[9px] px-1.5 py-0.5 bg-indigo-100 text-indigo-800 border border-indigo-200 rounded font-bold uppercase">{roleStr}</span>
                              {isSelf && (
                                <span className="text-[9px] px-1.5 py-0.5 bg-rose-100 text-rose-800 border border-rose-200 rounded font-black uppercase">Current You</span>
                              )}
                            </div>
                            {userIdStr && (
                              <div className="text-[10px] text-slate-400 font-mono">UUID: {userIdStr}</div>
                            )}
                          </div>
                          {!isSelf && (
                            <button
                              onClick={() => handleRemoveDeveloper(emailStr)}
                              className="text-[10px] text-rose-600 hover:text-rose-900 font-bold flex items-center gap-1 cursor-pointer transition p-1"
                            >
                              <UserMinus className="w-3.5 h-3.5" /> Revoke
                            </button>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* SYSTEM CONTACT REMOVALS TAB CONTENT */}
      {activeTab === "contact_removals" && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Left Column: Requests List */}
            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs md:col-span-2 h-fit">
              <div className="flex items-center justify-between border-b pb-3 mb-4">
                <h3 className="text-sm font-bold text-slate-800">Contact Support & Blacklist Requests ({removals.length})</h3>
                <span className="text-[10px] text-rose-500 font-extrabold uppercase">Legal & Compliance Queue</span>
              </div>

              {loadingRemovals ? (
                <div className="flex flex-col items-center justify-center p-12">
                  <Loader2 className="w-8 h-8 text-rose-500 animate-spin mb-2" />
                  <p className="text-slate-500 text-xs">Loading compliance database...</p>
                </div>
              ) : removals.length === 0 ? (
                <div className="p-8 text-center text-slate-400 italic text-xs">No removal requests filed yet. Clean database!</div>
              ) : (
                <div className="space-y-3 max-h-[480px] overflow-y-auto pr-1">
                  {removals.map((rem: any) => (
                    <div
                      key={rem.id}
                      onClick={() => setSelectedRemovalId(rem.id === selectedRemovalId ? null : rem.id)}
                      className={`p-4 rounded-xl border transition cursor-pointer text-left ${
                        selectedRemovalId === rem.id
                          ? "bg-rose-50/50 border-rose-200 ring-1 ring-rose-200"
                          : "bg-white hover:bg-slate-50 border-slate-200"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="text-xs font-black text-slate-900 flex items-center gap-2 flex-wrap">
                            <span className="flex items-center gap-1 text-slate-900 font-extrabold">
                              <UserMinus className="w-3.5 h-3.5 text-rose-500 shrink-0" />
                              {rem.name}
                            </span>
                            <span className="text-[9px] text-slate-500 font-medium">
                              {rem.email}
                            </span>
                            {rem.company && (
                              <span className="text-[8.5px] px-1.5 py-0.2 rounded bg-slate-100 text-slate-700 border border-slate-200 font-bold uppercase">
                                {rem.company}
                              </span>
                            )}
                            <span className={`text-[8px] px-1.5 py-0.2 rounded font-black uppercase tracking-wide border ${
                              rem.status === "approved"
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                : rem.status === "rejected"
                                ? "bg-rose-50 text-rose-700 border-rose-200"
                                : "bg-amber-50 text-amber-700 border-amber-200"
                            }`}>
                              {rem.status || "new"}
                            </span>
                          </div>
                          <p className="text-[11px] text-slate-500 mt-1.5 leading-relaxed font-medium">
                            Reason: <span className="text-slate-750 font-semibold">{rem.reason || "privacy_request"}</span>
                          </p>
                          <div className="flex items-center gap-4 text-[10px] text-slate-400 mt-2 font-medium">
                            <span>Listing: <span className="text-rose-600 font-semibold truncate max-w-xs block sm:inline">{rem.listingUrl}</span></span>
                            <span>On: {new Date(rem.submittedAt || Date.now()).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>

                      {/* Expanded View */}
                      {selectedRemovalId === rem.id && (
                        <div className="mt-4 pt-4 border-t border-slate-100 space-y-4 text-xs text-slate-700 cursor-default" onClick={(e) => e.stopPropagation()}>
                          <div>
                            <div className="font-extrabold text-slate-800 uppercase tracking-wider text-[9px] mb-1">Opt-out Message:</div>
                            <div className="bg-slate-50 p-2.5 rounded border border-slate-200 font-medium text-slate-700 whitespace-pre-wrap font-mono text-[11px]">
                              {rem.message || "No comments or details provided."}
                            </div>
                          </div>

                          {/* Action Bar inside Request */}
                          <div className="flex items-center justify-between gap-2 pt-2 border-t flex-wrap">
                            <span className="text-[10px] text-slate-400 font-semibold">Change Compliance Status:</span>
                            <div className="flex gap-1.5">
                              <button
                                onClick={() => handleChangeRemovalStatus(rem.id, "new")}
                                className={`px-2.5 py-1 text-[10px] font-extrabold rounded cursor-pointer transition ${
                                  (rem.status || "new") === "new" ? "bg-slate-200 text-slate-850 border border-slate-300" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                                }`}
                              >
                                NEW
                              </button>
                              <button
                                onClick={() => handleChangeRemovalStatus(rem.id, "approved")}
                                className={`px-2.5 py-1 text-[10px] font-extrabold rounded cursor-pointer transition ${
                                  rem.status === "approved" ? "bg-emerald-100 text-emerald-850 border border-emerald-200" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                                }`}
                              >
                                APPROVE & BLACKLIST
                              </button>
                              <button
                                onClick={() => handleChangeRemovalStatus(rem.id, "rejected")}
                                className={`px-2.5 py-1 text-[10px] font-extrabold rounded cursor-pointer transition ${
                                  rem.status === "rejected" ? "bg-rose-100 text-rose-850 border border-rose-200" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                                }`}
                              >
                                REJECT
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right Column: Information panel */}
            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs h-fit space-y-4">
              <h3 className="text-sm font-bold text-slate-800 border-b pb-2">Opt-Out & Compliance Center</h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                Under CCPA, GDPR, and global data protection guidelines, any individual or business listed has the absolute legal right to demand records removal.
              </p>
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 space-y-3 text-xs">
                <div className="font-bold text-slate-700">Compliance Action Rules:</div>
                <ul className="list-disc list-inside text-slate-500 space-y-1.5 text-[11px]">
                  <li><strong className="text-emerald-700 font-extrabold">Approve & Blacklist</strong>: Permanently flags the record's email/domain as blacklisted and deletes it from active scraper results.</li>
                  <li><strong className="text-rose-700 font-extrabold">Reject</strong>: Flags the request as malicious, spam, or invalid (e.g. competitor trying to hide active public profiles).</li>
                  <li>Simulated email alerts are dispatched immediately upon submission to prevent audit friction. {/* i18n-ignore */}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

let activeSessionRefreshPromise: Promise<string | null> | null = null;

const translateWithFallback = (t: any, key: string, defaultValue: string): string => {
  const result = t(key, defaultValue);
  if (result === key) {
    return defaultValue;
  }
  return result;
};

const getLegendItems = (mode: 'score' | 'outreach' | 'saved' | 'hiring', t: any) => {
  const items: { color: string; border: string; label: string }[] = [];
  
  if (mode !== 'saved') {
    items.push({
      color: "#10b981",
      border: "#047857",
      label: translateWithFallback(t, "legend_selected", "Selected / Active")
    });
  }

  switch (mode) {
    case 'score':
      items.push(
        { color: "#f59e0b", border: "#b45309", label: translateWithFallback(t, "legend_hot", "Hot Business (≥80)") },
        { color: "#3b82f6", border: "#1d4ed8", label: translateWithFallback(t, "legend_warm", "Warm Business (50-79)") },
        { color: "#94a3b8", border: "#475569", label: translateWithFallback(t, "legend_cold", "Cold Business (<50)") }
      );
      break;
    case 'outreach':
      items.push(
        { color: "#a855f7", border: "#7e22ce", label: translateWithFallback(t, "legend_won", "Won Status") },
        { color: "#f43f5e", border: "#be123c", label: translateWithFallback(t, "legend_interested", "Interested Status") },
        { color: "#3b82f6", border: "#1d4ed8", label: translateWithFallback(t, "legend_warm", "Standard / Uncontacted") }
      );
      break;
    case 'saved':
      items.push(
        { color: "#10b981", border: "#047857", label: translateWithFallback(t, "legend_saved_status", "Saved Lead") },
        { color: "#64748b", border: "#475569", label: translateWithFallback(t, "legend_unsaved_status", "Unsaved Lead") }
      );
      break;
    case 'hiring':
      items.push(
        { color: "#6366f1", border: "#4f46e5", label: translateWithFallback(t, "legend_hiring_status", "Actively Hiring") },
        { color: "#475569", border: "#334155", label: translateWithFallback(t, "legend_standard_status", "Standard Lead") }
      );
      break;
  }
  return items;
};

const getLegendModeTitle = (mode: 'score' | 'outreach' | 'saved' | 'hiring', t: any) => {
  switch (mode) {
    case 'score':
      return translateWithFallback(t, "legend_mode_score", "Prospect Score Hierarchy");
    case 'outreach':
      return translateWithFallback(t, "legend_mode_outreach", "Pipeline Status Phase");
    case 'saved':
      return translateWithFallback(t, "legend_mode_saved", "Durable Bookmarks");
    case 'hiring':
      return translateWithFallback(t, "legend_mode_hiring", "Recruitment Activity");
  }
};

const ADVANCED_SEARCH_STATE_VERSION = "v5";

export default function App() {
  const { t, locale, changeLanguage, formatDate, formatCurrency, missingKeys, languages } = useTranslation();
  const uniqueMissingKeys = Array.from(new Set(missingKeys));

  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  // Client-side routing helper
  const navigateTo = (path: string) => {
    setIsSettingsModalOpen(false);
    const { langCode, hasLangPrefix } = parseRouteLanguage(currentPath || window.location.pathname);
    const { routeWithoutLang: cleanRequestedPath } = parseRouteLanguage(path);
    const redirectMatch = getStandardizedLocationRedirect(cleanRequestedPath);
    let targetPath = redirectMatch ? redirectMatch.targetPath : cleanRequestedPath;

    if (hasLangPrefix && langCode !== "en" && !parseRouteLanguage(path).hasLangPrefix) {
      targetPath = buildLanguageUrl(targetPath, langCode);
    }

    window.history.pushState({}, "", targetPath);
    setCurrentPath(targetPath);
    window.scrollTo(0, 0);
    if (!targetPath.startsWith("/settings") && targetPath !== "/account") {
      setWorkspaceMode((prev) => (prev === "settings" ? "dashboard" : prev));
    }
  };

  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // Standardized Location Redirect effect
  useEffect(() => {
    const { routeWithoutLang: cleanPath, langCode, hasLangPrefix } = parseRouteLanguage(currentPath);
    const redirectMatch = getStandardizedLocationRedirect(cleanPath);
    if (redirectMatch) {
      const search = window.location.search;
      let target = redirectMatch.targetPath + search;
      if (hasLangPrefix && langCode !== "en" && !parseRouteLanguage(target).hasLangPrefix) {
        target = buildLanguageUrl(target, langCode);
      }
      if (target !== currentPath) {
        window.history.replaceState({}, document.title, target);
        setCurrentPath(target);
      }
    }
  }, [currentPath]);

  // SEO Metadata dynamic updater
  useEffect(() => {
    const config = getSeoConfig(currentPath);

    const title = config.title;
    const description = config.description;
    const canonicalUrl = config.canonicalUrl;
    const robots = config.robots || "index, follow";

    // Update DOM Title
    document.title = title;

    // Helper to upsert meta tags
    const upsertMeta = (name: string, content: string, isProperty = false) => {
      const attrName = isProperty ? "property" : "name";
      let meta = document.querySelector(`meta[${attrName}="${name}"]`);
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute(attrName, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", content);
    };

    upsertMeta("description", description);
    
    // Canonical Link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl);

    // Open Graph
    upsertMeta("og:title", title, true);
    upsertMeta("og:description", description, true);
    upsertMeta("og:url", canonicalUrl, true);
    upsertMeta("og:type", "website", true);
    upsertMeta("og:image", "https://getbiznearyou.com/favicon-512.png", true);

    // Twitter Cards
    upsertMeta("twitter:card", "summary_large_image");
    upsertMeta("twitter:title", title);
    upsertMeta("twitter:description", description);
    upsertMeta("twitter:image", "https://getbiznearyou.com/favicon-512.png");

    // Robots
    upsertMeta("robots", robots);

    // Schema JSON-LD (Filter out BreadcrumbList to prevent duplicate BreadcrumbList schemas)
    let scriptSchema = document.getElementById("seo-schema-jsonld");
    let cleanedSchema = config.schemaJson ? JSON.parse(JSON.stringify(config.schemaJson)) : undefined;
    if (cleanedSchema) {
      if (cleanedSchema["@type"] === "BreadcrumbList") {
        cleanedSchema = undefined;
      } else if (Array.isArray(cleanedSchema["@graph"])) {
        cleanedSchema["@graph"] = cleanedSchema["@graph"].filter((item: any) => item && item["@type"] !== "BreadcrumbList");
        if (cleanedSchema["@graph"].length === 0) {
          cleanedSchema = undefined;
        }
      }
    }

    if (cleanedSchema) {
      if (!scriptSchema) {
        scriptSchema = document.createElement("script");
        scriptSchema.setAttribute("id", "seo-schema-jsonld");
        scriptSchema.setAttribute("type", "application/ld+json");
        document.head.appendChild(scriptSchema);
      }
      scriptSchema.textContent = JSON.stringify(cleanedSchema);
    } else if (scriptSchema) {
      scriptSchema.remove();
    }
  }, [currentPath]);

  // User authenticated session
  const [currentUser, setCurrentUser] = useState<{ id: string; email: string; token: string; provider?: string; user_metadata?: any; created_at?: string; joinedAt?: string } | null>(() => {
    try {
      const stored = localStorage.getItem("Business_scout_user");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const [currentUserRole, setCurrentUserRole] = useState<string>("user");
  const [activeSessionUserEmail, setActiveSessionUserEmail] = useState<string | null>(null);
  const [oauthCallbackHandled, setOauthCallbackHandled] = useState<boolean>(false);

  // Authentication Status Flow State
  const [authStatus, setAuthStatus] = useState<"loading" | "logged_out" | "authenticated">("loading");
  const [isAuthChecking, setIsAuthChecking] = useState(true);
  const [isInitialAuthSettled, setIsInitialAuthSettled] = useState(false);
  const [authTimeoutOccurred, setAuthTimeoutOccurred] = useState(false);
  const [authCheckingSlow, setAuthCheckingSlow] = useState(false);
  const [supabaseSessionActive, setSupabaseSessionActive] = useState<boolean>(false);
  const [isEmailVerified, setIsEmailVerified] = useState<boolean>(true);
  const [checkingEmailVerification, setCheckingEmailVerification] = useState<boolean>(false);
  const [resendStatus, setResendStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [resendMessage, setResendMessage] = useState<string>("");
  const [cooldownTime, setCooldownTime] = useState<number>(0);

  useEffect(() => {
    if (cooldownTime <= 0) return;
    const timer = setInterval(() => {
      setCooldownTime(prev => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldownTime]);

  const lastProcessedSessionRef = useRef<string>("");
  const lastProcessedUserRef = useRef<string>("");
  const lastLoadedStartupUserIdRef = useRef<string>("");

  // Explicit Deterministic Auth Bootstrap with 3.5s timeout
  const performAuthBootstrap = useCallback(async () => {
    const startTime = performance.now();
    console.log(`[BIZ_BOOT] React mounted: ${Math.round(startTime)}ms`);
    console.log(`[BIZ_AUTH] auth check started`);

    setIsAuthChecking(true);
    setAuthTimeoutOccurred(false);
    setAuthCheckingSlow(false);

    // Set 2s timer to flag slow auth check for private route UI feedback
    const slowTimer = setTimeout(() => {
      setAuthCheckingSlow(true);
    }, 2000);

    // Race getSession against a safe 3.5s timeout
    const timeoutPromise = new Promise<{ session: null; isTimeout: true }>((resolve) => {
      setTimeout(() => resolve({ session: null, isTimeout: true }), 3500);
    });

    try {
      const result = await Promise.race([
        supabase.auth.getSession().then(({ data, error }) => {
          if (error) throw error;
          return { session: data.session, isTimeout: false };
        }),
        timeoutPromise
      ]);

      clearTimeout(slowTimer);
      const elapsed = Math.round(performance.now() - startTime);
      console.log(`[BIZ_AUTH] getSession resolved: ${elapsed}ms`, {
        isTimeout: result.isTimeout,
        hasSession: Boolean(result.session)
      });

      if (result.isTimeout) {
        console.warn(`[BIZ_AUTH] Session check timed out after ${elapsed}ms. Proceeding in degraded state.`);
        setAuthTimeoutOccurred(true);
        // Timeout is NOT proof of sign-out. Do not clear local session or log out!
        if (currentUser) {
          setAuthStatus("authenticated");
        } else {
          setAuthStatus("logged_out");
        }
      } else if (result.session) {
        setSupabaseSessionActive(true);
        const userMetadata = result.session.user.user_metadata || {};
        const userEmail = result.session.user.email || userMetadata.email || "";
        setActiveSessionUserEmail(userEmail);

        const appMetadata = result.session.user.app_metadata || {};
        const provider = appMetadata.provider || "";
        const isOAuth = Boolean(provider && provider !== "manual" && provider !== "email");
        const isConfirmed = Boolean(
          result.session.user.email_confirmed_at ||
          (result.session.user as any).confirmed_at ||
          userMetadata.email_verified ||
          userMetadata.is_verified ||
          userMetadata.email_confirmed ||
          isOAuth
        );

        const loggedUser = {
          id: result.session.user.id,
          email: userEmail,
          token: result.session.access_token,
          provider: provider,
          user_metadata: userMetadata,
          email_verified: isConfirmed,
          email_confirmed_at: result.session.user.email_confirmed_at || (result.session.user as any).confirmed_at || null,
          created_at: result.session.user.created_at || (result.session.user as any).created_at || null
        };

        if (isConfirmed) setIsEmailVerified(true);
        localStorage.setItem("Business_scout_user", JSON.stringify(loggedUser));
        setCurrentUser(loggedUser);
        setAuthStatus("authenticated");
      } else {
        // Definitive signed-out state returned by Supabase
        setSupabaseSessionActive(false);
        setActiveSessionUserEmail(null);
        localStorage.removeItem("Business_scout_user");
        setCurrentUser(null);
        setAuthStatus("logged_out");
      }
    } catch (err) {
      clearTimeout(slowTimer);
      console.error(`[BIZ_AUTH] Session check error:`, err);
      setAuthTimeoutOccurred(true);
      if (currentUser) {
        setAuthStatus("authenticated");
      } else {
        setAuthStatus("logged_out");
      }
    } finally {
      setIsAuthChecking(false);
      setIsInitialAuthSettled(true);
    }
  }, [currentUser]);

  useEffect(() => {
    performAuthBootstrap();
  }, [performAuthBootstrap]);

  useEffect(() => {
    if (isAuthChecking) {
      setAuthStatus("loading");
    } else if (currentUser) {
      setAuthStatus("authenticated");
    } else {
      setAuthStatus("logged_out");
    }
  }, [isAuthChecking, currentUser]);

  // Synchronize isSignUpMode based on current route
  useEffect(() => {
    if (currentPath === "/signup") {
      setIsSignUpMode(true);
    } else if (currentPath === "/login") {
      setIsSignUpMode(false);
    }
  }, [currentPath]);

  // Centralized Route Guarding Effect
  useEffect(() => {
    if (isAuthChecking) return;

    // Do NOT run automatic login / logout redirects when on password recovery or auth callback routes
    if (currentPath === "/reset-password" || currentPath.startsWith("/auth/callback")) {
      return;
    }

    if (authStatus === "authenticated") {
      // Authenticated users visiting home root (/), login, or signup redirect to dashboard workspace
      if (currentPath === "/" || currentPath === "/login" || currentPath === "/signup") {
        navigateTo("/dashboard");
      }
    } else if (authStatus === "logged_out") {
      // Unauthenticated users visiting private routes redirect to login
      const privatePrefixes = ["/dashboard", "/app", "/settings", "/saved", "/account"];
      const isPrivateRoute = privatePrefixes.some(p => currentPath === p || currentPath.startsWith(p + "/"));
      if (isPrivateRoute) {
        navigateTo("/login");
      }
    }
  }, [isAuthChecking, authStatus, currentPath]);

  const handleLinkIdentity = async (provider: string) => {
    try {
      const { data, error } = await supabase.auth.linkIdentity({
        provider: provider as any,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          skipBrowserRedirect: true,
        }
      });
      if (error) throw error;
      if (data?.url) {
        const popup = window.open(
          data.url,
          "oauth_popup",
          "width=600,height=700,status=no,menubar=no,toolbar=no"
        );
        if (!popup) {
          window.location.href = data.url;
        }
      }
    } catch (err: any) {
      console.error("Link identity failed:", err);
      showToast("Failed to link identity: " + (err.message || "Unknown error"), "error");
    }
  };

  const handleResendVerification = async () => {
    if (cooldownTime > 0) return;
    setResendStatus("sending");
    setResendMessage("");
    try {
      const res = await apiFetch("/api/auth/resend-verification", { method: "POST" });
      const data = await res.json();
      if (res.ok && data.success) {
        setResendStatus("sent");
        setResendMessage(data.message || "A new verification email has been sent. Please check your inbox.");
        setCooldownTime(60);
      } else {
        setResendStatus("error");
        setResendMessage(data.message || "Failed to resend verification email.");
        if (data.error === "COOLDOWN_ACTIVE") {
          setCooldownTime(60);
        }
      }
    } catch (err: any) {
      setResendStatus("error");
      setResendMessage("An error occurred while trying to resend the verification email.");
    }
  };

  useEffect(() => {
    // Evict any legacy/stale search caches to ensure the newest version of Advanced Search is loaded
    const currentCachePrefix = `map_hire_places_cache_${ADVANCED_SEARCH_STATE_VERSION}`;
    let clearedCount = 0;
    
    // Clear legacy sessionStorage keys
    for (const key of Object.keys(sessionStorage)) {
      if (
        (key.startsWith("map_hire_places_cache_") && !key.startsWith(currentCachePrefix)) ||
        key.startsWith("biznearyou_search_cache_") ||
        key.startsWith("advanced_search_") ||
        key.includes("places_cache")
      ) {
        sessionStorage.removeItem(key);
        clearedCount++;
      }
    }
    
    // Clear legacy localStorage keys
    for (const key of Object.keys(localStorage)) {
      if (
        (key.startsWith("map_hire_places_cache_") && !key.startsWith(currentCachePrefix)) ||
        key.startsWith("biznearyou_search_cache_") ||
        key.startsWith("advanced_search_") ||
        key.includes("places_cache")
      ) {
        localStorage.removeItem(key);
        clearedCount++;
      }
    }
    
    if (clearedCount > 0) {
      console.log(`[ADVANCED_SEARCH_UPGRADE] Cleared ${clearedCount} stale legacy search caches to force newest version.`);
    }
  }, []);

  useEffect(() => {
    if (authStatus === "authenticated") {
      apiFetch("/api/auth/role")
        .then((res) => {
          if (res.ok) {
            return res.json();
          }
          throw new Error("Failed to load user security role.");
        })
        .then((data) => {
          if (data.success && data.role) {
            setCurrentUserRole(data.role);
          }
        })
        .catch((err) => {
          console.warn("Dynamic role retrieval failed, using fallback:", err);
        });
    } else {
      setCurrentUserRole("user");
    }
  }, [authStatus]);

  useEffect(() => {
    if (authStatus === "authenticated" && currentPath === "/contact") {
      setWorkspaceMode("contact");
      setContactSubject(prev => prev || "Technical Support");
      window.history.replaceState({}, "", "/dashboard");
      setCurrentPath("/dashboard");
    }
  }, [authStatus, currentPath]);

  useEffect(() => {
    bizDebug("AUTH_LISTENER_INIT");

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      bizDebug("AUTH_STATE_CHANGE", { event, userId: session?.user?.id });

      if (event === "PASSWORD_RECOVERY") {
        bizDebug("PASSWORD_RECOVERY_EVENT", { event });
        if (session) {
          setSupabaseSessionActive(true);
          const userMetadata = session.user.user_metadata || {};
          const userEmail = session.user.email || userMetadata.email || "";
          setActiveSessionUserEmail(userEmail);
          const loggedUser = {
            id: session.user.id,
            email: userEmail,
            token: session.access_token,
            provider: "email",
            user_metadata: userMetadata,
            email_verified: true,
            email_confirmed_at: session.user.email_confirmed_at || null,
            created_at: session.user.created_at || null
          };
          setCurrentUser(loggedUser);
          setAuthStatus("authenticated");
        }
        setIsAuthChecking(false);
        setIsInitialAuthSettled(true);
        return;
      }

      const fingerprint = session 
        ? `${session.user.id}:${session.expires_at || 0}:${session.access_token ? "has_token" : "no_token"}` 
        : "unauthenticated";

      if (fingerprint === lastProcessedSessionRef.current) {
        bizDebug("AUTH_STATE_CHANGE_DUPLICATE_IGNORED", { event });
        setIsAuthChecking(false);
        setIsInitialAuthSettled(true);
        return;
      }

      const prevUser = lastProcessedUserRef.current;
      const currentUserUid = session?.user?.id || "";

      lastProcessedSessionRef.current = fingerprint;
      lastProcessedUserRef.current = currentUserUid;

      if (session) {
        setSupabaseSessionActive(true);
        setAuthTimeoutOccurred(false);
        
        const userMetadata = session.user.user_metadata || {};
        const userEmail = session.user.email || 
                          userMetadata.email || 
                          userMetadata.user_name || 
                          userMetadata.full_name || 
                          userMetadata.name || 
                          "";
        
        setActiveSessionUserEmail(userEmail);

        const appMetadata = session.user.app_metadata || {};
        const provider = appMetadata.provider || "";
        const isOAuth = Boolean(provider && provider !== "manual" && provider !== "email");
        const isConfirmed = Boolean(
          session.user.email_confirmed_at ||
          (session.user as any).confirmed_at ||
          userMetadata.email_verified ||
          userMetadata.is_verified ||
          userMetadata.email_confirmed ||
          isOAuth
        );

        const loggedUser = {
          id: session.user.id,
          email: userEmail,
          token: session.access_token,
          provider: provider,
          user_metadata: userMetadata,
          email_verified: isConfirmed,
          email_confirmed_at: session.user.email_confirmed_at || (session.user as any).confirmed_at || null,
          created_at: session.user.created_at || (session.user as any).created_at || null
        };

        if (isConfirmed) {
          setIsEmailVerified(true);
        }

        bizDebug("USER_LOADED_FROM_AUTH_STATE_CHANGE", { userId: loggedUser.id });
        localStorage.setItem("Business_scout_user", JSON.stringify(loggedUser));
        setCurrentUser(loggedUser);
        setAuthStatus("authenticated");

        // Only clear caches and reload user-scoped data if the user actually switched or logged in fresh
        if (prevUser !== currentUserUid) {
          bizDebug("USER_INITIAL_BOOTSTRAP", { userId: currentUserUid });
          setBusinesses([]);
          setSavedBusinesses([]);
          setHistory([]);
        } else {
          bizDebug("USER_CREDENTIALS_REFRESHED", { userId: currentUserUid });
        }
      } else if (event === "SIGNED_OUT") {
        setSupabaseSessionActive(false);
        bizDebug("AUTH_STATE_CHANGE_EMPTY_OR_SIGNED_OUT", { event });
        setActiveSessionUserEmail(null);
        localStorage.removeItem("Business_scout_user");
        localStorage.removeItem("first_signed_in_user_id");
        localStorage.removeItem("crm_lookup_history");
        setCurrentUser(null);
        setBusinesses([]);
        setSavedBusinesses([]);
        setHistory([]);
        setAuthStatus("logged_out");
      }
      setIsAuthChecking(false);
      setIsInitialAuthSettled(true);
    });

    return () => {
      bizDebug("AUTH_LISTENER_CLEANUP");
      subscription.unsubscribe();
    };
  }, []);

  const handleCallbackSuccess = useCallback((session: any) => {
    const provider = session.user?.app_metadata?.provider || "google";
    finalizeAuthenticatedSession(session, provider)
      .then(() => {
        const createdAt = session.user?.created_at;
        const lastSignInAt = session.user?.last_sign_in_at;
        const isNewOAuthUser = createdAt && lastSignInAt && 
          (Math.abs(new Date(lastSignInAt).getTime() - new Date(createdAt).getTime()) < 15000);
          
        if (isNewOAuthUser) {
          sessionStorage.setItem("registration_success", "true");
          navigateTo("/signup-success");
        } else {
          navigateTo("/login-success");
        }
        setOauthCallbackHandled(true);
        setIsAuthLoading(false);
      })
      .catch((err: any) => {
        console.error("Failed to finalize session after OAuth login:", err);
        setAuthError(err.message || "Failed to finalize session");
        setOauthCallbackHandled(true);
        setIsAuthLoading(false);
      });
  }, []);

  const handleCallbackFailure = useCallback((errMessage: string) => {
    setAuthError(errMessage);
    setCurrentUser(null);
    window.history.replaceState({}, document.title, "/");
    setOauthCallbackHandled(true);
    setIsAuthLoading(false);
  }, []);

  // Listen for popup OAuth messages
  useEffect(() => {
    const handleOAuthMessage = async (event: MessageEvent) => {
      const origin = event.origin;
      if (!origin.endsWith(".run.app") && !origin.includes("localhost")) {
        return;
      }

      if (event.data?.type === "OAUTH_AUTH_SUCCESS") {
        const session = event.data.session || event.data.data?.session;
        if (session) {
          console.log("Parent window received OAUTH_AUTH_SUCCESS with session:", session);
          try {
            const { data, error } = await supabase.auth.setSession({
              access_token: session.access_token,
              refresh_token: session.refresh_token,
            });
            if (error) throw error;
            if (data.session) {
              handleCallbackSuccess(data.session);
            } else {
              throw new Error("No session active after sync");
            }
          } catch (err: any) {
            console.error("Failed to sync session in parent:", err);
            handleCallbackFailure(err.message || "Failed to set session after popup auth.");
          }
        }
      } else if (event.data?.type === "OAUTH_AUTH_FAILURE") {
        console.error("Parent window received OAUTH_AUTH_FAILURE:", event.data.error);
        handleCallbackFailure(event.data.error || "OAuth failed in popup.");
      }
    };

    window.addEventListener("message", handleOAuthMessage);
    return () => {
      window.removeEventListener("message", handleOAuthMessage);
    };
  }, [handleCallbackSuccess, handleCallbackFailure]);

  const handleOAuthLogin = async (provider: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session || currentUser) {
        const existingEmail = session?.user?.email || currentUser?.email || "another user";
        console.warn("Blocking signInWithOAuth call since a session is already active.");
        setAuthError(`You are already signed in as ${existingEmail}. Sign out before switching accounts.`);
        setIsAuthLoading(false);
        return;
      }
    } catch (e) {
      console.warn("Failed to check active session prior to OAuth, proceeding with caution:", e);
    }

    setAuthError(null);
    setIsAuthLoading(true);
    try {
      // Use skipBrowserRedirect: true to get the authorize URL for popup use
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: provider as any,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          skipBrowserRedirect: true,
        },
      });
      if (error) throw error;

      if (data?.url) {
        console.log(`Successfully obtained OAuth URL for ${provider}:`, data.url);
        const popup = window.open(
          data.url,
          "oauth_popup",
          "width=600,height=700,status=no,menubar=no,toolbar=no"
        );
        if (!popup) {
          console.warn("Popup window blocked by browser, falling back to page redirect.");
          window.location.href = data.url;
        }
      } else {
        throw new Error("Failed to generate OAuth URL.");
      }
    } catch (err: any) {
      if (provider === "github") {
        console.error("[BIZ_ERROR] GITHUB_OAUTH_FAILED", {
          message: err.message || err,
          provider,
          redirectTo: `${window.location.origin}/auth/callback`,
        });
      } else {
        console.error("OAuth flow init failed:", err);
      }
      setAuthError(err.message || `Unable to start ${provider} authentication flow.`);
      setIsAuthLoading(false);
    }
  };

  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [authConfirmPassword, setAuthConfirmPassword] = useState("");
  const [isSignUpMode, setIsSignUpMode] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [signUpSuccessMessage, setSignUpSuccessMessage] = useState<string | null>(null);

  // States for resending verification link to users before login
  const [publicResendMessage, setPublicResendMessage] = useState<string | null>(null);
  const [isPublicResending, setIsPublicResending] = useState(false);

  const handlePublicResendVerification = async () => {
    if (!authEmail || !authEmail.includes("@")) {
      setPublicResendMessage("Please enter a valid email address first.");
      return;
    }
    setIsPublicResending(true);
    setPublicResendMessage(null);
    try {
      const data = await fetchJsonWithRetry("/api/auth/resend-verification-public", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: authEmail })
      });
      if (data && data.success) {
        setPublicResendMessage(data.message || "A verification email has been resent successfully!");
      } else {
        setPublicResendMessage(data?.error || data?.message || "Failed to resend verification email.");
      }
    } catch (err: any) {
      setPublicResendMessage("A network error occurred while requesting resend.");
    } finally {
      setIsPublicResending(false);
    }
  };

  const apiFetch = async (url: string, options: RequestInit = {}) => {
    let targetUrl = url;
    if (url.startsWith("/api/Businesses")) {
      targetUrl = url.replace("/api/Businesses", "/api/businesses");
    } else if (url.startsWith("/api/public/Businesses")) {
      targetUrl = url.replace("/api/public/Businesses", "/api/public/businesses");
    }

    try {
      const data = await apiRequest(targetUrl, {
        ...options,
        timeout: 20000
      });

      return {
        ok: true,
        status: 200,
        statusText: "OK",
        headers: new Headers({ "Content-Type": "application/json" }),
        json: async () => data,
        text: async () => typeof data === "string" ? data : JSON.stringify(data),
      } as any;
    } catch (err: any) {
      const status = err.status || 500;
      
      if (status === 401 && !targetUrl.includes("/api/auth/")) {
        console.warn(`[apiFetch] 401 Unauthorized for ${targetUrl}. Cleaning session...`);
        if (localStorage.getItem("Business_scout_user")) {
          setSupabaseSessionActive(false);
          localStorage.removeItem("Business_scout_user");
          setCurrentUser(null);
          setAuthStatus("logged_out");
          setAuthError("Session expired. Please sign in again.");
        }
      }

      return {
        ok: false,
        status,
        statusText: err.statusText || "Error",
        headers: new Headers({ "Content-Type": "application/json" }),
        json: async () => ({ error: err.message || "Request failed" }),
        text: async () => err.message || "Request failed",
      } as any;
    }
  };

  const checkVerificationStatus = useCallback(async () => {
    if (!currentUser) {
      setIsEmailVerified(true);
      return;
    }

    const isOAuth = Boolean(currentUser.provider && currentUser.provider !== "manual" && currentUser.provider !== "email");
    const isConfirmedInSession = Boolean(
      (currentUser as any).email_verified ||
      (currentUser as any).email_confirmed_at ||
      currentUser.user_metadata?.email_verified ||
      currentUser.user_metadata?.is_verified ||
      isOAuth
    );

    if (isConfirmedInSession) {
      setIsEmailVerified(true);
    }

    setCheckingEmailVerification(true);
    try {
      const res = await apiFetch("/api/auth/verification-status");
      if (res.ok) {
        const data = await res.json();
        const verified = Boolean(data.email_verified);
        setIsEmailVerified(verified);
        setCurrentUser(prev => {
          if (!prev) return prev;
          if ((prev as any).email_verified === verified) return prev;
          const updated = { ...prev, email_verified: verified };
          localStorage.setItem("Business_scout_user", JSON.stringify(updated));
          return updated;
        });
      } else {
        setIsEmailVerified(true);
      }
    } catch (err) {
      console.error("Failed to fetch email verification status:", err);
      setIsEmailVerified(true);
    } finally {
      setCheckingEmailVerification(false);
    }
  }, [currentUser, apiFetch]);

  useEffect(() => {
    if (authStatus === "authenticated" && currentUser) {
      checkVerificationStatus();
    } else {
      setIsEmailVerified(true);
    }
  }, [authStatus, currentUser?.id, currentPath]);

  const handleAuthSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    setSignUpSuccessMessage(null);
    setPublicResendMessage(null);

    const email = authEmail.trim();
    const password = authPassword.trim();

    if (!email || !password) {
      setAuthError("Please provide both an email address and a password.");
      return;
    }

    if (isSignUpMode) {
      if (password.length < 6) {
        setAuthError("To comply with security requirements, your password must contain at least 6 characters.");
        return;
      }
      if (password !== authConfirmPassword.trim()) {
        setAuthError("Passwords do not match. Please verify both fields.");
        return;
      }
    }

    setIsAuthLoading(true);
    try {
      const endpoint = isSignUpMode ? "/api/auth/signup" : "/api/auth/login";
      bizDebug(isSignUpMode ? "SIGNUP_START" : "LOGIN_START", { email });
      bizDebug(isSignUpMode ? "SIGNUP_CLICKED" : "LOGIN_CLICKED", { email });
      const data = await fetchJsonWithRetry(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (isSignUpMode) {
        bizDebug("SIGNUP_SUCCESS", { email });
        
        // Auto login with the returned session!
        if (data.session && data.session.access_token) {
          if (supabase && data.session.refresh_token) {
            const { data: setSessionData, error: setSessionError } =
              await supabase.auth.setSession({
                access_token: data.session.access_token,
                refresh_token: data.session.refresh_token,
              });
            if (setSessionError) throw setSessionError;
            
            const session = setSessionData.session || data.session;
            await finalizeAuthenticatedSession(session, "manual");
          } else {
            // Standalone/offline fallback
            const loggedUser = {
              id: data.user.id,
              email: data.user.email || email,
              token: data.session.access_token,
              provider: "manual",
              created_at: data.user.created_at || new Date().toISOString()
            };
            localStorage.setItem("Business_scout_user", JSON.stringify(loggedUser));
            setActiveSessionUserEmail(loggedUser.email);
            setCurrentUser(loggedUser);
            setAuthStatus("authenticated");
          }
        }
        
        sessionStorage.setItem("registration_success", "true");
        navigateTo("/signup-success");
        return;
      } else {
        if (data && (data.error || data.message) && !data.session) {
          throw new Error(data.message || data.error || "Login failed.");
        }

        if (!data?.session?.access_token) {
          throw new Error("Login failed to return a valid session.");
        }

        if (supabase && data.session.refresh_token) {
          const { data: setSessionData, error: setSessionError } =
            await supabase.auth.setSession({
              access_token: data.session.access_token,
              refresh_token: data.session.refresh_token,
            });

          if (setSessionError) throw setSessionError;

          if (!setSessionData.session?.access_token) {
            throw new Error("Manual login succeeded, but Supabase browser session was not created.");
          }

          const session = setSessionData.session;
          await finalizeAuthenticatedSession(session, "manual");
        } else {
          const loggedUser = {
            id: data.user?.id || "local_usr",
            email: data.user?.email || email,
            token: data.session.access_token,
            provider: "manual",
            created_at: data.user?.created_at || new Date().toISOString()
          };
          localStorage.setItem("Business_scout_user", JSON.stringify(loggedUser));
          setActiveSessionUserEmail(loggedUser.email);
          setCurrentUser(loggedUser);
          setAuthStatus("authenticated");
        }

        navigateTo("/login-success");

        bizDebug("MANUAL_LOGIN_SUPABASE_SESSION_CREATED", {
          userId: session.user.id,
          email: session.user.email || session.user.user_metadata?.email || "",
          hasAccessToken: Boolean(session.access_token),
          hasRefreshToken: Boolean(session.refresh_token)
        });

        bizDebug("LOGIN_SUCCESS", session.user);
      }
    } catch (err: any) {
      bizError(isSignUpMode ? "SIGNUP_ERROR" : "LOGIN_ERROR", { isSignUpMode, error: err.message || err });
      bizError("AUTH_SUBMIT_ERROR", { isSignUpMode, error: err.message || err });
      console.error("Auth action failed:", err);
      setAuthError(err.message || "An unexpected error occurred during auth transaction.");
    } finally {
      setIsAuthLoading(false);
    }
  };

  const handleLogout = () => {
    supabase.auth.signOut().catch(() => {});
    clearApiTokenCache();
    setSupabaseSessionActive(false);
    
    // Clear user-specific cached data (Rule 8) - Preserve guest theme preference settings_app_theme
    const itemsToRemove = [
      "Business_scout_user",
      "crm_lookup_history",
      "Business_scout_user_role",
      "dev_tools_enabled",
      "billing_is_free",
      "free_limit_reached",
      "settings_user_name",
      "settings_default_zipcode",
      "settings_max_results",
      "settings_bypass_paywall",
      "settings_enterprise_title",
      "settings_map_style",
      "biznearyou_user_stats_v1",
      "first_signed_in_user_id"
    ];
    
    itemsToRemove.forEach(item => {
      try {
        localStorage.removeItem(item);
      } catch (e) {
        console.warn(`Failed to clear local storage item ${item}:`, e);
      }
    });

    setActiveSessionUserEmail(null);
    setCurrentUser(null);
    setBusinesses([]);
    setSavedBusinesses([]);
    setHistory([]);
    // Do not clear stable global Google Maps configurations to prevent re-initialization loader parameter mismatches.
  };

  const [apiKey, setApiKey] = useState<string>(() => googleMapsConfig.apiKey);
  const [googleMapsMapId, setGoogleMapsMapId] = useState<string>(() => googleMapsConfig.mapId);
  const [isLoadingApiKey, setIsLoadingApiKey] = useState<boolean>(true);
  const [configStatus, setConfigStatus] = useState<"idle" | "loading" | "loaded" | "error">("idle");
  const [configErrorMessage, setConfigErrorMessage] = useState<string | null>(null);

  // Search parameters
  const [zipInput, setZipInput] = useState<string>("");
  const [travelMode, setTravelMode] = useState<"DRIVING" | "WALKING" | "BICYCLING" | "TRANSIT">("DRIVING");
  const [maxCommuteMinutes, setMaxCommuteMinutes] = useState<number>(30);
  const [sortByCommute, setSortByCommute] = useState<boolean>(false);
  const [keyword, setKeyword] = useState<string>("");
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [showSearchHowItWorks, setShowSearchHowItWorks] = useState<boolean>(false);
  const [showDevSearchDiagnostics, setShowDevSearchDiagnostics] = useState<boolean>(false);
  const [recentSearches, setRecentSearches] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem("recent_business_searches") || "[]");
    } catch {
      return [];
    }
  });
  const [autocompleteSuggestions, setAutocompleteSuggestions] = useState<any[]>([]);
  const [isFetchingAutocomplete, setIsFetchingAutocomplete] = useState<boolean>(false);

  useEffect(() => {
    const trimmed = keyword.trim();
    if (!trimmed) {
      setAutocompleteSuggestions([]);
      return;
    }

    if (authStatus !== "authenticated" || !supabaseSessionActive) {
      setAutocompleteSuggestions([]);
      return;
    }

    const delayDebounce = setTimeout(async () => {
      setIsFetchingAutocomplete(true);
      try {
        const params = new URLSearchParams({
          query: trimmed,
          location: zipInput
        });
        const res = await apiFetch(`/api/search-autocomplete?${params.toString()}`);
        if (res.ok) {
          const data = await res.json();
          setAutocompleteSuggestions(data.suggestions || []);
        }
      } catch (err) {
        console.error("Error fetching search autocomplete:", err);
      } finally {
        setIsFetchingAutocomplete(false);
      }
    }, 200);

    return () => clearTimeout(delayDebounce);
  }, [keyword, zipInput]);

  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isLocating, setIsLocating] = useState<boolean>(false);
  const [accuracyNotice, setAccuracyNotice] = useState<boolean>(false);
  
  // Coordinate references for distance calculations
  const [searchCenter, setSearchCenter] = useState<{ lat: number; lng: number } | null>(null);
  const [userCoords, setUserCoords] = useState<{ lat: number; lng: number } | null>(null);
  
  // App state
  const [Businesses, setBusinesses] = useState<BusinessBusiness[]>([]);
  const [searchPage, setSearchPage] = useState<number>(1);
  const [searchTotalPages, setSearchTotalPages] = useState<number>(1);
  const [searchLimit, setSearchLimit] = useState<number>(20);
  const [searchSessionId, setSearchSessionId] = useState<string | null>(null);
  const [searchTotalResults, setSearchTotalResults] = useState<number>(0);
  const [searchSummary, setSearchSummary] = useState<{
    candidateCount: number;
    dedupedCount: number;
    rankedCount: number;
    duplicateCount?: number;
    didYouMean?: string | null;
    relatedSearches?: string[];
    debugLogs?: string[];
    searchProgressDetails?: {
      step: number;
      ringName: string;
      tilesSearched: number;
      newUniqueFound: number;
      totalSourced: number;
    }[];
  } | null>(null);
  const [savedBusinesses, setSavedBusinesses] = useState<BusinessBusiness[]>([]);
  
  // Stable result ranking lock state
  const [stableSearchResults, setStableSearchResults] = useState<BusinessBusiness[]>([]);
  const [stableSnapshots, setStableSnapshots] = useState<RankedBusinessSnapshot[]>([]);
  const [stableSavedResults, setStableSavedResults] = useState<BusinessBusiness[]>([]);
  const [hasPendingBackgroundUpdates, setHasPendingBackgroundUpdates] = useState<boolean>(false);

  const rawSearchResultsRef = useRef<BusinessBusiness[]>([]);
  const lastOrderRef = useRef<{ order: string[]; sessionId: string; mode: AudienceModeId; sortBy: string } | null>(null);
  const isAllowedReorderActionRef = useRef<boolean>(false);
  const pipelineScrollRef = useRef<HTMLDivElement>(null);
  const lastSearchedLocationRef = useRef<string>("");
  const lastSearchedKeywordRef = useRef<string>("");
  const currentRequestIdRef = useRef<number>(0);
  const coordsUpdatedJustNowRef = useRef<boolean>(false);
  const [workspaceMode, setWorkspaceMode] = useState<"search" | "saved" | "history" | "jobs" | "pro_tools" | "docs" | "admin_cost" | "dashboard" | "contact" | "settings">("dashboard");

  const switchWorkspaceMode = (mode: "search" | "saved" | "history" | "jobs" | "pro_tools" | "docs" | "admin_cost" | "dashboard" | "contact" | "settings") => {
    setIsSettingsModalOpen(false);
    setWorkspaceMode(mode);
    if (currentPath.startsWith("/settings") || currentPath === "/account") {
      if (mode === "settings") {
        navigateTo("/settings/account");
      } else if (mode === "contact") {
        navigateTo("/contact");
      } else {
        navigateTo("/");
      }
    }
  };

  useEffect(() => {
    if (currentPath.startsWith("/settings") || currentPath === "/account") {
      setWorkspaceMode("settings");
    } else if (workspaceMode === "settings") {
      setWorkspaceMode("dashboard");
    }
  }, [currentPath]);
  const [audienceMode, setAudienceMode] = useState<AudienceModeId>(() => {
    try {
      const saved = localStorage.getItem("settings_audience_mode");
      return normalizeAudienceModeId(saved);
    } catch (e) {}
    return "job_seeker";
  });

  const handleSelectAudienceMode = async (modeId: AudienceModeId) => {
    const previousMode = audienceMode;
    const normalized = normalizeAudienceModeId(modeId);

    if (!isAudienceMode(normalized)) {
      console.error("Invalid audience mode value:", normalized);
      showToast(`Invalid audience mode preference: ${String(modeId)}`, "error");
      return;
    }

    if (normalized === previousMode && authStatus === "authenticated") {
      return;
    }

    const config = getAudienceModeConfig(normalized);

    // 1. Optimistic UI update
    setAudienceMode(normalized);
    try {
      localStorage.setItem("settings_audience_mode", normalized);
    } catch (e) {}

    // 1b. Re-rank active search or saved results for newly selected audience mode
    if (workspaceMode === "search" && rawSearchResultsRef.current.length > 0) {
      isAllowedReorderActionRef.current = true;
      const rankingTime = Date.now();
      const sessId = searchSessionId || `session_mode_${rankingTime}`;
      const { rankedSnapshots, sortedBusinesses: newSorted } = rankSearchResultsOnce(
        rawSearchResultsRef.current,
        normalized,
        resultsSortBy,
        searchCenter || userCoords,
        userCoords,
        keyword,
        sessId,
        rankingTime
      );

      if (process.env.NODE_ENV !== "production" && lastOrderRef.current) {
        verifyOrderStabilityDev(
          lastOrderRef.current.order,
          newSorted.map(b => b.id),
          "Audience Mode Intentional Switch",
          sessId
        );
      }

      lastOrderRef.current = {
        order: newSorted.map(b => b.id),
        sessionId: sessId,
        mode: normalized,
        sortBy: resultsSortBy
      };

      setStableSearchResults(newSorted);
      setStableSnapshots(rankedSnapshots);
      isAllowedReorderActionRef.current = false;
    } else if (workspaceMode === "saved" && savedBusinesses.length > 0) {
      const rankingTime = Date.now();
      const { sortedBusinesses: rankedSaved } = rankSearchResultsOnce(
        savedBusinesses,
        normalized,
        resultsSortBy,
        searchCenter || userCoords,
        userCoords,
        keyword,
        "saved_session",
        rankingTime
      );
      setStableSavedResults(rankedSaved);
    }

    // 2. Save to Supabase if user is authenticated
    if (authStatus === "authenticated" && supabaseSessionActive) {
      try {
        const {
          data: { user },
          error: userError,
        } = await supabase.auth.getUser();

        if (userError) throw userError;
        if (!user) throw new Error("User is not authenticated.");

        console.debug("Saving audience mode:", {
          selectedMode: normalized,
          type: typeof normalized,
        });

        const { error } = await supabase
          .from("user_settings")
          .upsert(
            {
              user_id: user.id,
              audience_mode: normalized,
              updated_at: new Date().toISOString(),
            },
            {
              onConflict: "user_id",
            }
          );

        if (error) {
          throw error;
        }

        showToast(`Audience mode changed to ${config.label}.`, "success");
      } catch (err: any) {
        console.error("Failed to save audience_mode to Supabase user_settings:", err);
        // Rollback on failure
        setAudienceMode(previousMode);
        try {
          localStorage.setItem("settings_audience_mode", previousMode);
        } catch (e) {}
        showToast("Failed to save audience mode preference. Rolled back changes.", "error");
      }
    } else {
      // Guest mode update
      showToast(`Audience mode changed to ${config.label}.`, "info");
    }
  };
  const [contactSubject, setContactSubject] = useState<string>("");
  const [selectedBusinessId, setSelectedBusinessId] = useState<string | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchStatusMessage, setSearchStatusMessage] = useState<string>("");
  const [resolvedLocationDisplay, setResolvedLocationDisplay] = useState<string>("");
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);

  const handleLocationChange = (value: string) => {
    setZipInput(value);

    // A manually edited location invalidates previously
    // resolved coordinates until the user chooses an
    // autocomplete result or performs another search.
    setSearchCenter(null);
    setUserCoords(null);
    setAccuracyNotice(false);
    setResolvedLocationDisplay("");
    setSearchError(null);
  };

  // Mobile responsive subviews and screen size tracking
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [mobileSearchSubView, setMobileSearchSubView] = useState<"form" | "results" | "map">("form");
  const [mobileSavedSubView, setMobileSavedSubView] = useState<"filters" | "content" | "map">("content");
  const [isMapFullscreen, setIsMapFullscreen] = useState<boolean>(false);

  // Save/restore scroll position of search page when map fullscreen toggles
  const savedScrollPosRef = useRef<number>(0);
  useEffect(() => {
    if (isMapFullscreen) {
      // Find the active scroll container on mobile
      const scroller = document.getElementById("search-controls") || document.getElementById("main-scrollable-container");
      if (scroller) {
        savedScrollPosRef.current = scroller.scrollTop;
      } else {
        savedScrollPosRef.current = window.scrollY;
      }
    } else {
      // Restore scroll
      setTimeout(() => {
        const scroller = document.getElementById("search-controls") || document.getElementById("main-scrollable-container");
        if (scroller) {
          scroller.scrollTop = savedScrollPosRef.current;
        } else {
          window.scrollTo(0, savedScrollPosRef.current);
        }
      }, 50);
    }
  }, [isMapFullscreen]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024; // Tailwind lg breakpoint is 1024px
      setIsMobile(mobile);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleHomepageSearch = (kw: string, loc: string) => {
    setKeyword(kw);
    setZipInput(loc);
    if (authStatus === "authenticated") {
      setWorkspaceMode("search");
      setMobileSearchSubView("results");
      navigateTo("/dashboard");
      setTimeout(() => {
        triggerBusinessesSearch(1, true);
      }, 50);
    } else {
      localStorage.setItem("pending_search_keyword", kw);
      localStorage.setItem("pending_search_location", loc);
      setIsSignUpMode(true);
      navigateTo("/signup");
    }
  };

  useEffect(() => {
    if (selectedBusinessId) {
      const found = Businesses.find(b => b.id === selectedBusinessId) || savedBusinesses.find(b => b.id === selectedBusinessId);
      if (found) {
        trackInteraction(selectedBusinessId, found.category, "view", found);
      }
    }
  }, [selectedBusinessId, Businesses, savedBusinesses]);

  // Perform Chicago date rollover check on window focus / tab visibility change
  useEffect(() => {
    let lastCheckedChicagoDate = "";
    
    const checkChicagoDateRollover = async () => {
      if (authStatus !== "authenticated") return;
      
      let nowChicagoDate = "";
      try {
        nowChicagoDate = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Chicago' });
      } catch (err) {
        nowChicagoDate = new Date().toISOString().slice(0, 10);
      }

      if (lastCheckedChicagoDate && lastCheckedChicagoDate === nowChicagoDate) return;
      lastCheckedChicagoDate = nowChicagoDate;

      try {
        await apiFetch("/api/quests/daily-check", { method: "POST" });
        window.dispatchEvent(new CustomEvent("biznearyou_quests_updated"));
      } catch (err) {
        console.warn("Failed visibility/focus Chicago quest check:", err);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkChicagoDateRollover();
      }
    };

    window.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", checkChicagoDateRollover);

    return () => {
      window.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", checkChicagoDateRollover);
    };
  }, [authStatus]);

  // Tag / Signal panel detail display states
  const [showAllSignals, setShowAllSignals] = useState<boolean>(false);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [showScoreBreakdown, setShowScoreBreakdown] = useState<boolean>(false);
  const [showScoreMathDebugId, setShowScoreMathDebugId] = useState<string | null>(null);
  const [selectedScoreTab, setSelectedScoreTab] = useState<'trust' | 'convenience' | 'hiring' | 'opportunity'>('trust');
  const [selectedExplainingTag, setSelectedExplainingTag] = useState<string | null>(null);
  const [showHelpGuide, setShowHelpGuide] = useState<boolean>(false);
  const [saveSearchStatus, setSaveSearchStatus] = useState<'idle' | 'loading' | 'success' | 'error' | 'duplicate'>('idle');
  const [copiedBusinessId, setCopiedBusinessId] = useState<string | null>(null);
  const [sharedBusinessLoading, setSharedBusinessLoading] = useState<boolean>(false);
  const [sharedBusinessNotFound, setSharedBusinessNotFound] = useState<boolean>(false);
  const [resultsSortBy, setResultsSortBy] = useState<"best_match" | "rating" | "distance" | "opportunity_score" | "Business_score">("best_match");
  const [showAudienceRankingExplanation, setShowAudienceRankingExplanation] = useState<boolean>(false);

  // Map marker styling/coloring mode state synchronized with legend and tools
  const [markerColorMode, setMarkerColorMode] = useState<'score' | 'outreach' | 'saved' | 'hiring'>('score');

  // Custom best match organic priority fine-tuners
  const [rankPrioritizeHiring, setRankPrioritizeHiring] = useState<boolean>(true);
  const [rankPrioritizeTransparency, setRankPrioritizeTransparency] = useState<boolean>(true);
  const [rankPrioritizeFlexibility, setRankPrioritizeFlexibility] = useState<boolean>(true);
  const [rankPrioritizeCommute, setRankPrioritizeCommute] = useState<boolean>(true);
  const [rankPrioritizeGrowthSecurity, setRankPrioritizeGrowthSecurity] = useState<boolean>(true);

  useEffect(() => {
    setShowAllSignals(false);
    setExpandedCategories({});
    setShowScoreBreakdown(false);
    setSelectedScoreTab('trust');
    setSelectedExplainingTag(null);
  }, [selectedBusinessId]);

  // Search, click, and crawl lookup history state
  const [history, setHistory] = useState<LookupHistoryItem[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(true);
  const [isSavingAll, setIsSavingAll] = useState<boolean>(false);

  const triggerQuestIncrement = async (eventType: string, amount: number = 1) => {
    if (!currentUser) return;
    try {
      const response = await apiFetch("/api/quests/increment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ eventType, amount })
      });
      if (response.ok) {
        const data = await response.json();
        if (data.newlyCompleted && Array.isArray(data.newlyCompleted) && data.newlyCompleted.length > 0) {
          data.newlyCompleted.forEach((comp: any) => {
            showToast(`🎉 Quest Completed: "${comp.title}"! +${comp.pointsAwarded || 20} XP awarded!`, "success");
          });
        }
        if (data.stats && data.stats.total_points !== undefined) {
          const local = getUserStats(currentUser?.id);
          local.total_points = data.stats.total_points;
          if (data.stats.lifetime_quests_completed !== undefined) {
            local.lifetime_quests_completed = data.stats.lifetime_quests_completed;
          }
          saveUserStats(local, currentUser?.id);
        }
        window.dispatchEvent(new CustomEvent("biznearyou_quests_updated", { detail: data }));
      }
    } catch (e) {
      console.warn("Failed to trigger quest increment:", e);
    }
  };

  // Append a lookup action to standard database and local storage history logs
  const addHistoryItem = async (item: Omit<LookupHistoryItem, "id" | "created_at">) => {
    // SECURITY & PRIVACY NOTICE: Raw crisis search queries are extremely sensitive medical/mental health records.
    // To protect user identity and meet stringent HIPAA/CCPA/GDPR guidelines, we must never persist or transmit
    // the exact raw crisis query keyword. We intercept any positive crisis matches locally here and replace them 
    // with standardized, non-identifiable resource strings.
    let safeItem = { ...item };
    if (safeItem.query_keyword) {
      const safety = detectCrisisSearch(safeItem.query_keyword);
      if (safety.severity !== "none") {
        safeItem.query_keyword = "Suicide Prevention & Support Resource Search";
        if (typeof safeItem.target_name === "string") {
          safeItem.target_name = "Support Resources Lookup (U.S. National Safety Grid)";
        }
      }
    }

    const newItem: LookupHistoryItem = {
      ...safeItem,
      id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2, 9),
      created_at: new Date().toISOString()
    };

    setHistory((prev) => {
      // De-clutter duplicate lookups of identical subcategories, contact pages, or clicked networks
      let filtered = prev;
      if (safeItem.lookup_type === "business_search") {
        filtered = prev.filter(h => !(h.lookup_type === "business_search" && h.query_keyword === safeItem.query_keyword && h.query_zipcode === safeItem.query_zipcode));
      } else if (safeItem.lookup_type === "contact_crawl") {
        filtered = prev.filter(h => !(h.lookup_type === "contact_crawl" && h.Business_id === safeItem.Business_id));
      } else if (safeItem.lookup_type === "social_link") {
        filtered = prev.filter(h => !(h.lookup_type === "social_link" && h.Business_id === safeItem.Business_id && h.target_name === safeItem.target_name));
      }
      const updated = [newItem, ...filtered].slice(0, 100);
      localStorage.setItem("crm_lookup_history", JSON.stringify(updated));
      return updated;
    });

    try {
      await apiFetch("/api/history", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newItem)
      });
      
      if (newItem.lookup_type === "business_search") {
        await triggerQuestIncrement("business_search", 1);
      } else if (newItem.lookup_type === "contact_crawl") {
        await triggerQuestIncrement("contact_lookup", 1);
      }
    } catch (e) {
      console.warn("History save to database bypassed or quest increment missed:", e);
    }
  };

  // Synchronously load historic tasks securely
  const loadHistory = async () => {
    const local = localStorage.getItem("crm_lookup_history");
    if (local) {
      try {
        setHistory(JSON.parse(local));
      } catch (e) {
        console.error("[SPECIFIC_FEATURE_ERROR] Failed parsing local crm lookup history:", e);
      }
    }

    try {
      const res = await apiFetch("/api/history");
      if (res.ok) {
        const data = await res.json();
        if (data.history && Array.isArray(data.history)) {
          setHistory(data.history);
          localStorage.setItem("crm_lookup_history", JSON.stringify(data.history));
        }
      }
    } catch (err) {
      console.warn("Could not retrieve remote search history:", err);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Social link click router logging specific search coordinates
  const handleSocialLookupClick = (network: string, url: string) => {
    if (!activeBusiness) return;
    addHistoryItem({
      lookup_type: 'social_link',
      Business_id: activeBusiness.id,
      Business_name: activeBusiness.name,
      target_name: `Clicked ${network} redirect link`,
      url: url
    });
  };

  const handlePullUpHistoryItem = (item: LookupHistoryItem) => {
    if (item.lookup_type === "business_search") {
      if (item.query_keyword) setKeyword(item.query_keyword);
      if (item.query_zipcode) setZipInput(item.query_zipcode);
      if (item.Businesses && item.Businesses.length > 0) {
        setBusinesses(item.Businesses);
        setSelectedBusinessId(item.Businesses[0].id);
        setWorkspaceMode("search");
        setSyncFeedback(`Restored ${item.Businesses.length} search results for "${item.query_keyword}" in ${item.query_zipcode} instantly (loaded from database)!`);
        setTimeout(() => setSyncFeedback(null), 4000);
      } else {
        setWorkspaceMode("search");
        setSyncFeedback(`Restored search term: "${item.query_keyword}". Ready to source business listings!`);
        setTimeout(() => setSyncFeedback(null), 3500);
      }
    } else if (item.lookup_type === "social_link") {
      if (item.url) window.open(item.url, "_blank");
    } else if (item.lookup_type === "contact_crawl") {
      let foundBusiness = Businesses.find(l => l.id === item.Business_id) || savedBusinesses.find(l => l.id === item.Business_id);
      
      if (!foundBusiness && item.Business_id && item.Business_name) {
        foundBusiness = {
          id: item.Business_id,
          name: item.Business_name,
          address: "Business retrieved from historical log",
          location: { lat: 0, lng: 0 },
          zipcode: "",
          crawlStatus: "success",
          contacts: item.contacts || []
        };
        // Insert into map selections view
        setBusinesses(prev => {
          if (!prev.some(l => l.id === item.Business_id)) {
            return [foundBusiness as BusinessBusiness, ...prev];
          }
          return prev;
        });
      } else if (foundBusiness) {
        const updatedBusiness = {
          ...foundBusiness,
          crawlStatus: "success" as const,
          contacts: item.contacts || []
        };
        setBusinesses(prev => prev.map(l => l.id === item.Business_id ? updatedBusiness : l));
        setSavedBusinesses(prev => prev.map(l => l.id === item.Business_id ? updatedBusiness : l));
      }
      
      if (item.Business_id) {
        setSelectedBusinessId(item.Business_id);
        setSyncFeedback(`Pulled up previous contact lookups for ${item.Business_name || 'Listing'}.`);
        setTimeout(() => setSyncFeedback(null), 3000);
      }
    }
  };

  // Supabase CRM Integration States
  const [dbStatus, setDbStatus] = useState<{
    connected: boolean;
    tablesExist?: boolean;
    message?: string;
    error?: string;
    writeError?: string;
  } | null>(null);
  const [isLoadingBusinesses, setIsLoadingBusinesses] = useState<boolean>(true);
  const [showSqlEditor, setShowSqlEditor] = useState<boolean>(false);
  const [showDevTools, setShowDevTools] = useState<boolean>(() => {
    try {
      return localStorage.getItem("dev_tools_enabled") === "true" || window.location.search.includes("dev=true");
    } catch {
      return false;
    }
  });
  const [devClicks, setDevClicks] = useState<number>(0);
  const [mobileIntelOpen, setMobileIntelOpen] = useState<boolean>(false);
  const [isSavingBusinessId, setIsSavingBusinessId] = useState<string | null>(null);
  const [syncFeedback, setSyncFeedback] = useState<string | null>(null);
  const [toastNotification, setToastNotification] = useState<{ message: string; type: "success" | "info" | "error" } | null>(null);
  const showToast = (message: string, type: "success" | "info" | "error" = "success") => {
    setToastNotification({ message, type });
    setTimeout(() => {
      setToastNotification(prev => prev?.message === message ? null : prev);
    }, 3000);
  };

  // CRM Pipeline and Territory Sub-Views
  const [savedSubview, setSavedSubview] = useState<"list" | "pipeline" | "territory">("list");
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const [savedSearches, setSavedSearches] = useState<any[]>([]);
  const [isSavingSearch, setIsSavingSearch] = useState<boolean>(false);

  // Website Audit State
  const [isAuditingId, setIsAuditingId] = useState<string | null>(null);

  // Follow-up reminder scheduler modal and view state
  const [reminderBusiness, setReminderBusiness] = useState<BusinessBusiness | null>(null);
  const [reminderDateSelection, setReminderDateSelection] = useState<string>("");
  const [reminderAlertMessage, setReminderAlertMessage] = useState<string | null>(null);

  // Share Business Modal State
  const [shareBusinessModalOpen, setShareBusinessModalOpen] = useState<boolean>(false);
  const [shareBusinessModalItem, setShareBusinessModalItem] = useState<BusinessBusiness | null>(null);
  const [shareBusinessModalUrl, setShareBusinessModalUrl] = useState<string>("");
  const [copiedShareLink, setCopiedShareLink] = useState<boolean>(false);

  // Filter settings & layout toggles
  const [showAdvancedFilters, setShowAdvancedFilters] = useState<boolean>(false);
  const [showOpportunityFilters, setShowOpportunityFilters] = useState<boolean>(false);
  const [minBusinessescore, setMinBusinessescore] = useState<number>(0);
  const [hasOpportunityTags, setHasOpportunityTags] = useState<boolean>(false);
  const [selectedOpportunityTag, setSelectedOpportunityTag] = useState<string>("any");
  const [websiteFilter, setWebsiteFilter] = useState<"any" | "has" | "missing">("any");
  const [phoneFilter, setPhoneFilter] = useState<"any" | "has" | "missing">("any");
  const [contactConfidenceFilter, setContactConfidenceFilter] = useState<"any" | "high" | "medium" | "low">("any");
  const [pipelineStageFilter, setPipelineStageFilter] = useState<"any" | "New" | "Contacted" | "Interested" | "Follow Up" | "Won" | "Lost">("any");
  const [contactStatusFilter, setContactStatusFilter] = useState<"any" | "has_contacts" | "no_contacts" | "not_searched" | "failed">("any");
  const [followUpStatusFilter, setFollowUpStatusFilter] = useState<"any" | "none" | "scheduled" | "due_today" | "overdue">("any");
  const [pitchStatusFilter, setPitchStatusFilter] = useState<"any" | "has_pitch" | "missing_pitch">("any");
  const [websiteReviewStatusFilter, setWebsiteReviewStatusFilter] = useState<"any" | "not_checked" | "checked" | "has_issues" | "failed">("any");
  const [aiAutomationFilter, setAiAutomationFilter] = useState<"any" | "known_ai_usage" | "automation_opportunity" | "no_ai_signal">("any");
  const [warningFilter, setWarningFilter] = useState<"any" | "has_warnings" | "no_warnings">("any");
  const [includeAdultContent, setIncludeAdultContent] = useState<boolean>(false);
  const [isPossiblyHiring, setIsPossiblyHiring] = useState<boolean>(false);
  const [searchTagQuery, setSearchTagQueryRaw] = useState<string>("");
  const [dbLoadFailed, setDbLoadFailed] = useState<boolean>(false);
  const [contactSyncOrLoadFailed, setContactSyncOrLoadFailed] = useState<boolean>(false);
  const [showTagSuggestions, setShowTagSuggestions] = useState<boolean>(false);

  // Opportunity Finder Filter Presets & States
  const [opportunityFinderState, setOpportunityFinderState] = useState<{
    outdatedWebsite: boolean;
    lowRating: boolean;
    highRating: boolean;
    recentlyOpened: boolean;
    hiringNow: boolean;
    noBookingSystem: boolean;
    noContactForm: boolean;
    weakSeo: boolean;
    poorMobile: boolean;
    slowWebsite: boolean;
    incompleteGoogleProfile: boolean;
    noSocialMedia: boolean;
    verifiedEmailFound: boolean;
    ownerContactFound: boolean;
    growingBusiness: boolean;
    highAdOpportunity: boolean;
    highAiScore: boolean;
  }>({
    outdatedWebsite: false,
    lowRating: false,
    highRating: false,
    recentlyOpened: false,
    hiringNow: false,
    noBookingSystem: false,
    noContactForm: false,
    weakSeo: false,
    poorMobile: false,
    slowWebsite: false,
    incompleteGoogleProfile: false,
    noSocialMedia: false,
    verifiedEmailFound: false,
    ownerContactFound: false,
    growingBusiness: false,
    highAdOpportunity: false,
    highAiScore: false,
  });

  const [savedPresets, setSavedPresets] = useState<{ name: string; filters: typeof opportunityFinderState }[]>([
    {
      name: "High Value Leads (Web Design & SEO)",
      filters: {
        outdatedWebsite: true,
        lowRating: false,
        highRating: false,
        recentlyOpened: false,
        hiringNow: false,
        noBookingSystem: true,
        noContactForm: true,
        weakSeo: true,
        poorMobile: true,
        slowWebsite: true,
        incompleteGoogleProfile: false,
        noSocialMedia: false,
        verifiedEmailFound: false,
        ownerContactFound: false,
        growingBusiness: false,
        highAdOpportunity: false,
        highAiScore: false,
      }
    },
    {
      name: "Fast Growing & Hiring Now",
      filters: {
        outdatedWebsite: false,
        lowRating: false,
        highRating: true,
        recentlyOpened: true,
        hiringNow: true,
        noBookingSystem: false,
        noContactForm: false,
        weakSeo: false,
        poorMobile: false,
        slowWebsite: false,
        incompleteGoogleProfile: false,
        noSocialMedia: false,
        verifiedEmailFound: true,
        ownerContactFound: true,
        growingBusiness: true,
        highAdOpportunity: false,
        highAiScore: true,
      }
    }
  ]);

  const [newPresetName, setNewPresetName] = useState<string>("");

  // Competitor Comparison States
  const [compareBusinessIds, setCompareBusinessIds] = useState<string[]>([]);
  const [isCompareModalOpen, setIsCompareModalOpen] = useState<boolean>(false);
  const [isHistorySectionExpanded, setIsHistorySectionExpanded] = useState<boolean>(true);
  const [isAiConsultantExpanded, setIsAiConsultantExpanded] = useState<boolean>(false);
  const [isWebsiteAuditExpanded, setIsWebsiteAuditExpanded] = useState<boolean>(false);

  // Outreach Copy Generator States
  const [isOutreachOpen, setIsOutreachOpen] = useState<boolean>(false);
  const [outreachStyle, setOutreachStyle] = useState<string>("Email");
  const [outreachGoal, setOutreachGoal] = useState<string>("Offer Web Design and SEO Refresh");
  const [outreachCustomDetails, setOutreachCustomDetails] = useState<string>("");
  const [outreachDraft, setOutreachDraft] = useState<string>("");
  const [isGeneratingOutreach, setIsGeneratingOutreach] = useState<boolean>(false);
  const [outreachProgressStep, setOutreachProgressStep] = useState<number>(0);
  const [outreachActiveTab, setOutreachActiveTab] = useState<"new" | "history">("new");
  const [pastOutreachDrafts, setPastOutreachDrafts] = useState<any[]>([]);
  const [isLoadingPastDrafts, setIsLoadingPastDrafts] = useState<boolean>(false);
  const [outreachHistorySearch, setOutreachHistorySearch] = useState<string>("");

  // Stripe Billing & Subscription states
  const [billingStatus, setBillingStatus] = useState<{
    plan: string;
    subscriptionStatus: string;
    currentPeriodEnd: string | null;
    isPro: boolean;
    stripeCustomerId: string | null;
    limits: Record<string, { count: number; limit: number; allowed: boolean }>;
    prices?: any;
    trialEligibility?: { eligible: boolean; reason: string };
  } | null>(null);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState<boolean>(false);
  const handleOpenUpgrade = guardedClick(() => {
    setIsUpgradeModalOpen(true);
  });
  const [isBillingLoading, setIsBillingLoading] = useState<boolean>(false);
  const [billingMessage, setBillingMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [isSyncingStripe, setIsSyncingStripe] = useState<boolean>(false);
  const [isActivationPending, setIsActivationPending] = useState<boolean>(false);
  const [activationError, setActivationError] = useState<string | null>(null);
  const [isRefreshingStatus, setIsRefreshingStatus] = useState<boolean>(false);

  const handleRefreshBillingStatus = async () => {
    setIsRefreshingStatus(true);
    setBillingMessage(null);
    try {
      logger.debug("[Refresh Billing Status] Calling /api/billing/status manually...");
      const res = await apiFetch("/api/billing/status");
      const data = await res.json();
      logger.debug("[Refresh Billing Status] Response data:", data);
      if (res.ok) {
        setBillingStatus(data);
        if (data.isPro) {
          setIsActivationPending(false);
          setActivationError(null);
          // Clear any stale cached flags
          localStorage.removeItem("billing_is_free");
          sessionStorage.removeItem("billing_is_free");
          setBillingMessage({
            type: "success",
            text: "Congratulations! Your Pro subscription status has been verified."
          });
        } else {
          setBillingMessage({
            type: "success",
            text: `Verification completed. Status: Free Plan (subscription_status: ${data.subscriptionStatus || data.subscription_status || "none"}).`
          });
        }
      } else {
        setBillingMessage({
          type: "error",
          text: data.error || "Failed to retrieve billing status."
        });
      }
    } catch (err: any) {
      console.error("Error manual refresh of billing status:", err);
      setBillingMessage({
        type: "error",
        text: err?.message || "Error retrieving status."
      });
    } finally {
      setIsRefreshingStatus(false);
    }
  };

  const handleSyncStripe = async () => {
    if (isEditorSafeMode) {
      logger.debug("[EDIT_MODE_CLICK_SUPPRESSED]");
      return;
    }
    setIsSyncingStripe(true);
    setActivationError(null);
    setBillingMessage(null);
    try {
      const res = await apiFetch("/api/billing/sync-stripe", {
        method: "POST"
      });
      const data = await res.json();
      if (res.ok) {
        await loadBillingStatus();
        if (data.isPro) {
          setIsActivationPending(false);
          setIsUpgradeModalOpen(false);
          // Clear any stale cached flags
          localStorage.removeItem("billing_is_free");
          sessionStorage.removeItem("billing_is_free");
          setBillingMessage({
            type: "success",
            text: "Congratulations! Your BizNearYou Pro Plan subscription was synchronized successfully!"
          });
        } else {
          setBillingMessage({
            type: "success",
            text: data.message || "Stripe synchronization complete."
          });
        }
      } else {
        setBillingMessage({
          type: "error",
          text: data.error || "Failed to synchronize status with Stripe."
        });
      }
    } catch (err: any) {
      console.error("Error syncing Stripe billing:", err);
      setBillingMessage({
        type: "error",
        text: err?.message || "Failed to synchronize with Stripe."
      });
    } finally {
      setIsSyncingStripe(false);
    }
  };

  const handleAutomaticActivation = async () => {
    if (!currentUser) return;
    setIsUpgradeModalOpen(true);
    setIsActivationPending(true);
    setActivationError(null);
    setBillingMessage({
      type: "success",
      text: "Payment received, activating account…"
    });

    // Invalidate local cache and clear stale free-plan flags
    localStorage.removeItem("billing_is_free");
    sessionStorage.removeItem("billing_is_free");
    localStorage.removeItem("free_limit_reached");

    try {
      logger.debug("[Auto-Activation] Success redirect. Calling sync-stripe once...");
      // 1. Call sync-stripe once
      const syncRes = await apiFetch("/api/billing/sync-stripe", { method: "POST" });
      
      if (syncRes.ok) {
        const syncData = await syncRes.json();
        
        // If no customer was found, report it clearly
        if (syncData.stripeCustomerId === null) {
          setIsActivationPending(false);
          setActivationError("No Stripe customer was found matching your account email. Please ensure your payment was completed under this email address, or click below to retry manual activation.");
          setBillingMessage({
            type: "error",
            text: "No Stripe customer found for this account. Make sure you checked out with the exact same email."
          });
          return;
        }
      }

      logger.debug("[Auto-Activation] Calling billing/status once...");
      // 2. Call status once
      const statusRes = await apiFetch("/api/billing/status");
      if (statusRes.ok) {
        const statusData = await statusRes.json();
        setBillingStatus(statusData);

        if (statusData.isPro) {
          logger.debug("[Auto-Activation] Account is now Pro!");
          setIsActivationPending(false);
          setIsUpgradeModalOpen(false); // Close premium modal automatically on success
          setBillingMessage({
            type: "success",
            text: "Congratulations! Your BizNearYou Pro Plan subscription was activated successfully!"
          });
          return;
        }
      }
    } catch (err: any) {
      console.warn("[Auto-Activation] Auto status/sync check failed:", err);
    }

    // 3. If still not Pro, show "Activation delayed, refresh billing status" with retry button
    setIsActivationPending(false);
    setActivationError("Activation delayed, refresh billing status");
    setBillingMessage({
      type: "error",
      text: "Activation delayed, refresh billing status"
    });
  };

  // Sandbox payment simulator state
  const [sandboxCheckout, setSandboxCheckout] = useState<{
    amount: number;
    title: string;
    successUrl: string;
    type: "subscription" | "campaign";
  } | null>(null);
  const [sandboxEmailField, setSandboxEmailField] = useState<string>("");
  const [sandboxCardName, setSandboxCardName] = useState<string>("");
  const [sandboxCardNum, setSandboxCardNum] = useState<string>("");
  const [sandboxExpiryField, setSandboxExpiryField] = useState<string>("");
  const [sandboxCvcField, setSandboxCvcField] = useState<string>("");
  const [sandboxZipField, setSandboxZipField] = useState<string>("");
  const [sandboxFormError, setSandboxFormError] = useState<string | null>(null);
  const [sandboxIsProcessing, setSandboxIsProcessing] = useState<boolean>(false);
  const [sandboxProcessingStep, setSandboxProcessingStep] = useState<number>(0);

  // User Custom Settings and Configuration states
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState<boolean>(false);
  // Account Deletion States
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] = useState<boolean>(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState<string>("");
  const [isDeletingAccount, setIsDeletingAccount] = useState<boolean>(false);
  const [deleteAccountError, setDeleteAccountError] = useState<string | null>(null);

  const [settingsUserName, setSettingsUserName] = useState<string>(() => {
    return safeLocalStorage.getItem("settings_user_name") || "Jane Doe";
  });
  const [settingsDefaultZipcode, setSettingsDefaultZipcode] = useState<string>(() => {
    return safeLocalStorage.getItem("settings_default_zipcode") || "";
  });
  const [settingsMaxResults, setSettingsMaxResults] = useState<number>(() => {
    return Number(safeLocalStorage.getItem("settings_max_results")) || 15;
  });
  const [settingsBypassPaywall, setSettingsBypassPaywall] = useState<boolean>(() => {
    return safeLocalStorage.getItem("settings_bypass_paywall") === "true";
  });
  const [adminUsePro, setAdminUsePro] = useState<boolean>(false);
  const [settingsEnterpriseTitle, setSettingsEnterpriseTitle] = useState<string>(() => {
    return safeLocalStorage.getItem("settings_enterprise_title") || "BizNearYou Pro";
  });
  const [settingsAppTheme, setSettingsAppTheme] = useState<string>(() => {
    const saved = safeLocalStorage.getItem("settings_app_theme");
    if (saved === "auto" || saved === "light" || saved === "dark") {
      return saved;
    }
    return "light";
  });
  const [mapStyle, setMapStyle] = useState<string>(() => {
    return safeLocalStorage.getItem("settings_map_style") || "standard";
  });
  const [settingsLanguage, setSettingsLanguage] = useState<LanguageCode>(locale);
  const [showLegendInfo, setShowLegendInfo] = useState<boolean>(false);

  useEffect(() => {
    setSettingsLanguage(locale);
  }, [locale]);

  // Gamified daily quest completed status for Pro benefits
  const [statsObj, setStatsObj] = useState<{ claimedDailyBonus?: boolean } | null>(() => {
    try {
      const data = localStorage.getItem("biznearyou_user_stats_v1");
      if (data) return JSON.parse(data);
    } catch (e) {
      console.error("[SPECIFIC_FEATURE_ERROR] Failed to load daily quest stats from localStorage:", e);
    }
    return null;
  });

  useEffect(() => {
    const handleStorageChange = () => {
      try {
        const data = localStorage.getItem("biznearyou_user_stats_v1");
        if (data) setStatsObj(JSON.parse(data));
      } catch (e) {
        console.error("[SPECIFIC_FEATURE_ERROR] Failed to update daily quest stats from localStorage event:", e);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    const interval = setInterval(handleStorageChange, 1500);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const hasDailyQuestProBooster = !!statsObj?.claimedDailyBonus;
  const isAdminUser = currentUserRole === "admin" || currentUserRole === "dev" || currentUserRole === "support";
  const isProUser = !!billingStatus?.isPro || (isAdminUser && (settingsBypassPaywall || adminUsePro));
  const setSearchTagQuery = (val: string | ((prev: string) => string)) => {
    const newVal = typeof val === "function" ? val(searchTagQuery) : val;
    setSearchTagQueryRaw(newVal);
  };
  const [trackingEnabled, setTrackingEnabled] = useState<boolean>(true);

  // Legal and Safety framework states
  const [isLegalModalOpen, setIsLegalModalOpen] = useState<boolean>(false);
  const [activeLegalTab, setActiveLegalTab] = useState<LegalTab>("terms");
  const [crisisDetection, setCrisisDetection] = useState<CrisisDetectionResult | null>(null);
  const [dismissedKeyword, setDismissedKeyword] = useState<string>("");

  useEffect(() => {
    if (keyword === dismissedKeyword) {
      setCrisisDetection(null);
      return;
    }
    const res = detectCrisisSearch(keyword);
    if (res.severity !== "none") {
      setCrisisDetection(res);
    } else {
      setCrisisDetection(null);
    }
  }, [keyword, dismissedKeyword]);

  useEffect(() => {
    if (currentUser?.email) {
      setSandboxEmailField(currentUser.email);
    }
  }, [currentUser]);

  useEffect(() => {
    const fetchTracking = async () => {
      try {
        const res = await apiFetch("/api/location/tracking");
        if (res.ok) {
          const data = await res.json();
          setTrackingEnabled(data.enabled);
        }
      } catch (err) {
        console.warn("Error fetching tracking settings:", err);
      }
    };
    if (authStatus === "authenticated" && supabaseSessionActive) {
      fetchTracking();
    }
  }, [authStatus, supabaseSessionActive]);

  const handleToggleTracking = async (enabled: boolean) => {
    setTrackingEnabled(enabled);
    try {
      await apiFetch("/api/location/tracking", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled })
      });
    } catch (err) {
      console.warn("Failed to update tracking toggle:", err);
    }
  };

  useEffect(() => {
    const fetchUserSettings = async () => {
      try {
        const res = await apiFetch("/api/user-settings");
        if (res.ok) {
          const data = await res.json();
          if (data.has_saved_settings && data.theme) {
            // Priority 1: If account already has a saved theme, use the account theme
            const validTheme = (data.theme === "dark" || data.theme === "auto") ? data.theme : "light";
            setSettingsAppTheme(validTheme);
            localStorage.setItem("settings_app_theme", validTheme);
          } else {
            // Priority 2: If account has no saved theme, copy current guest theme into the account once
            const currentGuestTheme = localStorage.getItem("settings_app_theme") || settingsAppTheme || "light";
            const validGuestTheme = (currentGuestTheme === "dark" || currentGuestTheme === "auto") ? currentGuestTheme : "light";
            setSettingsAppTheme(validGuestTheme);
            localStorage.setItem("settings_app_theme", validGuestTheme);

            apiFetch("/api/user-settings", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                theme: validGuestTheme,
                mapStyle: data.map_style || "standard",
                language: data.language || settingsLanguage
              })
            }).catch((err) => console.warn("Failed to copy guest theme preference into user account:", err));
          }

          if (data.map_style) {
            setMapStyle(data.map_style);
            localStorage.setItem("settings_map_style", data.map_style);
          }
        }
      } catch (err) {
        console.warn("Error fetching user settings from Supabase:", err);
      }
    };
    if (authStatus === "authenticated" && supabaseSessionActive) {
      fetchUserSettings();
    }
  }, [authStatus, supabaseSessionActive]);

  useEffect(() => {
    let isSubscribed = true;
    const fetchAudienceModeSetting = async () => {
      if (authStatus !== "authenticated" || !supabaseSessionActive) return;

      try {
        const {
          data: { user },
          error: userError,
        } = await supabase.auth.getUser();

        if (userError || !user || !isSubscribed) return;

        const { data, error } = await supabase
          .from("user_settings")
          .select("audience_mode")
          .eq("user_id", user.id)
          .maybeSingle();

        if (!isSubscribed) return;

        if (!error && data?.audience_mode) {
          const normalized = normalizeAudienceModeId(data.audience_mode);
          setAudienceMode(normalized);
          localStorage.setItem("settings_audience_mode", normalized);
        } else if (!error && !data) {
          // No user_settings row exists yet for this authenticated user.
          // Save the current guest preference into the new settings row.
          const guestPref = normalizeAudienceModeId(localStorage.getItem("settings_audience_mode") || audienceMode);
          if (!isAudienceMode(guestPref)) {
            console.error("Invalid audience mode value:", guestPref);
            return;
          }

          console.debug("Saving audience mode:", {
            selectedMode: guestPref,
            type: typeof guestPref,
          });

          await supabase
            .from("user_settings")
            .upsert(
              {
                user_id: user.id,
                audience_mode: guestPref,
                updated_at: new Date().toISOString(),
              },
              { onConflict: "user_id" }
            );

          if (isSubscribed) {
            setAudienceMode(guestPref);
            localStorage.setItem("settings_audience_mode", guestPref);
          }
        }
      } catch (err) {
        console.warn("Failed to fetch audience mode from Supabase user_settings:", err);
      }
    };

    if (authStatus === "authenticated" && supabaseSessionActive) {
      fetchAudienceModeSetting();
    }

    return () => {
      isSubscribed = false;
    };
  }, [authStatus, supabaseSessionActive]);

  useEffect(() => {
    const applyTheme = () => {
      const active = resolveTheme(settingsAppTheme);
      document.documentElement.classList.remove("theme-light", "theme-dark", "theme-sunset", "theme-ocean", "dark");
      document.documentElement.classList.add(`theme-${active}`);
      if (active === "dark") {
        document.documentElement.classList.add("dark");
      }

      const computedMapStyle = active === "dark" ? "standard" : "standard";
      setMapStyle(computedMapStyle);
      localStorage.setItem("settings_map_style", computedMapStyle);
    };

    applyTheme();

    if (settingsAppTheme === "auto" && typeof window !== "undefined" && window.matchMedia) {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handleChange = () => {
        applyTheme();
      };
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener("change", handleChange);
        return () => mediaQuery.removeEventListener("change", handleChange);
      } else if ((mediaQuery as any).addListener) {
        (mediaQuery as any).addListener(handleChange);
        return () => (mediaQuery as any).removeListener(handleChange);
      }
    }
  }, [settingsAppTheme]);

  const handleSaveUserSettings = async (theme: string, style: string, lang?: LanguageCode) => {
    setSettingsAppTheme(theme);
    const active = resolveTheme(theme);
    const computedMapStyle = active === "dark" ? "standard" : "standard";
    setMapStyle(computedMapStyle);
    localStorage.setItem("settings_app_theme", theme);
    localStorage.setItem("settings_map_style", computedMapStyle);
    if (currentUser) {
      try {
        await apiFetch("/api/user-settings", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ theme, mapStyle: computedMapStyle })
        });
        await triggerQuestIncrement("profile_update", 1);
      } catch (err) {
        console.warn("Failed to sync user settings with Supabase:", err);
      }
    }
  };

  const loadBillingStatus = async () => {
    if (!currentUser) return;
    try {
      bizDebug("PRO_CHECK_START", { userId: currentUser.id });
      const res = await apiFetch("/api/billing/status");
      if (res.ok) {
        const data = await res.json();
        setBillingStatus(data);
        bizDebug("PRO_CHECK_SUCCESS", { isPro: data.isPro, plan: data.plan });
      } else {
        if (res.status !== 401 && res.status !== 403 && res.status !== 400) {
          bizError("PRO_CHECK_ERROR", { status: res.status, url: "/api/billing/status" });
        }
      }
    } catch (err: any) {
      if (err?.message !== "401 Unauthorized" && err?.message !== "401" && err?.message !== "400" && err?.message !== "403") {
        bizError("PRO_CHECK_ERROR", { error: err?.message || err, url: "/api/billing/status" });
      }
      console.warn("Failed to retrieve subscription status:", err);
    }
  };

  const pollBillingStatus = async (attemptsLeft = 15) => {
    if (!currentUser) return;
    try {
      const res = await apiFetch("/api/billing/status");
      if (res.ok) {
        const data = await res.json();
        setBillingStatus(data);
        if (data.isPro) {
          setBillingMessage({
            type: "success",
            text: "Congratulations! Your BizNearYou Pro Plan subscription was activated successfully!"
          });
          return;
        }
      }
    } catch (err) {
      console.warn("Error polling billing status:", err);
    }

    if (attemptsLeft > 1) {
      setTimeout(() => pollBillingStatus(attemptsLeft - 1), 2000);
    } else {
      setBillingMessage({
        type: "error",
        text: "Subscription activation is taking a bit longer than expected. Please refresh this page shortly or contact support if the problem persists."
      });
    }
  };

  const handleStartCheckout = async (interval: "monthly" | "yearly", bypassTrial?: boolean) => {
    if (isEditorSafeMode) {
      console.log("[BIZ_DEBUG] EDIT_MODE_CLICK_SUPPRESSED");
      return;
    }
    if (isBillingLoading) {
      console.log("[Checkout] Blocked duplicate click because billing is currently loading.");
      return;
    }
    setIsBillingLoading(true);
    setBillingMessage(null);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const hasSupabaseSession = !!sessionData.session;
      const hasAccessToken = !!sessionData.session?.access_token;

      bizDebug("[BIZ_DEBUG] BILLING_AUTH_CHECK", {
        hasSupabaseSession,
        hasAccessToken,
        authStatus
      });

      if (!sessionData.session?.access_token) {
        throw new Error("Please sign in again before upgrading.");
      }

      const res = await apiFetch("/api/billing/create-checkout-session", {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${sessionData.session.access_token}`
        },
        body: JSON.stringify({ interval, bypassTrial }),
      });
      const data = await res.json();
      if (res.ok && data.url) {
        // Open the secure Stripe Checkout URL in a new window/tab as Stripe cannot load in an iframe due to security headers
        window.open(data.url, "_blank");
      } else {
        setBillingMessage({
          type: "error",
          text: data.error || "Failed to initialize active Stripe checkout session."
        });
      }
    } catch (err: any) {
      setBillingMessage({
        type: "error",
        text: err.message || "Failed to create secure billing redirection link."
      });
    } finally {
      setIsBillingLoading(false);
    }
  };

  const handleManageBilling = async () => {
    // Open the comprehensive in-app billing & subscription modal which supports self-service upgrades and downgrades
    setIsUpgradeModalOpen(true);
  };

  const handleOpenCustomerPortal = async () => {
    setIsBillingLoading(true);
    setBillingMessage(null);
    try {
      const res = await apiFetch("/api/billing/customer-portal", {
        method: "POST"
      });
      const data = await res.json();
      if (res.ok && data.url) {
        window.open(data.url, "_blank");
      } else {
        throw new Error(data.error || "Failed to launch billing customer portal.");
      }
    } catch (err: any) {
      setBillingMessage({
        type: "error",
        text: err.message || "Failed to launch billing portal."
      });
    } finally {
      setIsBillingLoading(false);
    }
  };

  const handleExportCsv = (targetBusinesses: BusinessBusiness[] = savedBusinesses) => {
    if (!isProUser) {
      setIsUpgradeModalOpen(true);
      return;
    }
    if (targetBusinesses.length === 0) return;

    // Trigger quest progress increment on the server
    triggerQuestIncrement("lead_export", 1);
    
    // Create CSV row records
    const headers = [
      "ID",
      "Business Name",
      "Address",
      "Zipcode",
      "Phone Number",
      "Website URL",
      "Rating Score",
      "Business Score",
      "Pipeline Phase",
      "Follow-up Date",
      "https_secure",
      "contact_form_found",
      "booking_button_found",
      "mobile_friendly",
      "search_enrich_status",
      "contacts_count",
      "contacts_list_semi_colon_delimited"
    ];

    const rows = targetBusinesses.map(l => {
      const listStr = (l.contacts || []).map(c => 
        `${c.name || "N/A"} (${c.role || "N/A"}) - Em: ${c.email || "N/A"} | Ph: ${c.phone || "N/A"}`
      ).join("; ");

      return [
        l.id,
        l.name,
        l.address || "",
        l.zipcode || "",
        l.phone || "",
        l.website || "",
        l.rating !== undefined ? l.rating : "",
        l.Businessescore !== undefined ? l.Businessescore : "",
        l.pipelineStage || "New",
        l.followUpAt || "",
        l.websiteAudit?.https ? "YES" : "NO",
        l.websiteAudit?.contactFormFound ? "YES" : "NO",
        l.websiteAudit?.bookingButtonFound ? "YES" : "NO",
        l.websiteAudit?.mobileFriendly ? "YES" : "NO",
        l.crawlStatus || "idle",
        (l.contacts || []).length,
        listStr
      ];
    });

    const csvContent = [
      headers.join(","),
      ...rows.map(parts => parts.map(v => `"${String(v || "").replace(/"/g, '""')}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `biznearyou_export_${workspaceMode}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Territory / Router Coordinates Sequence
  const [routeStartZip, setRouteStartZip] = useState<string>("");
  const [calculatedSequence, setCalculatedSequence] = useState<BusinessBusiness[]>([]);

  // 1. CRM Database alert sync loader
  const loadSavedSearches = async () => {
    // Disabled saved searches table usage
    setSavedSearches([]);
  };

  const handleSaveSearch = async () => {
    if (!keyword || !zipInput) return;
    
    // Local duplicate check before sending request
    const isDuplicate = savedSearches.some(
      (s) => s.keyword.toLowerCase().trim() === keyword.toLowerCase().trim() &&
             s.location.toLowerCase().trim() === zipInput.toLowerCase().trim()
    );
    if (isDuplicate) {
      setSaveSearchStatus('duplicate');
      setSyncFeedback(`Already Saved: "${keyword}" in ${zipInput} is in your saved list.`);
      setTimeout(() => {
        setSyncFeedback(null);
        setSaveSearchStatus('idle');
      }, 4000);
      return;
    }

    setIsSavingSearch(true);
    setSaveSearchStatus('loading');
    try {
      const res = await apiFetch("/api/saved-searches", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ keyword, location: zipInput })
      });
      if (res.ok) {
        await loadSavedSearches();
        setSaveSearchStatus('success');
        setSyncFeedback(`Successfully saved search: "${keyword}" in ${zipInput}!`);
        setTimeout(() => {
          setSyncFeedback(null);
          setSaveSearchStatus('idle');
        }, 4000);
      } else {
        const errorData = await res.json().catch(() => ({}));
        if (errorData.message?.toLowerCase().includes("already exists") || errorData.message?.toLowerCase().includes("duplicate")) {
          setSaveSearchStatus('duplicate');
          setSyncFeedback(`Search query alert already registered for "${keyword}" in ${zipInput}.`);
        } else {
          setSaveSearchStatus('error');
          setSyncFeedback(`Error saving search: ${errorData.message || 'Server rejected request'}`);
        }
        setTimeout(() => {
          setSyncFeedback(null);
          setSaveSearchStatus('idle');
        }, 4000);
      }
    } catch (err: any) {
      console.warn("Could not save search criteria:", err);
      setSaveSearchStatus('error');
      setSyncFeedback(`Error saving search: ${err.message || 'Network communication failed'}`);
      setTimeout(() => {
        setSyncFeedback(null);
        setSaveSearchStatus('idle');
      }, 4000);
    } finally {
      setIsSavingSearch(false);
    }
  };

  const handleDeleteSavedSearch = async (id: string, kw: string, loc: string) => {
    try {
      const res = await apiFetch(`/api/saved-searches/${id}`, {
        method: "DELETE"
      });
      if (res.ok) {
        await loadSavedSearches();
        setSyncFeedback(`Deleted search Alert for "${kw}" in ${loc}.`);
        setTimeout(() => setSyncFeedback(null), 3050);
      }
    } catch (err) {
      console.warn("Could not delete saved search alert:", err);
    }
  };

  // 2. CRM Update field proxy
  const updateBusinessCrm = async (BusinessId: string, stage: string, followUp: string | null) => {
    // Keep local states for backup in case backend returns failure or throws error (rollback pattern)
    const backupBusinesses = [...Businesses];
    const backupSavedBusinesses = [...savedBusinesses];

    setBusinesses(prev => prev.map(l => l.id === BusinessId ? { ...l, pipelineStage: stage as any, followUpAt: followUp || undefined } : l));
    setSavedBusinesses(prev => prev.map(l => l.id === BusinessId ? { ...l, pipelineStage: stage as any, followUpAt: followUp || undefined } : l));
    
    try {
      const response = await apiFetch(`/api/Businesses/${BusinessId}/crm`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pipelineStage: stage, followUpAt: followUp })
      });
      if (response.ok) {
        setSyncFeedback(`Successfully updated target opportunity to '${stage}' stage!`);
        setTimeout(() => setSyncFeedback(null), 3000);
        showToast(`Moved to pipeline stage: ${stage}`, "success");
      } else {
        const errorData = await response.json().catch(() => ({}));
        setSyncFeedback(`Error saving CRM update: ${errorData.error || 'Please save the business listing first.'}`);
        setTimeout(() => setSyncFeedback(null), 4550);
        showToast(errorData.error || "Save business before shifting stages.", "error");
        // Rollback state changes on failure
        setBusinesses(backupBusinesses);
        setSavedBusinesses(backupSavedBusinesses);
      }
    } catch (err: any) {
      console.warn("CRM database synchronization bypassed:", err);
      setSyncFeedback(`Error updating opportunity: connection issue.`);
      setTimeout(() => setSyncFeedback(null), 4550);
      // Rollback state changes on failure
      setBusinesses(backupBusinesses);
      setSavedBusinesses(backupSavedBusinesses);
    }
  };

  const toggleBusinessBlockStatus = async (Business: any, currentlyBlocked: boolean) => {
    try {
      const endpoint = currentlyBlocked ? "/api/admin/Businesses/unblock" : "/api/admin/Businesses/block";
      const res = await apiFetch(endpoint, {
        method: "POST",
        body: JSON.stringify({ BusinessId: Business.id, BusinessDetails: Business })
      });
      if (res.ok) {
        const nextBlocked = !currentlyBlocked;
        setBusinesses(prev => prev.map(l => l.id === Business.id ? { ...l, isBlocked: nextBlocked, pipeline_stage: nextBlocked ? "Blocked" : "New", pipelineStage: nextBlocked ? "Blocked" : "New" } : l));
        setSavedBusinesses(prev => prev.map(l => l.id === Business.id ? { ...l, isBlocked: nextBlocked, pipelineStage: nextBlocked ? "Blocked" : "New", pipeline_stage: nextBlocked ? "Blocked" : "New" } : l));
        setSyncFeedback(`Business successfully ${nextBlocked ? "blocked and banned globally" : "unblocked and restored"}.`);
        setTimeout(() => setSyncFeedback(null), 3000);
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || "Failed to update Business block status.");
      }
    } catch (e: any) {
      console.error(e);
      alert(e.message || "Connection error updating Business block status.");
    }
  };

  // 3. SEO audit website trigger
  const runWebsiteAudit = async (Business: BusinessBusiness) => {
    if (!Business.website) return;
    setIsAuditingId(Business.id);

    // Track "running" state and clear previous errors
    const runningBusiness: BusinessBusiness = {
      ...Business,
      websiteReviewStatus: "running" as const,
      websiteReviewError: undefined
    };
    setBusinesses(prev => prev.map(l => l.id === Business.id ? runningBusiness : l));
    setSavedBusinesses(prev => prev.map(l => l.id === Business.id ? runningBusiness : l));

    try {
      const res = await apiFetch("/api/audit-website", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: Business.website })
      });

      const data = await res.json().catch(() => ({}));

      if (res.status === 403 || data?.auditStatus === "allowance_exhausted") {
        const errMsg = data?.error || "Free plan Website Audits limit reached.";
        const exhaustedBusiness: BusinessBusiness = {
          ...Business,
          websiteReviewStatus: "allowance_exhausted" as const,
          websiteReviewError: errMsg
        };
        setBusinesses(prev => prev.map(l => l.id === Business.id ? exhaustedBusiness : l));
        setSavedBusinesses(prev => prev.map(l => l.id === Business.id ? exhaustedBusiness : l));
        loadBillingStatus();
        return;
      }

      if (res.ok && data.success && data.audit) {
        const updatedBusiness: BusinessBusiness = {
          ...Business,
          websiteAudit: {
            ...data.audit,
            failed: false,
            reviewStatus: "success" as const,
            reviewError: undefined
          },
          websiteReviewStatus: "success" as const,
          websiteReviewError: undefined
        };
        setBusinesses(prev => prev.map(l => l.id === Business.id ? updatedBusiness : l));
        setSavedBusinesses(prev => prev.map(l => l.id === Business.id ? updatedBusiness : l));
        await syncBusinessesToDatabase([updatedBusiness]);
        setSyncFeedback("Dynamic SEO & Business Capture audit complete!");
        setTimeout(() => setSyncFeedback(null), 3000);
        
        await triggerQuestIncrement("seo_audit", 1);
        return;
      }

      const specificStatus: WebsiteReviewState = (data?.auditStatus as WebsiteReviewState) || "temporary_failure";
      const errMsg = data?.error || data?.details || "Website could not be checked.";

      const failedAudit = {
        exists: false,
        https: Business.website.startsWith("https"),
        contactPageFound: false,
        contactFormFound: false,
        bookingButtonFound: false,
        socialLinksFound: [],
        seoTitleExists: false,
        metaDescriptionExists: false,
        mobileFriendly: false,
        outdatedDetails: false,
        lastAudited: new Date().toISOString(),
        failed: true,
        reviewStatus: specificStatus,
        reviewError: errMsg
      };
      const updatedBusiness: BusinessBusiness = {
        ...Business,
        websiteReviewStatus: specificStatus,
        websiteReviewError: errMsg,
        websiteAudit: failedAudit
      };
      setBusinesses(prev => prev.map(l => l.id === Business.id ? updatedBusiness : l));
      setSavedBusinesses(prev => prev.map(l => l.id === Business.id ? updatedBusiness : l));
      try {
        await syncBusinessesToDatabase([updatedBusiness]);
      } catch (syncErr) {
        console.warn("Bypassed sync for failed Business:", syncErr);
      }
    } catch (err: any) {
      console.warn("Audit collapsed, registering temporary failure:", err);
      const errMsg = err?.message || "Website inspection failed.";
      
      const failedAudit = {
        exists: false,
        https: Business.website.startsWith("https"),
        contactPageFound: false,
        contactFormFound: false,
        bookingButtonFound: false,
        socialLinksFound: [],
        seoTitleExists: false,
        metaDescriptionExists: false,
        mobileFriendly: false,
        outdatedDetails: false,
        lastAudited: new Date().toISOString(),
        failed: true,
        reviewStatus: "temporary_failure" as const,
        reviewError: errMsg
      };
      const updatedBusiness: BusinessBusiness = {
        ...Business,
        websiteReviewStatus: "temporary_failure" as const,
        websiteReviewError: errMsg,
        websiteAudit: failedAudit
      };
      setBusinesses(prev => prev.map(l => l.id === Business.id ? updatedBusiness : l));
      setSavedBusinesses(prev => prev.map(l => l.id === Business.id ? updatedBusiness : l));
    } finally {
      setIsAuditingId(null);
    }
  };

  // 4. Outreach copy engine
  const generateOutreachCopy = async (Business: BusinessBusiness) => {
    if (!isProUser) {
      setIsUpgradeModalOpen(true);
      return;
    }
    setIsGeneratingOutreach(true);
    setOutreachProgressStep(0);
    setOutreachDraft("");

    // Step-by-step progress simulation
    const step1 = setTimeout(() => setOutreachProgressStep(1), 320);
    const step2 = setTimeout(() => setOutreachProgressStep(2), 650);
    const step3 = setTimeout(() => setOutreachProgressStep(3), 980);

    setTimeout(async () => {
      try {
      const prospect = analyzeBusinessProspect(Business);
      const Businessescore = Business.Businessescore !== undefined ? Business.Businessescore : prospect.score;
      const tags = normalizeTags(Business.opportunityTags?.length ? Business.opportunityTags : prospect.tags);
      const pitch = Business.pitchAngle || prospect.pitchAngle;

      const res = await apiFetch("/api/outreach/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          style: outreachStyle,
          goal: outreachGoal,
          customDetails: outreachCustomDetails,
          BusinessName: Business.name,
          category: keyword || "Local Business",
          audit: Business.websiteAudit,
          tags,
          pitch,
          contactPerson: Business.contacts?.[0]
        })
      });

      if (res.ok) {
        const data = await res.json();
        setOutreachDraft(data.draft || "Unable to draft personalized copies.");
      } else {
        if (res.status === 403) {
          setIsUpgradeModalOpen(true);
          loadBillingStatus();
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || "Free Plan outreach drafts limit reached.");
        }
        throw new Error("Outreach endpoint response desynced.");
      }
    } catch (err: any) {
      console.warn("Outreach collapsed, falling back to local responsive personalized writer:", err);
      const personName = Business.contacts?.[0]?.name || "Team";
      const actualGoal = outreachGoal || "collaborate with your business";
      const actualDetails = outreachCustomDetails || `help with optimized local strategies and custom support tailored for your category.`;
      
      const subject = outreachStyle === "Email" ? `Subject: Custom Proposal for ${Business.name} - Partnership Opportunity` : ``;
      
      if (outreachStyle === "Email") {
        setOutreachDraft(`${subject}

Hi ${personName},

I hope this message finds you well. I was looking at ${Business.name} and wanted to reach out regarding a specific opportunity to ${actualGoal.toLowerCase()}.

We specialize in local business support and wanted to propose:
- ${actualDetails}
- Client-centered custom solutions tailored to your unique market demand
- Quick, professional collaboration with verified business metrics

I'd love to schedule a quick 5-minute call or reply here to share how we can support ${Business.name}.

Best regards,
[Your Name]
[Your Contact Info]`);
      } else if (outreachStyle === "SMS") {
        setOutreachDraft(`Hi ${personName}! I saw ${Business.name} in the local directory. Just wanted to reach out regarding our service to: ${actualGoal}. We'd love to help! Details: ${actualDetails}. Text back if you're interested. - [Your Name]`);
      } else {
        setOutreachDraft(`[Phone Call Script for ${Business.name}]
Hi, can I speak to ${personName} or the business owner?
(Wait for connection)
Hi ${personName}, my name is [Your Name]. I saw ${Business.name} on BizNearYou. I'm calling because we help businesses in your area ${actualGoal.toLowerCase()}.
Specifically: ${actualDetails}.
I wanted to see if you have 5 minutes this week for a brief conversation about how we can support your team. Would Wednesday or Thursday afternoon work better?`);
      }
      } finally {
        setIsGeneratingOutreach(false);
        clearTimeout(step1);
        clearTimeout(step2);
        clearTimeout(step3);
        // Refresh past drafts history after a generation attempt
        if (isProUser) {
          loadPastOutreachDrafts();
        }
      }
    }, 1300);
  };

  // 4.1 Load Past Outreach Drafts History
  const loadPastOutreachDrafts = async () => {
    setIsLoadingPastDrafts(true);
    try {
      const res = await apiFetch("/api/pro/outreach-drafts");
      if (res.ok) {
        const data = await res.json();
        setPastOutreachDrafts(data || []);
      }
    } catch (err) {
      console.warn("Failed to load past outreach drafts:", err);
    } finally {
      setIsLoadingPastDrafts(false);
    }
  };

  // Synchronize past drafts loading when the console is opened
  useEffect(() => {
    if (isOutreachOpen && isProUser) {
      loadPastOutreachDrafts();
      // Reset active tab to 'new' when opening modal
      setOutreachActiveTab("new");
    }
  }, [isOutreachOpen, isProUser]);

  // 5. Territory Router Sequence
  const handleSequenceRoute = () => {
    if (savedBusinesses.length === 0) {
      setCalculatedSequence([]);
      return;
    }
    
    let baseCoords = { lat: 37.0902, lng: -95.7129 };
    const matchingBusiness = savedBusinesses.find(l => l.zipcode === routeStartZip || l.address.includes(routeStartZip));
    if (matchingBusiness) {
      baseCoords = matchingBusiness.location;
    } else if (savedBusinesses[0]) {
      baseCoords = savedBusinesses[0].location;
    }

    const unvisited = [...savedBusinesses];
    const ordered: BusinessBusiness[] = [];
    let currentPoint = baseCoords;

    while (unvisited.length > 0) {
      let nearestIdx = 0;
      let nearestDist = Infinity;
      unvisited.forEach((Business, index) => {
        const d = getDistanceInMiles(Business.location.lat, Business.location.lng, currentPoint.lat, currentPoint.lng);
        if (d < nearestDist) {
          nearestDist = d;
          nearestIdx = index;
        }
      });
      const selected = unvisited.splice(nearestIdx, 1)[0];
      ordered.push(selected);
      currentPoint = selected.location;
    }

    setCalculatedSequence(ordered);
    setSyncFeedback("Dynamic routing calculation complete!");
    setTimeout(() => setSyncFeedback(null), 3000);
  };


  const renderErrorWithLinks = (errorText: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = errorText.split(urlRegex);
    return (
      <span className="break-words">
        {parts.map((part, index) => {
          if (urlRegex.test(part)) {
            return (
              <a 
                key={index} 
                href={part} 
                target="_blank" 
                referrerPolicy="no-referrer"
                className="underline text-blue-600 hover:text-blue-800 font-extrabold ml-1 inline-block break-all"
              >
                {part}
              </a>
            );
          }
          return part;
        })}
      </span>
    );
  };

  // Filter settings
  const [distance, setDistance] = useState<string>("any"); // "any" or "1", "5", "10", "25" miles
  const [minRating, setMinRating] = useState<number>(0); // 0, 3, 4, 4.5
  const [openNow, setOpenNow] = useState<boolean>(false);

  const isOpportunityFilterActive = Object.values(opportunityFinderState).some(Boolean);

  const isAnyFilterActive = isOpportunityFilterActive || isPossiblyHiring || (workspaceMode === "saved"
    ? (minBusinessescore > 0 ||
       hasOpportunityTags ||
       selectedOpportunityTag !== "any" ||
       websiteFilter !== "any" ||
       phoneFilter !== "any" ||
       contactStatusFilter !== "any" ||
       contactConfidenceFilter !== "any" ||
       followUpStatusFilter !== "any" ||
       pipelineStageFilter !== "any" ||
       websiteReviewStatusFilter !== "any" ||
       pitchStatusFilter !== "any" ||
       aiAutomationFilter !== "any" ||
       warningFilter !== "any" ||
       searchTagQuery !== "")
    : (distance !== "any" ||
       minRating > 0 ||
       websiteFilter !== "any" ||
       phoneFilter !== "any" ||
       openNow ||
       includeAdultContent ||
       aiAutomationFilter !== "any" ||
       searchTagQuery !== ""));

  const isAnyOpportunityFilterActive = isOpportunityFilterActive || searchTagQuery !== "";

  const isAnyStandardFilterActive = isPossiblyHiring || (workspaceMode === "saved"
    ? (minBusinessescore > 0 ||
       selectedOpportunityTag !== "any" ||
       websiteFilter !== "any" ||
       phoneFilter !== "any" ||
       contactStatusFilter !== "any" ||
       contactConfidenceFilter !== "any" ||
       followUpStatusFilter !== "any" ||
       pipelineStageFilter !== "any" ||
       websiteReviewStatusFilter !== "any" ||
       pitchStatusFilter !== "any" ||
       aiAutomationFilter !== "any" ||
       warningFilter !== "any")
    : (distance !== "any" ||
       minRating > 0 ||
       websiteFilter !== "any" ||
       phoneFilter !== "any" ||
       openNow ||
       includeAdultContent ||
       aiAutomationFilter !== "any"));

  const handleResetFilters = () => {
    setSearchTagQuery("");
    setIsPossiblyHiring(false);
    setAiAutomationFilter("any");
    setOpportunityFinderState({
      outdatedWebsite: false,
      lowRating: false,
      highRating: false,
      recentlyOpened: false,
      hiringNow: false,
      noBookingSystem: false,
      noContactForm: false,
      weakSeo: false,
      poorMobile: false,
      slowWebsite: false,
      incompleteGoogleProfile: false,
      noSocialMedia: false,
      verifiedEmailFound: false,
      ownerContactFound: false,
      growingBusiness: false,
      highAdOpportunity: false,
      highAiScore: false,
    });
    if (workspaceMode === "saved") {
      setMinBusinessescore(0);
      setHasOpportunityTags(false);
      setSelectedOpportunityTag("any");
      setWebsiteFilter("any");
      setPhoneFilter("any");
      setContactStatusFilter("any");
      setContactConfidenceFilter("any");
      setFollowUpStatusFilter("any");
      setPipelineStageFilter("any");
      setWebsiteReviewStatusFilter("any");
      setPitchStatusFilter("any");
      setWarningFilter("any");
    } else {
      setDistance("any");
      setMinRating(0);
      setWebsiteFilter("any");
      setPhoneFilter("any");
      setOpenNow(false);
      setIncludeAdultContent(false);
    }
  };

  // Derived filtered Businesses based on current selected list mode and client-side constraints
  const currentModeBusinesses = workspaceMode === "search" ? Businesses : savedBusinesses;

  const filteredBusinesses = currentModeBusinesses.filter((Business) => {
    const prospect = analyzeBusinessProspect(Business);
    const score = Business.Businessescore !== undefined ? Number(Business.Businessescore) : prospect.score;
    const localTags = normalizeTags(Business.opportunityTags?.length ? Business.opportunityTags : prospect.tags);

    const hScore = (Business as any).scores?.hiringScore ?? prospect.scores?.hiringScore ?? 0;
    const likelihood = getHiringLikelihood(hScore, localTags);
    const refined = refineUnconfirmedHiringSignal(Business, prospect, likelihood);
    const hasAnyHiringSignal = refined.type === "high" || refined.type === "possible";

    // Opportunity Finder Filters
    if (opportunityFinderState.outdatedWebsite && (!Business.website || !(localTags.includes("Website Review Outdated") || localTags.includes("Needs Website Cleanup")))) return false;
    if (opportunityFinderState.lowRating && (!Business.rating || Business.rating >= 3.8)) return false;
    if (opportunityFinderState.highRating && (!Business.rating || Business.rating < 4.5)) return false;
    if (opportunityFinderState.recentlyOpened && !localTags.includes("New Business") && !localTags.includes("Recently Opened")) return false;
    if (opportunityFinderState.hiringNow && !hasAnyHiringSignal) return false;
    if (opportunityFinderState.noBookingSystem && (Business.website && Business.websiteAudit?.bookingButtonFound)) return false;
    if (opportunityFinderState.noContactForm && (Business.website && Business.websiteAudit?.contactFormFound)) return false;
    if (opportunityFinderState.weakSeo && !localTags.includes("Weak SEO") && !localTags.includes("Needs Website Cleanup")) return false;
    if (opportunityFinderState.poorMobile && Business.websiteAudit?.mobileFriendly) return false;
    if (opportunityFinderState.slowWebsite && !localTags.includes("Website Check Failed") && !localTags.includes("Needs Website Cleanup")) return false;
    if (opportunityFinderState.incompleteGoogleProfile && !localTags.includes("Unclaimed Listing Opportunity") && Business.phone && Business.website && Business.hours) return false;
    if (opportunityFinderState.noSocialMedia && Business.socials && Object.keys(Business.socials).length > 0) return false;
    if (opportunityFinderState.verifiedEmailFound && (!Business.contacts || !Business.contacts.some(c => c.type === "email" || c.email))) return false;
    if (opportunityFinderState.ownerContactFound && (!Business.contacts || !Business.contacts.some(c => c.role?.toLowerCase().includes("owner") || c.role?.toLowerCase().includes("founder") || c.title?.toLowerCase().includes("owner")))) return false;
    if (opportunityFinderState.growingBusiness && !localTags.includes("Expanding Location") && !localTags.includes("Growing Team") && !localTags.includes("Strong Reviews")) return false;
    if (opportunityFinderState.highAdOpportunity && !localTags.includes("Needs Local Ads") && !localTags.includes("High Competition Area")) return false;
    if (opportunityFinderState.highAiScore && score < 80) return false;

    // AI Usage Filter (Shared)
    if (aiAutomationFilter !== "any") {
      const hasKnownAI = prospect.categorizedSignals.aiAutomation.includes("Known AI Usage");
      const hasAutomation = prospect.categorizedSignals.aiAutomation.includes("Automation Opportunity");
      const hasNoSignal = prospect.categorizedSignals.aiAutomation.includes("No AI Signal Found");

      if (aiAutomationFilter === "known_ai_usage" && !hasKnownAI) return false;
      if (aiAutomationFilter === "automation_opportunity" && !hasAutomation) return false;
      if (aiAutomationFilter === "no_ai_signal" && !hasNoSignal) return false;
    }

    // Search by Tag Filter (Shared)
    if (searchTagQuery.trim()) {
      const tags = normalizeTags(Business.opportunityTags?.length ? Business.opportunityTags : prospect.tags);
      
      const queryParts = searchTagQuery.split(",").map(part => part.trim().toLowerCase()).filter(Boolean);
      if (queryParts.length > 0) {
        const matchesAll = queryParts.every(part => 
          tags && tags.some(t => t && t.toLowerCase().includes(part))
        );
        if (!matchesAll) return false;
      }
    }

    // Dropdown opportunity tag filter (Shared)
    if (selectedOpportunityTag !== "any" && (!localTags || !localTags.includes(selectedOpportunityTag))) return false;

    // Is Possibly Hiring filter (Shared)
    if (isPossiblyHiring && !hasAnyHiringSignal) return false;

    // 1. Adult-oriented business filtering (OFF by default)
    if (!includeAdultContent) {
      const ADULT_KEYWORDS = ["adult", "erotic", "xxx", "sensual", "strip club", "gentlemens club", "escort service", "sensual massage", "porn", "hookah", "vapor bar", "night club", "cannabis", "dispensary", "marijuana"];
      const nameLower = Business.name.toLowerCase();
      const addressLower = (Business.address || "").toLowerCase();
      const websiteLower = (Business.website || "").toLowerCase();
      
      const isAdult = ADULT_KEYWORDS.some(kw => 
        nameLower.includes(kw) || 
        addressLower.includes(kw) || 
        websiteLower.includes(kw)
      );
      if (isAdult) return false;
    }

    // 2. Distance radius restriction (enforces search area boundary)
    const targetCenter = searchCenter || userCoords;
    if (targetCenter && workspaceMode === "search") {
      let maxDist = Infinity;
      if (distance !== "any") {
        maxDist = parseFloat(distance);
      } else {
        maxDist = 50.0; // Restrict "any" distance option to a 50-mile radius to guarantee locality
      }
      
      if (!isNaN(maxDist) && Business.location?.lat && Business.location?.lng) {
        const d = getDistanceInMiles(targetCenter.lat, targetCenter.lng, Business.location.lat, Business.location.lng);
        if (d > maxDist) return false;
      }
    }

    // Check shared / common search filters first
    if (minRating > 0 && (!Business.rating || Business.rating < minRating)) return false;

    // Website filter (Applied in both Search and Saved Mode)
    if (websiteFilter === "has" && !Business.website) return false;
    if (websiteFilter === "missing" && Business.website) return false;

    // Phone filter (Applied in both Search and Saved Mode)
    if (phoneFilter === "has" && !Business.phone) return false;
    if (phoneFilter === "missing" && Business.phone) return false;

    // SAVED-Business MODE SPECIFIC FILTERS
    if (workspaceMode === "saved") {
      const prospect = analyzeBusinessProspect(Business);
      const tags = normalizeTags(Business.opportunityTags?.length ? Business.opportunityTags : prospect.tags);

      // Business Score Filter
      if (minBusinessescore > 0) {
        const score = Business.Businessescore !== undefined ? Number(Business.Businessescore) : prospect.score;
        if (score < minBusinessescore) return false;
      }

      // Opportunity Tags presence
      if (hasOpportunityTags && (!tags || tags.length === 0)) return false;

      // Contact Status Filter
      if (contactStatusFilter !== "any") {
        const hasC = Business.contacts && Business.contacts.length > 0;
        const isSuccessNoContacts = Business.crawlStatus === "success" && (!Business.contacts || Business.contacts.length === 0);
        const isNotSearched = Business.crawlStatus === "idle" || !Business.crawlStatus;
        const isFailed = Business.crawlStatus === "error";

        if (contactStatusFilter === "has_contacts" && !hasC) return false;
        if (contactStatusFilter === "no_contacts" && !isSuccessNoContacts) return false;
        if (contactStatusFilter === "not_searched" && !isNotSearched) return false;
        if (contactStatusFilter === "failed" && !isFailed) return false;
      }

      // Contact Confidence filter
      if (contactConfidenceFilter !== "any") {
        if (!Business.contacts || Business.contacts.length === 0) return false;
        const hasConfidenceMatch = Business.contacts.some(c => {
          const conf = (c.confidence || "Medium").toLowerCase();
          return conf === contactConfidenceFilter;
        });
        if (!hasConfidenceMatch) return false;
      }

      // Follow-Up Status Filter
      if (followUpStatusFilter !== "any") {
        if (followUpStatusFilter === "none") {
          if (Business.followUpAt) return false;
        } else {
          if (!Business.followUpAt) return false;
          const fDate = new Date(Business.followUpAt);
          const now = new Date();
          const isToday = fDate.getDate() === now.getDate() && fDate.getMonth() === now.getMonth() && fDate.getFullYear() === now.getFullYear();
          const isOverdue = fDate.getTime() < now.getTime() && !isToday;

          if (followUpStatusFilter === "scheduled" && (isOverdue || isToday)) return false;
          if (followUpStatusFilter === "due_today" && !isToday) return false;
          if (followUpStatusFilter === "overdue" && !isOverdue) return false;
        }
      }

      // Sales Stage Filter (pipelineStageFilter)
      if (pipelineStageFilter !== "any") {
        const stage = Business.pipelineStage || "New";
        if (stage.toLowerCase() !== pipelineStageFilter.toLowerCase()) return false;
      }

      // Website Review Status Filter
      if (websiteReviewStatusFilter !== "any") {
        if (websiteReviewStatusFilter === "not_checked" && Business.websiteAudit) return false;
        if (websiteReviewStatusFilter === "checked" && !Business.websiteAudit) return false;
        if (websiteReviewStatusFilter === "has_issues") {
          if (!Business.websiteAudit) return false;
          const hasIssues = !Business.websiteAudit.exists || !Business.websiteAudit.mobileFriendly || Business.websiteAudit.outdatedDetails || !Business.websiteAudit.https;
          if (!hasIssues) return false;
        }
        if (websiteReviewStatusFilter === "failed") {
          const isFailed = (Business.websiteAudit && !Business.websiteAudit.exists) || Business.crawlStatus === "error";
          if (!isFailed) return false;
        }
      }

      // Suggested Pitch Status Filter
      if (pitchStatusFilter !== "any") {
        const hasPitch = !!Business.pitchAngle;
        if (pitchStatusFilter === "has_pitch" && !hasPitch) return false;
        if (pitchStatusFilter === "missing_pitch" && hasPitch) return false;
      }

      // Warning Tags Filter
      if (warningFilter !== "any") {
        const hasWarn = prospect.categorizedSignals.warnings.length > 0;
        if (warningFilter === "has_warnings" && !hasWarn) return false;
        if (warningFilter === "no_warnings" && hasWarn) return false;
      }
    }

    return true;
  });

  const displayedBusinesses = workspaceMode === "search" ? stableSearchResults : stableSavedResults;

  // Development mode diagnostic logger for audience-mode ranking verification
  useEffect(() => {
    if (displayedBusinesses && displayedBusinesses.length > 0) {
      logAudienceRankingDiagnostics(
        displayedBusinesses[0],
        null,
        keyword,
        audienceMode,
        1
      );
    }
  }, [displayedBusinesses, audienceMode]);

  // Unified, server-side paginated search executor
  const triggerBusinessesSearch = async (targetPage: number, isNewSearch: boolean = false) => {
    if (!isEmailVerified) {
      setSearchError("Verification Required: Please check your email and verify your account before searching for businesses.");
      setBusinesses([]);
      return;
    }

    const safetyCheck = detectCrisisSearch(keyword);
    if (safetyCheck.severity === "urgent_crisis") {
      setSearchError("Search Blocked: Immediate crisis safety triggers activated. If you are experiencing a life-threatening crisis, please refer to the emergency support resources.");
      setBusinesses([]);
      return;
    }

    const policyCheck = detectProhibitedUse(keyword);
    if (policyCheck.shouldBlock) {
      setSearchError(policyCheck.userMessage);
      setBusinesses([]);
      return;
    }

    if (!keyword.trim()) {
      setSearchError("Please specify a business sector, category, or keyword.");
      return;
    }

    const locationInput = zipInput.trim();

    const validationError = validateSingleLocation(locationInput);
    if (validationError) {
      setSearchError(validationError);
      setBusinesses([]);
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    setSearchStatusMessage("Resolving location coordinates...");
    setMobileSearchSubView("results");
    setWorkspaceMode("search");

    let resolvedCoords = searchCenter || userCoords;
    let finalLocationStr = zipInput;

    // Resolve the location before searching
    try {
      const resolveRes = await apiFetch(`/api/resolve-location?q=${encodeURIComponent(locationInput)}`);
      if (!resolveRes.ok) {
        const errData = await resolveRes.json();
        throw new Error(errData.error || "Could not resolve location.");
      }
      const resolveData = await resolveRes.json();
      
      resolvedCoords = {
        lat: resolveData.resolved.lat,
        lng: resolveData.resolved.lng
      };
      finalLocationStr = resolveData.resolved.formattedAddress;
      
      // Update states
      setSearchCenter(resolvedCoords);
      setUserCoords(resolvedCoords);
      setZipInput(finalLocationStr);
      setResolvedLocationDisplay(finalLocationStr);
      setSearchStatusMessage(`Location resolved to: ${finalLocationStr}. Searching businesses...`);
    } catch (resolveErr: any) {
      setIsSearching(false);
      setSearchError(resolveErr.message || "Failed to resolve search location.");
      setSearchStatusMessage("");
      return;
    }

    const locationChanged = finalLocationStr.trim() !== lastSearchedLocationRef.current.trim();
    const keywordChanged = keyword.trim() !== lastSearchedKeywordRef.current.trim();

    if (isNewSearch || locationChanged || keywordChanged) {
      setBusinesses([]);
      setStableSearchResults([]);
      setStableSnapshots([]);
      setSearchPage(1);
      setSearchTotalPages(1);
      setSearchTotalResults(0);
      setSearchSessionId(null);
    }
    // Update tracking refs
    lastSearchedLocationRef.current = finalLocationStr.trim();
    lastSearchedKeywordRef.current = keyword.trim();
    coordsUpdatedJustNowRef.current = false;

    currentRequestIdRef.current += 1;
    const thisRequestId = currentRequestIdRef.current;

    try {
      // Build active filters payload
      const filterPayload = {
        website: websiteFilter,
        phone: phoneFilter,
        distance: distance,
        selectedOpportunityTag: selectedOpportunityTag,
        isPossiblyHiring: isPossiblyHiring || opportunityFinderState.hiringNow
      };

      const params = new URLSearchParams();
      params.append("keyword", keyword);
      params.append("location", finalLocationStr);
      params.append("distance", distance);
      params.append("minRating", String(minRating));
      params.append("openNow", String(openNow));
      params.append("page", String(targetPage));
      params.append("limit", String(searchLimit));
      params.append("sort", resultsSortBy);
      params.append("travelMode", travelMode);
      params.append("maxCommuteMinutes", String(maxCommuteMinutes));
      params.append("sortByCommute", String(sortByCommute));
      
      // Pass coordinates to backend to ensure perfect location alignment
      if (resolvedCoords?.lat !== undefined && resolvedCoords?.lng !== undefined) {
        params.append("lat", String(resolvedCoords.lat));
        params.append("lng", String(resolvedCoords.lng));
      }
      
      // Pass existing search session ID only if neither query nor location changed
      if (!isNewSearch && !locationChanged && !keywordChanged && searchSessionId) {
        params.append("searchSessionId", searchSessionId);
      }
      
      params.append("filters", JSON.stringify(filterPayload));

      const res = await apiFetch(`/api/search?${params.toString()}`);
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || "Search execution failed.");
      }

      const data = await res.json();

      // Race condition protection: Ignore response if a newer search request was initiated
      if (thisRequestId !== currentRequestIdRef.current) {
        return;
      }
      
      rawSearchResultsRef.current = data.Businesses || [];
      setBusinesses(data.Businesses || []);
      setSearchPage(data.pagination.page);
      setSearchTotalPages(data.pagination.totalPages);
      setSearchTotalResults(data.pagination.totalResults);
      setSearchSessionId(data.searchSessionId);
      setSearchSummary(data.summary);
      
      if (data.center) {
        setSearchCenter(data.center);
      }

      // Calculate stable snapshot ranking ONCE on search execution
      isAllowedReorderActionRef.current = true;
      const rankingTime = Date.now();
      const sessId = data.searchSessionId || `session_${rankingTime}`;

      const { rankedSnapshots, sortedBusinesses: newSorted } = rankSearchResultsOnce(
        data.Businesses || [],
        audienceMode,
        resultsSortBy,
        data.center || resolvedCoords,
        userCoords,
        keyword,
        sessId,
        rankingTime
      );

      if (process.env.NODE_ENV !== "production" && lastOrderRef.current && lastOrderRef.current.sessionId === sessId) {
        verifyOrderStabilityDev(
          lastOrderRef.current.order,
          newSorted.map(b => b.id),
          "Search Execution Completion",
          sessId
        );
      }

      lastOrderRef.current = {
        order: newSorted.map(b => b.id),
        sessionId: sessId,
        mode: audienceMode,
        sortBy: resultsSortBy
      };

      setStableSearchResults(newSorted);
      setStableSnapshots(rankedSnapshots);
      setHasPendingBackgroundUpdates(false);
      isAllowedReorderActionRef.current = false;

      // Track search stats and trigger search-related badges
      const filtersUsed = Object.values(filterPayload).some(v => !!v) || distance !== "10" || minRating !== 0 || openNow;
      trackSearch(keyword, finalLocationStr, filtersUsed);

      if (keyword.trim()) {
        setRecentSearches(prev => {
          const updated = [keyword.trim(), ...prev.filter(q => q.toLowerCase() !== keyword.trim().toLowerCase())].slice(0, 10);
          try { localStorage.setItem("recent_business_searches", JSON.stringify(updated)); } catch {}
          return updated;
        });
      }

      setWorkspaceMode("search");
      if (newSorted.length > 0) {
        setSelectedBusinessId(newSorted[0].id);
      }

      // Record interaction in search history only on new searches
      if (isNewSearch) {
        addHistoryItem({
          lookup_type: 'business_search',
          query_keyword: keyword,
          query_zipcode: zipInput,
          target_name: `Automated Sourced List: "${keyword}" in ${zipInput} (${data.pagination.totalResults} results found)`,
          Businesses: data.Businesses || []
        });
      }

    } catch (err: any) {
      console.error("Discovery Search Error:", err);
      setSearchError(err.message || "An unexpected error occurred during search.");
      setBusinesses([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Re-trigger search on the backend whenever filters or sorting change, keeping pagination stable
  useEffect(() => {
    if (searchSessionId && workspaceMode === "search") {
      triggerBusinessesSearch(1, false);
    }
  }, [resultsSortBy, minRating, openNow, distance, websiteFilter, phoneFilter, selectedOpportunityTag, isPossiblyHiring, travelMode, maxCommuteMinutes, sortByCommute]);

  // Sync stable saved results when viewing saved workspace
  useEffect(() => {
    if (workspaceMode === "saved" && savedBusinesses.length > 0) {
      const rankingTime = Date.now();
      const { sortedBusinesses: rankedSaved } = rankSearchResultsOnce(
        savedBusinesses,
        audienceMode,
        resultsSortBy,
        searchCenter || userCoords,
        userCoords,
        keyword,
        "saved_session",
        rankingTime
      );
      setStableSavedResults(rankedSaved);
    } else if (workspaceMode === "saved" && savedBusinesses.length === 0) {
      setStableSavedResults([]);
    }
  }, [workspaceMode, savedBusinesses, audienceMode, resultsSortBy]);

  // Utility to handle copying feedback
  const [copiedText, setCopiedText] = useState<string | null>(null);

  const loadSavedBusinessesFromDb = async () => {
    if (!currentUser) return [];
    try {
      const res = await apiFetch("/api/Businesses");
      if (!res.ok) {
        if (res.status === 401 || res.status === 403 || res.status === 400) {
          return [];
        }
        throw new Error("Failed to load Businesses from database");
      }
      const data = await res.json();
      if (data.Businesses && Array.isArray(data.Businesses)) {
        setSavedBusinesses(data.Businesses);
        setDbLoadFailed(false);
        setContactSyncOrLoadFailed(false);
        return data.Businesses;
      }
    } catch (err) {
      console.error("Error loading saved database Businesses:", err);
      setDbLoadFailed(true);
      setContactSyncOrLoadFailed(true);
    }
    return [];
  };

  const finalizeAuthenticatedSession = async (session: any, source: string) => {
    if (!session || !session.user || !session.access_token) {
      throw new Error("Invalid Supabase session metadata provided.");
    }
    const { data: verifyData, error: verifyError } = await supabase.auth.getSession();
    if (verifyError || !verifyData.session || !verifyData.session.access_token) {
      throw new Error("Failed to verify active Supabase browser session: " + (verifyError?.message || "session is empty"));
    }

    const userMetadata = session.user.user_metadata || {};
    const userEmail = session.user.email || 
                      userMetadata.email || 
                      userMetadata.user_name || 
                      userMetadata.full_name || 
                      userMetadata.name || 
                      "";
    
    const appMetadata = session.user.app_metadata || {};
    const provider = appMetadata.provider || source || "";
    const isOAuth = Boolean(provider && provider !== "manual" && provider !== "email");
    const isConfirmed = Boolean(
      session.user.email_confirmed_at ||
      (session.user as any).confirmed_at ||
      userMetadata.email_verified ||
      userMetadata.is_verified ||
      userMetadata.email_confirmed ||
      isOAuth
    );

    const loggedUser = {
      id: session.user.id,
      email: userEmail,
      token: session.access_token,
      provider: provider,
      user_metadata: userMetadata,
      email_verified: isConfirmed,
      email_confirmed_at: session.user.email_confirmed_at || (session.user as any).confirmed_at || null,
      created_at: session.user.created_at || (session.user as any).created_at || null
    };

    if (isConfirmed) {
      setIsEmailVerified(true);
    }

    localStorage.setItem("Business_scout_user", JSON.stringify(loggedUser));
    const currentUid = session.user.id;
    const firstUid = localStorage.getItem("first_signed_in_user_id");
    if (!firstUid || firstUid !== currentUid) {
      localStorage.setItem("first_signed_in_user_id", currentUid);
    }

    clearApiTokenCache();
    setActiveSessionUserEmail(userEmail);
    setCurrentUser(loggedUser);
    setSupabaseSessionActive(true);
    setAuthStatus("authenticated");
    
    // Trigger daily login quest increment
    triggerQuestIncrement("login", 1);

    let removedKeyCount = 0;
    for (const key of Object.keys(sessionStorage)) {
      if (
        key.startsWith("map_hire_places_cache_") ||
        key.startsWith("biznearyou_search_cache_") ||
        key.startsWith("advanced_search_") ||
        key.includes("places_cache")
      ) {
        sessionStorage.removeItem(key);
        removedKeyCount++;
      }
    }

    setBusinesses([]);
    setSavedBusinesses([]);
    setHistory([]);
    setSearchError(null);
    setIsSearching(false);
    setSelectedBusinessId(null);
    setWorkspaceMode("search");

    if (source === "google" || source === "github") {
      bizDebug("OAUTH_SESSION_FINALIZED", {
        source,
        userId: session.user.id,
        email: userEmail,
        provider: session.user.app_metadata?.provider || source,
        hasAccessToken: Boolean(session.access_token),
        hasRefreshToken: Boolean(session.refresh_token)
      });
    }

    bizDebug("ADVANCED_SEARCH_STALE_CACHE_CLEARED", {
      removedKeyCount,
      reason: "oauth_login"
    });
  };

  // Load the GMaps API key, database status, and persistent Businesses from the server
  useEffect(() => {
    if (isAuthChecking) {
      return;
    }

    if (!currentUser || authStatus !== "authenticated" || !supabaseSessionActive) {
      setIsLoadingApiKey(false); // If we are not logged in, we are not loading the API key
      setConfigStatus("idle");
      return;
    }

    const loadStartupData = async () => {
      const currentId = currentUser?.id || "unauthenticated";
      if (currentId === lastLoadedStartupUserIdRef.current) {
        return;
      }
      lastLoadedStartupUserIdRef.current = currentId;

      const dataBootStartTime = performance.now();
      console.log(`[BIZ_DATA] startup user data started`);

      setIsLoadingApiKey(true);
      setConfigStatus("loading");
      setConfigErrorMessage(null);

      try {
        const [configResResult, dbStatusResResult] = await Promise.allSettled([
          apiFetch("/api/config"),
          apiFetch("/api/db-status"),
          loadSavedBusinessesFromDb(),
          loadHistory(),
          loadSavedSearches(),
          loadBillingStatus()
        ]);

        if (configResResult.status === "fulfilled" && configResResult.value) {
          const configRes = configResResult.value;
          if (configRes.status === 401) {
            setConfigStatus("error");
            setConfigErrorMessage("Session expired. Please log in again.");
            setAuthError("Session expired. Please log in again.");
            setIsLoadingApiKey(false);
            handleLogout();
            return;
          }
          if (configRes.ok) {
            const data = await configRes.json();
            setDynamicGoogleMapsConfig(data.googleMapsApiKey || "", data.googleMapsMapId || "");
            setApiKey(googleMapsConfig.apiKey);
            setGoogleMapsMapId(googleMapsConfig.mapId);
            setConfigStatus("loaded");
          } else {
            console.warn("[BIZ_DATA] Config endpoint returned status " + configRes.status);
          }
        }

        if (dbStatusResResult.status === "fulfilled" && dbStatusResResult.value && dbStatusResResult.value.ok) {
          const data = await dbStatusResResult.value.json();
          setDbStatus(data);
        }

        const urlParams = new URLSearchParams(window.location.search);
        const isSharedURL = urlParams.get("sharedBusinessId") || urlParams.get("sharedBusinessData");

        const pendingKeyword = localStorage.getItem("pending_search_keyword");
        const pendingLocation = localStorage.getItem("pending_search_location");

        if (pendingKeyword && authStatus === "authenticated") {
          localStorage.removeItem("pending_search_keyword");
          localStorage.removeItem("pending_search_location");
          setKeyword(pendingKeyword);
          if (pendingLocation) {
            setZipInput(pendingLocation);
          }
          setWorkspaceMode("search");
          setMobileSearchSubView("results");
          navigateTo("/dashboard");
          setTimeout(() => {
            triggerBusinessesSearch(1, true);
          }, 300);
        } else if (!isSharedURL) {
          setBusinesses([]);
          setSelectedBusinessId(null);
          setWorkspaceMode("search");
        }
        setIsLoadingBusinesses(false);
        const elapsed = Math.round(performance.now() - dataBootStartTime);
        console.log(`[BIZ_DATA] startup user data completed: ${elapsed}ms`);
      } catch (err: any) {
        if (err.message !== "401 Unauthorized") {
          console.error("Error loading startup data:", err);
          setConfigStatus("error");
          setConfigErrorMessage(err.message || "Failed to load Google Maps configuration");
        }
      } finally {
        setIsLoadingApiKey(false);
      }
    };

    loadStartupData();
  }, [currentUser, authStatus, supabaseSessionActive, isAuthChecking]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("billing_success") === "true") {
      if (authStatus === "authenticated") {
        window.history.replaceState({}, document.title, window.location.pathname);
        handleAutomaticActivation();
      }
    } else if (params.get("billing_cancel") === "true") {
      setBillingMessage({
        type: "error",
        text: "Plan checkout cancelled."
      });
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [authStatus]);

  // Shared Business Link & Route Loader (supports ?sharedBusinessId=... and /business/...)
  useEffect(() => {
    let targetBusinessId = "";

    const params = new URLSearchParams(window.location.search);
    const queryId = params.get("sharedBusinessId") || params.get("sharedBusinessData");

    if (queryId) {
      targetBusinessId = params.get("sharedBusinessId") || "";
      if (!targetBusinessId && params.get("sharedBusinessData")) {
        try {
          const decoded = JSON.parse(decodeURIComponent(escape(atob(params.get("sharedBusinessData")!))));
          if (decoded && (decoded.id || decoded.external_business_id)) {
            targetBusinessId = decoded.external_business_id || decoded.id;
          }
        } catch (e) {
          console.warn("Legacy link parsing fallback ignored:", e);
        }
      }
    } else if (currentPath.startsWith("/business/")) {
      const rawPathId = currentPath.replace(/^\/business\//, "").split("?")[0].split("#")[0];
      targetBusinessId = rawPathId ? decodeURIComponent(rawPathId) : "";
    }

    if (!targetBusinessId) {
      setSharedBusinessNotFound(false);
      return;
    }

    // Check if we already have it in state
    const localMatch = Businesses.find(b => b.id === targetBusinessId || b.external_business_id === targetBusinessId || (b as any).placeId === targetBusinessId) ||
                       savedBusinesses.find(b => b.id === targetBusinessId || b.external_business_id === targetBusinessId || (b as any).placeId === targetBusinessId);

    if (localMatch) {
      setSelectedBusinessId(localMatch.id);
      setMobileIntelOpen(true);
      setWorkspaceMode("search");
      setSharedBusinessNotFound(false);
      setSharedBusinessLoading(false);
      return;
    }

    // Fetch from backend API endpoint /api/public/businesses/:id
    setSharedBusinessLoading(true);
    setSharedBusinessNotFound(false);

    fetchJsonWithRetry(`/api/public/businesses/${encodeURIComponent(targetBusinessId)}`)
      .then((data) => {
        const b = data?.Business || data?.business;
        if (b && (b.id || b.external_business_id)) {
          const fetchedBusiness: BusinessBusiness = {
            ...b,
            id: b.id || b.external_business_id,
            contacts: b.contacts || []
          };
          setBusinesses(prev => {
            if (prev.some(x => x.id === fetchedBusiness.id)) return prev;
            return [fetchedBusiness, ...prev];
          });
          setSelectedBusinessId(fetchedBusiness.id);
          setMobileIntelOpen(true);
          setWorkspaceMode("search");
          setSharedBusinessNotFound(false);
        } else {
          setSharedBusinessNotFound(true);
        }
      })
      .catch((err) => {
        console.warn("Failed to load shared business:", err);
        setSharedBusinessNotFound(true);
      })
      .finally(() => {
        setSharedBusinessLoading(false);
      });
  }, [currentPath, Businesses, savedBusinesses]);

  // Utility to push newly found Businesses to database
  const syncBusinessesToDatabase = async (BusinessesToSync: BusinessBusiness[]) => {
    if (BusinessesToSync.length === 0) return;
    if (authStatus === "loading" || !currentUser) {
      console.log("[BusinessSync] Skipped database sync because user is unauthenticated or auth is loading.");
      return;
    }

    try {
      const response = await apiFetch("/api/Businesses", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ Businesses: BusinessesToSync }),
      });
      if (response.ok) {
        const resData = await response.json().catch(() => ({}));
        // Refresh sync check
        apiFetch("/api/db-status")
          .then((res) => res.json())
          .then((data) => setDbStatus(data))
          .catch(() => {});
        setContactSyncOrLoadFailed(false);

        if (resData?.questData) {
          if (Array.isArray(resData.questData.newlyCompleted) && resData.questData.newlyCompleted.length > 0) {
            resData.questData.newlyCompleted.forEach((comp: any) => {
              showToast(`🎉 Quest Completed: "${comp.title}"! +${comp.pointsAwarded || 20} XP awarded!`, "success");
            });
          }
          if (resData.questData.stats && resData.questData.stats.total_points !== undefined) {
            const localStats = getUserStats(currentUser?.id);
            localStats.total_points = resData.questData.stats.total_points;
            if (resData.questData.stats.lifetime_quests_completed !== undefined) {
              localStats.lifetime_quests_completed = resData.questData.stats.lifetime_quests_completed;
            }
            saveUserStats(localStats, currentUser?.id);
          }
          window.dispatchEvent(new CustomEvent("biznearyou_quests_updated", { detail: resData.questData }));
        }
        return resData;
      } else {
        const errJson = await response.json().catch(() => ({}));
        const errMsg = getErrorMessage(errJson, `Sync server responded with status ${response.status}`);
        console.error("Failed to sync Businesses to Supabase:", errMsg);
        if (response.status === 403) {
          setIsUpgradeModalOpen(true);
          loadBillingStatus();
        }
        setDbStatus(prev => prev ? { ...prev, writeError: errMsg } : { connected: true, tablesExist: true, writeError: errMsg });
        setContactSyncOrLoadFailed(true);
        throw new Error(errMsg);
      }
    } catch (err: any) {
      const errMsg = getErrorMessage(err, "Failed to sync Businesses to database.");
      console.error("Failed to sync Businesses to Supabase:", errMsg);
      setDbStatus(prev => prev ? { ...prev, writeError: errMsg } : { connected: true, tablesExist: true, writeError: errMsg });
      setContactSyncOrLoadFailed(true);
      throw new Error(errMsg);
    }
  };

  const handleSaveIndividualBusiness = async (Business: BusinessBusiness) => {
    const bizId = Business.external_business_id || Business.id;
    if (isSavingBusinessId === bizId || isSavingBusinessId === Business.id) return; // Prevent double-clicks / rapid submissions

    const alreadySaved = savedBusinesses.some(s => (s.external_business_id || s.id) === bizId || s.id === Business.id);
    if (alreadySaved) {
      showToast(`"${Business.name}" is already saved in your list.`, "info");
      return;
    }

    setIsSavingBusinessId(bizId);
    const backupSaved = [...savedBusinesses];

    try {
      await syncBusinessesToDatabase([Business]);
      await loadSavedBusinessesFromDb(); // Reload saved Businesses list
      setSyncFeedback(`Successfully saved "${Business.name}" to your list!`);
      setTimeout(() => setSyncFeedback(null), 3500);
      showToast(`Successfully saved "${Business.name}"!`, "success");
      trackInteraction(Business.id, Business.category, "save");
    } catch (err: any) {
      console.error("Manual Business sync issue:", err);
      setSavedBusinesses(backupSaved); // Rollback optimistic update
      const errMsg = err?.message || "Failed to save business.";
      showToast(errMsg, "error");
    } finally {
      setIsSavingBusinessId(null);
    }
  };

  const [isDeletingBusinessId, setIsDeletingBusinessId] = useState<string | null>(null);

  const handleDeleteIndividualBusiness = async (BusinessId: string, BusinessName: string) => {
    setIsDeletingBusinessId(BusinessId);
    try {
      const response = await apiFetch(`/api/Businesses/${BusinessId}`, {
        method: "DELETE",
      });
      if (response.ok) {
        const updated = await loadSavedBusinessesFromDb();
        setSyncFeedback(`Successfully removed "${BusinessName}" from your list.`);
        setTimeout(() => setSyncFeedback(null), 3500);
        showToast(`Removed "${BusinessName}" from saved list.`, "info");
        trackInteraction(BusinessId, "", "unsave");
        if (selectedBusinessId === BusinessId) {
          setSelectedBusinessId(updated.length > 0 ? updated[0].id : null);
        }
      } else {
        const errJson = await response.json().catch(() => ({}));
        throw new Error(errJson.error || "Failed to delete Business");
      }
    } catch (err: any) {
      console.error("Manual Business deletion issue:", err);
      showToast("Failed to remove business.", "error");
    } finally {
      setIsDeletingBusinessId(null);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    trackCopyClick();
    setCopiedText(text);
    setTimeout(() => setCopiedText(null), 2000);
    showToast(`Copied to clipboard: ${text.length > 30 ? text.slice(0, 27) + "..." : text}`, "success");
  };

  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      setSearchError("Location detection is not supported by this browser.");
      return;
    }
    setIsLocating(true);
    setSearchError(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        coordsUpdatedJustNowRef.current = true;
        setUserCoords({ lat, lng });
        setSearchCenter({ lat, lng });
        setAccuracyNotice(true);
        
        try {
          // Try reverse geocoding with Google Maps Geocoder if loaded
          if (typeof google !== "undefined" && google.maps && google.maps.Geocoder) {
            const geocoder = new google.maps.Geocoder();
            geocoder.geocode({ location: { lat, lng } }, (results, status) => {
              setIsLocating(false);
              if (status === "OK" && results && results[0]) {
                setZipInput(results[0].formatted_address);
              } else {
                setZipInput(`${lat.toFixed(5)}, ${lng.toFixed(5)}`);
              }
            });
          } else {
            setIsLocating(false);
            setZipInput(`${lat.toFixed(5)}, ${lng.toFixed(5)}`);
          }
        } catch (err) {
          setIsLocating(false);
          setZipInput(`${lat.toFixed(5)}, ${lng.toFixed(5)}`);
          setSearchError("Your location was detected, but the address could not be loaded.");
        }
      },
      (error) => {
        setIsLocating(false);
        console.error("Geolocation error:", error);
        if (error.code === error.PERMISSION_DENIED || error.code === 1) {
          setSearchError("Location access was denied. Enter a city, ZIP code, or address manually.");
        } else if (error.code === error.POSITION_UNAVAILABLE || error.code === 2) {
          setSearchError("Your current location could not be determined. Enter a location manually.");
        } else if (error.code === error.TIMEOUT || error.code === 3) {
          setSearchError("Location detection timed out. Please try again or enter a location manually.");
        } else {
          setSearchError("Your current location could not be determined. Enter a location manually.");
        }
      },
      { timeout: 10000, enableHighAccuracy: true }
    );
  };

  const { routeWithoutLang: cleanPath } = parseRouteLanguage(currentPath);

  if (cleanPath === "/forgot-password") {
    return <ForgotPasswordPage onNavigate={navigateTo} />;
  }

  if (cleanPath === "/reset-password") {
    return <ResetPasswordPage onNavigate={navigateTo} />;
  }

  const isOAuthReturn =
    !oauthCallbackHandled && (
      window.location.pathname.startsWith("/auth/callback") ||
      (window.location.hash.includes("access_token=") && !currentPath.startsWith("/reset-password")) ||
      (window.location.search.includes("code=") && !currentPath.startsWith("/reset-password"))
    );

  if (isOAuthReturn) {
    return (
      <AuthCallback 
        onLoginSuccess={handleCallbackSuccess} 
        onLoginFailure={handleCallbackFailure} 
      />
    );
  }

  if (currentPath === "/billing/cancel") {
    return (
      <BillingCancelPage 
        onReturnToPricing={() => {
          navigateTo("/");
        }}
      />
    );
  }

  if (currentPath === "/signup-success") {
    return <SignupSuccessPage onNavigate={navigateTo} />;
  }

  if (currentPath === "/login-success") {
    return <LoginSuccessPage onNavigate={navigateTo} apiFetch={apiFetch} />;
  }

  if (currentPath.startsWith("/verify-email")) {
    return (
      <VerifyEmailPage
        onNavigate={navigateTo}
        onVerificationComplete={() => {
          setIsEmailVerified(true);
          checkVerificationStatus();
        }}
      />
    );
  }

  if (currentPath === "/billing/success") {
    if (currentUser) {
      return (
        <BillingSuccessPage
          currentUser={currentUser}
          apiFetch={apiFetch}
          loadBillingStatus={loadBillingStatus}
          onBackToApp={() => {
            navigateTo("/dashboard");
          }}
        />
      );
    }
  }

  // Handle Public Pages first
  const publicRoutes = [
    "/", "/find-jobs", "/hidden-jobs", "/local-internships", "/first-jobs", 
    "/businesses-hiring-near-me", "/how-it-works", 
    "/how-to-contact-local-businesses-for-a-job", "/privacy", "/terms", "/contact", "/about",
    "/how-to-prioritize-businesses", "/how-to-organize-local-job-search",
    "/how-to-ask-if-hiring", "/faq", "/docs", "/hiring-signals-explained", "/how-signals-work",
    "/explore", "/locations", "/jobs/locations", "/businesses-hiring/locations", "/internships/locations",
    "/forgot-password", "/reset-password", "/auth/callback",
    "/hidden-jobs-near-me", "/local-businesses-hiring", "/companies-hiring-without-job-postings",
    "/small-businesses-hiring-near-me", "/local-job-search-tool", "/business-opportunity-finder",
    "/local-lead-finder", "/recruiter-business-search", "/staffing-agency-leads",
    "/career-coach-job-search-tool", "/workforce-development-business-search",
    "/college-career-center-employer-search", "/sales-prospecting-local-businesses",
    "/business-development-local-opportunities", "/agency-client-finder", "/business-explorer"
  ];

  const redirectMatch = getStandardizedLocationRedirect(cleanPath);

  // 1. Static Location Directory Pages
  const isStaticLocationDirectoryRoute = 
    cleanPath === "/locations" ||
    cleanPath === "/jobs/locations" ||
    cleanPath === "/businesses-hiring/locations" ||
    cleanPath === "/internships/locations";

  let directoryVariant: "all" | "jobs" | "businesses-hiring" | "internships" = "all";
  if (cleanPath === "/jobs/locations") directoryVariant = "jobs";
  if (cleanPath === "/businesses-hiring/locations") directoryVariant = "businesses-hiring";
  if (cleanPath === "/internships/locations") directoryVariant = "internships";

  // 2. Dynamic Location & State Hub resolution
  let stateHubSlug: string | null = null;
  let countryHubCode: string | null = null;
  let isDynamicLocationRoute = false;
  let locationVariant: "jobs" | "businesses-hiring" | "internships" = "jobs";
  let locationSlug = "";
  let locationNicheSlug = "";

  if (!isStaticLocationDirectoryRoute) {
    if (redirectMatch) {
      locationVariant = redirectMatch.variant;
      locationSlug = redirectMatch.citySlug;
      isDynamicLocationRoute = true;
    } else {
      let rawSlug = "";
      if (cleanPath.startsWith("/businesses-hiring/")) {
        locationVariant = "businesses-hiring";
        rawSlug = cleanPath.replace("/businesses-hiring/", "");
      } else if (cleanPath.startsWith("/internships/")) {
        locationVariant = "internships";
        rawSlug = cleanPath.replace("/internships/", "");
      } else if (cleanPath.startsWith("/jobs/")) {
        locationVariant = "jobs";
        rawSlug = cleanPath.replace("/jobs/", "");
      } else if (cleanPath.startsWith("/locations/")) {
        rawSlug = cleanPath.replace("/locations/", "");
      }

      if (rawSlug) {
        const parts = rawSlug.split("/").filter(Boolean);
        if (parts.length >= 2) {
          const lastPart = parts[parts.length - 1];
          const nicheIntent = getNicheIntentBySlug(lastPart);
          if (nicheIntent) {
            locationNicheSlug = nicheIntent.slug;
            rawSlug = parts[parts.length - 2];
          } else {
            rawSlug = parts[parts.length - 1];
          }
        }
        const resolved = resolveLocationSlug(rawSlug);
        if (resolved.type === "state") {
          stateHubSlug = resolved.normalizedSlug;
        } else if (resolved.type === "country" && resolved.countryRecord) {
          countryHubCode = resolved.countryRecord.countryCode;
        } else if (resolved.type === "supported" || resolved.type === "unsupported") {
          isDynamicLocationRoute = true;
          locationSlug = rawSlug;
        }
      }
    }
  }

  const seoEntry = getSeoEntryByRoute(cleanPath);
  const isPublicRoute = publicRoutes.includes(cleanPath) || isStaticLocationDirectoryRoute || isDynamicLocationRoute || Boolean(stateHubSlug) || Boolean(countryHubCode) || !!seoEntry;
  const privateAndAuthPrefixes = ["/dashboard", "/app", "/settings", "/saved", "/account", "/login", "/signup", "/billing", "/signup-success", "/login-success", "/forgot-password", "/reset-password", "/auth/callback"];
  const isPrivateOrAuthPrefix = privateAndAuthPrefixes.some(p => cleanPath === p || cleanPath.startsWith(p + "/"));
  const isUnknownRoute = !isPublicRoute && !isPrivateOrAuthPrefix;

  if (isPublicRoute || isUnknownRoute) {
    return (
      <div className="min-h-screen flex flex-col bg-slate-50 font-sans">
        <PublicHeader 
          currentPath={currentPath}
          onNavigate={navigateTo}
          currentUser={currentUser}
          settingsAppTheme={settingsAppTheme}
          onThemeChange={(newTheme) => handleSaveUserSettings(newTheme, mapStyle, settingsLanguage)}
          onLogin={(isSignUp) => {
            setIsSignUpMode(!!isSignUp);
            navigateTo(isSignUp ? "/signup" : "/login");
          }}
        />
        <main className="flex-grow">
          {isUnknownRoute && <PageNotFound onNavigate={navigateTo} />}
          {isStaticLocationDirectoryRoute && (
            <LocationDirectoryPage
              onNavigate={navigateTo}
              variant={directoryVariant}
              onLogin={(isSignUp) => {
                setIsSignUpMode(!!isSignUp);
                navigateTo(isSignUp ? "/signup" : "/login");
              }}
            />
          )}
          {Boolean(stateHubSlug) && (
            <StateHubPage
              stateSlug={stateHubSlug!}
              variant={locationVariant}
              onNavigate={navigateTo}
              onLogin={(isSignUp) => {
                setIsSignUpMode(!!isSignUp);
                navigateTo(isSignUp ? "/signup" : "/login");
              }}
              currentUser={currentUser}
            />
          )}
          {Boolean(countryHubCode) && (
            <LocationDirectoryPage
              onNavigate={navigateTo}
              variant={locationVariant}
              initialCountryCode={countryHubCode!}
              onLogin={(isSignUp) => {
                setIsSignUpMode(!!isSignUp);
                navigateTo(isSignUp ? "/signup" : "/login");
              }}
            />
          )}
          {isDynamicLocationRoute && (
            <LocationPage
              onNavigate={navigateTo}
              onLogin={(isSignUp) => {
                setIsSignUpMode(!!isSignUp);
                navigateTo(isSignUp ? "/signup" : "/login");
              }}
              variant={locationVariant}
              locationSlug={locationSlug}
              nicheSlug={locationNicheSlug}
            />
          )}
          {cleanPath === "/" && <PublicHomepage onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} onHomepageSearch={handleHomepageSearch} />}
          {cleanPath === "/find-jobs" && <FindJobsPage onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} />}
          {cleanPath === "/hidden-jobs" && <HiddenJobsPage onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} />}
          {cleanPath === "/local-internships" && <LocalInternshipsPage onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} />}
          {cleanPath === "/first-jobs" && <FirstJobsPage onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} />}
          {cleanPath === "/businesses-hiring-near-me" && <BusinessesHiringNearMePage onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} />}
          {cleanPath === "/how-it-works" && <HowItWorksPage onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} />}
          {cleanPath === "/how-to-contact-local-businesses-for-a-job" && <HowToContactBusinessesPage onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} />}
          {cleanPath === "/how-to-prioritize-businesses" && <HowToPrioritizeBusinessesPage onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} />}
          {cleanPath === "/how-to-organize-local-job-search" && <HowToOrganizeLocalJobSearchPage onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} />}
          {cleanPath === "/how-to-ask-if-hiring" && <HowToAskIfHiringPage onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} />}
          {cleanPath === "/privacy" && <PrivacyPage onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} />}
          {cleanPath === "/terms" && <TermsPage onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} />}
          {cleanPath === "/contact" && <ContactPage onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} />}
          {cleanPath === "/about" && <AboutPage onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} />}
          {cleanPath === "/pricing" && <PublicPricingPage onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} />}
          {cleanPath === "/why-biznearyou" && <PublicWhyBizNearYou onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} />}
          {cleanPath === "/faq" && <PublicFaq onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} />}
          {cleanPath === "/how-data-is-collected" && <PublicHowDataIsCollected onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} />}
          {cleanPath === "/how-scores-are-calculated" && <PublicHowScoresAreCalculated onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} />}
          {cleanPath === "/how-hiring-signals-work" && <PublicHowHiringSignalsWork onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} />}
          {cleanPath === "/how-signals-work" && <HowSignalsWorkPage onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} />}
          {cleanPath === "/why-businesses-before-job-listings" && <PublicWhyBusinessesBeforeJobListings onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} />}

          {/* Audience Directory Hub Page */}
          {cleanPath === "/for" && (
            <AudienceDirectoryPage
              onNavigate={navigateTo}
              onLogin={(isSignUp) => {
                setIsSignUpMode(!!isSignUp);
                navigateTo(isSignUp ? "/signup" : "/login");
              }}
            />
          )}

          {/* Multi-Audience Expansion & Methodology Pages */}
          {[
            "/for-job-seekers", "/for-recruiters", "/for-staffing-agencies",
            "/for-career-coaches", "/for-workforce-organizations", "/for-college-career-centers",
            "/for-sales-teams", "/for-business-development", "/for-agencies",
            "/business-hiring-signals", "/local-business-prospecting", "/employer-prospecting",
            "/staffing-prospecting", "/recruiter-business-search", "/hiring-signal-methodology",
            "/opportunity-score-methodology", "/data-sources", "/accuracy-and-limitations",
            "/hidden-jobs-near-me", "/local-businesses-hiring", "/companies-hiring-without-job-postings",
            "/small-businesses-hiring-near-me", "/local-job-search-tool", "/business-opportunity-finder",
            "/local-lead-finder", "/staffing-agency-leads",
            "/career-coach-job-search-tool", "/workforce-development-business-search",
            "/college-career-center-employer-search", "/sales-prospecting-local-businesses",
            "/business-development-local-opportunities", "/agency-client-finder", "/business-explorer"
          ].includes(cleanPath) && (
            <AudienceLandingPage
              route={cleanPath}
              onNavigate={navigateTo}
              onSelectAudienceMode={handleSelectAudienceMode}
              onLogin={(isSignUp) => {
                setIsSignUpMode(!!isSignUp);
                navigateTo(isSignUp ? "/signup" : "/login");
              }}
            />
          )}

          {/* New Expanded SEO Pages */}
          {(cleanPath === "/explore" || seoEntry?.pageType === "explore-hub") && (
            <ExploreHubPage
              onNavigate={navigateTo}
              onLogin={(isSignUp) => {
                setIsSignUpMode(!!isSignUp);
                navigateTo(isSignUp ? "/signup" : "/login");
              }}
              currentUser={currentUser}
            />
          )}
          {seoEntry?.pageType === "industry-directory" && (
            <IndustryDirectoryPage onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} currentUser={currentUser} />
          )}
          {(seoEntry?.pageType === "industry" || seoEntry?.pageType === "industry-location") && (() => {
            const parts = cleanPath.replace("/industries/", "").split("/");
            return (
              <IndustryPage
                industrySlug={parts[0] || ""}
                citySlug={parts[1] || ""}
                onNavigate={navigateTo}
                onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }}
                currentUser={currentUser}
              />
            );
          })()}
          {seoEntry?.pageType === "guide-hub" && (
            <GuideHubPage onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} currentUser={currentUser} />
          )}
          {seoEntry?.pageType === "guide" && (
            <GuidePage guideSlug={cleanPath.replace("/guides/", "")} onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} currentUser={currentUser} />
          )}
          {seoEntry?.pageType === "feature-hub" && (
            <FeatureHubPage onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} currentUser={currentUser} />
          )}
          {seoEntry?.pageType === "feature" && (
            <FeaturePage featureSlug={cleanPath.replace("/features/", "")} onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} currentUser={currentUser} />
          )}
          {seoEntry?.pageType === "report-hub" && (
            <ReportHubPage onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} currentUser={currentUser} />
          )}
          {seoEntry?.pageType === "report" && (
            <ReportPage reportSlug={cleanPath.replace("/reports/", "")} onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} currentUser={currentUser} />
          )}
          {seoEntry?.pageType === "methodology" && (
            <MethodologyPage slug={cleanPath.replace("/methodology/", "")} onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} currentUser={currentUser} />
          )}
          {seoEntry?.pageType === "compare-hub" && (
            <CompareHubPage onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} currentUser={currentUser} />
          )}
          {seoEntry?.pageType === "compare" && (
            <ComparePage compareSlug={cleanPath.replace("/compare/", "")} onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} currentUser={currentUser} />
          )}
          {cleanPath === "/jobs" && (
            <JobDirectoryHubPage onNavigate={navigateTo} />
          )}
          {cleanPath.startsWith("/jobs/") && (() => {
            const raw = cleanPath.replace("/jobs/", "");
            const parts = raw.split("/").filter(Boolean);

            if (parts.length === 1) {
              const catDef = getJobCategoryBySlug(parts[0]);
              if (catDef) {
                return <JobCategoryPage category={catDef} onNavigate={navigateTo} />;
              }
            } else if (parts.length === 2) {
              let locSlug = parts[0];
              let catSlug = parts[1];

              let resLoc = resolveLocationSlug(locSlug);
              let catDef = getJobCategoryBySlug(catSlug);

              // Try reverse order if first check didn't match both
              if ((resLoc.type === "invalid" || !resLoc.locationRecord) && !catDef) {
                resLoc = resolveLocationSlug(parts[1]);
                catDef = getJobCategoryBySlug(parts[0]);
              }

              if (resLoc.locationRecord && catDef) {
                return (
                  <JobCategoryLocationPage 
                    category={catDef}
                    location={resLoc.locationRecord}
                    onNavigate={navigateTo}
                    onLogin={(isSignUp) => {
                      setIsSignUpMode(!!isSignUp);
                      navigateTo(isSignUp ? "/signup" : "/login");
                    }}
                  />
                );
              }
            }
            return null;
          })()}
          {(cleanPath === "/docs" || cleanPath === "/faq") && (
            <div className="bg-slate-50 min-h-screen">
              <DocumentationPage
                onUpgradeClick={() => {
                  setIsSignUpMode(true);
                  navigateTo("/signup");
                }}
                onOpenSettingsClick={() => {}}
                onOpenLegalTab={(tab) => {
                  if (tab === "contact-removal") {
                    navigateTo("/contact");
                  } else {
                    setActiveLegalTab(tab as any);
                    setIsLegalModalOpen(true);
                  }
                }}
                isProUser={false}
                onContactSupportClick={() => navigateTo("/contact")}
                currentUser={currentUser}
                onNavigate={navigateTo}
              />
            </div>
          )}
          {cleanPath === "/hiring-signals-explained" && <HiringSignalsExplainedPage onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} />}
        </main>
        <PublicFooter onNavigate={navigateTo} onLogin={(isSignUp) => { setIsSignUpMode(!!isSignUp); navigateTo(isSignUp ? "/signup" : "/login"); }} />
      </div>
    );
  }

  // Handle Shared Business Route loading and error states
  const hasSharedBusinessQuery = typeof window !== "undefined" && new URLSearchParams(window.location.search).has("sharedBusinessId");
  const isSharedBusinessRoute = currentPath.startsWith("/business/") || hasSharedBusinessQuery;

  if (isSharedBusinessRoute && sharedBusinessLoading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0F172A] flex flex-col items-center justify-center p-6 text-center font-sans">
        <Loader2 className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin mb-3" />
        <h3 className="font-bold text-slate-800 dark:text-slate-200 text-sm">Finding shared business…</h3>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Locating listing details and intelligence data.</p>
      </div>
    );
  }

  if (isSharedBusinessRoute && sharedBusinessNotFound) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0F172A] flex flex-col items-center justify-center p-6 text-center font-sans">
        <div className="max-w-md w-full bg-white dark:bg-[#1E293B] border border-slate-200 dark:border-slate-700/80 rounded-2xl p-8 shadow-xl space-y-5">
          <div className="w-12 h-12 bg-rose-50 dark:bg-rose-950/50 border border-rose-200 dark:border-rose-800 rounded-full flex items-center justify-center mx-auto text-rose-600 dark:text-rose-400">
            <AlertCircle className="w-6 h-6" />
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-extrabold text-slate-900 dark:text-white tracking-tight">Business not found</h2>
            <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed font-medium">
              We could not locate the business from this shared link. It may no longer be available or the link may be incomplete.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2.5 pt-2">
            <button
              onClick={() => navigateTo("/dashboard")}
              className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-xl transition shadow-sm cursor-pointer"
            >
              Search Businesses
            </button>
            <button
              onClick={() => navigateTo("/dashboard")}
              className="flex-1 px-4 py-2.5 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-200 font-bold text-xs rounded-xl transition cursor-pointer"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Handle Login / Signup Routes
  if (cleanPath === "/login" || cleanPath === "/signup") {
    // If authenticated, render loader while centralized route guard redirects to dashboard
    if (authStatus === "authenticated") {
      return (
        <div className="min-h-screen bg-[#0F172A] text-slate-100 flex flex-col justify-center items-center p-4">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin mb-4" />
          <p className="text-sm text-slate-400">Opening workspace...</p>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0F172A] text-slate-900 dark:text-slate-100 flex flex-col justify-center items-center p-4 md:p-6 font-sans relative overflow-hidden">
        {/* Abstract Background Design Accents */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-500/10 dark:bg-blue-900/10 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-500/10 dark:bg-emerald-900/10 rounded-full blur-3xl pointer-events-none"></div>
        
        {/* Back Link to Home */}
        <button 
          onClick={() => navigateTo("/")}
          className="absolute top-6 left-6 text-xs text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition flex items-center gap-1 cursor-pointer"
        >
          <ChevronLeft className="w-4 h-4" /> Back to Home
        </button>

        {/* Public Theme & Language Selectors on Login/Signup Page */}
        <div className="absolute top-6 right-6 z-20 flex items-center gap-2">
          <ThemeSelector 
            currentTheme={settingsAppTheme} 
            onThemeChange={(newTheme) => handleSaveUserSettings(newTheme, mapStyle, settingsLanguage)} 
            variant="dropdown" 
            idPrefix="auth-page-theme" 
          />
          <LanguageSelector
            variant="dropdown"
            idPrefix="auth-page-lang"
          />
        </div>

        <div className="max-w-md w-full bg-white dark:bg-[#1E293B] border border-slate-200 dark:border-slate-700/60 rounded-xl p-8 shadow-xl dark:shadow-2xl relative z-10">
          <div className="flex flex-col items-center mb-8">
            <BizNearYouLogo textColor="text-slate-900 dark:text-white" className="scale-125 mb-3" />
            <p className="text-slate-600 dark:text-slate-400 text-center text-xs mt-1.5 font-medium tracking-wide">
              Find Business Opportunities in your area.
            </p>
          </div>

          {activeSessionUserEmail ? (
            <div className="bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700/60 rounded-lg p-5 text-center space-y-4">
              <div className="flex justify-center text-indigo-600 dark:text-blue-400">
                <ShieldCheck className="w-12 h-12" />
              </div>
              <h3 className="text-slate-900 dark:text-white font-semibold text-sm">Active Workspace Session</h3>
              <p className="text-slate-600 dark:text-slate-300 text-xs leading-relaxed">
                You are already signed in as <span className="font-semibold text-indigo-600 dark:text-blue-400">{activeSessionUserEmail}</span>. Sign out before switching accounts.
              </p>
              <button
                type="button"
                onClick={handleLogout}
                className="w-full bg-rose-600 hover:bg-rose-500 text-white font-bold py-2.5 px-4 rounded-lg text-xs transition tracking-wider flex items-center justify-center gap-2 cursor-pointer shadow-xs mt-2"
              >
                Sign Out of Account
              </button>
            </div>
          ) : (
            <>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-6 text-center">
                {isSignUpMode ? "Create an Account" : "Access Your Workspace"}
              </h2>

              <form onSubmit={handleAuthSubmit} className="space-y-4">
                {authError && (
                  <div className="flex flex-col gap-2.5 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800/50 rounded-lg p-3 text-xs text-rose-800 dark:text-rose-300">
                    <div className="flex gap-2">
                      <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                      <span>{authError}</span>
                    </div>
                    {(authError.toLowerCase().includes("confirm") || authError.toLowerCase().includes("verify") || authError.toLowerCase().includes("verification")) && (
                      <div className="mt-1 pt-2 border-t border-rose-200 dark:border-rose-800/30 flex flex-col gap-1.5">
                        <button
                          type="button"
                          id="btn-login-resend-verification"
                          disabled={isPublicResending}
                          onClick={handlePublicResendVerification}
                          className="w-full text-center py-1.5 px-3 bg-rose-100 dark:bg-rose-900/40 hover:bg-rose-200 dark:hover:bg-rose-900/70 text-rose-900 dark:text-rose-200 font-semibold rounded-md transition duration-150 disabled:opacity-50 cursor-pointer text-[10.5px] border border-rose-200 dark:border-rose-800/40"
                        >
                          {isPublicResending ? "Resending Email..." : "Resend Verification Link"}
                        </button>
                        {publicResendMessage && (
                          <div className="text-[10px] text-slate-700 dark:text-slate-300 mt-1 text-center bg-white dark:bg-slate-900/60 p-2 rounded-md border border-slate-200 dark:border-slate-800 leading-normal">
                            {publicResendMessage}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {signUpSuccessMessage && (
                  <div className="flex gap-2 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/50 rounded-lg p-3 text-xs text-emerald-800 dark:text-emerald-300">
                    <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>{signUpSuccessMessage}</span>
                  </div>
                )}

                <div>
                  <label className="block text-[10px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-widest mb-1.5">
                    Work Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 w-4 h-4 text-slate-400 dark:text-slate-500" />
                    <input 
                      type="email" 
                      required
                      value={authEmail}
                      onChange={(e) => setAuthEmail(e.target.value)}
                      placeholder="name@company.com" 
                      className="w-full bg-slate-50 dark:bg-[#0F172A] border border-slate-300 dark:border-slate-700 rounded-lg pl-10 pr-4 py-2.5 text-sm hover:border-slate-400 dark:hover:border-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 text-slate-900 dark:text-white font-medium placeholder:text-slate-400 dark:placeholder:text-slate-500"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-[10px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-widest">
                      Password
                    </label>
                    {!isSignUpMode && (
                      <button
                        type="button"
                        onClick={() => navigateTo("/forgot-password")}
                        className="text-[11px] font-semibold text-indigo-600 dark:text-blue-400 hover:underline cursor-pointer"
                      >
                        Forgot password?
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 w-4 h-4 text-slate-400 dark:text-slate-500" />
                    <input 
                      type="password" 
                      required
                      minLength={6}
                      value={authPassword}
                      onChange={(e) => setAuthPassword(e.target.value)}
                      placeholder="••••••••" 
                      className="w-full bg-slate-50 dark:bg-[#0F172A] border border-slate-300 dark:border-slate-700 rounded-lg pl-10 pr-4 py-2.5 text-sm hover:border-slate-400 dark:hover:border-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 text-slate-900 dark:text-white font-medium placeholder:text-slate-400 dark:placeholder:text-slate-500"
                    />
                  </div>
                  {isSignUpMode && (
                    <span className="text-[10px] text-slate-500 mt-1 block">
                      Must be at least 6 characters.
                    </span>
                  )}
                </div>

                {isSignUpMode && (
                  <div>
                    <label className="block text-[10px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-widest mb-1.5">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 w-4 h-4 text-slate-400 dark:text-slate-500" />
                      <input 
                        type="password" 
                        required
                        value={authConfirmPassword}
                        onChange={(e) => setAuthConfirmPassword(e.target.value)}
                        placeholder="••••••••" 
                        className="w-full bg-slate-50 dark:bg-[#0F172A] border border-slate-300 dark:border-slate-700 rounded-lg pl-10 pr-4 py-2.5 text-sm hover:border-slate-400 dark:hover:border-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 text-slate-900 dark:text-white font-medium placeholder:text-slate-400 dark:placeholder:text-slate-500"
                      />
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isAuthLoading}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 dark:bg-blue-600 dark:hover:bg-blue-500 text-white font-bold py-2.5 px-4 rounded-lg text-sm transition tracking-wider flex items-center justify-center gap-2 cursor-pointer shadow-xs disabled:bg-slate-200 dark:disabled:bg-slate-700 disabled:text-slate-400 disabled:cursor-not-allowed mt-6"
                >
                  {isAuthLoading ? (
                    <Loader2 className="w-4 h-4 text-white animate-spin" />
                  ) : isSignUpMode ? (
                    "Create Account"
                  ) : (
                    "Enter Workspace"
                  )}
                </button>
              </form>

              {/* OR CONTINUE WITH DIVIDER */}
              <div className="relative my-6 flex items-center justify-center">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-slate-200 dark:border-slate-700/60"></span>
                </div>
                <span className="relative bg-white dark:bg-[#1E293B] px-3 text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">
                  Or Continue With
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  id="google-oauth-btn"
                  type="button"
                  onClick={() => handleOAuthLogin("google")}
                  disabled={isAuthLoading}
                  className="flex items-center justify-center gap-2 bg-slate-50 dark:bg-[#0F172A] border border-slate-200 dark:border-slate-700/80 hover:border-slate-300 dark:hover:border-slate-500 hover:bg-slate-100 dark:hover:bg-[#1E293B] text-slate-800 dark:text-slate-200 text-xs font-semibold py-2.5 px-3 rounded-lg transition cursor-pointer disabled:opacity-50"
                >
                  <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.85z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.85c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span>Google</span>
                </button>
                <button
                  id="github-oauth-btn"
                  type="button"
                  onClick={() => handleOAuthLogin("github")}
                  disabled={isAuthLoading}
                  className="flex items-center justify-center gap-2 bg-slate-50 dark:bg-[#0F172A] border border-slate-200 dark:border-slate-700/80 hover:border-slate-300 dark:hover:border-slate-500 hover:bg-slate-100 dark:hover:bg-[#1E293B] text-slate-800 dark:text-slate-200 text-xs font-semibold py-2.5 px-3 rounded-lg transition cursor-pointer disabled:opacity-50"
                >
                  <svg className="w-4 h-4 shrink-0 fill-current" viewBox="0 0 24 24">
                    <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/>
                  </svg>
                  <span>GitHub</span>
                </button>
              </div>

              <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700/60 text-center">
                <button
                  type="button"
                  onClick={() => {
                    const newPath = isSignUpMode ? "/login" : "/signup";
                    navigateTo(newPath);
                  }}
                  className="text-xs text-indigo-600 dark:text-blue-400 hover:text-indigo-700 dark:hover:text-blue-300 font-semibold underline underline-offset-4 cursor-pointer focus:outline-none"
                >
                  {isSignUpMode ? "Already have an account? Log In" : "Need a workspace? Register Here"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // Handle Private Routes session checking & authentication gating
  if (isAuthChecking) {
    return (
      <div className="min-h-screen bg-[#0F172A] text-slate-100 flex flex-col justify-center items-center p-6 text-center font-sans">
        <div className="max-w-sm w-full bg-[#1E293B] border border-slate-700/80 rounded-2xl p-6 shadow-xl space-y-4">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto" />
          <div className="space-y-1">
            <h3 className="text-base font-semibold text-white">Verifying workspace session…</h3>
            <p className="text-xs text-slate-400">Checking authentication status.</p>
          </div>
          {(authCheckingSlow || authTimeoutOccurred) && (
            <div className="pt-3 border-t border-slate-700/60 space-y-3">
              <p className="text-xs text-amber-400 font-medium">
                Verification is taking longer than expected.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => performAuthBootstrap()}
                  className="flex-1 py-2 px-3 bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold rounded-lg transition cursor-pointer"
                >
                  Retry Verification
                </button>
                <button
                  onClick={() => navigateTo("/")}
                  className="flex-1 py-2 px-3 bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs font-semibold rounded-lg transition cursor-pointer"
                >
                  Go to Homepage
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (authStatus !== "authenticated" || !supabaseSessionActive) {
    if (authTimeoutOccurred) {
      return (
        <div className="min-h-screen bg-[#0F172A] text-slate-100 flex flex-col justify-center items-center p-6 text-center font-sans">
          <div className="max-w-sm w-full bg-[#1E293B] border border-slate-700/80 rounded-2xl p-6 shadow-xl space-y-4">
            <div className="w-10 h-10 bg-amber-500/10 border border-amber-500/30 rounded-full flex items-center justify-center mx-auto text-amber-400">
              <AlertCircle className="w-5 h-5" />
            </div>
            <div className="space-y-1">
              <h3 className="text-base font-semibold text-white">Unable to verify session right now</h3>
              <p className="text-xs text-slate-400">Please check your network connection or try again.</p>
            </div>
            <div className="flex gap-2 pt-2">
              <button
                onClick={() => performAuthBootstrap()}
                className="flex-1 py-2 px-3 bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold rounded-lg transition cursor-pointer"
              >
                Retry Verification
              </button>
              <button
                onClick={() => navigateTo("/")}
                className="flex-1 py-2 px-3 bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs font-semibold rounded-lg transition cursor-pointer"
              >
                Go to Homepage
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-[#0F172A] text-slate-100 flex flex-col justify-center items-center p-4">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin mb-4" />
        <p className="text-sm text-slate-400 font-sans">Please sign in to access this workspace...</p>
      </div>
    );
  }

  // Handle Logged-In Unknown Paths (Default to /dashboard)
  const isDashboardRoute = ["/dashboard", "/app", "/settings", "/saved", "/account"].some(p => currentPath.startsWith(p));
  if (!isDashboardRoute) {
    return (
      <div className="min-h-screen bg-[#0F172A] text-slate-100 flex flex-col justify-center items-center p-4">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin mb-4" />
        <p className="text-sm text-slate-400 font-sans">Opening workspace dashboard...</p>
      </div>
    );
  }

  if (isLoadingApiKey || configStatus === "loading") {
    return (
      <div className="min-h-screen bg-[#0F172A] flex flex-col items-center justify-center text-slate-100">
        <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
        <p className="text-slate-400 font-medium">Booting BizNearYou...</p>
      </div>
    );
  }

  if (authStatus === "authenticated" && configStatus === "error") {
    return (
      <div className="min-h-screen bg-[#F1F5F9] flex items-center justify-center p-6 text-slate-900 font-sans">
        <div className="max-w-xl w-full bg-white border border-slate-200 rounded-lg p-8 shadow-md text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-red-50 p-3 rounded-full border border-red-200">
              <AlertCircle className="w-10 h-10 text-red-600 animate-pulse" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Workspace Configuration Failed</h2>
          <p className="text-slate-500 text-sm mb-6 leading-relaxed">
            {configErrorMessage || "Failed to load Google Maps and workspace configuration. Please check your network connection or try again."}
          </p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => {
                window.location.reload();
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 px-6 rounded text-sm transition"
            >
              Retry Loading
            </button>
            <button
              onClick={() => {
                handleLogout();
              }}
              className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-2.5 px-6 rounded text-sm transition border border-slate-300"
            >
              Log Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check if they configured the platform key. In AI Studio editor mode, allow rendering with a map placeholder.
  const hasValidKey = isEditorSafeMode || googleMapsConfig.hasKey;
  
  // Only show key required page if authenticated, config loaded successfully, and key is missing/placeholder.
  const showKeyRequiredScreen = authStatus === "authenticated" && configStatus === "loaded" && !hasValidKey;

  if (showKeyRequiredScreen) {
    return (
      <div className="min-h-screen bg-[#F1F5F9] flex items-center justify-center p-6 text-slate-900 font-sans">
        <div className="max-w-xl w-full bg-white border border-slate-200 rounded-lg p-8 shadow-md">
          <div className="flex justify-center mb-6">
            <div className="bg-blue-50 p-3 rounded-full border border-blue-200">
              <MapPin className="w-10 h-10 text-blue-600 animate-pulse" />
            </div>
          </div>
          
          <h2 className="text-2xl font-bold text-center text-slate-900 mb-2">Google Maps API Key Required</h2>
          <p className="text-slate-500 text-center text-sm mb-6 leading-relaxed">
            This workspace utilizes the Google Maps Places SDK to fetch exact local business records.
          </p>
          
          <div className="space-y-4 bg-slate-50 p-6 rounded border border-slate-200 text-sm">
            <p className="font-semibold text-slate-800">Please configure and restrict your Google Maps API Key:</p>
            <ol className="list-decimal list-inside space-y-3 text-slate-600">
              <li>
                <a 
                  href="https://console.cloud.google.com/google/maps-apis/start?utm_campaign=gmp-code-assist-ais" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline inline-flex items-center gap-1 font-medium"
                >
                  Get a Google Maps API Key <ExternalLink className="w-3 h-3" />
                </a>
              </li>
              <li>
                In your Google Cloud Console, navigate to <strong className="text-slate-800">APIs & Services &gt; Credentials</strong> and select your key.
              </li>
              <li className="pl-4 border-l-2 border-amber-300 bg-amber-50/50 p-2 rounded text-xs text-amber-800 leading-normal">
                <strong>Crucial Security Step (RESTRICT BY DOMAIN):</strong> Under <em>Application restrictions</em>, select <strong>Websites (HTTP referrers)</strong> and authorize your preview workspace domain pattern (e.g. <code>*.run.app/*</code>, <code>localhost:*/*</code>). This stops unauthorized sites from stealing and running up charges on your API key. // i18n-ignore
              </li>
              <li className="pl-4 border-l-2 border-blue-300 bg-blue-50/50 p-2 rounded text-xs text-blue-800 leading-normal">
                <strong>API Restricting:</strong> Under <em>API restrictions</em>, check <strong>Restrict key</strong> and choose only the necessary Maps APIs: // i18n-ignore
                <div className="font-mono text-[10px] mt-1 text-slate-700 bg-white border border-slate-200 p-1.5 rounded space-y-0.5">
                  <div>✔ Maps JavaScript API</div>
                  <div>✔ Places API (and Places API New)</div>
                  <div>✔ Geocoding API</div>
                </div>
              </li>
              <li className="pl-4 border-l-2 border-emerald-300 bg-emerald-50/50 p-2 rounded text-xs text-emerald-800 leading-normal">
                <strong>Billing Guards (Alerts & Quotas): {/* i18n-ignore */}</strong>
                <ol className="list-disc list-inside mt-1 pl-2 space-y-0.5 text-slate-600 font-sans">
                  <li>Set up <strong>Billing Budgets & Alerts</strong> from the Billing section to automatically get notified before hitting unexpected costs. {/* i18n-ignore */}</li>
                  <li>Enable <strong>API Query limits (Quotas)</strong> inside to cap daily maximum usages and enforce run budgets. {/* i18n-ignore */}</li>
                </ol>
              </li>
              <li>
                Now open the <strong className="text-slate-900">Settings</strong> panel (⚙️ gear icon, top-right corner of the editor)
              </li>
              <li>
                Add a secret named <code className="bg-slate-200 text-blue-600 px-1.5 py-0.5 rounded font-mono text-xs">GOOGLE_MAPS_PLATFORM_KEY</code>
              </li>
              <li>
                Paste your API key as the value and save.
              </li>
            </ol>
          </div>
          
          <div className="mt-6 text-center text-xs text-slate-400">
            The app builder will read the environment variables and mount automatically.
          </div>
        </div>
      </div>
    );
  }

  // Active/selected Business details object
  const activeBusinessRaw = currentModeBusinesses.find((l) => l.id === selectedBusinessId) || null;
  const activeBusiness = activeBusinessRaw ? {
    ...activeBusinessRaw,
    crawlStatus: (activeBusinessRaw.contacts && activeBusinessRaw.contacts.length > 0) ? ("success" as const) : activeBusinessRaw.crawlStatus
  } : null;

  // Find previously indexed direct personnel lists from history logs
  const previousLookupMatch = activeBusiness 
    ? history.find(h => h.lookup_type === "contact_crawl" && (h.Business_id === activeBusiness.id || h.Business_name === activeBusiness.name) && h.contacts && h.contacts.length > 0)
    : null;

  const ALL_TAG_TEMPLATES = getAllIntelligenceMatrixTags();

  const uniqueAvailableTags = Array.from(new Set([
    ...ALL_TAG_TEMPLATES,
    ...currentModeBusinesses.flatMap(l => {
      const prospect = analyzeBusinessProspect(l);
      return normalizeTags(l.opportunityTags?.length ? l.opportunityTags : prospect.tags);
    })
  ])).filter(Boolean).sort();

  const getSearchInputClass = (extraPadding = "pl-9 pr-8") => {
    const baseClasses = "w-full border rounded py-2 text-base lg:text-sm focus:outline-none focus:ring-2 font-medium transition-all duration-200";
    if (settingsAppTheme === "light") {
      return `${baseClasses} border-slate-200 text-slate-900 bg-white focus:ring-blue-500 placeholder:text-slate-400 ${extraPadding}`;
    } else if (settingsAppTheme === "sunset") {
      return `${baseClasses} border-[#eadecc] text-[#2d251e] bg-[#fcfaf7] focus:ring-amber-500 placeholder:text-[#8a725e]/60 ${extraPadding}`;
    } else if (settingsAppTheme === "ocean") {
      return `${baseClasses} border-[#cddfe6] text-[#0b2533] bg-white focus:ring-teal-500 placeholder:text-[#405f70]/60 ${extraPadding}`;
    } else {
      return `${baseClasses} border-slate-700 text-white bg-slate-900 focus:ring-blue-500 placeholder:text-slate-500 ${extraPadding}`;
    }
  };

  return (
    <APIProvider apiKey={apiKey} libraries={["places", "marker"]}>
      <div className={`flex flex-col h-[100dvh] w-full bg-[#F1F5F9] text-slate-900 font-sans overflow-hidden theme-${settingsAppTheme}`}>
        
        {/* Top Navigation / Header */}
        {!(isMobile && isMapFullscreen) && (
        <header 
          id="app-header" 
          data-ai-editable="dashboard-header"
          className={`h-16 flex items-center justify-between px-6 border-b shrink-0 transition-all duration-300 ${
            settingsAppTheme === "light"
              ? "bg-white text-slate-900 border-slate-200"
              : settingsAppTheme === "sunset"
              ? "bg-[#faf6f0] text-[#2d251e] border-[#e6dfd5]"
              : settingsAppTheme === "ocean"
              ? "bg-white text-[#0b2533] border-[#cddfe6]"
              : "bg-[#0F172A] text-white border-slate-700"
          }`}
        >
          <div className="flex items-center gap-4">
            {/* Hamburger Button with 3 lines */}
            <button
              id="sidebar-toggle-btn"
              onClick={() => setIsSidebarOpen(true)}
              className={`p-2 -ml-2 rounded-lg transition cursor-pointer flex items-center justify-center ${
                settingsAppTheme === "light"
                  ? "hover:bg-slate-100 text-slate-600 hover:text-slate-900"
                  : settingsAppTheme === "sunset"
                  ? "hover:bg-[#f3ece2] text-[#5c4a3c] hover:text-[#2d251e]"
                  : settingsAppTheme === "ocean"
                  ? "hover:bg-[#dee8ee] text-[#405f70] hover:text-[#0b2533]"
                  : "hover:bg-slate-800 text-slate-300 hover:text-white"
              }`}
              title="Open Navigation Menu" // i18n-ignore
            >
              <Menu className="w-5.5 h-5.5" />
            </button>

            <BizNearYouLogo 
              textColor={
                settingsAppTheme === "light"
                  ? "text-slate-900"
                  : settingsAppTheme === "sunset"
                  ? "text-[#2d251e]"
                  : settingsAppTheme === "ocean"
                  ? "text-[#0b2533]"
                  : "text-white"
              } 
              isDark={settingsAppTheme !== "light" && settingsAppTheme !== "sunset" && settingsAppTheme !== "ocean"}
            />
          </div>
          <div className="flex items-center gap-6">
            <div className="hidden lg:flex items-center gap-2">
              <div className="flex flex-col items-end">
                <span className={`text-xs font-semibold max-w-[150px] truncate transition-all duration-300 ${
                  settingsAppTheme === "light"
                    ? "text-slate-600"
                    : settingsAppTheme === "sunset"
                    ? "text-[#5c4a3c]"
                    : settingsAppTheme === "ocean"
                    ? "text-[#405f70]"
                    : "text-slate-400"
                }`}>
                  {currentUser?.email || "User"}
                </span>
                {currentUser?.provider === "github" && (
                  <span className="text-[9px] font-black tracking-wider text-indigo-650 bg-indigo-50 px-1.5 py-0.2 rounded border border-indigo-100 flex items-center gap-0.5 uppercase mt-0.5">
                    <Github className="w-2.5 h-2.5" />
                    GITHUB
                  </span>
                )}
              </div>
              {currentUser?.user_metadata?.avatar_url || currentUser?.user_metadata?.picture ? (
                <img 
                  src={currentUser.user_metadata.avatar_url || currentUser.user_metadata.picture} 
                  alt="User Avatar" 
                  className="w-8 h-8 rounded-full object-cover border border-slate-200 dark:border-slate-700 shadow-sm"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-blue-600 border border-blue-500 flex items-center justify-center font-black text-xs text-white uppercase tracking-tight shadow-sm select-none">
                  {currentUser?.provider === "github" ? "GH" : (currentUser?.email?.substring(0, 2) || "U")}
                </div>
              )}
            </div>
            
            <div className="hidden lg:flex items-center gap-2.5">
              {/* Prominent Buy Pro Header Button */}
              {!isProUser ? (
                <button
                  onClick={handleOpenUpgrade}
                  data-ai-editable="pro-button"
                  className="h-8 px-3.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-lg text-xs font-extrabold tracking-wide transition cursor-pointer inline-flex items-center justify-center gap-1.5 font-sans shadow-md shadow-orange-500/10 hover:shadow-orange-500/25 active:scale-95 animate-pulse"
                  title="Upgrade to BizNearYou Pro to unlock unlimited searches, contact scrapers, and automated route planning!"
                >
                  <Sparkles className="w-3.5 h-3.5 text-amber-100" />
                  <span>Buy Pro</span>
                </button>
              ) : (
                <button
                  onClick={handleOpenUpgrade}
                  data-ai-editable="pro-button"
                  className={`h-8 px-3.5 border rounded-lg text-xs font-bold tracking-wide transition cursor-pointer inline-flex items-center justify-center gap-1.5 font-sans active:scale-95 ${
                    settingsAppTheme === "light"
                      ? "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                      : settingsAppTheme === "sunset"
                      ? "bg-[#faf0e6] border-[#decfae] text-[#b45309] hover:bg-[#f3ece2]"
                      : settingsAppTheme === "ocean"
                      ? "bg-[#ecfdf5] border-[#a7f3d0] text-[#065f46] hover:bg-[#d1fae5]"
                      : "bg-emerald-950/30 border-emerald-800 text-emerald-400 hover:bg-emerald-900/20"
                  }`}
                  title="You are a Pro Member! Click to view your subscription usage and limits."
                >
                  <Sparkles className="w-3.5 h-3.5 text-emerald-500" />
                  <span>Pro Active</span>
                </button>
              )}
              
              <button
                onClick={() => navigateTo("/settings/account")}
                className={`h-8 px-3.5 border rounded-lg text-xs font-bold tracking-wide transition cursor-pointer inline-flex items-center justify-center gap-1.5 font-sans ${
                  settingsAppTheme === "light"
                    ? "bg-slate-50 hover:bg-slate-100 text-slate-700 hover:text-slate-900 border-slate-200"
                    : settingsAppTheme === "sunset"
                    ? "bg-[#f3ece2] hover:bg-[#eadecc] text-[#5c4a3c] hover:text-[#2d251e] border-[#e6dfd5]"
                    : settingsAppTheme === "ocean"
                    ? "bg-[#dee8ee] hover:bg-[#cddfe6] text-[#0b2533] hover:text-[#020617] border-[#cddfe6]"
                    : "bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white border-slate-700"
                }`}
                title={t("nav:btn_settings_header")}
              >
                <Settings className={`w-3.5 h-3.5 ${
                  settingsAppTheme === "light"
                    ? "text-slate-500"
                    : settingsAppTheme === "sunset"
                    ? "text-[#8a725e]"
                    : settingsAppTheme === "ocean"
                    ? "text-[#405f70]"
                    : "text-slate-300"
                }`} />
                <span>{t("nav:btn_settings_header")}</span>
              </button>

              <button
                onClick={handleLogout}
                className={`h-8 px-3.5 border rounded-lg text-xs font-bold tracking-wide transition cursor-pointer inline-flex items-center justify-center gap-1.5 font-sans ${
                  settingsAppTheme !== "dark"
                    ? "bg-rose-50 hover:bg-rose-100 text-rose-600 hover:text-rose-750 border-rose-150"
                    : "bg-rose-950/20 hover:bg-rose-900/40 text-rose-300 hover:text-white border-rose-800/30"
                }`}
                title={t("nav:btn_sign_out")}
              >
                <LogOut className={`w-3.5 h-3.5 ${
                  settingsAppTheme !== "dark" ? "text-rose-500" : "text-rose-400"
                }`} />
                <span>{t("nav:btn_sign_out")}</span>
              </button>
            </div>
          </div>
        </header>
        )}

        {!isEmailVerified && (
          <div 
            id="email-verification-banner" 
            className="bg-amber-500/10 border-b border-amber-500/20 px-6 py-3.5 flex flex-col md:flex-row items-center justify-between gap-4 z-20 relative backdrop-blur-md"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 text-amber-400 rounded-lg shrink-0">
                <Mail className="w-5 h-5 animate-pulse text-amber-400" />
              </div>
              <div className="text-left">
                <h4 className="text-sm font-bold text-amber-200">Account Unverified</h4>
                <p className="text-xs text-amber-350 font-sans mt-0.5">
                  Your account has been created. Please check your email and verify your account before searching for businesses. Note: Unverified accounts are subject to automatic security deletion within 24 hours of registration.
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 w-full md:w-auto shrink-0 justify-end">
              {resendMessage && (
                <span className={`text-xs px-3 py-1.5 rounded-lg border font-medium ${
                  resendStatus === "sent" 
                    ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-400" 
                    : "bg-rose-500/10 border-rose-500/20 text-rose-400"
                }`}>
                  {resendMessage}
                </span>
              )}
              <button
                id="btn-resend-verification"
                disabled={cooldownTime > 0 || resendStatus === "sending"}
                onClick={handleResendVerification}
                className="px-4 py-1.5 bg-amber-500 hover:bg-amber-400 disabled:bg-amber-500/25 disabled:text-amber-500/40 text-slate-950 text-xs font-bold rounded-lg transition-colors cursor-pointer flex items-center gap-1.5 whitespace-nowrap shadow-[0_0_15px_rgba(245,158,11,0.1)]"
              >
                {resendStatus === "sending" ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Sending...
                  </>
                ) : cooldownTime > 0 ? (
                  `Resend in ${cooldownTime}s`
                ) : (
                  "Resend Verification"
                )}
              </button>
            </div>
          </div>
        )}

        {/* Main Workspace Structure */}
        <main className="flex flex-1 overflow-hidden min-h-0">
          
          {/* Left Panel: Search & Controls */}
          {((!isMobile && (workspaceMode === "search" || workspaceMode === "saved")) || 
            (isMobile && workspaceMode === "search" && mobileSearchSubView === "form") || 
            (isMobile && workspaceMode === "saved" && mobileSavedSubView === "filters")
          ) && (
            <aside 
              id="search-controls" 
              data-ai-editable="search-panel"
              className={`${isMobile ? "w-full pb-20" : "w-80"} bg-white border-r border-slate-200 p-6 flex flex-col gap-5 shrink-0 overflow-y-auto`}
            >
              {/* Mobile View Toggle Switcher (Top of Controls Panel) */}
              {isMobile && workspaceMode === "search" && (
                <div className="flex bg-slate-100 p-1 rounded-xl shrink-0">
                  <button
                    type="button"
                    onClick={() => setMobileSearchSubView("form")}
                    className={`flex-1 py-2 text-center text-xs font-extrabold rounded-lg transition-all cursor-pointer ${
                      mobileSearchSubView === "form"
                        ? "bg-white text-blue-600 shadow-xs"
                        : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    Search & Filters
                  </button>
                  <button
                    type="button"
                    onClick={() => setMobileSearchSubView("results")}
                    className={`flex-1 py-2 text-center text-xs font-extrabold rounded-lg transition-all cursor-pointer ${
                      mobileSearchSubView === "results"
                        ? "bg-white text-blue-600 shadow-xs"
                        : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    View Results ({Businesses.length})
                  </button>
                </div>
              )}

              {isMobile && workspaceMode === "saved" && (
                <div className="flex bg-slate-100 p-1 rounded-xl shrink-0">
                  <button
                    type="button"
                    onClick={() => setMobileSavedSubView("filters")}
                    className={`flex-1 py-2 text-center text-xs font-extrabold rounded-lg transition-all cursor-pointer ${
                      mobileSavedSubView === "filters"
                        ? "bg-white text-emerald-600 shadow-xs"
                        : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    Filters
                  </button>
                  <button
                    type="button"
                    onClick={() => setMobileSavedSubView("content")}
                    className={`flex-1 py-2 text-center text-xs font-extrabold rounded-lg transition-all cursor-pointer ${
                      mobileSavedSubView === "content"
                        ? "bg-white text-emerald-600 shadow-xs"
                        : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    Saved List ({savedBusinesses.length})
                  </button>
                </div>
              )}
            
            {/* Audience Mode Selector */}
            <div className="pb-3 border-b border-slate-100 dark:border-slate-800">
              <AudienceSelector 
                currentMode={audienceMode} 
                onSelectMode={handleSelectAudienceMode} 
              />
            </div>

            {/* Search Input block - Only shown in Search Mode */}
            {workspaceMode === "search" && (
              <section className="space-y-4">
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">{translateWithFallback(t, "search:label_location", "Location")}</label>
                  <PlaceAutocompleteInput
                    value={zipInput}
                    onChange={handleLocationChange}
                    onSelectCoords={(coords) => {
                      setSearchCenter(coords);
                    }}
                    isLocating={isLocating}
                    handleGetCurrentLocation={handleGetCurrentLocation}
                    theme={resolveTheme(settingsAppTheme)}
                  />
                  
                  {resolvedLocationDisplay && (
                    <div className="mt-2 flex items-center gap-1.5 text-[11px] text-teal-700 font-semibold bg-teal-50 border border-teal-100 rounded-lg p-2" id="resolved-location-badge">
                      <Compass className="w-3.5 h-3.5 animate-spin-once" />
                      <span>Searching in: {resolvedLocationDisplay}</span>
                    </div>
                  )}
                  
                  {accuracyNotice && (
                    <div className="mt-2 p-2.5 bg-indigo-50 border border-indigo-100 rounded-lg text-[11px] text-indigo-950 flex gap-2 items-start relative">
                      <Info className="w-3.5 h-3.5 text-indigo-600 mt-0.5 shrink-0" />
                      <div className="flex-1 pr-4">
                        <span className="font-semibold">Need pin-point accuracy?</span> Browser GPS can be off by 2-3 houses. Start typing your exact street address above for a perfect match!
                      </div>
                      <button 
                        onClick={() => setAccuracyNotice(false)} 
                        className="absolute right-1.5 top-1.5 text-indigo-400 hover:text-indigo-600 text-xs font-bold leading-none cursor-pointer"
                      >
                        ×
                      </button>
                    </div>
                  )}

                  <div className="flex gap-2 mt-1.5 justify-end items-center flex-wrap">
                    <span className="text-[9px] text-slate-400 italic mr-auto">Try:</span>
                    <button 
                      onClick={() => handleLocationChange("90210")}
                      className="text-slate-400 text-[10px] hover:text-blue-600 transition"
                    >
                      90210
                    </button>
                    <span className="text-slate-300 text-[11px] font-mono">•</span>
                    <button 
                      onClick={() => handleLocationChange("Seattle, WA")}
                      className="text-slate-400 text-[10px] hover:text-blue-600 transition"
                    >
                      Seattle
                    </button>
                    <span className="text-slate-300 text-[11px] font-mono">•</span>
                    <button 
                      onClick={() => handleLocationChange("Miami, FL")}
                      className="text-slate-400 text-[10px] hover:text-blue-600 transition"
                    >
                      Miami
                    </button>
                  </div>
                </div>

                <div className="relative">
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      Business type or keywords
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowSearchHowItWorks(prev => !prev)}
                      className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800 transition flex items-center gap-1 cursor-pointer"
                    >
                      <HelpCircle className="w-3 h-3" />
                      <span>How this search works</span>
                    </button>
                  </div>

                  {/* How this search works collapsible panel */}
                  {showSearchHowItWorks && (
                    <div className="mb-2.5 p-2.5 bg-indigo-50/80 border border-indigo-100 rounded-lg text-xs text-indigo-950 font-sans space-y-1.5 animate-fade">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-semibold leading-relaxed text-[11px]">
                          Enter a business type and any details that matter to you. You can search for services, insurance, languages, accessibility, hours, or other features. You do not need to write a full sentence.
                        </p>
                        <button
                          type="button"
                          onClick={() => setShowSearchHowItWorks(false)}
                          className="text-indigo-400 hover:text-indigo-700 font-bold text-xs"
                        >
                          ×
                        </button>
                      </div>
                      {keyword.trim() && (() => {
                        const parsed = parseBusinessQuery(keyword);
                        return (
                          <div className="pt-1 border-t border-indigo-100 text-[10.5px] text-indigo-900 font-mono">
                            <strong>Interpreted query:</strong> Type: {parsed.businessTypes.join(", ") || "General"}
                            {parsed.insurancePrograms.length > 0 && ` • Insurance: ${parsed.insurancePrograms.join(", ")}`}
                            {parsed.languages.length > 0 && ` • Language: ${parsed.languages.join(", ")}`}
                            {parsed.accessibilityFeatures.length > 0 && ` • Accessibility: ${parsed.accessibilityFeatures.join(", ")}`}
                            {parsed.operatingHours.length > 0 && ` • Hours: ${parsed.operatingHours.join(", ")}`}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      type="text"
                      data-ai-editable="keyword-input"
                      value={keyword}
                      onChange={(e) => {
                        setKeyword(e.target.value);
                        setShowSuggestions(true);
                      }}
                      onFocus={() => setShowSuggestions(true)}
                      onBlur={() => {
                        setTimeout(() => setShowSuggestions(false), 220);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          setShowSuggestions(false);
                          triggerBusinessesSearch(1, true);
                        } else if (e.key === "Escape") {
                          setShowSuggestions(false);
                        }
                      }}
                      placeholder="Search category or keyword..."
                      className={getSearchInputClass("pl-9 pr-8")}
                      style={{
                        color: settingsAppTheme === "dark" ? "#ffffff" : (settingsAppTheme === "sunset" ? "#2d251e" : (settingsAppTheme === "ocean" ? "#0b2533" : "#0f172a")),
                        backgroundColor: settingsAppTheme === "dark" ? "#0f172a" : (settingsAppTheme === "sunset" ? "#fcfaf7" : "#ffffff"),
                        borderColor: settingsAppTheme === "dark" ? "#334155" : (settingsAppTheme === "sunset" ? "#eadecc" : (settingsAppTheme === "ocean" ? "#cddfe6" : "#e2e8f0"))
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowSuggestions(!showSuggestions)}
                      className="absolute right-2 top-2 p-0.5 text-slate-400 hover:text-slate-600 focus:outline-none cursor-pointer"
                      title="Toggle categories suggestions"
                    >
                      <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showSuggestions ? "rotate-180" : ""}`} />
                    </button>
                  </div>

                  {/* Supporting helper text */}
                  <p className="text-[10px] text-slate-500 mt-1 leading-normal font-sans hidden sm:block">
                    Search by business type, services, insurance, features, brand, or other keywords. No full sentence needed.
                  </p>
                  <p className="text-[10px] text-slate-500 mt-1 leading-normal font-sans sm:hidden">
                    Add a type, service, insurance, or feature.
                  </p>



                  {/* Search query parsed requirement chips */}
                  {keyword.trim() && (() => {
                    const parsed = parseBusinessQuery(keyword);
                    if (parsed.parsedChips.length > 0) {
                      return (
                        <div className="flex flex-wrap items-center gap-1.5 mt-2 pt-1.5 border-t border-slate-150">
                          <span className="text-[9.5px] font-bold text-slate-400">Recognized terms:</span>
                          {parsed.parsedChips.map((chip) => (
                            <span
                              key={chip.id}
                              className="text-[9.5px] font-extrabold bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 rounded-md flex items-center gap-1 shadow-4xs"
                            >
                              <span className="text-[8.5px] uppercase opacity-70">{chip.category}:</span>
                              <span>{chip.label}</span>
                              <button
                                type="button"
                                onClick={() => {
                                  const newKw = removeTermFromQuery(keyword, chip.originalText || chip.label);
                                  setKeyword(newKw);
                                }}
                                className="text-indigo-400 hover:text-indigo-900 font-bold ml-0.5 cursor-pointer"
                                title={`Remove ${chip.label}`}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* Expanded Categories & Search Dropdown */}
                  {showSuggestions && (
                    <div className="absolute z-50 left-0 right-0 mt-1 max-h-80 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-xl py-1.5 text-slate-700 divide-y divide-slate-100">
                      {/* Option to use typed text directly */}
                      {keyword.trim() && (
                        <div className="px-3 py-1.5 bg-indigo-50/50">
                          <button
                            type="button"
                            onMouseDown={() => {
                              setShowSuggestions(false);
                              triggerBusinessesSearch(1, true);
                            }}
                            className="w-full text-left text-xs font-bold text-indigo-700 flex items-center justify-between cursor-pointer"
                          >
                            <span>Search for custom query: "{keyword}"</span>
                            <Search className="w-3.5 h-3.5 text-indigo-500" />
                          </button>
                        </div>
                      )}

                      {/* Recent Searches */}
                      {recentSearches.length > 0 && !keyword.trim() && (
                        <div className="py-1">
                          <div className="px-3 py-1 text-[9px] font-bold text-slate-400 uppercase tracking-wider bg-slate-50">
                            Recent Searches
                          </div>
                          {recentSearches.slice(0, 4).map((rs) => (
                            <button
                              key={rs}
                              type="button"
                              onMouseDown={() => {
                                setKeyword(rs);
                                setShowSuggestions(false);
                                triggerBusinessesSearch(1, true);
                              }}
                              className="w-full text-left px-4 py-1.5 text-xs hover:bg-slate-50 text-slate-700 font-medium flex items-center justify-between cursor-pointer"
                            >
                              <span className="flex items-center gap-1.5">
                                <History className="w-3 h-3 text-slate-400" />
                                {rs}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Filtered Category Groups */}
                      {(() => {
                        const query = keyword.trim().toLowerCase();
                        
                        const filteredGroups = CATEGORY_GROUPS.map(group => {
                          const matchedItems = group.items.filter(item => 
                            item.toLowerCase().includes(query)
                          );
                          return {
                            name: group.name,
                            items: matchedItems
                          };
                        }).filter(group => group.items.length > 0);

                        const totalMatches = filteredGroups.reduce((acc, g) => acc + g.items.length, 0);

                        if (totalMatches === 0) {
                          return (
                            <div className="px-3 py-2 text-[11px] text-slate-400 italic">
                              No matching default categories. Press Enter to search freely!
                            </div>
                          );
                        }

                        return (
                          <div className="divide-y divide-slate-100">
                            {filteredGroups.map((group) => (
                              <div key={group.name} className="py-1">
                                <div className="px-3 py-1 text-[9px] font-bold text-slate-400 uppercase tracking-wider bg-slate-50/50">
                                  {group.name}
                                </div>
                                <div className="mt-0.5">
                                  {group.items.map((cat) => (
                                    <button
                                      key={`${group.name}-${cat}`}
                                      type="button"
                                      onMouseDown={() => {
                                        setKeyword(cat);
                                        setShowSuggestions(false);
                                        triggerBusinessesSearch(1, true);
                                      }}
                                      className="w-full text-left px-4 py-1.5 text-xs hover:bg-slate-50 text-slate-700 font-medium flex items-center justify-between transition cursor-pointer"
                                    >
                                      <span>{cat}</span>
                                      {keyword.toLowerCase() === cat.toLowerCase() && (
                                        <Check className="w-3.5 h-3.5 text-blue-500" />
                                      )}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>

                             {/* Restructured Filters Choice Accordion Panel */}
                <div id="advanced-search-filters" className={`border border-slate-150 bg-slate-50/50 rounded-lg transition-all duration-200 ${showAdvancedFilters ? "overflow-visible" : "overflow-hidden"}`}>
                  {/* Accordion Toggle Header */}
                  <button
                    type="button"
                    onClick={() => setShowAdvancedFilters(prev => !prev)}
                    className="w-full flex items-center justify-between p-3 hover:bg-slate-100/55 transition text-left cursor-pointer"
                  >
                    <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1.5 font-sans">
                      <SlidersHorizontal className="w-3.5 h-3.5 text-slate-400" />
                      {t("search:btn_advanced_search")}
                    </span>
                    <div className="flex items-center gap-1.5">
                      {isAnyStandardFilterActive && (
                        <span className="bg-blue-105 text-blue-700 text-[8.5px] font-extrabold px-1.5 py-0.2 rounded-full border border-blue-200">
                          Active
                        </span>
                      )}
                      <ChevronDown className={`w-4 h-4 text-slate-450 transition-transform duration-200 ${showAdvancedFilters ? "rotate-180" : ""}`} />
                    </div>
                  </button>

                  {/* Expanded Filter Panel */}
                  {showAdvancedFilters && (
                    <div className="p-3 pt-0 border-t border-slate-150 space-y-4 text-slate-705 font-sans animate-fade">
                      <div className="flex items-center justify-between pt-2.5">
                        <span className="text-[9.5px] font-bold text-slate-400 uppercase tracking-wider block">
                          Search Filters
                        </span>
                        {isAnyFilterActive && (
                          <button 
                            type="button"
                            onClick={handleResetFilters}
                            className="text-[9px] hover:text-blue-600 text-slate-450 font-bold transition flex items-center gap-0.5 cursor-pointer bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200"
                          >
                            <RotateCcw className="w-2.5 h-2.5" />
                            Clear Filters
                          </button>
                        )}
                      </div>

                      {/* BUSINESS SEARCH MODE VIEW */}
                      <div className="space-y-3.5">
                        {/* Helper text */}
                        <div className="p-2 border border-blue-100 bg-blue-50/50 rounded text-[10.5px] font-medium leading-relaxed text-slate-600">
                          ℹ️ Use these filters to narrow the live listings found in your search.
                        </div>

                        {/* Sort Results By (Very accessible on Mobile & Desktop) */}
                        <div className="space-y-1.5 bg-blue-50/30 border border-blue-100 p-2.5 rounded-xl">
                          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Sort Results By</label>
                          <select
                            value={resultsSortBy}
                            onChange={(e) => setResultsSortBy(e.target.value as any)}
                            className="w-full bg-white border border-slate-200 hover:border-slate-300 rounded-lg px-2 py-1.5 font-bold text-slate-800 shadow-sm transition cursor-pointer text-xs focus:outline-hidden"
                          >
                            <option value="best_match">Best Match</option>
                            <option value="last_updated">Last Updated</option>
                            <option value="rating">Google Rating (High to Low)</option>
                            <option value="distance">Distance (Closest First)</option>
                            <option value="opportunity_score">Opportunity Score (Highest First)</option>
                          </select>
                          <div className="text-[10.5px] text-slate-500 font-medium pt-0.5">
                            Best Match is optimized for <span className="font-bold text-blue-700">{AUDIENCE_MODES[audienceMode]?.label || "Active Audience"}</span>.
                          </div>
                        </div>

                        {/* Search Area */}
                        <div className="space-y-1.5">
                          <div className="flex justify-between text-[10px] font-medium text-slate-500">
                            <span>Search Area {/* i18n-ignore */}</span>
                            <span className="font-bold text-blue-600 font-mono text-[10px] bg-blue-50 px-1.5 py-0.5 rounded border border-blue-100">
                              {distance === "any" ? "Anywhere" : `${distance} mi`}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 pt-1">
                            <input
                              type="range"
                              min="0"
                              max="100"
                              step="5"
                              value={distance === "any" ? 0 : parseFloat(distance)}
                              onChange={(e) => {
                                const val = parseInt(e.target.value, 10);
                                setDistance(val === 0 ? "any" : String(val));
                              }}
                              className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-500/20"
                            />
                          </div>
                          <div className="flex justify-between text-[8px] text-slate-400 font-mono px-0.5">
                            <span>Anywhere</span>
                            <span>50 mi</span>
                            <span>100 mi</span>
                          </div>
                        </div>

                        {/* Minimum Google Rating */}
                        <div className="space-y-1.5">
                          <div className="flex justify-between text-[10px] font-medium text-slate-500">
                            <span>Minimum Google Rating {/* i18n-ignore */}</span>
                            <span className="font-bold text-amber-500 font-mono text-[10px] flex items-center gap-0.5 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-100">
                              {minRating === 0 ? "Any Rating" : `★ ${minRating.toFixed(1)}+`}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 pt-1">
                            <input
                              type="range"
                              min="0"
                              max="5.0"
                              step="0.1"
                              value={minRating}
                              onChange={(e) => {
                                setMinRating(parseFloat(e.target.value));
                              }}
                              className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-400/20"
                            />
                          </div>
                          <div className="flex justify-between text-[8px] text-slate-400 font-mono px-0.5">
                            <span>Any</span>
                            <span>2.5★</span>
                            <span>5.0★</span>
                          </div>
                        </div>

                        {/* Search by Tag */}
                        <div className="space-y-1">
                          <span className="text-[10px] font-sans font-bold text-slate-500 uppercase tracking-normal block">Search by Tag</span>
                          <div className="relative">
                            <input
                              type="text"
                              value={searchTagQuery}
                              onChange={(e) => {
                                setSearchTagQuery(e.target.value);
                                setShowTagSuggestions(true);
                              }}
                              onFocus={() => setShowTagSuggestions(true)}
                              onBlur={() => {
                                setTimeout(() => setShowTagSuggestions(false), 200);
                              }}
                              placeholder="Type a tag (e.g., Hiring, SSL)..."
                              className={getSearchInputClass("pl-3 pr-14")}
                            />
                            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                              {searchTagQuery && (
                                <button
                                  type="button"
                                  onClick={() => setSearchTagQuery("")}
                                  className="text-slate-400 hover:text-slate-600 font-bold text-sm cursor-pointer p-0.5"
                                >
                                  ×
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => setShowTagSuggestions(!showTagSuggestions)}
                                className="text-slate-400 hover:text-slate-600 focus:outline-none cursor-pointer p-0.5"
                                title="Toggle tag suggestions"
                              >
                                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showTagSuggestions ? "rotate-180" : ""}`} />
                              </button>
                            </div>

                            {showTagSuggestions && (
                              <div className="absolute z-50 left-0 right-0 mt-1 max-h-60 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-xl py-1.5 text-slate-700">
                                {(() => {
                                  const queryParts = searchTagQuery.split(",");
                                  const lastPart = queryParts[queryParts.length - 1] || "";
                                  const currentTypedWord = lastPart.trim().toLowerCase();
                                  const selectedTagsLower = queryParts.map(p => p.trim().toLowerCase()).filter(Boolean);

                                  const filteredTags = uniqueAvailableTags.filter(tag => {
                                    if (currentTypedWord) {
                                      return tag.toLowerCase().includes(currentTypedWord);
                                    }
                                    return true;
                                  });

                                  if (filteredTags.length === 0) {
                                    return (
                                      <div className="px-3 py-1.5 text-[11px] text-slate-400 italic">
                                        No matching tags found.
                                      </div>
                                    );
                                  }

                                  return (
                                    <div className="divide-y divide-slate-100">
                                      {filteredTags.map(tag => {
                                        const tagLower = tag.toLowerCase();
                                        const isSelected = selectedTagsLower.includes(tagLower);
                                        return (
                                          <button
                                            key={tag}
                                            type="button"
                                            onMouseDown={() => {
                                              let newQueryValue = "";
                                              if (isSelected) {
                                                const updatedParts = queryParts.map(p => p.trim()).filter(p => p.toLowerCase() !== tagLower);
                                                newQueryValue = updatedParts.join(", ") + (updatedParts.length > 0 ? ", " : "");
                                              } else {
                                                const updatedParts = [...queryParts.slice(0, -1).map(p => p.trim()), tag];
                                                newQueryValue = updatedParts.join(", ") + ", ";
                                              }
                                              setSearchTagQuery(newQueryValue);
                                            }}
                                            className="w-full text-left px-4 py-1.5 text-xs hover:bg-slate-50 text-slate-700 font-medium flex items-center justify-between transition cursor-pointer"
                                          >
                                            <span>{tag}</span>
                                            {isSelected && (
                                              <Check className="w-3.5 h-3.5 text-blue-500" />
                                            )}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  );
                                })()}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Sourcing Requirements checklists */}
                        <div className="space-y-3 pt-1 border-t border-slate-200/50">
                          <label className="flex items-center gap-2 cursor-pointer group">
                            <input 
                              type="checkbox"
                              checked={openNow}
                              onChange={(e) => setOpenNow(e.target.checked)}
                              className="rounded text-blue-600 border-slate-300 focus:ring-blue-500 h-3.5 w-3.5 transition cursor-pointer"
                            />
                            <span className="text-[10px] select-none text-slate-600 font-semibold group-hover:text-slate-800 transition-colors">
                              Open Now
                            </span>
                          </label>

                          <label className="flex items-center gap-2 cursor-pointer group">
                            <input 
                              type="checkbox"
                              checked={isPossiblyHiring}
                              onChange={(e) => setIsPossiblyHiring(e.target.checked)}
                              className="rounded text-blue-600 border-slate-300 focus:ring-blue-500 h-3.5 w-3.5 transition cursor-pointer"
                            />
                            <span className="text-[10px] select-none text-slate-600 font-semibold group-hover:text-slate-800 transition-colors">
                              Is Possibly Hiring
                            </span>
                          </label>

                          {/* Adult product filtering checkbox */}
                          <div className="pt-2 border-t border-dashed border-slate-200 space-y-1">
                            <label className="flex items-center gap-2 cursor-pointer group">
                              <input 
                                type="checkbox"
                                checked={includeAdultContent}
                                onChange={(e) => setIncludeAdultContent(e.target.checked)}
                                className="rounded text-blue-600 border-slate-300 focus:ring-blue-500 h-3.5 w-3.5 transition cursor-pointer"
                              />
                              <span className="text-[10px] select-none text-slate-600 font-semibold group-hover:text-slate-800 transition-colors">
                                Include adult-oriented businesses
                              </span>
                            </label>
                          </div>
                        </div>

                      </div>
                    </div>
                  )}
                </div>

                {/* Error banner block */}
                {searchError && (
                  <div className="p-3 bg-red-50 border border-red-150 rounded text-red-700 text-xs flex gap-1.5 items-start">
                    <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>{renderErrorWithLinks(searchError)}</span>
                  </div>
                )}

                {/* Extract triggers */}
                <SearchButton 
                  isSearching={isSearching}
                  settingsAppTheme={settingsAppTheme}
                  onClick={() => triggerBusinessesSearch(1, true)}
                />

              </section>
            )}

            {/* Map Preview section - Live interactive map inside beautiful panel structure */}
            {!isMobile && (
              <section id="map-preview-section" className={`hidden lg:flex flex-1 flex-col min-h-0 ${workspaceMode === "search" ? "border-t pt-4" : "pt-1"} ${
                settingsAppTheme === "light" ? "border-slate-100" :
                settingsAppTheme === "sunset" ? "border-[#e6dfd5]" :
                settingsAppTheme === "ocean" ? "border-[#cddfe6]/60" :
                "border-slate-850"
              }`}>
                <label className={`block text-[10px] font-bold uppercase tracking-wider mb-2 ${
                  settingsAppTheme === "light" ? "text-slate-400" :
                  settingsAppTheme === "sunset" ? "text-[#8a725e]" :
                  settingsAppTheme === "ocean" ? "text-[#405f70]" :
                  "text-slate-400"
                }`}>Live Map Preview</label>
                <div className={`w-full flex-1 rounded border relative overflow-hidden shadow-inner min-h-[180px] ${
                  settingsAppTheme === "light" ? "bg-slate-100 border-slate-200" :
                  settingsAppTheme === "sunset" ? "bg-[#f3ece2] border-[#e6dfd5]" :
                  settingsAppTheme === "ocean" ? "bg-[#ecf3f6] border-[#cddfe6]" :
                  "bg-slate-950 border-slate-800"
                }`}>
                  <MapView 
                    Businesses={filteredBusinesses}
                    selectedBusinessId={selectedBusinessId}
                    onSelectBusiness={setSelectedBusinessId}
                    onCrawlBusiness={enrichBusiness}
                    mapStyle={mapStyle}
                    onMapStyleChange={(style) => handleSaveUserSettings(settingsAppTheme, style, locale)}
                    settingsAppTheme={settingsAppTheme}
                    googleMapsMapId={googleMapsMapId}
                    center={searchCenter || userCoords}
                    markerColorMode={markerColorMode}
                    onMarkerColorModeChange={setMarkerColorMode}
                    onSearchArea={(lat, lng) => {
                      const coordString = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
                      coordsUpdatedJustNowRef.current = true;
                      setZipInput(coordString);
                      setSearchCenter({ lat, lng });
                      setUserCoords({ lat, lng });
                      // Use a clean next-tick trigger to allow state updates to settle
                      setTimeout(() => {
                        triggerBusinessesSearch(1, true);
                      }, 50);
                    }}
                    savedBusinesses={savedBusinesses}
                    isProUser={isProUser}
                    onUpgradeClick={() => setIsUpgradeModalOpen(true)}
                    isSavingBusinessId={isSavingBusinessId}
                    isDeletingBusinessId={isDeletingBusinessId}
                    onSaveBusiness={handleSaveIndividualBusiness}
                    onDeleteBusiness={handleDeleteIndividualBusiness}
                    isFullscreen={isMapFullscreen}
                    onFullscreenChange={setIsMapFullscreen}
                  />
                </div>

                {/* Integrated Map Legend Panel with fade-in transition - desktop only */}
                {!isMobile && (
                  <div key={filteredBusinesses.length + (selectedBusinessId || "") + markerColorMode} className={`mt-2.5 p-3 rounded-lg border text-[11px] animate-fade-in transition-all duration-300 ${
                    settingsAppTheme !== "dark"
                      ? "bg-slate-50 border-slate-200 text-slate-700 font-bold"
                      : "bg-slate-900 border-slate-800 text-slate-300 font-bold"
                  }`}>
                    <div className={`font-extrabold text-[10px] mb-1.5 flex items-center justify-between ${
                      settingsAppTheme !== "dark" ? "text-slate-500" : "text-slate-400"
                    }`}>
                      <div className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
                        <div className="flex flex-col gap-0.5 text-left">
                          <span className="text-[10px] uppercase font-extrabold tracking-wider">{t("legend_title")}</span>
                          <span className={`text-[8.5px] font-bold ${settingsAppTheme !== "dark" ? "text-indigo-600/80" : "text-indigo-400/80"}`}>
                            {getLegendModeTitle(markerColorMode, t)}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowLegendInfo(prev => !prev)}
                        className="p-1 hover:bg-slate-200/60 dark:hover:bg-slate-800 rounded transition duration-200 cursor-pointer self-start"
                        title={t("common:btn_toggle_details")}
                      >
                        <Info className="w-3.5 h-3.5 text-indigo-500 hover:text-indigo-600" />
                      </button>
                    </div>

                    {showLegendInfo && (
                      <div className={`mb-2 p-2 rounded-md text-[10px] leading-relaxed border font-normal transition-all duration-300 animate-fade-in ${
                        settingsAppTheme !== "dark"
                          ? "bg-indigo-50/50 border-indigo-100 text-slate-600"
                          : "bg-indigo-950/20 border-indigo-900/40 text-slate-400"
                      }`}>
                        {t("legend_info_tooltip")}
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 border-t border-slate-200/20 pt-2">
                      {getLegendItems(markerColorMode, t).map((item, idx) => (
                        <div key={idx} className="flex items-center gap-1.5 font-semibold">
                          <span 
                            className="w-2.5 h-2.5 rounded-full shrink-0" 
                            style={{ backgroundColor: item.color, border: `1px solid ${item.border}` }}
                          />
                          <span className="truncate">{item.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            )}

            {/* Sidebar bottom spacing / decoration */}
            <div className="shrink-0 pt-4 border-t border-slate-100 text-center">
              <p className="text-[10px] text-slate-400 font-sans tracking-wide">
                BizNearYou Workspace Engine v3.0
              </p>
            </div>
          </aside>
          )}

                    {/* Center Panel: Results List */}
          {(!isMobile || 
            (workspaceMode === "search" && (mobileSearchSubView === "results" || mobileSearchSubView === "map")) || 
            (workspaceMode === "saved" && (mobileSavedSubView === "content" || mobileSavedSubView === "map")) ||
            (workspaceMode !== "search" && workspaceMode !== "saved")
          ) && (
            <div 
              id="results-workspace" 
              className={`flex-1 flex flex-col bg-slate-50 min-w-0 pb-16 lg:pb-0 ${
                isMobile && (
                  (workspaceMode === "search" && mobileSearchSubView === "map") ||
                  (workspaceMode === "saved" && mobileSavedSubView === "map")
                ) ? "overflow-hidden" : "overflow-y-auto"
              }`}
            >
            {workspaceMode === "settings" || currentPath.startsWith("/settings") || currentPath === "/account" ? (
              <ErrorBoundary>
                <SettingsDashboard
                  currentUser={currentUser}
                  currentPath={currentPath}
                  navigateTo={navigateTo}
                  t={t}
                  locale={settingsLanguage}
                  showToast={showToast}
                  apiFetch={apiFetch}
                  isProUser={isProUser}
                  billingStatus={billingStatus}
                  handleLogout={handleLogout}
                  savedBusinesses={savedBusinesses}
                  settingsAppTheme={settingsAppTheme}
                  handleSaveUserSettings={handleSaveUserSettings}
                  mapStyle={mapStyle}
                  onMapStyleChange={setMapStyle}
                  settingsLanguage={settingsLanguage}
                  setSettingsLanguage={(lang) => setSettingsLanguage(lang as any)}
                  settingsDefaultZipcode={settingsDefaultZipcode}
                  setSettingsDefaultZipcode={setSettingsDefaultZipcode}
                  settingsMaxResults={settingsMaxResults}
                  setSettingsMaxResults={setSettingsMaxResults}
                  trackingEnabled={trackingEnabled}
                  handleToggleTracking={handleToggleTracking}
                  audienceMode={audienceMode}
                  handleSelectAudienceMode={handleSelectAudienceMode}
                  adminUsePro={adminUsePro}
                  setAdminUsePro={setAdminUsePro}
                  settingsBypassPaywall={settingsBypassPaywall}
                  setSettingsBypassPaywall={setSettingsBypassPaywall}
                  handleLinkIdentity={handleLinkIdentity}
                  onUpgradeClick={() => setIsUpgradeModalOpen(true)}
                  onContactSupportClick={() => {
                    setContactSubject("Technical Support");
                    setWorkspaceMode("contact");
                    navigateTo("/contact");
                  }}
                  onOpenLegalTab={(tab) => {
                    setActiveLegalTab(tab as any);
                    setIsLegalModalOpen(true);
                  }}
                />
              </ErrorBoundary>
            ) : workspaceMode === "dashboard" ? (
              <ErrorBoundary>
                <DashboardView
                  currentUser={currentUser}
                  savedBusinesses={savedBusinesses}
                  Businesses={Businesses}
                  apiFetch={apiFetch}
                  showToast={showToast}
                  onSelectBusiness={(id) => {
                    setSelectedBusinessId(id);
                    setMobileIntelOpen(true);
                  }}
                  onSetWorkspaceMode={(mode) => switchWorkspaceMode(mode as any)}
                  onTriggerSearch={(kw, loc, tagsFilter) => {
                    if (kw !== undefined && kw !== null) setKeyword(kw);
                    if (loc !== undefined && loc !== null && loc !== "") {
                      setZipInput(loc);
                    }
                    if (tagsFilter && tagsFilter.length > 0) {
                      setSearchTagQuery(tagsFilter[0]);
                    } else {
                      setSearchTagQuery("");
                    }
                    setWorkspaceMode("search");
                    setMobileSearchSubView("results");
                  }}
                  settingsAppTheme={settingsAppTheme}
                  isProUser={isProUser}
                  onUpgradeClick={() => setIsUpgradeModalOpen(true)}
                  onBillingChange={() => loadBillingStatus()}
                />
              </ErrorBoundary>
            ) : workspaceMode === "docs" ? (
              <DocumentationPage
                onUpgradeClick={() => setIsUpgradeModalOpen(true)}
                onOpenSettingsClick={() => navigateTo("/settings/account")}
                onOpenLegalTab={(tab) => {
                  if (tab === "contact-removal") {
                    setContactSubject("Profile Removal");
                    setWorkspaceMode("contact");
                  } else {
                    setActiveLegalTab(tab as any);
                    setIsLegalModalOpen(true);
                  }
                }}
                onMenuClick={() => setIsSidebarOpen(true)}
                isProUser={isProUser}
                onContactSupportClick={() => {
                  setContactSubject("Technical Support");
                  setWorkspaceMode("contact");
                }}
                currentUser={currentUser}
                onNavigate={navigateTo}
              />
            ) : workspaceMode === "contact" ? (
              <ErrorBoundary>
                <div className="flex-1 flex flex-col bg-slate-50 min-w-0 pb-16 lg:pb-0">
                  {/* Action Bar / Header for Support Workspace */}
                  <div className="min-h-[56px] h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-6 shrink-0">
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setIsSidebarOpen(true)}
                        className="p-1.5 bg-slate-50 border border-slate-200 rounded text-slate-700 hover:bg-slate-100 hover:text-slate-900 transition cursor-pointer lg:hidden flex items-center justify-center gap-1.5 text-xs font-bold font-sans"
                        title={t("nav:btn_open_nav_menu")}
                      >
                        <Menu className="w-4 h-4" />
                      </button>
                      <h2 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-2">
                        <Mail className="w-4 h-4 text-indigo-500" />
                        Support Workspace
                      </h2>
                    </div>
                  </div>
                  <div className="flex-grow p-4 sm:p-6 lg:p-8">
                    <ContactPage
                      onNavigate={navigateTo}
                      currentUser={currentUser}
                      initialSubject={contactSubject}
                      onLogin={() => {}}
                    />
                  </div>
                </div>
              </ErrorBoundary>
            ) : (
              <>
                {/* Action Bar with Mode Info & Sidebar Trigger */}
                <div className="min-h-[56px] h-auto py-2 flex-wrap gap-2 lg:h-14 lg:py-0 lg:flex-nowrap bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-6 shrink-0">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setIsSidebarOpen(true)}
                  className="p-1.5 bg-slate-50 border border-slate-200 rounded text-slate-700 hover:bg-slate-100 hover:text-slate-900 transition cursor-pointer flex items-center justify-center gap-1.5 text-xs font-bold font-sans"
                  title={t("nav:btn_open_nav_menu")}
                >
                  <Menu className="w-4 h-4" />
                  <span className="hidden sm:inline">{t("btn_menu")}</span>
                </button>

                {workspaceMode === "saved" && (
                  <div className="flex items-center bg-slate-100/90 p-1 rounded-xl gap-1 border border-slate-200/80">
                    <button
                      type="button"
                      onClick={() => setSavedSubview("list")}
                      className={`px-3 py-1 rounded-lg text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
                        savedSubview === "list"
                          ? "bg-white text-emerald-700 shadow-xs border border-slate-200/60"
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                    >
                      <List className="w-3.5 h-3.5" />
                      <span>List View</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setSavedSubview("pipeline")}
                      className={`px-3 py-1 rounded-lg text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
                        savedSubview === "pipeline"
                          ? "bg-white text-emerald-700 shadow-xs border border-slate-200/60"
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                    >
                      <Briefcase className="w-3.5 h-3.5 text-indigo-500" />
                      <span>Pro Kanban Pipeline</span>
                      {!isProUser && (
                        <span className="bg-amber-100 text-amber-800 text-[9px] font-black px-1.5 py-0.2 rounded border border-amber-200 uppercase">
                          PRO
                        </span>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => setSavedSubview("territory")}
                      className={`px-3 py-1 rounded-lg text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
                        savedSubview === "territory"
                          ? "bg-white text-emerald-700 shadow-xs border border-slate-200/60"
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                    >
                      <Compass className="w-3.5 h-3.5 text-blue-500" />
                      <span>Territory Pipeline</span>
                      {!isProUser && (
                        <span className="bg-amber-100 text-amber-800 text-[9px] font-black px-1.5 py-0.2 rounded border border-amber-200 uppercase">
                          PRO
                        </span>
                      )}
                    </button>
                  </div>
                )}
              </div>
              
              <div className="flex flex-wrap items-center gap-2 justify-end">
                {workspaceMode === "search" && Businesses.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <button 
                      onClick={() => {
                        setBusinesses([]);
                        setSelectedBusinessId(null);
                      }}
                      className="text-[11px] bg-white px-3 py-1.5 rounded border border-slate-200 hover:bg-slate-50 text-slate-600 font-semibold cursor-pointer transition"
                    >
                      Clear Results
                    </button>
                    <button 
                      onClick={async () => {
                        const targetBusinesses = displayedBusinesses;
                        if (targetBusinesses.length === 0) return;
                        if (!currentUser) {
                          alert("Please sign in first to save Businesses.");
                          return;
                        }
                        try {
                          await syncBusinessesToDatabase(targetBusinesses);
                          await loadSavedBusinessesFromDb();
                          setSyncFeedback(`Successfully saved all ${targetBusinesses.length} visible Businesses to your Saved Businesses list!`);
                          setTimeout(() => setSyncFeedback(null), 4000);
                        } catch (err: any) {
                          console.error("Batch save error:", err);
                        }
                      }}
                      disabled={displayedBusinesses.length === 0}
                      className={`text-[11px] border px-3 py-1.5 rounded font-bold transition flex items-center gap-1 shadow-sm ${
                        displayedBusinesses.length === 0
                          ? "bg-slate-150 text-slate-400 border-slate-200 cursor-not-allowed opacity-60"
                          : "bg-emerald-600 border-emerald-700 hover:bg-emerald-700 text-white cursor-pointer"
                      }`}
                      title={t("search:tooltip_save_all_visible_Businesses")}
                    >
                      <Save className="w-3 h-3 text-emerald-100" />
                      Save All Visible ({displayedBusinesses.length})
                    </button>
                    <button 
                      onClick={() => {
                        if (isProUser) {
                          handleExportCsv(filteredBusinesses);
                        } else {
                          setIsUpgradeModalOpen(true);
                        }
                      }}
                      disabled={filteredBusinesses.length === 0}
                      className={`text-[11px] border px-3 py-1.5 rounded font-bold transition flex items-center gap-1 shadow-sm ${
                        filteredBusinesses.length === 0
                          ? "bg-slate-150 text-slate-400 border-slate-200 cursor-not-allowed opacity-60"
                          : "bg-slate-900 border-slate-800 hover:bg-slate-800 text-emerald-400 cursor-pointer"
                      }`}
                      title={isProUser ? "Export filtered search results to client-side CSV format" : "Upgrade to Pro to export search results to CSV"}
                    >
                      <Download className="w-3 h-3 text-emerald-400" />
                      {isProUser ? `Export CSV (${filteredBusinesses.length})` : "Export CSV (Pro)"}
                    </button>
                  </div>
                )}
                
                {/* Active contact filter toggle directly editable */}
                <button
                  type="button"
                  onClick={() => setContactStatusFilter(prev => prev === "has_contacts" ? "any" : "has_contacts")}
                  className={`text-[11px] px-3 py-1.5 rounded border transition-all font-semibold flex items-center gap-1.5 select-none cursor-pointer ${
                    contactStatusFilter === "has_contacts"
                      ? "bg-blue-50 border-blue-200 text-blue-700"
                      : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700"
                  }`}
                  title={t("search:tooltip_filter_contact_found")}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${contactStatusFilter === "has_contacts" ? "bg-blue-600 animate-pulse" : "bg-slate-300"}`}></span> 
                  Filter: {contactStatusFilter === "has_contacts" ? "Direct Contacts Only" : "Show All Business Types"}
                </button>

                {workspaceMode === "saved" && savedBusinesses.length > 0 && (
                  <button 
                    type="button"
                    onClick={() => {
                      if (isProUser) {
                        handleExportCsv();
                      } else {
                        setIsUpgradeModalOpen(true);
                      }
                    }}
                    className={`text-[11px] font-bold px-3 py-1.5 rounded transition flex items-center gap-1 cursor-pointer shadow-sm border ${
                      settingsAppTheme === "sunset"
                        ? "bg-[#faf0e6] border-[#decfae] text-[#b45309] hover:bg-[#f3ece2]"
                        : settingsAppTheme === "ocean"
                        ? "bg-[#ecfdf5] border-[#a7f3d0] text-[#065f46] hover:bg-[#d1fae5]"
                        : resolveTheme(settingsAppTheme) === "dark"
                        ? "bg-slate-900 border-slate-800 hover:bg-slate-800 text-emerald-400"
                        : "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                    }`}
                    title={isProUser ? "Export all your saved business Business details directly to client-side CSV format" : "Upgrade to Pro to export saved Businesses to CSV"}
                  >
                    <Download className={`w-3.5 h-3.5 ${
                      settingsAppTheme === "sunset"
                        ? "text-[#b45309]"
                        : settingsAppTheme === "ocean"
                        ? "text-[#065f46]"
                        : resolveTheme(settingsAppTheme) === "dark"
                        ? "text-emerald-400"
                        : "text-emerald-600"
                    }`} />
                    <span>{isProUser ? t("common:btn_export_csv") : "Export CSV (Pro)"}</span>
                  </button>
                )}
              </div>
            </div>

            {/* Mobile view switchers in the results column */}
            {isMobile && workspaceMode === "search" && (
              <div className="bg-white border-b border-slate-200 p-2.5 shrink-0">
                <div className="flex bg-slate-100 p-1 rounded-xl gap-1">
                  <button
                    type="button"
                    onClick={() => setMobileSearchSubView("results")}
                    className={`flex-1 py-2 text-center text-xs font-extrabold rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
                      mobileSearchSubView === "results" || mobileSearchSubView === "form"
                        ? "bg-white text-blue-600 shadow-xs"
                        : "text-slate-500 hover:text-slate-800"
                    }`}
                    aria-label="Switch to List View"
                  >
                    <List className="w-3.5 h-3.5" />
                    <span>List</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMobileSearchSubView("map")}
                    className={`flex-1 py-2 text-center text-xs font-extrabold rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
                      mobileSearchSubView === "map"
                        ? "bg-white text-blue-600 shadow-xs"
                        : "text-slate-500 hover:text-slate-800"
                    }`}
                    aria-label="Switch to Map View"
                  >
                    <MapIcon className="w-3.5 h-3.5" />
                    <span>Map</span>
                  </button>
                </div>
              </div>
            )}

            {isMobile && workspaceMode === "saved" && (
              <div className="bg-white border-b border-slate-200 p-2.5 shrink-0">
                <div className="flex bg-slate-100 p-1 rounded-xl gap-1 flex-wrap">
                  <button
                    type="button"
                    onClick={() => {
                      setSavedSubview("list");
                      setMobileSavedSubView("content");
                    }}
                    className={`flex-1 min-w-[70px] py-1.5 text-center text-[11px] font-extrabold rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1 ${
                      mobileSavedSubView !== "map" && savedSubview === "list"
                        ? "bg-white text-emerald-700 shadow-xs"
                        : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    <List className="w-3.5 h-3.5" />
                    <span>List</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSavedSubview("pipeline");
                      setMobileSavedSubView("content");
                    }}
                    className={`flex-1 min-w-[80px] py-1.5 text-center text-[11px] font-extrabold rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1 ${
                      mobileSavedSubView !== "map" && savedSubview === "pipeline"
                        ? "bg-white text-emerald-700 shadow-xs"
                        : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    <Briefcase className="w-3.5 h-3.5 text-indigo-500" />
                    <span>Kanban</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSavedSubview("territory");
                      setMobileSavedSubView("content");
                    }}
                    className={`flex-1 min-w-[80px] py-1.5 text-center text-[11px] font-extrabold rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1 ${
                      mobileSavedSubView !== "map" && savedSubview === "territory"
                        ? "bg-white text-emerald-700 shadow-xs"
                        : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    <Compass className="w-3.5 h-3.5 text-blue-500" />
                    <span>Territory</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMobileSavedSubView("map")}
                    className={`flex-1 min-w-[70px] py-1.5 text-center text-[11px] font-extrabold rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1 ${
                      mobileSavedSubView === "map"
                        ? "bg-white text-emerald-700 shadow-xs"
                        : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    <MapIcon className="w-3.5 h-3.5" />
                    <span>Map</span>
                  </button>
                </div>
              </div>
            )}

            {/* Scrollable list content */}
            {isMobile && (
              (workspaceMode === "search" && mobileSearchSubView === "map") ||
              (workspaceMode === "saved" && mobileSavedSubView === "map")
            ) ? (
              <div className="flex-1 flex flex-col relative w-full h-full bg-white border-t border-slate-100">
                <MapView 
                  Businesses={filteredBusinesses}
                  selectedBusinessId={selectedBusinessId}
                  onSelectBusiness={(id) => {
                    setSelectedBusinessId(id);
                    setMobileIntelOpen(true);
                  }}
                  onCrawlBusiness={enrichBusiness}
                  mapStyle={mapStyle}
                  onMapStyleChange={(style) => handleSaveUserSettings(settingsAppTheme, style, locale)}
                  settingsAppTheme={settingsAppTheme}
                  googleMapsMapId={googleMapsMapId}
                  center={searchCenter || userCoords}
                  markerColorMode={markerColorMode}
                  onMarkerColorModeChange={setMarkerColorMode}
                  onSearchArea={(lat, lng) => {
                    const coordString = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
                    coordsUpdatedJustNowRef.current = true;
                    setZipInput(coordString);
                    setSearchCenter({ lat, lng });
                    setUserCoords({ lat, lng });
                    setTimeout(() => {
                      triggerBusinessesSearch(1, true);
                    }, 50);
                  }}
                  savedBusinesses={savedBusinesses}
                  isProUser={isProUser}
                  onUpgradeClick={() => setIsUpgradeModalOpen(true)}
                  isSavingBusinessId={isSavingBusinessId}
                  isDeletingBusinessId={isDeletingBusinessId}
                  onSaveBusiness={handleSaveIndividualBusiness}
                  onDeleteBusiness={handleDeleteIndividualBusiness}
                  isFullscreen={isMapFullscreen}
                  onFullscreenChange={setIsMapFullscreen}
                />
              </div>
            ) : (
              <div id="main-scrollable-container" className="flex-1 p-6 space-y-3 overflow-y-auto font-sans">
              
              {/* Mobile Filter Quick-Access Button */}
              {isMobile && workspaceMode === "search" && (mobileSearchSubView === "results" || mobileSearchSubView === "form") && (
                <button
                  type="button"
                  onClick={() => setMobileSearchSubView("form")}
                  className="w-full flex items-center justify-between p-3.5 bg-blue-50/75 border border-blue-100 hover:bg-blue-100/75 text-blue-700 rounded-xl mb-1 text-xs font-bold transition cursor-pointer shadow-xs"
                >
                  <div className="flex items-center gap-2">
                    <SlidersHorizontal className="w-4 h-4" />
                    <span>Adjust Search Filters & Location</span>
                  </div>
                  <span className="bg-blue-600 text-white text-[10px] font-black px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                    Filters
                  </span>
                </button>
              )}

              {isMobile && workspaceMode === "saved" && (mobileSavedSubView === "content" || mobileSavedSubView === "filters") && (
                <button
                  type="button"
                  onClick={() => setMobileSavedSubView("filters")}
                  className="w-full flex items-center justify-between p-3.5 bg-emerald-50/75 border border-emerald-100 hover:bg-emerald-100/75 text-emerald-700 rounded-xl mb-1 text-xs font-bold transition cursor-pointer shadow-xs"
                >
                  <div className="flex items-center gap-2">
                    <SlidersHorizontal className="w-4 h-4" />
                    <span>Adjust Saved Filters & Tags</span>
                  </div>
                  <span className="bg-emerald-600 text-white text-[10px] font-black px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                    Filters
                  </span>
                </button>
              )}
              
              {/* Dynamic Results Sorting and Counter */}
              {(workspaceMode === "search" || (workspaceMode === "saved" && savedSubview === "list")) && currentModeBusinesses.length > 0 && (
                <div 
                  className={`flex ${isMobile ? "flex-col items-stretch" : "flex-row items-center justify-between"} gap-3 bg-slate-50 border border-slate-150 p-3.5 rounded-xl mb-2 text-xs`} 
                  id="results-sorting-widget"
                >
                  <div className={`text-slate-500 font-semibold ${isMobile ? "text-center" : ""}`}>
                    Showing <span className="text-slate-900 font-extrabold">{filteredBusinesses.length}</span> of <span className="text-slate-800 font-black">{currentModeBusinesses.length}</span> {workspaceMode === "search" ? "searched" : "saved"} businesses
                  </div>
                  <div className={`flex flex-col sm:flex-row sm:items-center gap-2 ${isMobile ? "bg-white border border-slate-200/80 p-2 rounded-lg" : ""}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-550 font-bold">Sort by:</span>
                      <select
                        value={resultsSortBy}
                        onChange={(e) => setResultsSortBy(e.target.value as any)}
                        className={`${isMobile ? "flex-1 text-right bg-transparent border-none outline-hidden font-extrabold" : "bg-white border border-slate-250 rounded-lg px-2 py-1 font-bold"} hover:border-slate-350 text-slate-800 transition cursor-pointer text-xs`}
                      >
                        <option value="best_match">Best Match</option>
                        <option value="last_updated">Last Updated</option>
                        <option value="rating">Google Rating (High to Low)</option>
                        <option value="distance">Distance (Closest First)</option>
                        <option value="opportunity_score">Opportunity Score (Highest First)</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {crisisDetection && (crisisDetection.severity === "crisis_support" || crisisDetection.severity === "urgent_crisis" || crisisDetection.severity === "low_concern") && (
                <CrisisSupportCard 
                  severity={crisisDetection.severity}
                  onDismiss={() => {
                    setDismissedKeyword(keyword);
                    setCrisisDetection(null);
                  }}
                  onOpenSafetyResources={() => {
                    setActiveLegalTab("safety");
                    setIsLegalModalOpen(true);
                  }}
                  className="mb-4"
                />
              )}

              {workspaceMode === "saved" && savedSubview === "list" && (
                <>

            </>
          )}

              {workspaceMode === "search" && isSearching ? (
                <div className="space-y-4 animate-pulse p-4" id="search-loading-skeleton">
                  <div className="flex items-center gap-3 p-3 bg-slate-100 border border-slate-200 rounded-xl">
                    <Compass className="w-5 h-5 text-blue-600 animate-spin" />
                    <span className="text-xs font-extrabold text-slate-700">{searchStatusMessage || "Executing Deep Search Near You..."}</span>
                  </div>
                  {[1, 2, 3].map(i => (
                    <div key={i} className="p-5 border border-slate-150 rounded-xl space-y-3 bg-white">
                      <div className="flex justify-between items-start">
                        <div className="space-y-2 w-2/3">
                          <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                          <div className="h-3 bg-slate-150 rounded w-1/2"></div>
                        </div>
                        <div className="h-6 bg-slate-250 rounded w-16"></div>
                      </div>
                      <div className="h-3 bg-slate-100 rounded w-full"></div>
                      <div className="flex gap-2">
                        <div className="h-5 bg-slate-100 rounded w-14"></div>
                        <div className="h-5 bg-slate-100 rounded w-16"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : workspaceMode === "search" && searchError ? (
                <div className="h-full flex flex-col items-center justify-center p-8 text-center max-w-md mx-auto font-sans" id="search-error-container">
                  <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center border border-red-100 mb-4 text-red-500">
                    <AlertCircle className="w-8 h-8" />
                  </div>
                  <h3 className="font-bold text-slate-800 text-sm">Search Execution Failed</h3>
                  <p className="text-xs text-red-700 mt-2 bg-red-50 border border-red-150 p-3 rounded-lg leading-relaxed font-semibold">
                    {searchError}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
                    Please check your search filters, location parameters, or network connection and try again.
                  </p>
                </div>
              ) : workspaceMode === "saved" && savedSubview === "pipeline" ? (
                !isProUser ? (
                  <div className="bg-white border border-slate-200 rounded-2xl p-10 flex flex-col items-center justify-center text-center max-w-2xl mx-auto my-12 shadow-sm w-full">
                    <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center border border-emerald-100 mb-5 text-emerald-600">
                      <Lock className="w-8 h-8" />
                    </div>
                    <h3 className="text-lg font-black text-slate-800 tracking-tight">Interactive CRM Pipeline Board is a Pro Feature</h3>
                    <p className="text-slate-500 text-xs mt-2 leading-relaxed max-w-md">
                      Organize your local Businesses and prospects with a drag-and-drop Kanban workflow. Track deal states, customize outreach stages, and drive sales.
                    </p>
                    <button 
                      onClick={() => setIsUpgradeModalOpen(true)}
                      className="mt-6 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black rounded-lg transition-all shadow-sm hover:shadow active:scale-95 flex items-center gap-2 cursor-pointer"
                    >
                      <Sparkles className="w-4 h-4 text-amber-300 animate-pulse" />
                      <span>Upgrade to Pro to Unlock</span>
                    </button>
                  </div>
                ) : (
                  <div 
                    ref={pipelineScrollRef}
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (pipelineScrollRef.current) {
                      const container = pipelineScrollRef.current;
                      const rect = container.getBoundingClientRect();
                      const x = e.clientX - rect.left;
                      const edgeWidth = 100;
                      const scrollSpeed = 24;
                      if (x < edgeWidth) {
                        container.scrollLeft -= scrollSpeed;
                      } else if (rect.width - x < edgeWidth) {
                        container.scrollLeft += scrollSpeed;
                      }
                    }
                  }}
                  className="h-full flex gap-4 overflow-x-auto pb-4 scrollbar-thin"
                >
                  {["New", "Contacted", "Interested", "Follow Up", "Won", "Lost"].map((stage) => {
                    const stageBusinesses = savedBusinesses.filter((l) => (l.pipelineStage || "New") === stage);
                    const isOver = dragOverColumn === stage;
                    return (
                      <div 
                        key={stage} 
                        onDragOver={(e) => e.preventDefault()}
                        onDragEnter={(e) => { e.preventDefault(); setDragOverColumn(stage); }}
                        onDragLeave={() => setDragOverColumn(null)}
                        onDrop={(e) => {
                          e.preventDefault();
                          const BusinessId = e.dataTransfer.getData("text/plain");
                          if (BusinessId) {
                            const targetBusiness = savedBusinesses.find(l => l.id === BusinessId);
                            if (targetBusiness) {
                              updateBusinessCrm(BusinessId, stage, targetBusiness.followUpAt || null);
                            }
                          }
                          setDragOverColumn(null);
                        }}
                        className={`flex-1 min-w-[265px] rounded-lg p-4 flex flex-col max-h-[calc(100vh-210px)] border transition-all duration-200 ${
                          isOver 
                            ? "bg-emerald-50/70 border-emerald-300 ring-2 ring-emerald-400/20 shadow-xs scale-[1.01]" 
                            : "bg-slate-50/80 border-slate-205 shadow-3xs"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-3 border-b border-slate-200 pb-2">
                          <div className="flex items-center gap-1.5 font-sans">
                            <span className={`w-2.5 h-2.5 rounded-full ${
                              stage === "Won" ? "bg-emerald-500 animate-pulse" :
                              stage === "Lost" ? "bg-rose-450" :
                              stage === "Follow Up" ? "bg-indigo-500" :
                              stage === "Interested" ? "bg-amber-400" :
                              stage === "Contacted" ? "bg-blue-400" : "bg-slate-400"
                            }`}></span>
                            <h4 className="font-extrabold text-slate-800 text-sm tracking-tight">{stage}</h4>
                          </div>
                          <span className="bg-slate-200 px-2 py-0.5 rounded text-[10.5px] font-black text-slate-600">
                            {stageBusinesses.length}
                          </span>
                        </div>

                        <div className="flex-1 space-y-3 overflow-y-auto scrollbar-thin">
                          {stageBusinesses.length === 0 ? (
                            <div className="text-center py-10 text-[11px] text-slate-400 font-medium italic">
                              No businesses in this column. Drag cards here to move!
                            </div>
                          ) : (
                            stageBusinesses.map((Business) => {
                              const prospect = analyzeBusinessProspect(Business);
                              const score = Business.Businessescore !== undefined ? Business.Businessescore : prospect.score;
                              const tags = Business.opportunityTags?.length ? Business.opportunityTags : prospect.tags;
                              const cardAudienceScore = calculateAudienceScore(Business, null, "", audienceMode);
                              return (
                                <div
                                  key={Business.id}
                                  draggable={true}
                                  onDragStart={(e) => {
                                    e.dataTransfer.setData("text/plain", Business.id);
                                    e.currentTarget.style.opacity = "0.5";
                                  }}
                                  onDragEnd={(e) => {
                                    e.currentTarget.style.opacity = "1";
                                  }}
                                  onClick={() => {
                                    setSelectedBusinessId(Business.id);
                                    setMobileIntelOpen(true);
                                  }}
                                  className={`bg-white p-3 rounded-lg shadow-3xs border border-slate-200 hover:border-slate-350 transition cursor-grab active:cursor-grabbing select-none space-y-2 relative ${
                                    selectedBusinessId === Business.id ? "ring-2 ring-emerald-555/25 bg-emerald-50/10 border-emerald-300" : ""
                                  }`}
                                >
                                  <div className="business-title-row">
                                    <h5 className="business-title font-bold text-slate-900 text-[12px] leading-snug truncate">{Business.name}</h5>
                                    {(cardAudienceScore.popularityWarning || Business.popularityWarning) && (
                                      <span
                                        className="popularity-competition-warning"
                                        title={(cardAudienceScore.popularityWarning || Business.popularityWarning)?.reason}
                                        aria-label={(cardAudienceScore.popularityWarning || Business.popularityWarning)?.reason}
                                      >
                                        <span aria-hidden="true">⚠</span>
                                        {(cardAudienceScore.popularityWarning || Business.popularityWarning)?.label}
                                      </span>
                                    )}
                                    {(() => {
                                      const isHiring = tags && (
                                        tags.includes("Currently Hiring") || 
                                        tags.includes("Hiring Multiple Roles") || 
                                        tags.includes("High Hiring Signal") || 
                                        tags.includes("Seasonal Hiring Likely")
                                      );
                                      if (isHiring) {
                                        return (
                                          <span className="text-[8px] font-black bg-amber-500 text-white px-1.5 py-0.2 rounded shadow-3xs uppercase tracking-wide shrink-0 select-none">
                                            Hiring
                                          </span>
                                        );
                                      }
                                      return null;
                                    })()}
                                  </div>
                                  <div className="flex items-center justify-between text-[10px] text-slate-400">
                                    <span className="truncate max-w-[120px]">{Business.address}</span>
                                    <span className="font-black text-amber-500 bg-amber-50 px-1 py-0.2 rounded border border-amber-100 text-[9.5px]">
                                      ★ {score} pts
                                    </span>
                                  </div>
                                  
                                  {tags && tags.length > 0 && (
                                    <div className="flex flex-wrap gap-1">
                                      {tags.slice(0, 2).map((tg, idx) => (
                                        <span 
                                          key={idx} 
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSearchTagQuery(tg);
                                            setShowTagSuggestions(false);
                                          }}
                                          className="text-[8.5px] font-bold bg-blue-50 text-blue-700 border border-blue-200 px-1.5 py-0.2 rounded hover:bg-blue-100 cursor-pointer transition select-none"
                                          title={`Click to filter by tag: ${translateTag(tg, t, audienceMode)}`}
                                        >
                                          {translateTag(tg, t, audienceMode)}
                                        </span>
                                      ))}
                                    </div>
                                  )}

                                  <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
                                    <select
                                      value={Business.pipelineStage || "New"}
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={(e) => updateBusinessCrm(Business.id, e.target.value, Business.followUpAt || null)}
                                      className="text-[9.5px] bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5 font-bold text-slate-650 animate-fade"
                                    >
                                      {["New", "Contacted", "Interested", "Follow Up", "Won", "Lost"].map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                    
                                    {Business.followUpAt && (
                                      <span className={`text-[8.5px] font-extrabold px-1.5 py-0.5 rounded border flex items-center gap-0.5 ${
                                        (Business.followUpAt && new Date(Business.followUpAt).getTime() < Date.now())
                                          ? "text-red-700 bg-red-50 border-red-200 animate-pulse" 
                                          : "text-indigo-750 bg-indigo-50 border-indigo-100"
                                      }`}
                                      title={(Business.followUpAt && new Date(Business.followUpAt).getTime() < Date.now()) ? "Overdue follow-up!" : "Next follow-up date"}>
                                        {(Business.followUpAt && new Date(Business.followUpAt).getTime() < Date.now()) ? "⚠️ Overdue: " : "📅 Due: "} {new Date(Business.followUpAt).toLocaleDateString()}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                )
              ) : workspaceMode === "saved" && savedSubview === "territory" ? (
                !isProUser ? (
                  <div className="bg-white border border-slate-200 rounded-2xl p-10 flex flex-col items-center justify-center text-center max-w-2xl mx-auto my-12 shadow-sm w-full">
                    <div className="w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center border border-indigo-100 mb-5 text-indigo-600">
                      <Lock className="w-8 h-8" />
                    </div>
                    <h3 className="text-lg font-black text-slate-800 tracking-tight">Territory Intelligence Planner is a Pro Feature</h3>
                    <p className="text-slate-500 text-xs mt-2 leading-relaxed max-w-md">
                      Analyze geographic concentration of Businesses. Gain ZIP code market share indexes, category breakdowns, and optimize travel plans or sales territories.
                    </p>
                    <button 
                      onClick={() => setIsUpgradeModalOpen(true)}
                      className="mt-6 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-black rounded-lg transition-all shadow-sm hover:shadow active:scale-95 flex items-center gap-2 cursor-pointer"
                    >
                      <Sparkles className="w-4 h-4 text-amber-300 animate-pulse" />
                      <span>Upgrade to Pro to Unlock</span>
                    </button>
                  </div>
                ) : (
                  (() => {
                  const zipGrouping = savedBusinesses.reduce((acc, Business) => {
                    const zip = Business.zipcode || "Unknown Area";
                    if (!acc[zip]) {
                      acc[zip] = { count: 0, totalScore: 0, Businesses: [] as BusinessBusiness[] };
                    }
                    const score = Business.Businessescore !== undefined ? Business.Businessescore : analyzeBusinessProspect(Business).score;
                    acc[zip].count += 1;
                    acc[zip].totalScore += score;
                    acc[zip].Businesses.push(Business);
                    return acc;
                  }, {} as Record<string, { count: number; totalScore: number; Businesses: BusinessBusiness[] }>);

                  const averageScoreTotal = savedBusinesses.reduce((tot, l) => {
                    return tot + (l.Businessescore !== undefined ? l.Businessescore : analyzeBusinessProspect(l).score);
                  }, 0);
                  const globalSavedAverage = savedBusinesses.length > 0 ? Math.round(averageScoreTotal / savedBusinesses.length) : 0;

                  // Define theme-aware color styles for the cards and text
                  const activeTerritoryTheme = settingsAppTheme === "sunset" ? "sunset" : settingsAppTheme === "ocean" ? "ocean" : resolveTheme(settingsAppTheme);

                  const kpiBgClass = 
                    activeTerritoryTheme === "light" 
                      ? "bg-white border-slate-200 text-slate-850 shadow-3xs" 
                      : activeTerritoryTheme === "sunset"
                      ? "bg-[#faf6f0] border-[#e6dfd5] text-[#2d251e] shadow-xs"
                      : activeTerritoryTheme === "ocean"
                      ? "bg-white border-[#cddfe6] text-[#0b2533] shadow-xs"
                      : "bg-[#1e293b] border-slate-800 text-white shadow-3xs";

                  const textPrimaryClass = 
                    activeTerritoryTheme === "light" 
                      ? "text-slate-900" 
                      : activeTerritoryTheme === "sunset"
                      ? "text-[#2d251e]"
                      : activeTerritoryTheme === "ocean"
                      ? "text-[#0b2533]"
                      : "text-white";

                  const textSecondaryClass = 
                    activeTerritoryTheme === "light" 
                      ? "text-slate-500" 
                      : activeTerritoryTheme === "sunset"
                      ? "text-[#5c4a3c]"
                      : activeTerritoryTheme === "ocean"
                      ? "text-[#405f70]"
                      : "text-slate-400";

                  const boardBgClass = 
                    activeTerritoryTheme === "light" 
                      ? "bg-white border-slate-200 shadow-3xs" 
                      : activeTerritoryTheme === "sunset"
                      ? "bg-[#faf6f0] border-[#e6dfd5] shadow-xs"
                      : activeTerritoryTheme === "ocean"
                      ? "bg-white border-[#cddfe6] shadow-xs"
                      : "bg-[#1e293b] border-slate-800 shadow-3xs";

                  const rowBgClass = 
                    activeTerritoryTheme === "light" 
                      ? "bg-slate-50 border border-slate-100 text-slate-800" 
                      : activeTerritoryTheme === "sunset"
                      ? "bg-[#f3ece2]/60 border border-[#eadecc] text-[#2d251e]"
                      : activeTerritoryTheme === "ocean"
                      ? "bg-[#f4f7f6] border border-[#cddfe6] text-[#0b2533]"
                      : "bg-slate-850 border border-slate-800 text-slate-250";

                  const rowBadgeClass = 
                    activeTerritoryTheme === "light" 
                      ? "bg-indigo-50 text-indigo-700" 
                      : activeTerritoryTheme === "sunset"
                      ? "bg-amber-100 text-amber-900"
                      : activeTerritoryTheme === "ocean"
                      ? "bg-[#dee8ee] text-[#0d5c63]"
                      : "bg-indigo-950/50 text-indigo-300";

                  const headingColorClass = 
                    activeTerritoryTheme === "light" 
                      ? "text-indigo-600" 
                      : activeTerritoryTheme === "sunset"
                      ? "text-amber-700"
                      : activeTerritoryTheme === "ocean"
                      ? "text-[#0d5c63]"
                      : "text-indigo-400";

                  const inputClass = 
                    activeTerritoryTheme === "light" 
                      ? "bg-white border-slate-250 text-slate-800 focus:ring-indigo-500 placeholder:text-slate-400" 
                      : activeTerritoryTheme === "sunset"
                      ? "bg-[#fcfaf7] border-[#eadecc] text-[#2d251e] focus:ring-amber-500 placeholder:text-[#8a725e]/60"
                      : activeTerritoryTheme === "ocean"
                      ? "bg-[#f4f7f6] border-[#cddfe6] text-[#0b2533] focus:ring-teal-500 placeholder:text-[#405f70]/60"
                      : "bg-slate-900 border-slate-700 text-white focus:ring-indigo-500 placeholder:text-slate-550";

                  const buttonClass = 
                    savedBusinesses.length === 0
                      ? (activeTerritoryTheme === "light"
                          ? "bg-slate-100 border-slate-350 text-slate-400 cursor-not-allowed opacity-80"
                          : activeTerritoryTheme === "sunset"
                          ? "bg-[#faf6f0] border-[#e6dfd5] text-[#8a725e]/40 cursor-not-allowed opacity-80"
                          : activeTerritoryTheme === "ocean"
                          ? "bg-[#dee8ee] border-[#cddfe6] text-[#405f70]/40 cursor-not-allowed opacity-80"
                          : "bg-slate-850 border-slate-800 text-slate-600 cursor-not-allowed opacity-80")
                      : (activeTerritoryTheme === "light" || activeTerritoryTheme === "dark"
                          ? "bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 border-indigo-700 text-white cursor-pointer"
                          : activeTerritoryTheme === "sunset"
                          ? "bg-[#f97316] hover:bg-[#ea580c] active:bg-[#c2410c] border-[#ea580c] text-white cursor-pointer"
                          : "bg-[#0d5c63] hover:bg-[#094247] active:bg-[#052d30] border-[#094247] text-white cursor-pointer");

                  const listRowBgClass = 
                    activeTerritoryTheme === "light" 
                      ? "bg-slate-50 hover:bg-slate-100 border border-slate-150 text-slate-900" 
                      : activeTerritoryTheme === "sunset"
                      ? "bg-[#fcfaf7] hover:bg-[#f3ece2] border border-[#eadecc] text-[#2d251e]"
                      : activeTerritoryTheme === "ocean"
                      ? "bg-[#f4f7f6] hover:bg-[#dee8ee] border border-[#cddfe6] text-[#0b2533]"
                      : "bg-slate-900 hover:bg-slate-850 border border-slate-800 text-slate-100";

                  const stepBubbleBgClass = 
                    activeTerritoryTheme === "light" 
                      ? "bg-indigo-600" 
                      : activeTerritoryTheme === "sunset"
                      ? "bg-amber-600"
                      : activeTerritoryTheme === "ocean"
                      ? "bg-[#0d5c63]"
                      : "bg-indigo-500";

                  return (
                    <div className="space-y-6">
                      {/* Territory KPI Cards */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className={`p-4 rounded-xl border text-center space-y-1 ${kpiBgClass}`}>
                          <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Territory Density</p>
                          <p className={`text-3xl font-black ${textPrimaryClass}`}>{savedBusinesses.length}</p>
                          <p className={`text-[11px] font-medium ${textSecondaryClass}`}>Total Saved Businesses</p>
                        </div>
                        <div className={`p-4 rounded-xl border text-center space-y-1 ${kpiBgClass}`}>
                          <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Avg Quality Rating</p>
                          <p className="text-3xl font-black text-emerald-600 font-mono">{globalSavedAverage}<span className="text-xs text-slate-400">/100</span></p>
                          <p className={`text-[11px] font-medium ${textSecondaryClass}`}>Sales Potential Grade</p>
                        </div>
                        <div className={`p-4 rounded-xl border text-center space-y-1 ${kpiBgClass}`}>
                          <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Regional Coverage</p>
                          <p className="text-3xl font-black text-indigo-705" style={{ color: settingsAppTheme === "ocean" ? "#0d5c63" : settingsAppTheme === "sunset" ? "#f97316" : "" }}>{Object.keys(zipGrouping).length}</p>
                          <p className={`text-[11px] font-medium ${textSecondaryClass}`}>Active Zone ZIP Codes</p>
                        </div>
                      </div>

                      {/* ZIP Code Density Board */}
                      <div className={`p-5 space-y-3.5 rounded-xl border font-sans ${boardBgClass}`}>
                        <h4 className={`font-extrabold text-sm tracking-tight ${headingColorClass}`}>Active Heat Zone Breakdown</h4>
                        <div className="space-y-2">
                          {Object.keys(zipGrouping).map((zip) => {
                            const data = zipGrouping[zip];
                            const avg = Math.round(data.totalScore / data.count);
                            const percent = Math.min(100, Math.max(5, (avg / 100) * 100));
                            return (
                              <div key={zip} className={`flex items-center justify-between p-3 rounded-lg flex-wrap gap-2 text-xs ${rowBgClass}`}>
                                <div className="space-y-1">
                                  <p className="font-extrabold flex items-center gap-1.5">
                                    📍 ZIP Zone: {zip}
                                    <span className={`text-[10px] px-2 py-0.2 rounded font-black ${rowBadgeClass}`}>
                                      {data.count} business{data.count > 1 ? "es" : ""}
                                    </span>
                                  </p>
                                  <p className={`font-medium text-[11px] ${textSecondaryClass}`}>Average Prospect Score: {avg}/100</p>
                                </div>
                                <div className="w-full sm:w-44 flex items-center gap-2">
                                  <div className={`flex-1 rounded-full h-1.5 overflow-hidden ${activeTerritoryTheme === "dark" ? "bg-slate-800" : "bg-slate-200"}`}>
                                    <div className="bg-gradient-to-r from-orange-500 to-emerald-500 h-1.5 rounded-full" style={{ width: `${percent}%` }} />
                                  </div>
                                  <span className={`font-bold font-mono text-[11px] shrink-0 ${textSecondaryClass}`}>{avg} pts</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Turning Router Sequence Section */}
                      <div className={`p-5 space-y-4 rounded-xl border font-sans ${boardBgClass}`}>
                        <div className="space-y-1">
                          <h4 className={`font-extrabold text-sm tracking-tight flex items-center gap-1.5 font-sans ${textPrimaryClass}`}>
                            🗺️ Route Planner
                          </h4>
                          <p className={`text-xs leading-normal ${textSecondaryClass}`}>
                            Enter a starting ZIP code or address. We’ll order your saved businesses from nearest to farthest so you can plan outreach by area.
                          </p>
                        </div>

                        <div className="space-y-2">
                          <div className="flex gap-2 max-w-sm sm:max-w-md">
                            <input
                              type="text"
                              placeholder={t("search:placeholder_enter_zip")}
                              value={routeStartZip}
                              onChange={(e) => setRouteStartZip(e.target.value)}
                              className={`rounded px-3 py-2 text-xs font-semibold focus:outline-none focus:ring-2 flex-1 ${inputClass}`}
                            />
                            <button
                              type="button"
                              onClick={handleSequenceRoute}
                              disabled={savedBusinesses.length === 0}
                              title={savedBusinesses.length === 0 ? "You need to add businesses to your Saved Businesses to plan a route plan." : "Calculate the optimal nearest-first sequence for your saved businesses."}
                              className={`text-xs px-4 py-2 rounded font-bold transition flex items-center justify-center gap-1 shrink-0 border shadow-sm ${buttonClass}`}
                            >
                              Calculate Sequence
                            </button>
                          </div>
                          
                          {savedBusinesses.length === 0 && (
                            <p className="text-[10.5px] text-amber-700 font-medium flex items-center gap-1 mt-1 bg-amber-50 border border-amber-100 p-2 rounded">
                              ⚠️ You need to add businesses to your Saved Businesses to calculate your suggested visit order.
                            </p>
                          )}
                        </div>

                        {calculatedSequence.length > 0 && (
                          <div className={`space-y-3.5 pt-3 border-t ${activeTerritoryTheme === "light" ? "border-slate-100" : activeTerritoryTheme === "sunset" ? "border-[#eadecc]" : activeTerritoryTheme === "ocean" ? "border-[#cddfe6]" : "border-slate-800"}`}>
                            <h5 className={`text-xs font-extrabold ${headingColorClass}`}>Calculated Route Plan ({calculatedSequence.length} Steps):</h5>
                            <ol className={`relative border-l-2 border-dashed ml-2.5 space-y-3 ${activeTerritoryTheme === "light" ? "border-indigo-200" : activeTerritoryTheme === "sunset" ? "border-[#eadecc]" : activeTerritoryTheme === "ocean" ? "border-[#cddfe6]" : "border-slate-700"}`}>
                              {calculatedSequence.map((Business, idx) => {
                                const prospect = analyzeBusinessProspect(Business);
                                const score = Business.Businessescore !== undefined ? Business.Businessescore : prospect.score;
                                return (
                                  <li key={Business.id} className="relative pl-6">
                                    <span className={`absolute -left-[14px] top-0 flex items-center justify-center w-6 h-6 rounded-full border border-white text-white font-black text-[10px] ${stepBubbleBgClass}`}>
                                      {idx + 1}
                                    </span>
                                    <div
                                      onClick={() => {
                                        setSelectedBusinessId(Business.id);
                                        setMobileIntelOpen(true);
                                      }}
                                      className={`p-3 rounded-lg transition cursor-pointer flex items-center justify-between gap-3 ${listRowBgClass}`}
                                    >
                                      <div className="min-w-0 flex-1 space-y-0.5">
                                        <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                                          <p className={`font-extrabold text-xs truncate leading-normal ${textPrimaryClass}`}>{Business.name}</p>
                                          {(() => {
                                            const tags = Business.opportunityTags?.length ? Business.opportunityTags : prospect.tags;
                                            const isHiring = tags && (
                                              tags.includes("Currently Hiring") || 
                                              tags.includes("Hiring Multiple Roles") || 
                                              tags.includes("High Hiring Signal") || 
                                              tags.includes("Seasonal Hiring Likely")
                                            );
                                            if (isHiring) {
                                              return (
                                                <span className="text-[8px] font-black bg-amber-500 text-white px-1.5 py-0.2 rounded shadow-3xs uppercase tracking-wide shrink-0 select-none">
                                                  Hiring
                                                </span>
                                              );
                                            }
                                            return null;
                                          })()}
                                        </div>
                                        <p className={`text-[10.5px] truncate ${textSecondaryClass}`}>{Business.address}</p>
                                      </div>
                                      <span className="bg-amber-50 text-amber-700 border border-amber-100 text-[10.5px] font-black px-2 py-0.5 rounded shrink-0 font-mono">
                                        ★ {score} pts
                                      </span>
                                    </div>
                                  </li>
                                );
                              })}
                            </ol>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()
                )
              ) : workspaceMode === "admin_cost" ? (
                <AdminCostPanel apiFetch={apiFetch} currentUser={currentUser} settingsAppTheme={settingsAppTheme} />
              ) : workspaceMode === "pro_tools" ? (
                <ProToolsPanel
                  currentUser={currentUser}
                  billingStatus={{
                    plan: isProUser ? "Pro" : "Free",
                    limits: {
                      searches: {
                        count: billingStatus?.limits?.searches?.count || 0,
                        limit: billingStatus?.limits?.searches?.limit || 25
                      },
                      contact_crawl: {
                        count: billingStatus?.limits?.contact_crawl?.count || 0,
                        limit: billingStatus?.limits?.contact_crawl?.limit || 15
                      }
                    }
                  }}
                  savedBusinesses={savedBusinesses}
                  apiFetch={apiFetch}
                  onUpgradeClick={() => setIsUpgradeModalOpen(true)}
                  onExportCsv={(targetBusinesses) => {
                    handleExportCsv(targetBusinesses);
                  }}
                  onBusinessCrmUpdate={(BusinessId, stage, followUpAt) => {
                    setSavedBusinesses(prev => prev.map(l => l.id === BusinessId ? { ...l, pipelineStage: stage, followUpAt } : l));
                  }}
                  allBusinesses={Businesses}
                  onLaunchSearch={(params) => {
                    if (params.location) setZipInput(params.location);
                    setWorkspaceMode("search");
                  }}
                  theme={resolveTheme(settingsAppTheme)}
                />
              ) : workspaceMode === "history" ? (
                <div className="space-y-6">
                  {history.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center p-8 text-center max-w-sm mx-auto font-sans">
                      <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center border border-slate-200 mb-4 text-slate-400 animate-pulse">
                        <RotateCcw className="w-8 h-8" />
                      </div>
                      <h3 className="font-bold text-slate-800 text-sm">No Search History Yet</h3>
                      <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
                        {t("admin:desc_actions_history")}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3.5">
                      <div className="flex justify-between items-center text-[10px] font-bold text-slate-400 uppercase tracking-widest pb-1 border-b border-slate-100 mb-2 font-sans select-none">
                        <span>{t("admin:btn_actions_lookup_logs")}</span>
                        <button 
                          onClick={async () => {
                            if (confirm(t("admin:confirm_clear_history"))) {
                              setHistory([]);
                              localStorage.removeItem("crm_lookup_history");
                              try {
                                await apiFetch("/api/history", { method: "DELETE" });
                              } catch (e) {
                                console.error("Failed to clear remote lookup history:", e);
                              }
                            }
                          }}
                          className="text-[9px] text-red-500 hover:text-red-700 normal-case cursor-pointer font-bold tracking-normal"
                        >
                          {t("admin:btn_clear_all_history")}
                        </button>
                      </div>

                    {history.map((item) => {
                      let tagBg = "bg-blue-50 text-blue-700 border-blue-100";
                      let tagText = t("common:btn_search");
                      if (item.lookup_type === "social_link") {
                        tagBg = "bg-purple-50 text-purple-700 border-purple-100";
                        tagText = t("admin:tag_link_redirect");
                      } else if (item.lookup_type === "contact_crawl") {
                        tagBg = "bg-emerald-50 text-emerald-700 border-emerald-100";
                        tagText = t("admin:tag_contact_crawl");
                      }

                      return (
                        <div 
                          key={item.id}
                          onClick={() => handlePullUpHistoryItem(item)}
                          className="p-4 bg-white rounded-lg border border-slate-200/85 hover:border-slate-400 cursor-pointer transition-all hover:shadow-xs group relative"
                        >
                          <div className="flex items-center justify-between gap-2.5 mb-2">
                            <span className={`text-[8.5px] font-extrabold uppercase px-2 py-0.5 rounded-full border ${tagBg}`}>
                              {tagText}
                            </span>
                            <span className="text-[9.5px] text-slate-400 font-mono">
                              {new Date(item.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} ({new Date(item.created_at).toLocaleDateString()})
                            </span>
                          </div>

                          {item.lookup_type === "business_search" ? (
                            <div>
                              <p className="text-xs font-extrabold text-slate-800 leading-tight">
                                {t("admin:label_searched")} <span className="text-blue-700 font-black">"{item.query_keyword}"</span>
                              </p>
                              <p className="text-[10.5px] text-slate-500 font-semibold mt-1">
                                {t("admin:label_zipcode")} <span className="text-slate-700 font-bold">{item.query_zipcode}</span>
                              </p>
                            </div>
                          ) : (
                            <div>
                              <p className="text-xs font-bold text-slate-800 leading-tight">
                                {item.Business_name}
                              </p>
                              <p className="text-[10px] text-slate-505 font-medium mt-1">
                                {item.target_name}
                              </p>
                            </div>
                          )}

                          <div className="absolute right-4 bottom-4 opacity-0 group-hover:opacity-100 transition-opacity">
                            <span className="text-[9.5px] font-extrabold text-blue-600 bg-blue-50 border border-blue-150 px-2 py-0.5 rounded flex items-center gap-1">
                              {t("common:btn_restore")} <ExternalLink className="w-2.5 h-2.5" />
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : currentModeBusinesses.length === 0 ? (
                workspaceMode === "saved" ? (
                  <div className="h-full flex flex-col items-center justify-center p-8 text-center max-w-md mx-auto font-sans">
                    <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center border border-emerald-100 mb-4 text-emerald-600">
                      <Save className="w-8 h-8" />
                    </div>
                    <h3 className="font-bold text-slate-800 text-sm">{t("search:empty_saved_businesses_title")}</h3>
                    <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
                      {t("search:empty_saved_businesses_desc")}
                    </p>
                  </div>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center p-6 text-center max-w-md mx-auto font-sans space-y-4">
                    <div className="w-14 h-14 bg-indigo-50 rounded-full flex items-center justify-center border border-indigo-150 text-indigo-600 shadow-sm">
                      <Compass className="w-7 h-7" />
                    </div>
                    <div>
                      <h3 className="font-extrabold text-slate-850 text-base">No businesses matched every keyword</h3>
                      <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                        {keyword.trim() ? `Search "${keyword}" yielded 0 exact matches in this area.` : "Try expanding your location or adjusting keyword requirements."}
                      </p>
                    </div>

                    {keyword.trim() && (() => {
                      const parsed = parseBusinessQuery(keyword);
                      return (
                        <div className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3.5 text-left text-xs space-y-2.5">
                          <p className="font-bold text-[10.5px] text-slate-500 uppercase tracking-wider">Suggested Recovery Steps:</p>
                          <div className="space-y-2">
                            {parsed.parsedChips.map(chip => (
                              <button
                                key={chip.id}
                                type="button"
                                onClick={() => {
                                  const newKw = removeTermFromQuery(keyword, chip.originalText || chip.label);
                                  setKeyword(newKw);
                                  triggerBusinessesSearch(1, true);
                                }}
                                className="w-full text-left px-3 py-1.5 bg-white hover:bg-indigo-50/60 border border-slate-200 rounded-lg text-slate-700 font-semibold transition flex items-center justify-between text-xs cursor-pointer"
                              >
                                <span>Remove requirement <strong className="text-indigo-600">"{chip.label}"</strong></span>
                                <span className="text-indigo-500 text-[11px] font-bold">Remove ×</span>
                              </button>
                            ))}
                            <button
                              type="button"
                              onClick={() => {
                                setDistance("50");
                                triggerBusinessesSearch(1, true);
                              }}
                              className="w-full text-left px-3 py-1.5 bg-white hover:bg-indigo-50/60 border border-slate-200 rounded-lg text-slate-700 font-semibold transition flex items-center justify-between text-xs cursor-pointer"
                            >
                              <span>Expand search radius to <strong className="text-indigo-600">50 miles</strong></span>
                              <span className="text-indigo-500 text-[11px] font-bold">Expand →</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setKeyword("");
                                triggerBusinessesSearch(1, true);
                              }}
                              className="w-full text-left px-3 py-1.5 bg-white hover:bg-indigo-50/60 border border-slate-200 rounded-lg text-slate-700 font-semibold transition flex items-center justify-between text-xs cursor-pointer"
                            >
                              <span>Show all businesses in area</span>
                              <span className="text-indigo-500 text-[11px] font-bold">Reset</span>
                            </button>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )
              ) : filteredBusinesses.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center p-8 text-center max-w-sm mx-auto font-sans">
                  <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center border border-slate-200 mb-3 text-slate-400">
                    <SlidersHorizontal className="w-6 h-6" />
                  </div>
                  <h3 className="font-bold text-slate-800 text-sm">{t("search:no_businesses_matching_title")}</h3>
                  <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                    {t("search:no_businesses_matching_desc")}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Floating Competitor Comparison Bar */}
                  {compareBusinessIds.length > 0 && (
                    <div className="sticky top-2 z-40 bg-gradient-to-r from-indigo-700 to-blue-700 text-white rounded-xl p-3.5 shadow-xl border border-indigo-650 flex items-center justify-between animate-bounce-short">
                      <div className="flex items-center gap-2.5">
                        <div className="bg-indigo-600/50 p-1.5 rounded-lg border border-indigo-400/30">
                          <Compass className="w-4 h-4 text-amber-300 animate-spin-slow" />
                        </div>
                        <div>
                          <p className="text-[11.5px] font-black tracking-wide">
                            Competitor Comparison Ready
                          </p>
                          <p className="text-[9.5px] text-slate-100 font-semibold">
                            Selected {compareBusinessIds.length} business{compareBusinessIds.length > 1 ? "es" : ""} to compare metrics
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setCompareBusinessIds([])}
                          className="px-2.5 py-1 text-[10px] font-black text-indigo-200 hover:text-white transition cursor-pointer"
                        >
                          Clear
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setIsCompareModalOpen(true);
                          }}
                          className="px-3.5 py-1.5 bg-amber-400 hover:bg-amber-350 active:scale-95 text-slate-900 font-extrabold text-[11px] rounded-lg shadow-sm transition flex items-center gap-1 cursor-pointer"
                        >
                          <span>Compare Now</span>
                          <span className="bg-slate-900 text-amber-400 text-[9px] px-1.5 py-0.2 rounded-full font-black">
                            {compareBusinessIds.length}
                          </span>
                        </button>
                      </div>
                    </div>
                  )}



                  {displayedBusinesses.map((Business, bIdx) => {
                  const isSelected = Business.id === selectedBusinessId;
                  
                  // Calculate distance from searchCenter or user geolocated coordinates
                  let BusinessDistanceMiles: number | null = null;
                  const targetCenter = searchCenter || userCoords;
                  if (targetCenter && Business.location?.lat && Business.location?.lng) {
                    BusinessDistanceMiles = getDistanceInMiles(
                      targetCenter.lat,
                      targetCenter.lng,
                      Business.location.lat,
                      Business.location.lng
                    );
                  }
                  
                  const rankingOptions = {
                    prioritizeHiring: rankPrioritizeHiring,
                    prioritizeTransparency: rankPrioritizeTransparency,
                    prioritizeFlexibility: rankPrioritizeFlexibility,
                    prioritizeCommute: rankPrioritizeCommute,
                    prioritizeGrowthSecurity: rankPrioritizeGrowthSecurity,
                  };

                  const cardAudienceScore = calculateAudienceScore(Business, BusinessDistanceMiles, keyword, audienceMode);
                  const {
                    totalScore: score,
                    prospect
                  } = getUpgradedOrganicScore(Business, BusinessDistanceMiles, keyword, undefined, rankingOptions, audienceMode);
                  const tags = Business.opportunityTags?.length ? Business.opportunityTags : prospect.tags;
                  const hiringScore = prospect.scores?.hiringScore || 0;
                  const legacyResult = getHiringLikelihood(hiringScore, tags);
                  const refinedResult = refineUnconfirmedHiringSignal(Business, prospect, legacyResult);
                  
                  let hiringBadgeBg = 'bg-slate-100 text-slate-500 border-slate-200';
                  switch (refinedResult.type) {
                    case 'high':
                      hiringBadgeBg = 'bg-emerald-50 text-emerald-700 border-emerald-200';
                      break;
                    case 'possible':
                      hiringBadgeBg = 'bg-blue-50 text-blue-700 border-blue-200';
                      break;
                    case 'unconfirmed':
                      hiringBadgeBg = 'bg-slate-100 text-slate-500 border-slate-200';
                      break;
                    case 'improbable':
                      hiringBadgeBg = 'bg-orange-50 text-orange-700 border-orange-200';
                      break;
                    case 'low':
                      hiringBadgeBg = 'bg-red-50 text-red-700 border-red-200';
                      break;
                  }
                  
                  // Style left borders correctly according to theme
                  let borderStyle = "border-l-4 border-slate-300";
                  if (isSelected) {
                    borderStyle = "border-l-4 border-emerald-500 ring-2 ring-emerald-505/20";
                  } else if (Business.crawlStatus === "success" && Business.contacts.length > 0) {
                    borderStyle = "border-l-4 border-blue-500";
                  }

                  return (
                    <div
                      key={Business.id}
                      id={`Business-card-${Business.id}`}
                      data-ai-editable="business-card"
                      onClick={() => {
                        setSelectedBusinessId(Business.id);
                        setMobileIntelOpen(true);
                      }}
                      className={`bg-white p-5 ${borderStyle} hover:shadow-md border border-slate-150 rounded-xl flex flex-col justify-center group transition-all duration-200 cursor-pointer space-y-3.5 ${
                        isSelected 
                          ? "bg-slate-50/70 shadow-sm" 
                          : "hover:bg-slate-50/40"
                      }`}
                    >
                      <div className="flex flex-col gap-3">
                        {/* Title Row with badges */}
                        <div className="space-y-1.5 font-sans">
                          <div className="flex flex-col gap-2">
                            <div className="business-title-row">
                              <h3 className="business-title font-extrabold text-slate-850 text-sm md:text-base leading-snug group-hover:text-blue-600 transition-colors">
                                {Business.name}
                              </h3>
                              {(cardAudienceScore.popularityWarning || Business.popularityWarning) && (
                                <span
                                  className="popularity-competition-warning"
                                  title={(cardAudienceScore.popularityWarning || Business.popularityWarning)?.reason}
                                  aria-label={(cardAudienceScore.popularityWarning || Business.popularityWarning)?.reason}
                                >
                                  <span aria-hidden="true">⚠</span>
                                  {(cardAudienceScore.popularityWarning || Business.popularityWarning)?.label}
                                </span>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-1.5 items-center">
                              <label 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!isProUser) {
                                    setIsUpgradeModalOpen(true);
                                  }
                                }} 
                                className={`inline-flex items-center gap-1 cursor-pointer px-1.5 py-0.5 rounded border select-none text-[9.5px] font-black transition shadow-4xs ${
                                  isProUser 
                                    ? "bg-slate-100 hover:bg-slate-200 border-slate-200 text-indigo-700" 
                                    : "bg-amber-50 hover:bg-amber-100 border-amber-200 text-amber-800"
                                }`}
                                title={isProUser ? "Select for competitor comparison" : "Upgrade to Pro to compare prospects side-by-side"}
                              >
                                {isProUser ? (
                                  <input
                                    type="checkbox"
                                    checked={compareBusinessIds.includes(Business.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setCompareBusinessIds(prev => [...prev, Business.id]);
                                      } else {
                                        setCompareBusinessIds(prev => prev.filter(id => id !== Business.id));
                                      }
                                    }}
                                    className="rounded text-indigo-600 border-slate-300 focus:ring-indigo-500 h-3 w-3 cursor-pointer"
                                  />
                                ) : (
                                  <Lock className="w-2.5 h-2.5 text-amber-600" />
                                )}
                                <span>Compare</span>
                                {!isProUser && <span className="text-[7.5px] bg-amber-200/80 text-amber-900 px-0.5 rounded font-black">PRO</span>}
                              </label>
                              <span className={`text-[11px] font-black uppercase tracking-wide px-2 py-0.5 rounded shadow-3xs border flex items-center gap-1.5 flex-wrap ${hiringBadgeBg}`}>
                                <span>{cardAudienceScore.badge || cardAudienceScore.levelLabel}</span>
                                {cardAudienceScore.last_recorded_date && cardAudienceScore.last_recorded_date !== "Unknown" && cardAudienceScore.last_recorded_date !== "Date not specified by source" ? (
                                  <span className="font-mono text-[10px] font-bold opacity-90 border-l border-current/30 pl-1.5 normal-case">
                                    Evidence dated: {cardAudienceScore.last_recorded_date}
                                  </span>
                                ) : (
                                  <span className="font-sans text-[9px] font-semibold opacity-90 border-l border-current/30 pl-1.5 normal-case tracking-normal">
                                    Source date not specified • Checked by BizNearYou: {formatCompactDate(cardAudienceScore.last_checked_at || Business.last_checked_at) || formatCompactDate(new Date())}
                                  </span>
                                )}
                              </span>
                              {savedBusinesses.some((sl) => sl.id === Business.id) && (
                                <span className="text-[9px] font-black bg-emerald-50 text-emerald-700 border border-emerald-205 px-1.5 py-0.2 rounded uppercase flex items-center gap-0.5 shadow-2xs">
                                  <Check className="w-3 h-3 text-emerald-600" /> {t("pipeline:status_saved")}
                                </span>
                              )}
                              {Business.rating && (
                                <span className="flex items-center gap-0.5 text-amber-500 font-bold text-[10px] bg-amber-50 px-1 py-0.2 rounded border border-amber-100 font-mono">
                                  ★{Business.rating}
                                </span>
                              )}

                              {BusinessDistanceMiles !== null && (
                                <span className="text-[9px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200 px-1.5 py-0.2 rounded flex items-center gap-0.5 shadow-2xs font-sans">
                                  📍 {BusinessDistanceMiles.toFixed(1)} mi away
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Middle Grouped Section - Contact Information & Website Leads */}
                        <div className="bg-slate-50/70 border border-slate-150/70 rounded-xl p-3 text-xs text-slate-600 space-y-2.5 text-left font-sans">
                          <p className="font-extrabold text-[9px] text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                            <span>📞 Contact & Location Details</span>
                          </p>
                          
                          <p 
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              copyToClipboard(Business.address || ""); 
                            }} 
                            className="text-[11.5px] text-slate-600 hover:text-blue-600 transition cursor-pointer flex items-center gap-2 truncate" 
                            title={t("common:tooltip_copy_address")}
                          >
                            <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            <span className="truncate font-medium">{Business.address || "No address listed"}</span>
                            {copiedText === Business.address && (
                              <span className="text-[9px] text-emerald-600 font-bold bg-emerald-50 px-1 rounded border border-emerald-200 shrink-0">{t("common:status_copied")}</span>
                            )}
                          </p>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2 border-t border-slate-150/40">
                            <div>
                              {Business.phone ? (
                                <span 
                                  onClick={(e) => { 
                                    e.stopPropagation(); 
                                    copyToClipboard(Business.phone || ""); 
                                  }} 
                                  className="font-mono text-[11px] hover:text-blue-600 cursor-pointer transition flex items-center gap-1.5 truncate" 
                                  title={t("common:tooltip_copy_phone")}
                                >
                                  <Copy className="w-3 h-3 text-slate-400 shrink-0" />
                                  <span className="truncate">{Business.phone}</span>
                                  {copiedText === Business.phone && (
                                    <span className="text-[8px] text-emerald-600 font-bold bg-emerald-50 px-1 rounded border border-emerald-200 ml-1">{t("common:status_copied")}</span>
                                  )}
                                </span>
                              ) : (
                                <span className="text-[10.5px] text-slate-400 font-medium flex items-center gap-1.5">
                                  <Copy className="w-3 h-3 text-slate-300 shrink-0 animate-pulse" />
                                  <span className="italic text-[10px]">No phone listed</span>
                                </span>
                              )}
                            </div>

                            <div>
                              {Business.website ? (
                                <a 
                                  href={Business.website} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  onClick={(e) => e.stopPropagation()} 
                                  className="text-[11px] font-medium text-slate-600 hover:underline hover:text-blue-600 transition flex items-center gap-1.5 truncate" 
                                  title={t("common:tooltip_open_website")}
                                >
                                  <ExternalLink className="w-3 h-3 text-slate-400 shrink-0" />
                                  <span className="truncate">{Business.website.replace(/^https?:\/\/(www\.)?/, "")}</span>
                                </a>
                              ) : (
                                <span className="text-[10.5px] text-amber-600 font-bold flex items-center gap-1.5" title="No active website detected. Immediate landing page sales lead!">
                                  <Globe className="w-3 h-3 text-amber-400 shrink-0" />
                                  <span className="text-[10px]">⚠️ Needs Landing Page</span>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>



                        {/* Mode-Specific Audience Opportunity Score Explanation block */}
                        {(() => {
                          const audienceResult = cardAudienceScore;
                          const tagsToDisplay = (audienceResult.summaryTags && audienceResult.summaryTags.length > 0)
                            ? audienceResult.summaryTags
                            : (audienceResult.reasons || []).slice(0, 3);
                          return (
                            <div className="bg-slate-50/70 border border-slate-200/80 rounded-xl p-3 flex flex-col gap-2 font-sans text-left">
                              <div className="flex items-center justify-between gap-3">
                                <div className="flex items-center gap-2">
                                  <div className="w-7 h-7 rounded-lg bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 font-bold shadow-4xs shrink-0 text-xs">
                                    🎯
                                  </div>
                                  <div>
                                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-wider">
                                      {audienceResult.scoreLabel}
                                    </p>
                                  </div>
                                </div>
                                <div className="bg-gradient-to-br from-indigo-600 to-indigo-700 text-white font-extrabold font-mono text-xs px-2.5 py-1 rounded-lg shadow-xs shrink-0 flex items-center gap-1">
                                  <span>{audienceResult.score}</span>
                                  <span className="text-[9px] font-normal opacity-80">/100</span>
                                </div>
                              </div>

                              {/* Concise explanation of why the business received this score */}
                              {audienceResult.summary && (
                                <p className="text-[11px] text-slate-600 font-medium leading-relaxed">
                                  {audienceResult.summary}
                                </p>
                              )}

                              {/* Evidence factor tags */}
                              {tagsToDisplay.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 pt-1.5 border-t border-slate-200/60">
                                  {tagsToDisplay.map((tagStr, rIdx) => (
                                    <span
                                      key={rIdx}
                                      className="text-[9.5px] font-bold bg-white text-slate-700 border border-slate-200 px-2 py-0.5 rounded-md shadow-4xs flex items-center gap-1"
                                    >
                                      <span className="text-indigo-500">•</span> {tagStr}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Verified Audience Action Buttons */}
                        <AudienceActionButtonsBar
                          business={Business}
                          audienceMode={audienceMode}
                          verifiedEvidences={Business.hiringEvidences}
                          className="pt-1 pb-1 border-t border-slate-150/60"
                        />

                        {/* Audience-Specific Action Controls */}
                        {(() => {
                          const modeActionConfig = AUDIENCE_ACTION_LABELS[audienceMode] || AUDIENCE_ACTION_LABELS.job_seeker;
                          const isSaved = savedBusinesses.some((sl) => sl.id === Business.id);
                          const isSaving = isSavingBusinessId === Business.id;
                          const isDeleting = isDeletingBusinessId === Business.id;

                          return (
                            <div className="flex items-center justify-between w-full gap-2 pt-1 flex-wrap">
                              <div className="shrink-0 flex items-center gap-1.5 ml-auto">
                                {(currentUserRole === "admin" || currentUserRole === "dev" || currentUserRole === "support") && (() => {
                                  const isBlocked = Business.isBlocked || Business.pipeline_stage === "Blocked" || Business.pipelineStage === "Blocked";
                                  return (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleBusinessBlockStatus(Business, isBlocked);
                                      }}
                                      className={`px-2 py-1 rounded text-[9.5px] font-black tracking-wide uppercase transition cursor-pointer flex items-center gap-1 shadow-3xs ${
                                        isBlocked
                                          ? "bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100"
                                          : "bg-rose-50 text-rose-800 border border-rose-200 hover:bg-rose-100"
                                      }`}
                                      title={isBlocked ? "Unban this Business globally" : "Ban this Business globally"}
                                    >
                                      <Ban className="w-3 h-3" />
                                      <span>{isBlocked ? "UNBAN" : "BAN"}</span>
                                    </button>
                                  );
                                })()}

                                {Business.crawlStatus === "crawling" ? (
                                  <div className="flex items-center gap-1 text-[11px] text-blue-600 font-bold bg-blue-50 px-2.5 py-1 rounded border border-blue-150 animate-pulse">
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                    {t("pipeline:finding_contacts")}
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1.5">
                                    {/* Save Bookmark button with audience-specific label */}
                                    <button
                                      type="button"
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        if (isSaved) {
                                          await handleDeleteIndividualBusiness(Business.id, Business.name);
                                        } else {
                                          await handleSaveIndividualBusiness(Business);
                                        }
                                      }}
                                      disabled={isSaving || isDeleting}
                                      className={`px-2 py-1.5 rounded-lg border transition-all cursor-pointer flex items-center gap-1 shrink-0 text-[10.5px] font-bold ${
                                        isSaved
                                          ? "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100 shadow-4xs"
                                          : "bg-slate-50 border-slate-200 text-slate-600 hover:text-slate-800 hover:border-slate-350 shadow-4xs"
                                      }`}
                                      title={isSaved ? "Remove from saved list" : modeActionConfig.saveAction}
                                    >
                                      {isSaving || isDeleting ? (
                                        <Loader2 className="w-3 h-3 animate-spin text-slate-400" />
                                      ) : (
                                        <Bookmark className={`w-3.5 h-3.5 ${isSaved ? "fill-current text-emerald-600" : ""}`} fill={isSaved ? "currentColor" : "none"} />
                                      )}
                                      <span className="hidden sm:inline">{isSaved ? "Saved" : modeActionConfig.saveAction}</span>
                                    </button>

                                    {Business.crawlStatus === "error" && (
                                      <button 
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          enrichBusiness(Business);
                                        }}
                                        className="bg-red-50 hover:bg-red-100 text-red-700 text-[10px] px-2.5 py-1 rounded border border-red-200 font-bold uppercase tracking-wide cursor-pointer transition shadow-3xs"
                                        title={t("pipeline:btn_retry_search")}
                                      >
                                        {t("pipeline:btn_retry_search")}
                                      </button>
                                    )}

                                    {/* View Details button with audience-specific label */}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedBusinessId(Business.id);
                                        setMobileIntelOpen(true);
                                      }}
                                      className="px-2.5 py-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:shadow text-white rounded-lg text-[10.5px] font-extrabold cursor-pointer transition flex items-center gap-1 hover:from-blue-700 hover:to-indigo-700 active:scale-95 shadow-3xs"
                                      title={modeActionConfig.viewDetails}
                                    >
                                      <Eye className="w-3 h-3 text-white" />
                                      <span>{modeActionConfig.viewDetails}</span>
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                      </div>

                      {/* CRM Specific details shown directly on card */}
                      {workspaceMode === "saved" && (
                        isProUser ? (
                          <div className="flex gap-2 items-center mt-1.5 pt-2 border-t border-slate-100 flex-wrap">
                            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">{t("pipeline:label_opportunity_pipeline")}</span>
                            <select
                              value={Business.pipelineStage || "New"}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => updateBusinessCrm(Business.id, e.target.value, Business.followUpAt || null)}
                              className="text-[10px] bg-slate-50 border border-slate-200/80 rounded px-2 py-0.5 font-extrabold text-slate-700 outline-none focus:ring-1 focus:ring-emerald-500 cursor-pointer"
                            >
                              {["New", "Contacted", "Interested", "Follow Up", "Won", "Lost"].map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                            
                            {Business.followUpAt ? (
                              (() => {
                                const isOverdue = Business.followUpAt && new Date(Business.followUpAt).getTime() < Date.now();
                                return (
                                  <div className="flex items-center gap-2 ml-auto">
                                    <span className={`text-[9.5px] font-black px-2 py-0.5 rounded flex items-center gap-0.5 border ${
                                      isOverdue 
                                        ? "text-red-700 bg-red-50 border-red-150 animate-pulse" 
                                        : "text-indigo-750 bg-indigo-50 border-indigo-150"
                                    }`}>
                                      {isOverdue ? "⚠️ Overdue Follow-Up: " : "⏰ Next Follow-Up: "} {new Date(Business.followUpAt).toLocaleDateString()}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setReminderBusiness(Business);
                                        setReminderDateSelection("");
                                      }}
                                      className="text-[9px] font-semibold text-slate-400 hover:text-blue-600 transition underline cursor-pointer"
                                    >
                                      Change
                                    </button>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        updateBusinessCrm(Business.id, Business.pipelineStage || "New", null);
                                      }}
                                      className="text-[9px] font-semibold text-red-400 hover:text-red-650 transition underline cursor-pointer"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                );
                              })()
                            ) : (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setReminderBusiness(Business);
                                  setReminderDateSelection("");
                                }}
                                className="text-[9.5px] font-bold text-slate-500 hover:text-indigo-650 bg-slate-50 border border-slate-200 px-2.5 py-1 rounded transition cursor-pointer ml-auto flex items-center gap-1"
                                title="Set follow-up reminder for this Business"
                              >
                                <span>📅 Set Follow-Up Reminder</span>
                              </button>
                            )}
                          </div>
                        ) : (
                          <div 
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsUpgradeModalOpen(true);
                            }}
                            className="flex gap-2 items-center mt-1.5 pt-2 border-t border-slate-200/80 flex-wrap opacity-75 hover:opacity-100 transition cursor-pointer group/crmlock bg-slate-50/60 p-1.5 rounded-lg border border-dashed border-slate-300"
                            title="Click to upgrade to Pro and unlock CRM stage tracking and follow-up reminders"
                          >
                            <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider flex items-center gap-1">
                              <Lock className="w-3 h-3 text-amber-500" />
                              {t("pipeline:label_opportunity_pipeline")}
                            </span>
                            
                            <select
                              disabled
                              value={Business.pipelineStage || "New"}
                              className="text-[10px] bg-slate-100 border border-slate-250 rounded px-2 py-0.5 font-extrabold text-slate-400 pointer-events-none"
                            >
                              {["New", "Contacted", "Interested", "Follow Up", "Won", "Lost"].map(s => <option key={s} value={s}>{s}</option>)}
                            </select>

                            <div className="ml-auto flex items-center gap-1.5">
                              <button
                                type="button"
                                disabled
                                className="text-[9.5px] font-bold text-slate-400 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded pointer-events-none flex items-center gap-1"
                              >
                                <span>📅 Set Follow-Up Reminder</span>
                              </button>
                              <span className="bg-amber-100 text-amber-800 text-[9px] font-black px-1.5 py-0.5 rounded border border-amber-200/80 uppercase shrink-0 flex items-center gap-0.5 group-hover/crmlock:bg-amber-500 group-hover/crmlock:text-white transition">
                                <Sparkles className="w-2.5 h-2.5" /> PRO
                              </span>
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  );
                })}
              </div>
              )}

              {/* Modern Google-like Pagination controls container */}
              {workspaceMode === "search" && searchTotalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 mt-4 bg-slate-50 border border-slate-200/80 rounded-xl mx-4 mb-4 shadow-3xs">
                  <div className="flex flex-col items-start gap-1">
                    <span className="text-xs text-slate-500 font-medium">
                      Showing <span className="font-extrabold text-slate-800">{(searchPage - 1) * searchLimit + 1}</span> to{" "}
                      <span className="font-extrabold text-slate-800">
                        {Math.min(searchPage * searchLimit, searchTotalResults)}
                      </span>{" "}
                      of <span className="font-extrabold text-slate-800">{searchTotalResults}</span> organic results
                    </span>
                    {searchSummary && (
                      <span className="text-[10px] text-slate-400 font-mono">
                        Deeper Discovery: Analyzed {searchSummary.candidateCount} matches, deduped to {searchSummary.dedupedCount} organic listings.
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Results per page selector */}
                    <div className="flex items-center gap-1.5 mr-2">
                      <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Per Page:</span>
                      <select
                        value={searchLimit}
                        onChange={(e) => setSearchLimit(parseInt(e.target.value))}
                        className="text-[11px] bg-white border border-slate-200 rounded-lg px-2 py-1 font-bold text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer shadow-4xs"
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </select>
                    </div>

                    <div className="flex items-center gap-1.5 bg-white p-1 rounded-lg border border-slate-200 shadow-4xs">
                      <button
                        type="button"
                        disabled={searchPage === 1}
                        onClick={() => triggerBusinessesSearch(searchPage - 1, false)}
                        className="px-3 py-1.5 rounded-md text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:text-slate-300 disabled:hover:bg-transparent cursor-pointer transition-colors flex items-center gap-1 border border-slate-150/60"
                        title="Previous Page"
                      >
                        <ChevronLeft className="w-3.5 h-3.5" />
                        <span>Previous</span>
                      </button>

                      <div className="hidden sm:flex items-center gap-1">
                        {(() => {
                          const pages: number[] = [];
                          const start = Math.max(1, searchPage - 2);
                          const end = Math.min(searchTotalPages, searchPage + 2);
                          for (let i = start; i <= end; i++) {
                            pages.push(i);
                          }
                          return pages.map((p) => {
                            const isCurrent = p === searchPage;
                            return (
                              <button
                                key={p}
                                type="button"
                                onClick={() => triggerBusinessesSearch(p, false)}
                                className={`px-3 py-1.5 text-xs font-black rounded-md cursor-pointer transition-all ${
                                  isCurrent
                                    ? "bg-blue-600 text-white shadow-3xs"
                                    : "text-slate-600 hover:bg-slate-50"
                                }`}
                              >
                                {p}
                              </button>
                            );
                          });
                        })()}
                      </div>

                      <button
                        type="button"
                        disabled={searchPage === searchTotalPages}
                        onClick={() => triggerBusinessesSearch(searchPage + 1, false)}
                        className="px-3 py-1.5 rounded-md text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:text-slate-300 disabled:hover:bg-transparent cursor-pointer transition-colors flex items-center gap-1 border border-slate-150/60"
                        title="Next Page"
                      >
                        <span>Next</span>
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Developer Search Query Diagnostics Panel */}
              {keyword.trim() && (currentUserRole === "admin" || currentUserRole === "dev" || process.env.NODE_ENV !== "production") && (() => {
                const parsed = parseBusinessQuery(keyword);
                return (
                  <div className="mx-4 mb-4 p-3.5 bg-slate-900 text-slate-100 rounded-xl border border-slate-700 text-xs font-mono space-y-2">
                    <div className="flex items-center justify-between pb-1.5 border-b border-slate-800">
                      <span className="text-[10px] font-black uppercase text-amber-400 tracking-wider">🛠 Developer Search Diagnostics</span>
                      <button
                        type="button"
                        onClick={() => setShowDevSearchDiagnostics(prev => !prev)}
                        className="text-[10px] text-slate-400 hover:text-white underline cursor-pointer"
                      >
                        {showDevSearchDiagnostics ? "Hide Diagnostics" : "Show Diagnostics"}
                      </button>
                    </div>

                    {showDevSearchDiagnostics && (
                      <div className="space-y-1.5 text-[11px] leading-relaxed pt-1">
                        <div><strong className="text-slate-400">Raw Input:</strong> "{keyword}"</div>
                        <div><strong className="text-slate-400">Normalized Term:</strong> "{parsed.normalizedQuery}"</div>
                        <div><strong className="text-slate-400">Detected Types:</strong> {parsed.businessTypes.join(", ") || "None"}</div>
                        <div><strong className="text-slate-400">Specialties / Services:</strong> {parsed.specialties.join(", ") || "None"}</div>
                        <div><strong className="text-slate-400">Insurance Requirements:</strong> {parsed.insurancePrograms.join(", ") || "None"}</div>
                        <div><strong className="text-slate-400">Languages:</strong> {parsed.languages.join(", ") || "None"}</div>
                        <div><strong className="text-slate-400">Accessibility:</strong> {parsed.accessibilityFeatures.join(", ") || "None"}</div>
                        <div><strong className="text-slate-400">Hours:</strong> {parsed.operatingHours.join(", ") || "None"}</div>
                        <div><strong className="text-slate-400">Explicit Exclusions:</strong> {parsed.excludedTerms.join(", ") || "None"}</div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
            )}
            </>
          )}
          </div>
          )}

          {/* Backdrop overlay when panel is open */}
          {mobileIntelOpen && activeBusiness && (
            <div 
              onClick={() => setMobileIntelOpen(false)}
              className="fixed inset-0 bg-slate-950/25 backdrop-blur-xs z-30 transition-all duration-300"
            />
          )}

          {/* Right Panel: Intelligence Insights / Executive details */}
          {workspaceMode !== "docs" && (
            <aside 
              id="intel-panel" 
              className={`bg-white border-slate-200 flex flex-col shrink-0 transition-all duration-300 ease-in-out z-50
                fixed inset-y-0 right-0 w-full md:top-16 md:bottom-0 md:h-[calc(100vh-64px)] md:w-[380px] md:max-w-[90vw] shadow-2xl border-l
                ${mobileIntelOpen && activeBusiness ? "translate-x-0" : "translate-x-full"}`}
            >
            {activeBusiness ? (
              <div className="flex flex-col h-full overflow-hidden">
                
                {/* Panel Header */}
                <div className="p-6 border-b border-slate-150 bg-slate-50/50 shrink-0 relative">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[9px] font-extrabold text-blue-600 uppercase tracking-widest block">Business Details</span>
                    <button 
                      type="button"
                      onClick={() => setMobileIntelOpen(false)}
                      className="text-slate-400 hover:text-slate-700 p-1 hover:bg-slate-100 rounded transition cursor-pointer"
                      title={t("pipeline:close_profile_panel")}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-slate-100 text-slate-500 rounded flex items-center justify-center border border-slate-250 font-bold text-xl uppercase shadow-sm">
                      {activeBusiness.name.charAt(0) || "B"}
                    </div>
                    <div className="space-y-1 min-w-0 flex-1">
                      <h2 className="text-base font-extrabold text-slate-900 leading-tight truncate">{activeBusiness.name}</h2>
                      <p className="text-xs text-slate-500 truncate">{activeBusiness.address}</p>
                      
                      <div className="flex flex-wrap gap-2 pt-1">
                        {activeBusiness.website && (
                          <a 
                            href={activeBusiness.website} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="text-[10px] text-blue-600 hover:underline flex items-center gap-1 font-medium bg-white px-2 py-1 rounded border border-slate-200 shadow-sm"
                          >
                            <Globe className="w-3 h-3 text-slate-400" />
                            {t("pipeline:visit_website")} <ExternalLink className="w-2.5 h-2.5 text-blue-400" />
                          </a>
                        )}
                        {savedBusinesses.some(l => l.id === activeBusiness.id) ? (
                          <button
                            type="button"
                            onClick={() => handleDeleteIndividualBusiness(activeBusiness.id, activeBusiness.name)}
                            disabled={isDeletingBusinessId === activeBusiness.id}
                            className={`text-[10px] flex items-center gap-1.5 font-bold px-2.5 py-1 rounded border transition-all shadow-sm cursor-pointer bg-red-50 hover:bg-red-100 text-red-700 hover:text-red-800 border-red-200`}
                            title={t("pipeline:remove_from_list_tooltip")}
                          >
                            {isDeletingBusinessId === activeBusiness.id ? (
                              <>
                                <Loader2 className="w-3 h-3 animate-spin text-red-700" />
                                {t("pipeline:removing")}
                              </>
                            ) : (
                              <>
                                <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                {t("pipeline:remove_from_list")}
                              </>
                            )}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleSaveIndividualBusiness(activeBusiness)}
                            disabled={isSavingBusinessId === activeBusiness.id}
                            className={`text-[10px] flex items-center gap-1.5 font-bold px-2.5 py-1 rounded border transition-all shadow-sm cursor-pointer ${
                              isSavingBusinessId === activeBusiness.id
                                ? "bg-slate-50 text-slate-400 border-slate-200"
                                : "bg-emerald-50 hover:bg-emerald-100 text-emerald-700 hover:text-emerald-800 border-emerald-250"
                            }`}
                            title={t("pipeline:save_to_list_tooltip")}
                          >
                            {isSavingBusinessId === activeBusiness.id ? (
                              <>
                                <Loader2 className="w-3 h-3 animate-spin text-slate-400" />
                                {t("pipeline:saving")}
                              </>
                            ) : (
                              <>
                                <Save className="w-3.5 h-3.5 text-emerald-600" />
                                {t("pipeline:save_to_list")}
                              </>
                            )}
                          </button>
                        )}

                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const publicAppUrl = (
                                (import.meta as any).env?.VITE_PUBLIC_APP_URL ||
                                (import.meta as any).env?.VITE_APP_URL ||
                                "https://getbiznearyou.com"
                              ).replace(/\/$/, "");

                              const businessId =
                                activeBusiness.external_business_id ||
                                (activeBusiness as any).externalBusinessId ||
                                (activeBusiness as any).placeId ||
                                activeBusiness.id;

                              const shareUrl = `${publicAppUrl}/?sharedBusinessId=${encodeURIComponent(businessId)}`;
                              
                              setShareBusinessModalItem(activeBusiness);
                              setShareBusinessModalUrl(shareUrl);
                              setShareBusinessModalOpen(true);
                              setCopiedShareLink(false);

                              // Attempt background copy
                              try {
                                await navigator.clipboard.writeText(shareUrl);
                                setCopiedShareLink(true);
                              } catch (err) {
                                console.warn("Clipboard auto-copy failed:", err);
                              }
                            } catch (e) {
                              console.error("Error creating share link:", e);
                            }
                          }}
                          className={`text-[10px] flex items-center gap-1.5 font-bold px-2.5 py-1 rounded border transition-all shadow-sm cursor-pointer bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200`}
                          title={t("pipeline:share_Business_tooltip")}
                        >
                          <Share2 className="w-3.5 h-3.5 text-blue-600 animate-pulse" />
                          <span>Share Business</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {syncFeedback && (
                  (() => {
                    const isError = /error|failed|violat|denied|restrict|bypass/i.test(syncFeedback);
                    const themeClasses = 
                      settingsAppTheme === "sunset"
                        ? isError ? "bg-rose-950/90 text-rose-100 border-rose-800" : "bg-[#5c4a3c] text-[#faf6f0]"
                        : settingsAppTheme === "ocean"
                        ? isError ? "bg-rose-950/90 text-rose-100 border-rose-800" : "bg-[#0d5c63] text-[#ecf3f6]"
                        : settingsAppTheme === "light"
                        ? isError ? "bg-rose-600 text-white" : "bg-emerald-600 text-white"
                        : isError ? "bg-rose-900 text-rose-100 border-rose-800" : "bg-emerald-700 text-emerald-100";
                    return (
                      <div className={`px-6 py-2.5 ${themeClasses} text-[11px] font-bold text-center flex items-center justify-center gap-1.5 transition-all shadow-xs`}>
                        {isError ? (
                          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                        ) : (
                          <Check className="w-3.5 h-3.5 shrink-0" />
                        )}
                        {syncFeedback}
                      </div>
                    );
                  })()
                )}

                {/* Intelligence contacts details scrollable panel body */}
                <div className="p-6 space-y-5 flex-1 overflow-y-auto scrollbar-thin">
                  
                  {(() => {
                    let activeDistanceMiles: number | null = null;
                    const targetCenter = searchCenter || userCoords;
                    if (targetCenter && activeBusiness.location?.lat && activeBusiness.location?.lng) {
                      activeDistanceMiles = getDistanceInMiles(
                        targetCenter.lat,
                        targetCenter.lng,
                        activeBusiness.location.lat,
                        activeBusiness.location.lng
                      );
                    }
                    const rankingOptions = {
                      prioritizeHiring: rankPrioritizeHiring,
                      prioritizeTransparency: rankPrioritizeTransparency,
                      prioritizeFlexibility: rankPrioritizeFlexibility,
                      prioritizeCommute: rankPrioritizeCommute,
                      prioritizeGrowthSecurity: rankPrioritizeGrowthSecurity,
                    };
                    const upgradedResult = getUpgradedOrganicScore(activeBusiness, activeDistanceMiles, keyword, undefined, rankingOptions);
                    const score = upgradedResult.totalScore;
                    const negativeSignalData = upgradedResult.negativeSignalData;
                    const { 
                      scoreBreakdown, 
                      scoreConfidence, 
                      missingDataNotes, 
                      tags, 
                      pitchAngle, 
                      evidence, 
                      isAdult, 
                      categorizedSignals,
                      tagExplanations,
                      scores,
                      whyThisBusinessStandsOut,
                      whyThisJobStandsOut,
                      plainTags,
                      strengths,
                      opportunitiesToImprove,
                      recommendedNextStep
                    } = upgradedResult.prospect;
                    
                    const detailConfig = getAudienceDetailConfig(audienceMode);
                    const audienceScoreResult = calculateAudienceScore(activeBusiness, activeDistanceMiles, keyword, audienceMode);
                    const activeAudienceModeObj = AUDIENCE_MODES[audienceMode];

                    const unifiedCategories = [
                      {
                        id: "audience",
                        label: `${activeAudienceModeObj?.label || "Audience"} Mode Signals`,
                        icon: "🎯",
                        bg: "bg-indigo-50/90",
                        text: "text-indigo-900",
                        border: "border-indigo-200 hover:bg-indigo-100",
                        tags: audienceScoreResult.reasons || []
                      },
                      {
                        id: "trust",
                        label: "Trust & Verification",
                        icon: "🤝",
                        bg: "bg-teal-50",
                        text: "text-teal-700",
                        border: "border-teal-250 hover:bg-teal-100",
                        tags: plainTags?.trust ?? []
                      },
                      {
                        id: "convenience",
                        label: "Commute & Convenience",
                        icon: "🚗",
                        bg: "bg-blue-50",
                        text: "text-blue-700",
                        border: "border-blue-250 hover:bg-blue-100",
                        tags: plainTags?.convenience ?? []
                      },
                      {
                        id: "hiring",
                        label: "Hiring & Turnover Demand",
                        icon: "📈",
                        bg: "bg-emerald-50",
                        text: "text-emerald-750",
                        border: "border-emerald-250 hover:bg-emerald-100",
                        tags: Array.from(new Set([
                          ...(plainTags?.hiring ?? []),
                          ...(categorizedSignals?.growthSignals ?? [])
                        ]))
                      },
                      {
                        id: "opportunities",
                        label: "Optimization Businesses",
                        icon: "💡",
                        bg: "bg-amber-50",
                        text: "text-amber-700",
                        border: "border-amber-250 hover:bg-amber-100",
                        tags: Array.from(new Set([
                          ...(plainTags?.opportunity ?? []),
                          ...(categorizedSignals?.serviceOpportunities ?? []),
                          ...(categorizedSignals?.workOpportunities ?? [])
                        ]))
                      },
                      {
                        id: "contacts",
                        label: "Contact Channels",
                        icon: "🔍",
                        bg: "bg-sky-50",
                        text: "text-sky-700",
                        border: "border-sky-200 hover:bg-sky-100",
                        tags: categorizedSignals?.contactSignals ?? []
                      },
                      {
                        id: "ai",
                        label: AI_FEATURES_ENABLED ? "AI & Automation Insights" : "Digital & Tech Insights",
                        icon: "🤖",
                        bg: "bg-indigo-50",
                        text: "text-indigo-700",
                        border: "border-indigo-200 hover:bg-indigo-100",
                        tags: categorizedSignals?.aiAutomation ?? []
                      },
                      {
                        id: "warnings",
                        label: "Warnings & Red Flags",
                        icon: "⚠️",
                        bg: "bg-rose-50/80",
                        text: "text-rose-700",
                        border: "border-rose-250 hover:bg-rose-100",
                        tags: categorizedSignals?.warnings ?? []
                      }
                    ].filter(cat => cat.tags.length > 0);

                    const hasCoreTags = unifiedCategories.length > 0;

                    return (
                      <div className="space-y-5">

                        {/* ==========================================
                            1. BUSINESS IDENTITY
                           ========================================== */}
                        <div id="sec-identity" className="border border-slate-200 rounded-xl p-4 bg-white dark:bg-slate-900 space-y-3.5 text-left shadow-xs font-sans">
                          <span className="text-[10px] font-black uppercase text-slate-800 dark:text-slate-200 tracking-widest block select-none flex items-center gap-1.5">
                            <Building2 className="w-3.5 h-3.5 text-slate-600" />
                            <span>🏢 1. Business Identity</span>
                          </span>
                          
                          <div className="space-y-2.5">
                            <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                              <span className="text-[11px] font-black text-slate-800 bg-slate-100 px-2 py-0.5 rounded-md">
                                {activeBusiness.category || "Local Business"}
                              </span>
                              {activeBusiness.rating && (
                                <div className="flex items-center gap-1 text-[11px] font-black text-amber-600">
                                  <span>★</span>
                                  <span>{activeBusiness.rating.toFixed(1)}</span>
                                  <span className="text-slate-500 font-bold">({activeBusiness.reviewsCount || 0})</span>
                                </div>
                              )}
                            </div>

                            <div className="space-y-1">
                              <span className="text-[8.5px] font-black text-slate-600 dark:text-slate-400 uppercase tracking-wider block">Address</span>
                              <p className="text-[11px] text-slate-800 dark:text-slate-200 font-semibold leading-relaxed">
                                {activeBusiness.address || "No address listed"}
                              </p>
                            </div>

                            <div className="space-y-1">
                              <span className="text-[8.5px] font-black text-slate-600 dark:text-slate-400 uppercase tracking-wider block">Website</span>
                              {activeBusiness.website ? (
                                <a 
                                  href={activeBusiness.website} 
                                  target="_blank" 
                                  referrerPolicy="no-referrer"
                                  className="text-[11px] text-blue-600 font-extrabold hover:underline break-all block"
                                >
                                  {activeBusiness.website}
                                </a>
                              ) : (
                                <p className="text-[11px] text-rose-600 font-extrabold">Offline (Needs Website setup)</p>
                              )}
                            </div>

                            <div className="space-y-1">
                              <span className="text-[8.5px] font-black text-slate-600 dark:text-slate-400 uppercase tracking-wider block">Phone Number</span>
                              {activeBusiness.phone ? (
                                <p className="text-[11.5px] font-mono text-slate-800 dark:text-slate-200 font-black">
                                  {activeBusiness.phone}
                                </p>
                              ) : (
                                <p className="text-[11px] text-slate-500 italic">No phone listed</p>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100">
                            {activeBusiness.phone && (
                              <a
                                href={`tel:${activeBusiness.phone}`}
                                onClick={() => trackPhoneCallClick()}
                                className="bg-slate-50 hover:bg-slate-100 text-slate-800 font-bold py-2 px-3 rounded-lg text-[10.5px] transition-all flex items-center justify-center gap-1.5 border border-slate-200 shadow-3xs cursor-pointer"
                              >
                                <Phone className="w-3.5 h-3.5 text-slate-600" />
                                Call Business
                              </a>
                            )}
                            {activeBusiness.website && (
                              <a
                                href={activeBusiness.website}
                                target="_blank"
                                referrerPolicy="no-referrer"
                                className="bg-slate-50 hover:bg-slate-100 text-slate-800 font-bold py-2 px-3 rounded-lg text-[10.5px] transition-all flex items-center justify-center gap-1.5 border border-slate-200 shadow-3xs cursor-pointer"
                              >
                                <Globe className="w-3.5 h-3.5 text-slate-600" />
                                Visit Website
                              </a>
                            )}
                            {activeBusiness.address && (
                              <a
                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(activeBusiness.name + " " + activeBusiness.address)}`}
                                target="_blank"
                                onClick={() => trackDirectionsRequest()}
                                className="bg-slate-50 hover:bg-slate-100 text-slate-800 font-bold py-2 px-3 rounded-lg text-[10.5px] transition-all flex items-center justify-center gap-1.5 border border-slate-200 shadow-3xs cursor-pointer col-span-2"
                              >
                                <MapPin className="w-3.5 h-3.5 text-slate-600" />
                                {activeBusiness.address}
                              </a>
                            )}
                          </div>
                        </div>

                        {/* ==========================================
                            0.0 QUICK ACTIONS SECTION
                           ========================================== */}
                        {(() => {
                          const hiringPageUrl = activeBusiness.hiringPageUrl || (activeBusiness.websiteAudit as any)?.hiringPageUrl || (activeBusiness as any).hiring_page_url;
                          const activeTags = normalizeTags(activeBusiness.opportunityTags);
                          const hasHighHiringSignal = !!(
                            (activeBusiness as any).highHiringSignal ||
                            (scores?.hiringScore && scores.hiringScore > 65) ||
                            activeBusiness.hiringStatus === "hiring" ||
                            activeTags.includes("Likely Hiring Category") ||
                            activeTags.includes("Careers Page Found") ||
                            activeTags.includes("Seasonal Hiring Likely")
                          );
                          
                          const getBusinessDomain = (url: string) => {
                            try {
                              return url.replace(/^(https?:\/\/)?(www\.)?/, "").split("/")[0].split("?")[0];
                            } catch (e) {
                              return "";
                            }
                          };
                          const businessDomain = activeBusiness.website ? getBusinessDomain(activeBusiness.website) : "";

                          const buttons = [];

                          // 1. Open Hiring Page
                          if (hiringPageUrl) {
                            buttons.push({
                              key: "hiringPage",
                              label: "Open Hiring Page",
                              icon: <Briefcase className="w-3.5 h-3.5" />,
                              onClick: () => window.open(hiringPageUrl, "_blank", "noopener,noreferrer"),
                              className: "bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200 hover:border-emerald-300"
                            });
                          }

                          // 2. Search Careers
                          if (!hiringPageUrl && hasHighHiringSignal && activeBusiness.website) {
                            buttons.push({
                              key: "searchCareers",
                              label: "Search Careers",
                              icon: <Search className="w-3.5 h-3.5" />,
                              onClick: () => {
                                const q = `site:${businessDomain} careers OR jobs OR hiring`;
                                window.open(`https://www.google.com/search?q=${encodeURIComponent(q)}`, "_blank", "noopener,noreferrer");
                              },
                              className: "bg-sky-50 hover:bg-sky-100 text-sky-700 border-sky-200 hover:border-sky-300"
                            });
                          }

                          // 3. Open Website
                          if (activeBusiness.website) {
                            buttons.push({
                              key: "website",
                              label: "Open Website",
                              icon: <Globe className="w-3.5 h-3.5" />,
                              onClick: () => window.open(activeBusiness.website, "_blank", "noopener,noreferrer"),
                              className: "bg-white hover:bg-slate-50 text-slate-700 border-slate-200"
                            });
                          }

                          // 4. Call Business
                          if (activeBusiness.phone) {
                            buttons.push({
                              key: "phone",
                              label: "Call Business",
                              icon: <Phone className="w-3.5 h-3.5" />,
                              onClick: () => {
                                trackPhoneCallClick();
                                window.location.href = `tel:${activeBusiness.phone}`;
                              },
                              className: "bg-white hover:bg-slate-50 text-slate-700 border-slate-200"
                            });
                          }

                          // 5. Get Directions
                          if (activeBusiness.address) {
                            buttons.push({
                              key: "directions",
                              label: "Get Directions",
                              icon: <Navigation className="w-3.5 h-3.5" />,
                              onClick: () => {
                                trackDirectionsRequest();
                                const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(activeBusiness.name + " " + activeBusiness.address)}`;
                                window.open(url, "_blank", "noopener,noreferrer");
                              },
                              className: "bg-white hover:bg-slate-50 text-slate-700 border-slate-200"
                            });
                          }

                          // 6. Copy Outreach Message
                          if (activeBusiness.name) {
                            const outreachAngle = activeBusiness.pitchAngle || pitchAngle || "";
                            const outreachMessage = `Hi, I found your business and wanted to ask if you have any entry-level, part-time, internship, shadowing, or seasonal opportunities available. I would love to learn more about how I could help.${outreachAngle ? `\n\nOpportunity identified: ${outreachAngle}` : ""}`;
                            const isCopied = copiedText === outreachMessage;
                            buttons.push({
                              key: "outreach",
                              label: isCopied ? "Outreach Copied!" : "Copy Outreach Message",
                              icon: isCopied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Sparkles className="w-3.5 h-3.5" />,
                              onClick: () => copyToClipboard(outreachMessage),
                              className: isCopied 
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200" 
                                : "bg-white hover:bg-slate-50 text-slate-700 border-slate-200"
                            });
                          }

                          // 7. Copy Business Info
                          if (activeBusiness.name) {
                            const businessInfo = [
                              `Name: ${activeBusiness.name}`,
                              activeBusiness.address ? `Address: ${activeBusiness.address}` : "",
                              activeBusiness.phone ? `Phone: ${activeBusiness.phone}` : "",
                              activeBusiness.website ? `Website: ${activeBusiness.website}` : "",
                              hiringPageUrl ? `Hiring Page: ${hiringPageUrl}` : ""
                            ].filter(Boolean).join("\n");
                            const isCopied = copiedText === businessInfo;
                            buttons.push({
                              key: "businessInfo",
                              label: isCopied ? "Info Copied!" : "Copy Business Info",
                              icon: isCopied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />,
                              onClick: () => copyToClipboard(businessInfo),
                              className: isCopied 
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200" 
                                : "bg-white hover:bg-slate-50 text-slate-700 border-slate-200"
                            });
                          }

                          // 8. Search Contact Page
                          if (activeBusiness.website) {
                            buttons.push({
                              key: "searchContact",
                              label: "Search Contact Page",
                              icon: <Search className="w-3.5 h-3.5" />,
                              onClick: () => {
                                const q = `site:${businessDomain} contact OR staff OR team OR about`;
                                window.open(`https://www.google.com/search?q=${encodeURIComponent(q)}`, "_blank", "noopener,noreferrer");
                              },
                              className: "bg-white hover:bg-slate-50 text-slate-700 border-slate-200"
                            });
                          }

                          let helperText = "Use these actions to contact or research this business.";
                          if (hiringPageUrl) {
                            helperText = "Hiring page found — open it directly.";
                          } else if (hasHighHiringSignal) {
                            helperText = "High hiring signal detected — try searching this website for careers or jobs.";
                          }

                          if (buttons.length === 0) return null;

                          return (
                            <div id="sec-quickactions" className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3 text-left">
                              <span className="text-[10px] font-black uppercase text-slate-800 dark:text-slate-200 tracking-widest block select-none">
                                ⚡ 2. Quick Actions
                              </span>
                              
                              <div className="flex flex-wrap gap-1.5">
                                {buttons.map((btn) => (
                                  <button
                                    key={btn.key}
                                    type="button"
                                    onClick={btn.onClick}
                                    className={`text-[10.5px] font-bold py-1 px-2.5 rounded-md flex items-center gap-1.5 transition-all shadow-2xs border cursor-pointer ${btn.className}`}
                                  >
                                    {btn.icon}
                                    <span>{btn.label}</span>
                                  </button>
                                ))}
                              </div>

                              <p className="text-[10px] font-medium text-slate-600 dark:text-slate-400 italic">
                                {helperText}
                              </p>
                            </div>
                          );
                        })()}

                        {/* ==========================================
                            3. OPPORTUNITY SUMMARY SECTION
                           ========================================== */}
                        <div id="sec-snapshot" className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3.5 text-left">
                          <span className="text-[10px] font-black uppercase text-slate-800 dark:text-slate-200 tracking-widest block select-none">
                            📊 3. Opportunity Summary
                          </span>
                          
                          <div className="grid grid-cols-2 gap-2.5">
                            {/* Score Display */}
                            <div className="bg-white border border-slate-150 p-2.5 rounded-lg flex flex-col justify-between min-h-[64px]">
                              <span className="text-[9px] font-bold text-slate-600 uppercase tracking-wider">Overall Score</span>
                              <div className="flex items-baseline gap-1 mt-1">
                                <span className="text-lg font-black text-slate-900 font-mono">{score}</span>
                                <span className="text-[10px] font-bold text-slate-400">/ 100</span>
                              </div>
                            </div>

                            {/* Trust Score */}
                            <div className="bg-white border border-slate-150 p-2.5 rounded-lg flex flex-col justify-between min-h-[64px]">
                              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Trust Score</span>
                              <div className="flex items-baseline gap-1 mt-1">
                                <span className="text-lg font-black text-slate-900 font-mono">{scores?.trustScore ?? 75}%</span>
                              </div>
                            </div>

                            {/* Website Quality */}
                            <div className="bg-white border border-slate-150 p-2.5 rounded-lg flex flex-col justify-between min-h-[64px]">
                              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Website Quality</span>
                              <span className={`text-[10px] font-extrabold mt-1 ${activeBusiness.website ? "text-emerald-600" : "text-rose-600"}`}>
                                {activeBusiness.website 
                                  ? (activeBusiness.websiteAudit?.failed ? "Needs Review" : "Online") 
                                  : "Offline"
                                }
                              </span>
                            </div>

                            {/* Contactability */}
                            <div className="bg-white border border-slate-150 p-2.5 rounded-lg flex flex-col justify-between min-h-[64px]">
                              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Contactability</span>
                              <span className={`text-[10px] font-extrabold mt-1 ${
                                (activeBusiness.phone || activeBusiness.contacts?.length) ? "text-emerald-600" : "text-amber-600"
                              }`}>
                                {activeBusiness.phone 
                                  ? (activeBusiness.contacts?.length ? "High" : "Medium") 
                                  : "Low"
                                }
                              </span>
                            </div>

                            {/* Growth/Activity */}
                            <div className="bg-white border border-slate-150 p-2.5 rounded-lg flex flex-col justify-between min-h-[64px]">
                              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Activity Index</span>
                              <span className="text-[10px] font-extrabold text-indigo-650 mt-1">
                                {scores?.hiringScore && scores.hiringScore > 65 ? "Active Hiring" : "Moderate"}
                              </span>
                            </div>

                            {/* Best Opportunity */}
                            <div className="bg-white border border-slate-150 p-2.5 rounded-lg flex flex-col justify-between min-h-[64px] col-span-2">
                              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Best Opportunity</span>
                              <span className="text-[10px] font-bold text-slate-700 leading-tight mt-1 line-clamp-2">
                                {recommendedNextStep}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* ==========================================
                            4. RECOMMENDED NEXT STEP SECTION
                           ========================================== */}
                        <div id="sec-nextstep" className="bg-indigo-50/70 border border-indigo-200 rounded-xl p-3.5 space-y-1.5 text-left">
                          <span className="text-[10px] font-black uppercase text-indigo-900 tracking-wider flex items-center gap-1.5 select-none">
                            🚀 4. Recommended Next Step
                          </span>
                          <p className="text-[11px] text-slate-800 font-bold leading-relaxed">
                            {recommendedNextStep}
                          </p>
                        </div>

                        {/* ==========================================
                            HIRING SIGNAL EVIDENCE & EXPLANATION
                           ========================================== */}
                        {(() => {
                          const { prospect } = getUpgradedOrganicScore(activeBusiness, activeDistanceMiles, keyword, undefined, rankingOptions, audienceMode);
                          const tags = activeBusiness.opportunityTags?.length ? activeBusiness.opportunityTags : prospect.tags;
                          const hiringScore = prospect.scores?.hiringScore || 0;
                          const legacyResult = getHiringLikelihood(hiringScore, tags);
                          const refined = refineUnconfirmedHiringSignal(activeBusiness, prospect, legacyResult);

                          const badgeBg = refined.type === 'high' ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                            : refined.type === 'possible' ? 'bg-blue-50 text-blue-800 border-blue-200'
                            : refined.type === 'unconfirmed' ? 'bg-amber-50 text-amber-800 border-amber-200'
                            : refined.type === 'improbable' ? 'bg-orange-50 text-orange-800 border-orange-200'
                            : 'bg-rose-50 text-rose-800 border-rose-200';

                          return (
                            <div id="sec-hiring-explanation" className="border border-slate-200 rounded-xl p-4 bg-white space-y-3 text-left shadow-xs">
                              <div className="flex items-center justify-between border-b border-slate-100 pb-2.5">
                                <div className="space-y-0.5">
                                  <span className="text-[10px] font-black uppercase text-indigo-900 tracking-wider block select-none flex items-center gap-1.5">
                                    <span>🎯 Hiring Signal Evidence & Explanation</span>
                                  </span>
                                  <p className="text-[9.5px] text-slate-400 font-medium">
                                    Business-specific evidence analysis & verification breakdown
                                  </p>
                                </div>
                                <span className={`text-[9.5px] font-extrabold px-2.5 py-0.5 rounded-full border ${badgeBg}`}>
                                  {refined.label}
                                </span>
                              </div>

                              {/* Evidence Summary */}
                              <div className="bg-indigo-50/60 border border-indigo-100 p-3 rounded-lg space-y-1">
                                <span className="text-[9px] font-extrabold uppercase tracking-wider text-indigo-800 block select-none">
                                  Evidence Explanation
                                </span>
                                <p className="text-[11px] font-semibold text-slate-800 leading-relaxed">
                                  {refined.signal_evidence_summary}
                                </p>
                              </div>

                              {/* Direct Verified HiringEvidences Array */}
                              {activeBusiness.hiringEvidences && activeBusiness.hiringEvidences.length > 0 && (
                                <div className="space-y-1.5 pt-1">
                                  <span className="text-[9px] font-extrabold uppercase tracking-wider text-emerald-800 flex items-center gap-1">
                                    <span>📍 Local Verified Hiring Evidence ({activeBusiness.hiringEvidences.length})</span>
                                  </span>
                                  <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                                    {activeBusiness.hiringEvidences.map((ev, idx) => {
                                      const scopeColor =
                                        ev.locationScope === "exact_location"
                                          ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                                          : ev.locationScope === "regional"
                                          ? "bg-blue-100 text-blue-800 border-blue-300"
                                          : ev.locationScope === "company_wide"
                                          ? "bg-amber-100 text-amber-800 border-amber-300"
                                          : "bg-slate-100 text-slate-600 border-slate-300";

                                      return (
                                        <div key={idx} className="p-2.5 rounded-lg border border-slate-200 bg-slate-50 space-y-1.5 text-[10.5px]">
                                          <div className="flex items-center justify-between flex-wrap gap-1">
                                            <span className="font-extrabold text-slate-800 uppercase tracking-wide text-[9.5px] flex items-center gap-1">
                                              <span>{ev.sourceType.replace(/_/g, " ")}</span>
                                            </span>
                                            <div className="flex items-center gap-1.5">
                                              <span className={`text-[8.5px] font-black uppercase px-2 py-0.5 rounded border ${scopeColor}`}>
                                                {ev.locationScope.replace(/_/g, " ")}
                                              </span>
                                              <span className="text-[8.5px] font-bold px-1.5 py-0.5 rounded bg-white border border-slate-200 text-slate-700">
                                                {Math.round(ev.confidence * 100)}% Match
                                              </span>
                                            </div>
                                          </div>
                                          <p className="text-[11px] font-medium text-slate-800 leading-snug">
                                            {ev.evidenceText}
                                          </p>
                                          <div className="flex items-center justify-between text-[9px] text-slate-400 font-sans pt-0.5">
                                            <span>
                                               {ev.sourcePublishedAt || ev.sourceUpdatedAt
                                                 ? `Evidence dated: ${formatDateString(ev.sourcePublishedAt || ev.sourceUpdatedAt)}`
                                                 : `Source date not specified • Checked: ${formatDateString(ev.lastCheckedAt)}`}
                                             </span>
                                            {ev.sourceUrl && (
                                              <a
                                                href={ev.sourceUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-indigo-600 font-bold hover:underline flex items-center gap-0.5"
                                              >
                                                <span>{ev.sourceTitle || "View Public Source"}</span> ↗
                                              </a>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {/* Evidence Items */}
                              {refined.signal_evidence_items.length > 0 && (
                                <div className="space-y-1.5">
                                  <span className="text-[9px] font-extrabold uppercase tracking-wider text-slate-500 block">
                                    Evidence Details & Sources ({refined.signal_evidence_items.length})
                                  </span>
                                  <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                                    {refined.signal_evidence_items.map((item, idx) => {
                                      let itemBg = "bg-slate-50 text-slate-700 border-slate-200";
                                      if (item.type === "Confirmed Hiring Evidence") itemBg = "bg-emerald-50/90 text-emerald-900 border-emerald-200";
                                      else if (item.type === "Possible Hiring Indicator") itemBg = "bg-blue-50/90 text-blue-900 border-blue-200";
                                      else if (item.type === "Weak Evidence") itemBg = "bg-slate-50 text-slate-700 border-slate-200";
                                      else if (item.type === "Missing Evidence") itemBg = "bg-amber-50/90 text-amber-900 border-amber-200";
                                      else if (item.type === "Negative Evidence") itemBg = "bg-rose-50/90 text-rose-900 border-rose-200";

                                      return (
                                        <div key={idx} className={`p-2.5 rounded-lg border text-[10.5px] space-y-1 ${itemBg}`}>
                                          <div className="flex items-center justify-between font-bold text-[9.5px]">
                                            <span className="uppercase tracking-wider flex items-center gap-1">
                                              <span>{item.type}</span>
                                              <span className="text-slate-400 font-normal">({item.source_type})</span>
                                            </span>
                                            {item.confidence && (
                                              <span className="text-[8.5px] font-black px-1.5 py-0.2 rounded bg-white/80 border border-slate-200 text-slate-600">
                                                {item.confidence}
                                              </span>
                                            )}
                                          </div>
                                          <p className="text-[10.5px] font-semibold leading-normal">{item.detail}</p>
                                          {item.source_url && (
                                            <a
                                              href={item.source_url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-[9.5px] text-indigo-600 hover:underline inline-flex items-center gap-1 font-bold pt-0.5"
                                            >
                                              <span>Source</span> ↗
                                            </a>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {/* Timestamps */}
                              <div className="pt-2 border-t border-slate-150 space-y-1 text-[10px] font-sans">
                                <div className="font-extrabold text-slate-800">
                                  Signal Verified: {formatDateString(refined.signal_last_checked_at, true)}
                                </div>
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-slate-600 font-medium text-[9.5px]">
                                  <span>
                                    <strong>Public Source Date:</strong> {refined.signal_source_updated_at ? formatDateString(refined.signal_source_updated_at) : "Date not specified by source"}
                                  </span>
                                  <span>
                                    <strong>Checked by BizNearYou:</strong> {formatDateString(refined.signal_last_checked_at, true)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })()}

                        {/* ==========================================
                            EMPLOYMENT QUALITY INSIGHTS
                           ========================================== */}
                        {(() => {
                          if (!detailConfig.visibleSections.includes("employment")) return null;
                          const enrichment = activeBusiness.jobEnrichment || generateJobEnrichment(activeBusiness);
                          
                          // Determine validation/verification category label
                          let verificationBadge = "Inferred Estimate";
                          let verificationColor = "text-amber-800 bg-amber-50 border-amber-200";
                          let dotColor = "bg-amber-500";
                          
                          if (enrichment.compensation.isConfirmed || enrichment.workplaceFlexibility.isConfirmed) {
                            verificationBadge = "Verified Employer Claim";
                            verificationColor = "text-emerald-800 bg-emerald-50 border-emerald-200";
                            dotColor = "bg-emerald-500";
                          } else if (enrichment.jobSecurity.source?.includes("Registry")) {
                            verificationBadge = "Registry Audit";
                            verificationColor = "text-indigo-800 bg-indigo-50 border-indigo-200";
                            dotColor = "bg-indigo-500";
                          }

                          return (
                            <div id="sec-employment" className="border border-slate-200 rounded-xl p-5 bg-white space-y-4 text-left shadow-xs relative overflow-hidden">
                              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                                <div className="space-y-0.5">
                                  <span className="text-[10px] font-black uppercase text-indigo-900 tracking-wider block select-none flex items-center gap-1.5">
                                    <span>{detailConfig.sectionTitles.employment || "💼 Employment and Hiring Intelligence"}</span>
                                    {!isProUser && <span className="text-[8px] bg-amber-100 text-amber-800 font-extrabold px-1.5 py-0.2 rounded border border-amber-200">PRO FEATURE</span>}
                                  </span>
                                  <p className="text-[9.5px] text-slate-400 font-medium">
                                    {detailConfig.sectionSubtitles.employment || "Multi-signal transparency evaluation of local conditions."}
                                  </p>
                                </div>
                                <span className={`text-[9.5px] font-bold px-2.5 py-0.5 rounded-full border flex items-center gap-1.5 ${verificationColor}`}>
                                  <span className={`w-1.5 h-1.5 rounded-full ${dotColor} animate-pulse`} />
                                  {verificationBadge}
                                </span>
                              </div>

                              {!isProUser ? (
                                <div className="p-6 bg-slate-50/90 rounded-xl border border-slate-200 text-center space-y-3">
                                  <div className="w-10 h-10 bg-indigo-50 rounded-full flex items-center justify-center border border-indigo-150 text-indigo-600 mx-auto shadow-3xs">
                                    <Lock className="w-5 h-5 text-indigo-600" />
                                  </div>
                                  <div className="space-y-1">
                                    <h4 className="text-xs font-black text-slate-800 tracking-tight">Employment Quality & Benefits Metrics Locked</h4>
                                    <p className="text-[10px] text-slate-500 leading-normal max-w-sm mx-auto">
                                      Unlock base pay ranges, estimated annual earnings, structured health/retirement package audits, workplace flexibility ratings, and stability scores for {activeBusiness.name}.
                                    </p>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setIsUpgradeModalOpen(true)}
                                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold text-[10.5px] rounded-lg transition-all shadow-sm active:scale-95 flex items-center justify-center gap-1.5 mx-auto cursor-pointer"
                                  >
                                    <Sparkles className="w-3.5 h-3.5 text-amber-300 animate-pulse" />
                                    <span>Unlock Employment Quality Insights</span>
                                  </button>
                                </div>
                              ) : (
                                <>
                                  {/* 1. Compensation & Benefits */}
                              <div className="bg-slate-50/50 rounded-xl p-3 border border-slate-150/60 space-y-2.5">
                                <div className="flex items-center justify-between">
                                  <span className="text-[9.5px] font-extrabold uppercase text-slate-500 tracking-wider block">Compensation & Benefits</span>
                                  <span className="text-[9px] font-extrabold bg-indigo-50 text-indigo-700 px-1.5 py-0.2 rounded">
                                    Source: {enrichment.compensation.source || "Market Estimate"}
                                  </span>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <span className="text-[8.5px] font-bold text-slate-400 block">Base Pay Range</span>
                                    <span className="text-[11.5px] font-black text-slate-800">{enrichment.compensation.basePay}</span>
                                  </div>
                                  <div>
                                    <span className="text-[8.5px] font-bold text-slate-400 block">Est. Annual Earnings</span>
                                    <span className="text-[11.5px] font-black text-slate-800">{enrichment.compensation.estimatedTotal}</span>
                                  </div>
                                </div>

                                <div className="grid grid-cols-3 gap-2 py-1.5 border-y border-slate-100">
                                  <div className="text-center bg-white p-1 rounded border border-slate-100">
                                    <span className="text-[8px] font-bold text-slate-400 block">Bonus status</span>
                                    <span className={`text-[9.5px] font-extrabold ${enrichment.compensation.bonusAvailable ? "text-emerald-600" : "text-slate-400"}`}>
                                      {enrichment.compensation.bonusAvailable ? "✓ Active Bonus" : "No Bonus"}
                                    </span>
                                  </div>
                                  <div className="text-center bg-white p-1 rounded border border-slate-100">
                                    <span className="text-[8px] font-bold text-slate-400 block">Commission</span>
                                    <span className={`text-[9.5px] font-extrabold ${enrichment.compensation.commissionAvailable ? "text-emerald-600" : "text-slate-400"}`}>
                                      {enrichment.compensation.commissionAvailable ? "✓ Active Comm." : "No Comm."}
                                    </span>
                                  </div>
                                  <div className="text-center bg-white p-1 rounded border border-slate-100">
                                    <span className="text-[8px] font-bold text-slate-400 block">Overtime</span>
                                    <span className={`text-[9.5px] font-extrabold ${enrichment.compensation.overtimeAvailable ? "text-emerald-600" : "text-slate-400"}`}>
                                      {enrichment.compensation.overtimeAvailable ? "✓ Time-and-a-Half" : "No Overtime"}
                                    </span>
                                  </div>
                                </div>

                                <div className="space-y-1">
                                  <span className="text-[8.5px] font-bold text-slate-400 block">Structured Benefit Packages</span>
                                  <div className="flex flex-wrap gap-1.5">
                                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded-md ${enrichment.compensation.healthcareBenefitsObj?.medical ? 'bg-emerald-50 text-emerald-700 border border-emerald-150' : 'bg-slate-100 text-slate-400'}`}>
                                      {enrichment.compensation.healthcareBenefitsObj?.medical ? "✓ Medical" : "✗ Medical"}
                                    </span>
                                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded-md ${enrichment.compensation.healthcareBenefitsObj?.dental ? 'bg-emerald-50 text-emerald-700 border border-emerald-150' : 'bg-slate-100 text-slate-400'}`}>
                                      {enrichment.compensation.healthcareBenefitsObj?.dental ? "✓ Dental" : "✗ Dental"}
                                    </span>
                                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded-md ${enrichment.compensation.healthcareBenefitsObj?.vision ? 'bg-emerald-50 text-emerald-700 border border-emerald-150' : 'bg-slate-100 text-slate-400'}`}>
                                      {enrichment.compensation.healthcareBenefitsObj?.vision ? "✓ Vision" : "✗ Vision"}
                                    </span>
                                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded-md ${enrichment.compensation.retirementBenefitsObj?.available ? 'bg-emerald-50 text-emerald-700 border border-emerald-150' : 'bg-slate-100 text-slate-400'}`}>
                                      {enrichment.compensation.retirementBenefitsObj?.available ? (enrichment.compensation.retirementBenefitsObj?.employerMatch ? "✓ 401k (Match)" : "✓ 401k") : "✗ Retirement"}
                                    </span>
                                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded-md ${enrichment.compensation.paidTimeOff ? 'bg-emerald-50 text-emerald-700 border border-emerald-150' : 'bg-slate-100 text-slate-400'}`}>
                                      {enrichment.compensation.paidTimeOff ? "✓ Paid Time Off" : "✗ Paid Time Off"}
                                    </span>
                                  </div>
                                </div>

                                {enrichment.compensation.additionalBenefits && enrichment.compensation.additionalBenefits.length > 0 && (
                                  <div className="pt-1 text-[9.5px] text-slate-500 font-medium flex flex-wrap gap-x-2">
                                    <span className="font-extrabold text-slate-400">Extras:</span>
                                    {enrichment.compensation.additionalBenefits.map((b, i) => (
                                      <span key={i} className="text-slate-650">• {b}</span>
                                    ))}
                                  </div>
                                )}

                                <div className="flex items-center justify-between pt-1 text-[9.5px] text-slate-500 border-t border-slate-100">
                                  <span>Compensation Transparency Index:</span>
                                  <span className="font-extrabold text-indigo-700">{enrichment.compensation.transparencyScore}/100</span>
                                </div>
                              </div>

                              {/* 2. Workplace Flexibility */}
                              <div className="bg-slate-50/50 rounded-xl p-3 border border-slate-150/60 space-y-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-[9.5px] font-extrabold uppercase text-slate-500 tracking-wider block">Workplace Flexibility</span>
                                  <span className="text-[9px] font-extrabold bg-indigo-50 text-indigo-700 px-1.5 py-0.2 rounded">
                                    Evidence: {enrichment.workplaceFlexibility.confidence ? enrichment.workplaceFlexibility.confidence.toUpperCase() : "MEDIUM"} CONFIDENCE
                                  </span>
                                </div>
                                <div className="grid grid-cols-2 gap-3 text-[10.5px]">
                                  <div>
                                    <span className="text-[8.5px] font-bold text-slate-400 block">Work Location Setup</span>
                                    <span className="font-extrabold text-slate-700 capitalize flex items-center gap-1">
                                      🗺️ {enrichment.workplaceFlexibility.workLocationType || enrichment.workplaceFlexibility.locationType}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-[8.5px] font-bold text-slate-400 block">Hours Flexibility</span>
                                    <span className="font-extrabold text-slate-700 flex items-center gap-1">
                                      ⏰ {enrichment.workplaceFlexibility.flexibleHours ? "Flexible Schedule" : "Standard Schedule"}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-[8.5px] font-bold text-slate-400 block">Employment Capability</span>
                                    <span className="font-extrabold text-slate-700 capitalize flex items-center gap-1">
                                      👔 {enrichment.workplaceFlexibility.employmentType}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-[8.5px] font-bold text-slate-400 block">Available Schedule Blocks</span>
                                    <span className="font-bold text-slate-700 text-[10px] truncate block" title={enrichment.workplaceFlexibility.shiftOptions}>
                                      📅 {enrichment.workplaceFlexibility.shiftOptions}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* 3. Job Security & Growth */}
                              <div className="bg-slate-50/50 rounded-xl p-3 border border-slate-150/60 space-y-2.5">
                                <div className="flex items-center justify-between">
                                  <span className="text-[9.5px] font-extrabold uppercase text-slate-500 tracking-wider block">Stability & Career Growth</span>
                                  <span className="text-[9px] font-extrabold bg-indigo-50 text-indigo-700 px-1.5 py-0.2 rounded">
                                    Score: {enrichment.jobSecurity.stabilityScore || 75}/100
                                  </span>
                                </div>

                                <div className="space-y-1">
                                  <div className="flex justify-between text-[9px] font-bold text-slate-500">
                                    <span>Employment Security Rating:</span>
                                    <span className="text-slate-700 font-extrabold">{enrichment.jobSecurity.stabilityScore || 75}%</span>
                                  </div>
                                  <div className="w-full bg-slate-200 rounded-full h-1.5">
                                    <div 
                                      className="bg-emerald-500 h-1.5 rounded-full transition-all duration-300" 
                                      style={{ width: `${enrichment.jobSecurity.stabilityScore || 75}%` }}
                                    />
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3 text-[10.5px]">
                                  <div>
                                    <span className="text-[8.5px] font-bold text-slate-400 block">Financial Outlook</span>
                                    <span className={`font-extrabold capitalize ${enrichment.jobSecurity.financialHealth === "growing" ? "text-emerald-600" : "text-slate-700"}`}>
                                      📈 {enrichment.jobSecurity.financialHealth}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-[8.5px] font-bold text-slate-400 block">Average Staff Tenure</span>
                                    <span className="font-extrabold text-slate-700">⏱️ {enrichment.jobSecurity.tenureYrs} Years Avg</span>
                                  </div>
                                  <div>
                                    <span className="text-[8.5px] font-bold text-slate-400 block">Establishment Age</span>
                                    <span className="font-extrabold text-slate-700">🏢 {enrichment.jobSecurity.businessHistoryYrs} Years Active</span>
                                  </div>
                                  <div>
                                    <span className="text-[8.5px] font-bold text-slate-400 block">Job Security Class</span>
                                    <span className={`font-extrabold capitalize ${enrichment.jobSecurity.securityIndicator === "high" ? "text-emerald-600" : "text-slate-700"}`}>
                                      🛡️ {enrichment.jobSecurity.securityIndicator}
                                    </span>
                                  </div>
                                </div>

                                {enrichment.jobSecurity.indicators && enrichment.jobSecurity.indicators.length > 0 && (
                                  <div className="border-t border-slate-100 pt-2 space-y-1">
                                    <span className="text-[8.5px] font-bold text-slate-400 block">Verified Safety Footprints</span>
                                    <div className="flex flex-wrap gap-1">
                                      {enrichment.jobSecurity.indicators.map((ind, i) => (
                                        <span key={i} className="text-[8.5px] font-semibold bg-emerald-50 text-emerald-800 border border-emerald-100 px-1.5 py-0.2 rounded">
                                          ✓ {ind}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                <div className="space-y-1 border-t border-slate-100 pt-2">
                                  <span className="text-[8.5px] font-bold text-slate-400 block">Career Advancement Path</span>
                                  <p className="text-[10px] text-slate-650 leading-relaxed font-semibold">
                                    {enrichment.careerGrowth.growthOpportunities}
                                  </p>
                                  <div className="flex flex-wrap gap-1 pt-1">
                                    <span className={`text-[8.5px] font-bold px-1.5 py-0.5 rounded ${enrichment.careerGrowth.trainingAvailable ? 'bg-indigo-50 text-indigo-700 border border-indigo-100' : 'bg-slate-100 text-slate-400'}`}>
                                      {enrichment.careerGrowth.trainingAvailable ? "✓ Hands-on Training" : "✗ Training"}
                                    </span>
                                    <span className={`text-[8.5px] font-bold px-1.5 py-0.5 rounded ${enrichment.careerGrowth.tuitionAssistance ? 'bg-indigo-50 text-indigo-700 border border-indigo-100' : 'bg-slate-100 text-slate-400'}`}>
                                      {enrichment.careerGrowth.tuitionAssistance ? "✓ Tuition Support" : "✗ Tuition Support"}
                                    </span>
                                    <span className={`text-[8.5px] font-bold px-1.5 py-0.5 rounded ${enrichment.careerGrowth.mentorshipAvailable ? 'bg-indigo-50 text-indigo-700 border border-indigo-100' : 'bg-slate-100 text-slate-400'}`}>
                                      {enrichment.careerGrowth.mentorshipAvailable ? "✓ Mentorship Available" : "✗ Mentorship"}
                                    </span>
                                    <span className={`text-[8.5px] font-bold px-1.5 py-0.5 rounded ${enrichment.careerGrowth.certificationsSupported ? 'bg-indigo-50 text-indigo-700 border border-indigo-100' : 'bg-slate-100 text-slate-400'}`}>
                                      {enrichment.careerGrowth.certificationsSupported ? "✓ Certifications Paid" : "✗ Certifications"}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* 4. Company Culture */}
                              <div className="bg-slate-50/50 rounded-xl p-3 border border-slate-150/60 space-y-2.5">
                                <div className="flex items-center justify-between">
                                  <span className="text-[9.5px] font-extrabold uppercase text-slate-500 tracking-wider block">Company Culture</span>
                                  <div className="flex items-center gap-1 text-[10px] font-black text-indigo-600 bg-white px-1.5 py-0.5 rounded border border-slate-200">
                                    <span>★</span>
                                    <span>{enrichment.companyCulture.rating.toFixed(1)}</span>
                                    <span className="text-[8.5px] text-slate-400 font-bold">({enrichment.companyCulture.reviewCount || 15} revs)</span>
                                  </div>
                                </div>

                                <div className="space-y-1">
                                  <div className="flex justify-between text-[9px] font-bold text-slate-500">
                                    <span>Workplace Culture score:</span>
                                    <span className="text-slate-700 font-extrabold">{enrichment.companyCulture.cultureScore || 70}%</span>
                                  </div>
                                  <div className="w-full bg-slate-200 rounded-full h-1.5">
                                    <div 
                                      className="bg-indigo-500 h-1.5 rounded-full transition-all duration-300" 
                                      style={{ width: `${enrichment.companyCulture.cultureScore || 70}%` }}
                                    />
                                  </div>
                                </div>

                                <p className="text-[10px] leading-relaxed font-semibold italic text-slate-600 bg-white p-2 rounded border border-slate-100">
                                  "{enrichment.companyCulture.feedbackSummary || enrichment.companyCulture.employeeFeedbackSummary}"
                                </p>

                                <div className="grid grid-cols-2 gap-3 text-[10px] pt-1">
                                  <div className="space-y-1">
                                    <span className="text-[8px] font-bold text-slate-400 uppercase block">Culture Strengths</span>
                                    <div className="flex flex-col gap-0.5 text-slate-650 font-bold">
                                      {enrichment.companyCulture.positiveIndicators?.slice(0, 2).map((pi, idx) => (
                                        <span key={idx} className="text-emerald-700 text-[9px] flex items-center gap-1">
                                          💚 {pi}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                  <div className="space-y-1">
                                    <span className="text-[8px] font-bold text-slate-400 uppercase block">Workplace Balance</span>
                                    <span className="font-extrabold text-slate-700 capitalize block flex items-center gap-1">
                                      ⚖️ {enrichment.companyCulture.workLifeBalance}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* 5. Confidence Metadata & Freshness */}
                              <div className="flex items-center justify-between text-[9px] text-slate-400 font-bold px-1 pt-1 border-t border-slate-100">
                                <span className="flex items-center gap-1">
                                  <span>Confidence Index:</span>
                                  <span className="text-indigo-650">{enrichment.metadata.confidenceScore}%</span>
                                </span>
                                <span className="flex items-center gap-1 text-slate-500">
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                  {enrichment.metadata.lastCheckedDate}
                                </span>
                              </div>
                                </>
                              )}
                            </div>
                          );
                        })()}

                            {(() => {
                              let activeDistanceMiles: number | null = null;
                              const targetCenter = searchCenter || userCoords;
                              if (targetCenter && activeBusiness.location?.lat && activeBusiness.location?.lng) {
                                activeDistanceMiles = getDistanceInMiles(
                                  targetCenter.lat,
                                  targetCenter.lng,
                                  activeBusiness.location.lat,
                                  activeBusiness.location.lng
                                );
                              }
                              const prospect = analyzeBusinessProspect(activeBusiness, activeDistanceMiles);
                              const score = prospect.score;

                              return (
                            <>
                              {/* ==========================================
                                  1. DIRECT CONTACT SEARCH (TOOL 1 - PROMINENT AT TOP)
                                 ========================================== */}
                              {detailConfig.visibleSections.includes("contacts") && (
                              <div id="sec-contacts" className="border border-blue-200 dark:border-blue-900/60 rounded-xl p-4 bg-white dark:bg-slate-900 space-y-3.5 text-left shadow-xs">
                                <div className="flex items-center justify-between">
                                  <label className="text-[10.5px] font-black text-blue-950 dark:text-blue-100 uppercase tracking-widest block select-none flex items-center gap-1.5">
                                    <Search className="w-3.5 h-3.5 text-blue-700 dark:text-blue-400" />
                                    <span>{detailConfig.sectionTitles.contacts || "Contact Information & Direct Contact Finder"}</span>
                                  </label>
                                  <span className="text-[9.5px] font-mono font-black text-slate-800 dark:text-slate-200 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-2 py-0.5 rounded">
                                    {billingStatus?.isPro || billingStatus?.limits?.contact_crawl?.limit === 9999
                                      ? "Unlimited Contact Searches"
                                      : `${Math.max(0, (billingStatus?.limits?.contact_crawl?.limit ?? 15) - (billingStatus?.limits?.contact_crawl?.count ?? 0))} remaining`}
                                  </span>
                                </div>

                                {/* Explanation: Why On-Demand? */}
                                <div className="bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-left flex items-start gap-2 shadow-3xs">
                                  <HelpCircle className="w-4 h-4 text-blue-700 dark:text-blue-400 shrink-0 mt-0.5" />
                                  <div className="space-y-0.5 text-[10px] text-blue-950 dark:text-blue-100 leading-relaxed">
                                    <p className="font-black text-blue-950 dark:text-blue-100 flex items-center gap-1">
                                      <span>Why search on-demand?</span>
                                    </p>
                                    <p className="text-slate-800 dark:text-slate-200 font-semibold">
                                      Direct executive emails and phone numbers are verified live against WHOIS records, public corporate filings, and domain registries only when requested—ensuring 100% fresh, accurate details without stale cached data.
                                    </p>
                                  </div>
                                </div>
                                
                                {(() => {
                                  const showRecoveryBox = !!(
                                    contactSyncOrLoadFailed &&
                                    previousLookupMatch &&
                                    previousLookupMatch.contacts &&
                                    previousLookupMatch.contacts.length > 0
                                  );

                                  return showRecoveryBox && previousLookupMatch ? (
                                    <div className="bg-amber-50/70 border border-amber-250 rounded-lg p-3 text-left">
                                      <p className="text-[10px] font-extrabold text-amber-800 flex items-center gap-1">
                                        <ShieldCheck className="w-3.5 h-3.5 text-amber-600" />
                                        {t("pipeline:recover_saved_contacts")}
                                      </p>
                                      <p className="text-[9.5px] text-amber-705 mt-1 leading-relaxed">
                                        {t("pipeline:recover_saved_contacts_desc")}
                                      </p>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const restoredContacts = previousLookupMatch.contacts || [];
                                          const updatedBusiness = {
                                            ...activeBusiness,
                                            crawlStatus: "success" as const,
                                            contacts: restoredContacts
                                          };
                                          setBusinesses(prev => prev.map(l => l.id === activeBusiness.id ? updatedBusiness : l));
                                          setSavedBusinesses(prev => prev.map(l => l.id === activeBusiness.id ? updatedBusiness : l));
                                          setSyncFeedback(`Successfully restored ${restoredContacts.length} contacts!`);
                                          setTimeout(() => setSyncFeedback(null), 3000);
                                        }}
                                        className="w-full mt-2 bg-amber-600 hover:bg-amber-700 text-white font-bold py-1.5 px-3 rounded text-[10px] cursor-pointer"
                                      >
                                        {t("pipeline:restore_contacts_btn", { count: previousLookupMatch.contacts.length })}
                                      </button>
                                    </div>
                                  ) : null;
                                })()}

                                {activeBusiness.crawlStatus === "idle" && (
                                  <div className="p-3 bg-white border border-slate-200 rounded-lg text-center shadow-3xs space-y-2">
                                    <p className="text-[11px] text-slate-600 font-semibold">Search for public executive contact details.</p>
                                    <button
                                      onClick={() => enrichBusiness(activeBusiness)}
                                      className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-3 rounded-lg text-[11px] transition-all w-full flex items-center justify-center gap-2 shadow-sm cursor-pointer border border-blue-750"
                                    >
                                      <Search className="w-3.5 h-3.5 text-blue-100" />
                                      {previousLookupMatch ? t("pipeline:search_again") : t("pipeline:search_contacts")}
                                    </button>
                                  </div>
                                )}

                                {activeBusiness.crawlStatus === "crawling" && (
                                  <div className="p-4 bg-blue-50/40 border border-blue-150 rounded-lg flex flex-col items-center justify-center text-center gap-2">
                                    <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                                    <p className="text-[11px] font-bold text-blue-900">Searching for public contact information…</p>
                                    <p className="text-[9.5px] text-slate-500 leading-relaxed max-w-[280px]">
                                      Scanning public company footprint and executive registries in real time.
                                    </p>
                                  </div>
                                )}

                                {activeBusiness.crawlStatus === "error" && (
                                  <div className="p-3.5 bg-red-50 border border-red-150 rounded-lg text-left space-y-2">
                                    <p className="text-xs text-red-800 font-bold flex items-center gap-1.5">
                                      <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
                                      {activeBusiness.crawlErrorCode === "user_allowance_exhausted"
                                        ? "You have used all available Contact Searches."
                                        : "Contact Search Temporarily Unavailable"}
                                    </p>
                                    <p className="text-[10px] text-red-700 leading-relaxed">
                                      {activeBusiness.crawlError || "Contact Search is temporarily unavailable. Your search allowance was not used."}
                                    </p>
                                    {activeBusiness.crawlErrorCode === "user_allowance_exhausted" ? (
                                      <button
                                        onClick={() => setIsUpgradeModalOpen(true)}
                                        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-1.5 px-3 rounded text-[10.5px] transition cursor-pointer"
                                      >
                                        Upgrade to Pro
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => enrichBusiness(activeBusiness)}
                                        className="w-full bg-white hover:bg-red-100 border border-red-200 text-red-700 font-bold py-1.5 px-3 rounded text-[10.5px] transition cursor-pointer shadow-3xs"
                                      >
                                        Try Again
                                      </button>
                                    )}
                                  </div>
                                )}

                                {activeBusiness.crawlStatus === "success" && (
                                  <div className={`p-3 border rounded-lg ${activeBusiness.contacts.length > 0 ? "bg-emerald-50 border-emerald-100" : "bg-slate-50 border-slate-200"}`}>
                                    <div className="flex items-center gap-2">
                                      {activeBusiness.contacts.length > 0 ? (
                                        <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                                      ) : (
                                        <Info className="w-4 h-4 text-slate-500 shrink-0" />
                                      )}
                                      <div className="text-left">
                                        <p className={`text-[11px] font-bold ${activeBusiness.contacts.length > 0 ? "text-emerald-800" : "text-slate-800"}`}>
                                          {activeBusiness.contacts.length > 0
                                            ? "Contact Search Complete"
                                            : "No public contact information was found for this business."}
                                        </p>
                                        <p className={`text-[9.5px] font-medium ${activeBusiness.contacts.length > 0 ? "text-emerald-600 font-mono" : "text-slate-500"}`}>
                                          {activeBusiness.contacts.length > 0
                                            ? `${activeBusiness.contacts.length} direct contact details identified`
                                            : "The search completed successfully, but no public executive contact details were located."}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Crawled contact items listing */}
                                {activeBusiness.crawlStatus === "success" && activeBusiness.contacts.length > 0 && (
                                  <div className="space-y-2.5 pt-1 text-left">
                                    <label className="text-[10px] font-black text-slate-700 dark:text-slate-300 uppercase tracking-widest block font-sans select-none">{t("pipeline:contacts_found")}</label>
                                    
                                    {activeBusiness.contacts.map((contact, idx) => (
                                      <div key={idx} className="bg-white border border-slate-200 rounded-lg p-3 space-y-2.5 relative shadow-2xs">
                                        <div className="flex items-start justify-between pb-1.5 border-b border-slate-150">
                                          <div>
                                            <p className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                                              <User className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                                              {contact.name}
                                            </p>
                                            <p className="text-[10px] text-slate-500 ml-5 font-semibold">
                                              {contact.role}
                                            </p>
                                          </div>
                                          <span className="bg-blue-50 border border-blue-200 text-blue-700 text-[8.5px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded shadow-3xs">
                                            {t("pipeline:verified_Business")}
                                          </span>
                                        </div>

                                        <div className="space-y-2 pl-5">
                                          {contact.email && (
                                            <div>
                                              <label className="text-[8.5px] font-black text-slate-800 dark:text-slate-200 uppercase tracking-wide block mb-0.5 select-none">{t("pipeline:email_address")}</label>
                                              <div className="flex items-center justify-between p-2 bg-slate-50 border border-slate-200 rounded-md">
                                                <span className="text-[10.5px] font-mono text-slate-800 dark:text-slate-200 truncate select-all flex items-center gap-1.5 font-sans font-medium">
                                                  <Mail className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                                                  {contact.email}
                                                </span>
                                                <button 
                                                  onClick={() => copyToClipboard(contact.email)}
                                                  className="text-slate-500 hover:text-blue-600 p-1 rounded hover:bg-slate-100 transition shrink-0 cursor-pointer"
                                                  title={t("common:copy") || "Copy"}
                                                >
                                                  {copiedText === contact.email ? (
                                                    <Check className="w-3.5 h-3.5 text-emerald-600" />
                                                  ) : (
                                                    <Copy className="w-3.5 h-3.5" />
                                                  )}
                                                </button>
                                              </div>
                                            </div>
                                          )}

                                          {contact.phone && (
                                            <div>
                                              <label className="text-[8.5px] font-black text-slate-800 dark:text-slate-200 uppercase tracking-wide block mb-0.5 select-none">{t("pipeline:direct_phone")}</label>
                                              <div className="flex items-center justify-between p-2 bg-slate-50 border border-slate-200 rounded-md text-[10.5px]">
                                                <span className="text-[10.5px] font-mono text-slate-900 dark:text-slate-100 truncate select-all flex items-center gap-1.5 font-bold font-sans">
                                                  <Phone className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                                                  {contact.phone}
                                                </span>
                                                <button 
                                                  onClick={() => copyToClipboard(contact.phone)}
                                                  className="text-slate-500 hover:text-blue-600 p-1 rounded hover:bg-slate-100 transition shrink-0 cursor-pointer"
                                                  title={t("common:copy") || "Copy"}
                                                >
                                                  {copiedText === contact.phone ? (
                                                    <Check className="w-3.5 h-3.5 text-emerald-600" />
                                                  ) : (
                                                    <Copy className="w-3.5 h-3.5" />
                                                  )}
                                                </button>
                                              </div>
                                            </div>
                                          )}

                                          {contact.sourceDescription && (
                                            <div className="pt-1.5 border-t border-slate-150 flex items-start gap-1">
                                              <Info className="w-3 h-3 text-slate-500 shrink-0 mt-0.5" />
                                              <p className="text-[9px] text-slate-600 dark:text-slate-400 italic font-sans font-semibold">
                                                {t("pipeline:source", { source: contact.sourceDescription })}
                                              </p>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              )}

                              {/* ==========================================
                                  2. AI WEBSITE AUDIT & LIVE INSPECTION (TOOL 2)
                                 ========================================== */}
                              <div id="sec-audit" className="border border-teal-200 dark:border-teal-900/60 rounded-xl p-4 bg-white dark:bg-slate-900 space-y-3.5 text-left shadow-xs relative overflow-hidden font-sans">
                                <div className="flex items-center justify-between">
                                  <label className="text-[10.5px] font-black text-teal-950 dark:text-teal-100 uppercase tracking-widest block select-none flex items-center gap-1.5">
                                    <Globe className="w-3.5 h-3.5 text-teal-700 dark:text-teal-400" />
                                    <span>🌐 8. Website Review</span>
                                  </label>
                                  <span className="bg-teal-100 dark:bg-teal-900/80 border border-teal-200 dark:border-teal-700 text-teal-950 dark:text-teal-100 text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider">
                                    Web Audit
                                  </span>
                                </div>

                                {/* Explanation: Why On-Demand? */}
                                <div className="bg-teal-50 dark:bg-teal-950/50 border border-teal-200 dark:border-teal-800 rounded-lg p-3 text-left flex items-start gap-2 shadow-3xs">
                                  <HelpCircle className="w-4 h-4 text-teal-700 dark:text-teal-400 shrink-0 mt-0.5" />
                                  <div className="space-y-0.5 text-[10px] text-teal-950 dark:text-teal-100 leading-relaxed">
                                    <p className="font-black text-teal-950 dark:text-teal-100 flex items-center gap-1">
                                      <span>Why audit on-demand?</span>
                                    </p>
                                    <p className="text-slate-800 dark:text-slate-200 font-semibold">
                                      Website speed, SSL security certificates, mobile viewport layouts, and online booking buttons change constantly. Clicking "Run Website Review" performs a live HTTP scan directly against {activeBusiness.name}'s active web server for up-to-second diagnostic metrics.
                                    </p>
                                  </div>
                                </div>

                                {/* Review Action Trigger Button */}
                                {activeBusiness.website ? (
                                  <div className="space-y-2">
                                    {(() => {
                                      const state: WebsiteReviewState = (() => {
                                        if (isAuditingId === activeBusiness.id || activeBusiness.websiteReviewStatus === "running" || activeBusiness.websiteReviewStatus === "reviewing") return "running";
                                        if (activeBusiness.websiteReviewStatus === "allowance_exhausted") return "allowance_exhausted";
                                        if (activeBusiness.websiteReviewStatus === "advanced_pro_required") return "advanced_pro_required";
                                        if (activeBusiness.websiteReviewStatus === "blocked" || activeBusiness.websiteAudit?.reviewStatus === "blocked") return "blocked";
                                        if (activeBusiness.websiteReviewStatus === "unreachable" || activeBusiness.websiteAudit?.reviewStatus === "unreachable") return "unreachable";
                                        if (activeBusiness.websiteReviewStatus === "timeout" || activeBusiness.websiteAudit?.reviewStatus === "timeout") return "timeout";
                                        if (activeBusiness.websiteReviewStatus === "temporary_failure" || activeBusiness.websiteReviewStatus === "failed" || activeBusiness.websiteAudit?.failed) return "temporary_failure";
                                        if (activeBusiness.websiteAudit && activeBusiness.websiteAudit.exists && !activeBusiness.websiteAudit.failed) return "success";
                                        return "available";
                                      })();

                                      if (state === "running") {
                                        return (
                                          <button
                                            disabled
                                            className="w-full py-2.5 px-3 rounded-lg text-[11px] font-bold bg-teal-50 text-teal-700 border border-teal-200 flex items-center justify-center gap-2"
                                          >
                                            <Loader2 className="w-4 h-4 animate-spin text-teal-600" />
                                            Scanning {activeBusiness.name}'s website live...
                                          </button>
                                        );
                                      }

                                      if (state === "allowance_exhausted" || state === "advanced_pro_required") {
                                        return (
                                          <button
                                            onClick={() => {
                                              setIsUpgradeModalOpen(true);
                                              loadBillingStatus();
                                            }}
                                            className="w-full py-2.5 px-3 rounded-lg text-[11px] font-bold bg-amber-600 hover:bg-amber-700 text-white border border-amber-700 shadow-sm transition-all flex items-center justify-center gap-2 cursor-pointer"
                                          >
                                            <Lock className="w-3.5 h-3.5 text-amber-200" />
                                            Upgrade to Pro for Higher Limits
                                          </button>
                                        );
                                      }

                                      if (["blocked", "unreachable", "timeout", "temporary_failure"].includes(state)) {
                                        return (
                                          <button
                                            onClick={() => runWebsiteAudit(activeBusiness)}
                                            className="w-full py-2.5 px-3 rounded-lg text-[11px] font-bold bg-rose-50 hover:bg-rose-100 text-rose-800 border border-rose-200 shadow-2xs transition-all flex items-center justify-center gap-2 cursor-pointer"
                                          >
                                            <RotateCcw className="w-3.5 h-3.5 text-rose-600" />
                                            {t("pipeline:try_website_review_again")}
                                          </button>
                                        );
                                      }

                                      if (state === "success") {
                                        return (
                                          <button
                                            onClick={() => runWebsiteAudit(activeBusiness)}
                                            className="w-full py-2 px-3 rounded-lg text-[11px] font-bold bg-white hover:bg-slate-50 text-teal-800 border border-teal-200 shadow-2xs transition-all flex items-center justify-center gap-2 cursor-pointer"
                                          >
                                            <RotateCcw className="w-3.5 h-3.5 text-teal-600" />
                                            {t("pipeline:rerun_website_review")}
                                          </button>
                                        );
                                      }

                                      return (
                                        <button
                                          onClick={() => runWebsiteAudit(activeBusiness)}
                                          className="w-full py-2.5 px-3 rounded-lg text-[11px] font-bold bg-teal-600 hover:bg-teal-700 text-white border border-teal-750 shadow-sm transition-all flex items-center justify-center gap-2 cursor-pointer"
                                        >
                                          <Search className="w-3.5 h-3.5 text-teal-100" />
                                          <span>Run Website Review Now</span>
                                        </button>
                                      );
                                    })()}
                                  </div>
                                ) : (
                                  <p className="text-[10.5px] text-slate-500 font-medium italic">
                                    No website listed for this business. Website reviews require an active domain URL.
                                  </p>
                                )}

                                 {/* Collapsible/Expandable Audit Report Breakdown */}
                                <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
                                  <button
                                    type="button"
                                    onClick={() => setIsWebsiteAuditExpanded(!isWebsiteAuditExpanded)}
                                    className="w-full flex items-center justify-between group cursor-pointer select-none focus:outline-none"
                                  >
                                    <span className="text-[10.5px] font-black text-slate-900 dark:text-slate-100 uppercase tracking-wider block group-hover:text-teal-800 dark:group-hover:text-teal-300 transition-colors">
                                      View Full Audit Breakdown
                                    </span>
                                    <span className={`text-xs text-teal-600 dark:text-teal-400 group-hover:text-teal-800 transition-transform duration-200 transform ${
                                      isWebsiteAuditExpanded ? "rotate-180" : "rotate-0"
                                    }`}>
                                      ▼
                                    </span>
                                  </button>

                                  {isWebsiteAuditExpanded && (
                                    <div className="space-y-4 pt-3 mt-2 border-t border-slate-100 dark:border-slate-800 animate-slide-down">
                                      {!isProUser ? (
                                        <div className="bg-slate-50/90 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 rounded-xl p-4 text-center space-y-2.5 shadow-2xs">
                                          <div className="w-9 h-9 bg-teal-50 dark:bg-teal-950/60 rounded-full flex items-center justify-center border border-teal-150 dark:border-teal-800 text-teal-700 dark:text-teal-300 mx-auto">
                                            <Lock className="w-4 h-4" />
                                          </div>
                                          <h4 className="text-[11.5px] font-extrabold text-slate-900 dark:text-slate-100">Dynamic website engine audit is locked</h4>
                                          <p className="text-[10px] text-slate-700 dark:text-slate-300 max-w-xs mx-auto leading-normal font-medium">
                                            Generate real-time metrics on loading speed, mobile friendly responsive viewports, broken links, accessibility scoring, and CTA conversion opportunities.
                                          </p>
                                          <button
                                            type="button"
                                            onClick={() => setIsUpgradeModalOpen(true)}
                                            className="px-3.5 py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-[9.5px] font-extrabold rounded-lg transition-all shadow-sm active:scale-95 flex items-center justify-center gap-1 mx-auto cursor-pointer"
                                          >
                                            <Sparkles className="w-3 h-3 text-amber-300 animate-pulse" />
                                            <span>{AI_FEATURES_ENABLED ? "Unlock AI Website Audit" : "Unlock Website Audit"}</span>
                                          </button>
                                        </div>
                                      ) : (
                                        <div className="space-y-4 text-xs font-sans">
                                          {/* Score Overview and conversion opportunity */}
                                          <div className="flex items-center justify-between bg-white dark:bg-slate-800 p-3 rounded-lg border border-slate-200 dark:border-slate-700 shadow-3xs">
                                            <div>
                                              <span className="text-[9.5px] font-black text-slate-700 dark:text-slate-300 uppercase tracking-wider block">Audit Score</span>
                                              <span className="text-xl font-black text-amber-600 dark:text-amber-400 font-mono">68 / 100</span>
                                            </div>
                                            <div className="text-right">
                                              <span className="text-[9.5px] font-extrabold text-slate-700 dark:text-slate-300 uppercase block">Conversion Boost</span>
                                              <span className="text-xs font-black text-emerald-700 dark:text-emerald-400 font-sans">+38% Estimated</span>
                                            </div>
                                          </div>

                                          {/* Dynamic checklist grid */}
                                          <div className="grid grid-cols-2 gap-2 text-[10.5px]">
                                            <div className="flex items-center gap-1.5 bg-slate-50 dark:bg-slate-800 p-1.5 rounded border border-slate-200 dark:border-slate-700">
                                              <span className="text-emerald-600 dark:text-emerald-400 font-bold">✓</span>
                                              <span className="font-semibold text-slate-900 dark:text-slate-100">SSL Secure</span>
                                            </div>
                                            <div className="flex items-center gap-1.5 bg-slate-50 dark:bg-slate-800 p-1.5 rounded border border-slate-200 dark:border-slate-700">
                                              <span className="text-rose-600 dark:text-rose-400 font-bold">✗</span>
                                              <span className="font-semibold text-slate-900 dark:text-slate-100">Poor Load Speed</span>
                                            </div>
                                            <div className="flex items-center gap-1.5 bg-slate-50 dark:bg-slate-800 p-1.5 rounded border border-slate-200 dark:border-slate-700">
                                              <span className="text-emerald-600 dark:text-emerald-400 font-bold">✓</span>
                                              <span className="font-semibold text-slate-900 dark:text-slate-100">Mobile Friendly</span>
                                            </div>
                                            <div className="flex items-center gap-1.5 bg-slate-50 dark:bg-slate-800 p-1.5 rounded border border-slate-200 dark:border-slate-700">
                                              <span className="text-rose-600 dark:text-rose-400 font-bold">✗</span>
                                              <span className="font-semibold text-slate-900 dark:text-slate-100">Missing Booking</span>
                                            </div>
                                            <div className="flex items-center gap-1.5 bg-slate-50 dark:bg-slate-800 p-1.5 rounded border border-slate-200 dark:border-slate-700">
                                              <span className="text-amber-600 dark:text-amber-400 font-bold">⚠</span>
                                              <span className="font-semibold text-slate-900 dark:text-slate-100">Weak CTA Signals</span>
                                            </div>
                                            <div className="flex items-center gap-1.5 bg-slate-50 dark:bg-slate-800 p-1.5 rounded border border-slate-200 dark:border-slate-700">
                                              <span className="text-emerald-600 dark:text-emerald-400 font-bold">✓</span>
                                              <span className="font-semibold text-slate-900 dark:text-slate-100">Trust Badges OK</span>
                                            </div>
                                            <div className="flex items-center gap-1.5 bg-slate-50 dark:bg-slate-800 p-1.5 rounded border border-slate-200 dark:border-slate-700">
                                              <span className="text-amber-600 dark:text-amber-400 font-bold">⚠</span>
                                              <span className="font-semibold text-slate-900 dark:text-slate-100">Low SEO Metatags</span>
                                            </div>
                                            <div className="flex items-center gap-1.5 bg-slate-50 dark:bg-slate-800 p-1.5 rounded border border-slate-200 dark:border-slate-700 col-span-2">
                                              <span className="text-rose-600 dark:text-rose-400 font-bold">✗</span>
                                              <span className="font-semibold text-slate-900 dark:text-slate-100">No Contact Form Found</span>
                                            </div>
                                          </div>

                                          {/* Critical Issues & Impact */}
                                          <div className="space-y-1.5">
                                            <span className="text-[9.5px] font-black text-slate-700 dark:text-slate-300 uppercase tracking-widest block">Detected Vulnerabilities</span>
                                            <div className="space-y-2 bg-rose-50/70 dark:bg-rose-950/40 p-3 rounded-lg border border-rose-200 dark:border-rose-800 text-slate-900 dark:text-slate-100 text-[10.5px] leading-relaxed font-medium">
                                              <p className="flex items-start gap-1">
                                                <span className="text-rose-600 dark:text-rose-400 font-bold">•</span>
                                                <span><strong className="text-rose-950 dark:text-rose-200 font-extrabold">Speed Lag:</strong> Asset rendering takes 3.8s on 4G networks due to uncompressed hero layout.</span>
                                              </p>
                                              <p className="flex items-start gap-1">
                                                <span className="text-rose-600 dark:text-rose-400 font-bold">•</span>
                                                <span><strong className="text-rose-950 dark:text-rose-200 font-extrabold">Broken Links:</strong> "Book appointment" button routes to a missing external domain link.</span>
                                              </p>
                                              <p className="flex items-start gap-1">
                                                <span className="text-rose-600 dark:text-rose-400 font-bold">•</span>
                                                <span><strong className="text-rose-950 dark:text-rose-200 font-extrabold">SEO Deficit:</strong> No meta description found on primary root index header.</span>
                                              </p>
                                            </div>
                                          </div>

                                          {/* Audit Strategic recommendations */}
                                          <div className="space-y-1.5">
                                            <span className="text-[9.5px] font-black text-slate-700 dark:text-slate-300 uppercase tracking-widest block">Audit Recommendations</span>
                                            <div className="space-y-2 bg-emerald-50/70 dark:bg-emerald-950/40 p-3 rounded-lg border border-emerald-200 dark:border-emerald-800 text-slate-900 dark:text-slate-100 text-[10.5px] leading-relaxed font-medium">
                                              <p className="flex items-start gap-1">
                                                <span className="text-emerald-700 dark:text-emerald-400 font-extrabold">1.</span>
                                                <span>Deploy modern lazy-loading for image files and compile assets to next-gen WebP formatting.</span>
                                              </p>
                                              <p className="flex items-start gap-1">
                                                <span className="text-emerald-700 dark:text-emerald-400 font-extrabold">2.</span>
                                                <span>Integrate floating conversion contact widgets at bottom-right viewport container.</span>
                                              </p>
                                            </div>
                                          </div>

                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* ==========================================
                                  3. AI CONSULTANT EXECUTIVE REPORT (TOOL 3)
                                 ========================================== */}
                              <div id="sec-aiconsultant" className="border border-indigo-200 dark:border-indigo-900/60 rounded-xl p-4 bg-white dark:bg-slate-900 space-y-3.5 text-left shadow-xs relative overflow-hidden font-sans">
                                {/* Premium badge at top right */}
                                <div className="absolute top-3.5 right-3.5 flex items-center gap-1 bg-indigo-100 dark:bg-indigo-900/60 text-indigo-800 dark:text-indigo-200 text-[8.5px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider">
                                  <Sparkles className="w-2.5 h-2.5 animate-pulse text-amber-500" />
                                  <span>{AI_FEATURES_ENABLED ? "AI Consultant" : "Advisor"}</span>
                                </div>

                                <button
                                  type="button"
                                  onClick={() => setIsAiConsultantExpanded(!isAiConsultantExpanded)}
                                  className="w-full flex items-center justify-between group cursor-pointer select-none focus:outline-none"
                                >
                                  <div className="space-y-0.5">
                                    <span className="text-[10.5px] font-black text-indigo-950 dark:text-indigo-100 uppercase tracking-widest block group-hover:text-indigo-800 transition-colors">
                                      {AI_FEATURES_ENABLED ? "🤖 9. AI Consultant" : "📋 9. Business Advisor"}
                                    </span>
                                    <p className="text-[10px] text-slate-700 dark:text-slate-300 font-semibold">
                                      Executive summary, outreach angles, and revenue estimate.
                                    </p>
                                  </div>
                                  <span className={`text-xs text-indigo-600 dark:text-indigo-400 group-hover:text-indigo-800 transition-transform duration-200 transform ${
                                    isAiConsultantExpanded ? "rotate-180" : "rotate-0"
                                  }`}>
                                    ▼
                                  </span>
                                </button>

                                {isAiConsultantExpanded && (
                                  <div className="space-y-4 pt-3.5 border-t border-slate-100 animate-slide-down">
                                    {!isProUser ? (
                                      <div className="bg-slate-50/90 border border-slate-200 rounded-xl p-4 text-center space-y-2.5 shadow-2xs">
                                        <div className="w-9 h-9 bg-indigo-50 rounded-full flex items-center justify-center border border-indigo-150 text-indigo-600 mx-auto">
                                          <Lock className="w-4 h-4" />
                                        </div>
                                        <h4 className="text-[11.5px] font-extrabold text-slate-800">Executive Advisor report is locked</h4>
                                        <p className="text-[9.5px] text-slate-500 max-w-xs mx-auto leading-normal">
                                          Unlock target decision makers, custom email templates, estimated urgency metrics, and pitch strategies tailored to {activeBusiness.name}.
                                        </p>
                                        <button
                                          type="button"
                                          onClick={() => setIsUpgradeModalOpen(true)}
                                          className="px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-[9.5px] font-extrabold rounded-lg transition-all shadow-sm active:scale-95 flex items-center justify-center gap-1 mx-auto cursor-pointer"
                                        >
                                          <Sparkles className="w-3 h-3 text-amber-300 animate-pulse" />
                                          <span>{AI_FEATURES_ENABLED ? "Unlock AI Advisor Report" : "Unlock Advisor Report"}</span>
                                        </button>
                                      </div>
                                    ) : (
                                      <div className="space-y-4 text-xs">
                                        {/* Business Summary */}
                                        <div className="space-y-1 bg-white p-3 rounded-lg border border-slate-150 shadow-3xs">
                                          <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Advisor Summary</span>
                                          <p className="text-[11px] text-slate-700 leading-relaxed font-medium">
                                            {activeBusiness.name} is a high-potential local provider in the {activeBusiness.category || "service"} sector. Based on our crawl of their public footprint, they have strong core customer rating ({activeBusiness.rating || "N/A"}★) but lag heavily in website conversions, automated booking capabilities, and outbound channels, presenting an outstanding vendor opportunity.
                                          </p>
                                        </div>

                                        {/* Metrics Bento Layout */}
                                        <div className="grid grid-cols-2 gap-2 text-[10.5px]">
                                          <div className="bg-emerald-50/50 border border-emerald-150 p-2.5 rounded-lg space-y-0.5">
                                            <span className="text-[8.5px] font-extrabold text-emerald-800 uppercase block">Close Probability</span>
                                            <p className="text-sm font-black text-emerald-955 font-mono">
                                              {score > 75 ? "78%" : score > 50 ? "64%" : "42%"}
                                            </p>
                                          </div>
                                          <div className="bg-amber-50/50 border border-amber-150 p-2.5 rounded-lg space-y-0.5">
                                            <span className="text-[8.5px] font-extrabold text-amber-800 uppercase block">Estimated Urgency</span>
                                            <p className="text-sm font-black text-amber-955">
                                              {activeBusiness.phone ? "Medium-High" : "High Priority"}
                                            </p>
                                          </div>
                                          <div className="bg-indigo-50/50 border border-indigo-150 p-2.5 rounded-lg space-y-0.5">
                                            <span className="text-[8.5px] font-extrabold text-indigo-800 uppercase block">Ideal Contact Method</span>
                                            <p className="text-[11px] font-bold text-indigo-950 font-sans">
                                              {activeBusiness.phone ? "Phone / SMS Warm Introduction" : "Email & LinkedIn combo"}
                                            </p>
                                          </div>
                                          <div className="bg-pink-50/50 border border-pink-150 p-2.5 rounded-lg space-y-0.5">
                                            <span className="text-[8.5px] font-extrabold text-pink-800 uppercase block">Ideal Decision Maker</span>
                                            <p className="text-[11px] font-bold text-pink-955">
                                              Owner / Managing Partner
                                            </p>
                                          </div>
                                        </div>

                                        {/* Revenue & Pitch Details */}
                                        <div className="space-y-2 bg-slate-50/80 p-3 rounded-lg border border-slate-200">
                                          <div className="flex items-center justify-between">
                                            <span className="text-[8.5px] font-extrabold text-slate-500 uppercase tracking-wider block">Potential Contract Value</span>
                                            <span className="text-xs font-black text-emerald-700 font-mono">$2,450 - $4,800/yr</span>
                                          </div>
                                          <div className="space-y-1">
                                            <span className="text-[8.5px] font-extrabold text-indigo-800 uppercase tracking-wider block">Best Outreach Angle</span>
                                            <p className="text-[11px] text-slate-705 leading-normal font-semibold">
                                              "Let's integrate an automated booking widget onto your listing to instantly capture high-intent customers searching off-hours."
                                            </p>
                                          </div>
                                          <div className="flex justify-between gap-2.5 text-[9.5px] text-slate-500 pt-1.5 border-t border-slate-200 font-mono">
                                            <span>🕒 Best Day: Tue / Thu</span>
                                            <span>⏰ Best Time: 1:30 - 3:00 PM</span>
                                          </div>
                                        </div>

                                        {/* Strengths & Weaknesses */}
                                        <div className="grid grid-cols-2 gap-2 text-[10.5px]">
                                          <div className="space-y-1">
                                            <span className="text-[8.5px] font-black text-slate-450 uppercase tracking-widest block">Biggest Strengths</span>
                                            <ul className="space-y-1 text-slate-650 font-semibold list-disc list-inside">
                                              <li>High customer rating</li>
                                              <li>Verified active number</li>
                                              <li>Strong local reputation</li>
                                            </ul>
                                          </div>
                                          <div className="space-y-1">
                                            <span className="text-[8.5px] font-black text-slate-455 uppercase tracking-widest block">Core Weaknesses</span>
                                            <ul className="space-y-1 text-slate-650 font-semibold list-disc list-inside">
                                              <li>No online calendar booking</li>
                                              <li>Weak conversion CTAs</li>
                                              <li>SSL warning status</li>
                                            </ul>
                                          </div>
                                        </div>

                                        {/* Sales Scripts Tabs */}
                                        <div className="bg-white p-3 rounded-lg border border-slate-150 space-y-2">
                                          <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Pre-drafted Consultant Scripts</span>
                                          
                                          <div className="space-y-3.5">
                                            <div className="space-y-1">
                                              <div className="flex items-center justify-between">
                                                <span className="text-[9px] font-black text-blue-650 bg-blue-50 px-1.5 py-0.2 rounded uppercase">Suggested Email</span>
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    copyToClipboard(`Subject: Automated booking widget setup for ${activeBusiness.name}\n\nHi there,\n\nI noticed customers are loving your service, but I couldn't find an easy way to book an appointment directly from your profile. Would you be open to adding a quick self-booking link to double your off-hours bookings?\n\nBest,\n[Your Name]`);
                                                  }}
                                                  className="text-[9px] font-extrabold text-slate-400 hover:text-slate-650 cursor-pointer"
                                                >
                                                  Copy
                                                </button>
                                              </div>
                                              <pre className="text-[10px] text-slate-600 bg-slate-50 p-2 rounded border border-slate-100 font-mono whitespace-pre-wrap leading-normal font-sans">
                                                Subject: Automated booking for {activeBusiness.name}{"\n\n"}Hi there, I noticed customers are loving your service, but I couldn't find an easy self-booking link. Would you be open to adding a quick self-booking widget to double off-hours bookings?
                                              </pre>
                                            </div>

                                            <div className="space-y-1">
                                              <div className="flex items-center justify-between">
                                                <span className="text-[9px] font-black text-emerald-700 bg-emerald-50 px-1.5 py-0.2 rounded uppercase">Phone Cold Opening</span>
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    copyToClipboard(`"Hey [Owner Name], this is [Your Name]. I was looking up local services near me and saw your amazing reviews, but when I went to book a slot, I ran into a bit of a roadblock. Is the owner in so I could share a quick 1-minute way to fix this and claim missed bookings?"`);
                                                  }}
                                                  className="text-[9px] font-extrabold text-slate-400 hover:text-slate-650 cursor-pointer"
                                                >
                                                  Copy
                                                </button>
                                              </div>
                                              <pre className="text-[10px] text-slate-600 bg-slate-50 p-2 rounded border border-slate-100 font-mono whitespace-pre-wrap leading-normal italic font-sans">
                                                "Hey, this is [Name]. I saw your amazing reviews, but saw a roadblock booking online. Is the owner available for a quick 1-minute way to fix this and claim missed bookings?"
                                              </pre>
                                            </div>

                                            <div className="space-y-1">
                                              <div className="flex items-center justify-between">
                                                <span className="text-[9px] font-black text-indigo-700 bg-indigo-50 px-1.5 py-0.2 rounded uppercase">LinkedIn Reachout</span>
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    copyToClipboard(`"Hi there, I help local business owners in the ${activeBusiness.category || "service"} sector scale up by plugging conversion leaks. I drafted a quick 3-step audit for ${activeBusiness.name} showing why current visitors might be bouncing. Would love to send it over!"`);
                                                  }}
                                                  className="text-[9px] font-extrabold text-slate-400 hover:text-slate-650 cursor-pointer"
                                                >
                                                  Copy
                                                </button>
                                              </div>
                                              <pre className="text-[10px] text-slate-600 bg-slate-50 p-2 rounded border border-slate-100 font-mono whitespace-pre-wrap leading-normal font-sans">
                                                "Hi, I noticed ${activeBusiness.name} has incredible customer reviews, but has a few digital leaks causing customers to bounce. I've drafted a quick 3-step audit for you. Open to a look?"
                                              </pre>
                                            </div>
                                          </div>

                                        </div>

                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </>
                          );
                        })()}

                        {isAdult && (
                          <div className="bg-amber-50 border border-amber-250 text-amber-900 text-[10.5px] p-3 rounded-lg flex items-center gap-2 font-semibold shadow-3xs">
                            <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                            <span><strong>{t("pipeline:age_restricted_label")}</strong> {t("pipeline:age_restricted_text")}</span>
                          </div>
                        )}



                        {/* ==========================================
                            INFO 1: BIZNEARYOU INTELLIGENCE CENTER (SMART SCORES)
                           ========================================== */}
                        {detailConfig.visibleSections.includes("scores") && (
                        <div id="sec-scores" className="bg-white border border-slate-200 rounded-xl shadow-xs overflow-hidden font-sans text-left relative z-15">
                          {/* Section Header */}
                          <div className="bg-slate-50 px-4 py-3 border-b border-slate-150 flex items-center justify-between">
                            <span className="text-[11px] font-black uppercase text-slate-800 dark:text-slate-200 tracking-wider select-none">
                              {detailConfig.sectionTitles.scores || "Detailed Score Breakdown"}
                            </span>
                            <div className="flex items-center gap-2">
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                                score >= 80 
                                  ? "bg-amber-50 text-amber-700 border-amber-200" 
                                  : score >= 50 
                                    ? "bg-blue-50 text-blue-700 border-blue-200" 
                                    : "bg-slate-50 text-slate-600 border-slate-200"
                              }`}>
                                {score >= 80 ? t("pipeline:Business_temp_hot") : score >= 50 ? t("pipeline:Business_temp_warm") : t("pipeline:Business_temp_cold")}
                              </span>
                            </div>
                          </div>

                          {/* 5-Category Score Dashboard */}
                          <div className="p-4 space-y-4">
                            {/* Premium Total Score Gauge / Hero display */}
                            <div className="flex items-center justify-between p-3 bg-gradient-to-r from-slate-50/80 to-indigo-50/15 rounded-xl border border-slate-150">
                              <div className="space-y-1 pr-2">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-[10px] font-black uppercase tracking-wider text-slate-500 block">{t("pipeline:total_smart_score")}</span>
                                  <span className="text-[9px] font-bold text-slate-400 bg-slate-100 px-1 py-0.5 rounded">{t("pipeline:weighted")}</span>
                                </div>
                                <p className="text-[10px] text-slate-550 font-medium leading-normal">
                                  {t("pipeline:total_smart_score_desc")}
                                </p>
                              </div>
                              <div className="flex flex-col items-center justify-center pl-3 border-l border-slate-150 shrink-0">
                                <div className="relative flex items-center justify-center">
                                  {/* Dynamic SVG Ring progress indicator */}
                                  <svg className="w-14 h-14 transform -rotate-90">
                                    <circle
                                      cx="28"
                                      cy="28"
                                      r="22"
                                      stroke="#f1f5f9"
                                      strokeWidth="4"
                                      fill="transparent"
                                    />
                                    <circle
                                      cx="28"
                                      cy="28"
                                      r="22"
                                      stroke={score >= 80 ? "#f59e0b" : score >= 50 ? "#3b82f6" : "#94a3b8"}
                                      strokeWidth="4"
                                      fill="transparent"
                                      strokeDasharray={138.2}
                                      strokeDashoffset={138.2 - (138.2 * Math.min(100, Math.max(0, score))) / 100}
                                      strokeLinecap="round"
                                      className="transition-all duration-1000 ease-out"
                                    />
                                  </svg>
                                  <span className="absolute text-sm font-black text-slate-900 font-mono tracking-tighter">
                                    {score}
                                  </span>
                                </div>
                                <span className="text-[8px] font-bold text-slate-400 mt-1 uppercase">{t("pipeline:max_score")}</span>
                              </div>
                            </div>

                            {/* Score Tabs/Grid selector to inspect different angles */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5 pb-2 border-b border-slate-105">
                              {/* 1. Trust Score Button */}
                              <button
                                type="button"
                                onClick={() => setSelectedScoreTab('trust')}
                                className={`p-1.5 sm:p-2 rounded-lg border text-left transition relative cursor-pointer ${
                                  selectedScoreTab === 'trust'
                                    ? "bg-indigo-50/50 border-indigo-300 ring-1 ring-indigo-300"
                                    : "bg-slate-50/50 border-slate-200 hover:bg-slate-50"
                                }`}
                              >
                                <span className="text-[8px] sm:text-[9px] font-bold text-slate-400 block uppercase tracking-tight leading-none truncate select-none" title={t("pipeline:tab_trust")}>
                                  {t("pipeline:tab_trust")}
                                </span>
                                <span className="text-xs sm:text-sm font-black text-slate-800 font-mono block mt-0.5">
                                  {scores?.trustScore ?? 75}%
                                </span>
                                <div className="flex gap-0.5 mt-0.5">
                                  <div className="h-1 flex-1 bg-teal-500 rounded-full" />
                                </div>
                              </button>

                              {/* 2. Convenience Score Button */}
                              <button
                                type="button"
                                onClick={() => setSelectedScoreTab('convenience')}
                                className={`p-1.5 sm:p-2 rounded-lg border text-left transition relative cursor-pointer ${
                                  selectedScoreTab === 'convenience'
                                    ? "bg-indigo-50/50 border-indigo-300 ring-1 ring-indigo-300"
                                    : "bg-slate-50/50 border-slate-200 hover:bg-slate-50"
                                }`}
                              >
                                <span className="text-[8px] sm:text-[9px] font-bold text-slate-400 block uppercase tracking-tight leading-none truncate select-none" title={t("pipeline:tab_commute")}>
                                  {t("pipeline:tab_commute")}
                                </span>
                                <span className="text-xs sm:text-sm font-black text-slate-800 font-mono block mt-0.5">
                                  {scores?.convenienceScore ?? 80}%
                                </span>
                                <div className="flex gap-0.5 mt-0.5">
                                  <div className="h-1 flex-1 bg-blue-500 rounded-full" />
                                </div>
                              </button>

                              {/* 3. Hiring Score Button (Pro Only) */}
                              <button
                                type="button"
                                onClick={() => setSelectedScoreTab('hiring')}
                                className={`p-1.5 sm:p-2 rounded-lg border text-left transition relative cursor-pointer ${
                                  selectedScoreTab === 'hiring'
                                    ? "bg-indigo-50/50 border-indigo-300 ring-1 ring-indigo-300"
                                    : "bg-slate-50/50 border-slate-200 hover:bg-slate-50"
                                }`}
                              >
                                <div className="relative pr-5 select-none flex items-center justify-between">
                                  <span className="text-[8px] sm:text-[9px] font-bold text-slate-400 block uppercase tracking-tight leading-none truncate" title={t("pipeline:tab_hiring")}>
                                    {t("pipeline:tab_hiring")}
                                  </span>
                                  {!isProUser && (
                                    <span className="text-[7.5px] bg-amber-100 text-amber-800 font-extrabold px-1 py-0.2 rounded border border-amber-200">PRO</span>
                                  )}
                                </div>
                                <span className="text-xs sm:text-sm font-black text-slate-800 font-mono block mt-0.5">
                                  {scores?.hiringScore ?? 60}%
                                </span>
                                <div className="flex gap-0.5 mt-0.5">
                                  <div className={`h-1 flex-1 rounded-full ${((scores?.hiringScore ?? 60) >= 70) ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                                </div>
                              </button>

                              {/* 4. Opportunity Score Button (Pro Only) */}
                              <button
                                type="button"
                                onClick={() => setSelectedScoreTab('opportunity')}
                                className={`p-1.5 sm:p-2 rounded-lg border text-left transition relative cursor-pointer ${
                                  selectedScoreTab === 'opportunity'
                                    ? "bg-indigo-50/50 border-indigo-300 ring-1 ring-indigo-300"
                                    : "bg-slate-50/50 border-slate-200 hover:bg-slate-50"
                                }`}
                              >
                                <div className="relative pr-5 select-none flex items-center justify-between">
                                  <span className="text-[8px] sm:text-[9px] font-bold text-slate-400 block uppercase tracking-tight leading-none truncate" title={t("pipeline:tab_Businesses")}>
                                    {t("pipeline:tab_Businesses")}
                                  </span>
                                  {!isProUser && (
                                    <span className="text-[7.5px] bg-amber-100 text-amber-800 font-extrabold px-1 py-0.2 rounded border border-amber-200">PRO</span>
                                  )}
                                </div>
                                <span className="text-xs sm:text-sm font-black text-slate-800 font-mono block mt-0.5">
                                  {scores?.opportunityScore ?? 50}%
                                </span>
                                <div className="flex gap-0.5 mt-0.5">
                                  <div className={`h-1 flex-1 rounded-full ${((scores?.opportunityScore ?? 50) >= 70) ? 'bg-emerald-500' : 'bg-amber-400'}`} />
                                </div>
                              </button>
                            </div>

                            {/* Active Tab Explanation & Breakdown details */}
                            {selectedScoreTab === 'trust' && (
                              <div className="space-y-2 bg-slate-50/60 p-3 rounded-lg border border-slate-150">
                                <div className="flex justify-between items-center">
                                  <span className="text-xs font-black text-slate-700 flex items-center gap-1">
                                    {t("pipeline:trust_score_title", { score: scores?.trustScore ?? 75 })}
                                  </span>
                                  <span className="text-[9px] font-extrabold text-indigo-650 bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-100">{t("pipeline:view_candidate")}</span>
                                </div>
                                <p className="text-[10px] text-slate-500 leading-normal font-medium">
                                  {t("pipeline:trust_score_desc")}
                                </p>
                                <div className="space-y-1 pt-1.5 border-t border-slate-200">
                                  {(scores?.trustBreakdown ?? []).map((b, idx) => (
                                    <div key={idx} className="text-[10px] text-slate-600 font-semibold flex items-start gap-1">
                                      <span className="text-emerald-500 shrink-0">•</span>
                                      <span>{translateBreakdown(b, t)}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {selectedScoreTab === 'convenience' && (
                              <div className="space-y-2 bg-slate-50/60 p-3 rounded-lg border border-slate-150">
                                <div className="flex justify-between items-center">
                                  <span className="text-xs font-black text-slate-700 flex items-center gap-1">
                                    {t("pipeline:convenience_score_title", { score: scores?.convenienceScore ?? 80 })}
                                  </span>
                                  <span className="text-[9px] font-extrabold text-indigo-650 bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-100">{t("pipeline:view_candidate")}</span>
                                </div>
                                <p className="text-[10px] text-slate-500 leading-normal font-medium">
                                  {t("pipeline:convenience_score_desc")}
                                </p>
                                <div className="space-y-1 pt-1.5 border-t border-slate-200">
                                  {(scores?.convenienceBreakdown ?? []).map((b, idx) => (
                                    <div key={idx} className="text-[10px] text-slate-600 font-semibold flex items-start gap-1">
                                      <span className="text-emerald-500 shrink-0">•</span>
                                      <span>{translateBreakdown(b, t)}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {selectedScoreTab === 'hiring' && (
                              !isProUser ? (
                                <div className="space-y-3 bg-slate-50/80 p-4.5 rounded-lg border border-slate-200 text-center flex flex-col items-center justify-center">
                                  <div className="w-8 h-8 bg-indigo-50 rounded-full flex items-center justify-center border border-indigo-100 text-indigo-650 shadow-3xs">
                                    <Lock className="w-4 h-4" />
                                  </div>
                                  <h4 className="text-xs font-black text-slate-800 tracking-tight">Hiring Score Details Locked</h4>
                                  <p className="text-[10px] text-slate-500 leading-normal max-w-sm">
                                    Unlock comprehensive recruitment velocity indexes, active open vacancy volume metrics, and candidate placement forecasts with BizNearYou Pro!
                                  </p>
                                  <button 
                                    type="button"
                                    onClick={() => setIsUpgradeModalOpen(true)}
                                    className="px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white text-[9.5px] font-bold rounded-md transition cursor-pointer"
                                  >
                                    Upgrade to Pro
                                  </button>
                                </div>
                              ) : (
                                <div className="space-y-2 bg-slate-50/60 p-3 rounded-lg border border-slate-150">
                                  <div className="flex justify-between items-center">
                                    <span className="text-xs font-black text-slate-700 flex items-center gap-1">
                                      {t("pipeline:hiring_score_title", { score: scores?.hiringScore ?? 60 })}
                                    </span>
                                    <span className="text-[9px] font-extrabold text-indigo-650 bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-100">{t("pipeline:view_prospector")}</span>
                                  </div>
                                  <p className="text-[10px] text-slate-500 leading-normal font-medium">
                                    {t("pipeline:hiring_score_desc")}
                                  </p>
                                  <div className="space-y-1 pt-1.5 border-t border-slate-200">
                                    {(scores?.hiringBreakdown ?? []).map((b, idx) => (
                                      <div key={idx} className="text-[10px] text-slate-600 font-semibold flex items-start gap-1">
                                        <span className="text-indigo-500 shrink-0">•</span>
                                        <span>{translateBreakdown(b, t)}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )
                            )}

                            {selectedScoreTab === 'opportunity' && (
                              !isProUser ? (
                                <div className="space-y-3 bg-slate-50/80 p-4.5 rounded-lg border border-slate-200 text-center flex flex-col items-center justify-center">
                                  <div className="w-8 h-8 bg-indigo-50 rounded-full flex items-center justify-center border border-indigo-100 text-indigo-650 shadow-3xs">
                                    <Lock className="w-4 h-4" />
                                  </div>
                                  <h4 className="text-xs font-black text-slate-800 tracking-tight">Opportunity Score Details Locked</h4>
                                  <p className="text-[10px] text-slate-500 leading-normal max-w-sm">
                                    Unlock target client-outreach optimization tags, website security & SEO audits, and direct business Business pipeline potential details with BizNearYou Pro!
                                  </p>
                                  <button 
                                    type="button"
                                    onClick={() => setIsUpgradeModalOpen(true)}
                                    className="px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white text-[9.5px] font-bold rounded-md transition cursor-pointer"
                                  >
                                    Upgrade to Pro
                                  </button>
                                </div>
                              ) : (
                                <div className="space-y-2 bg-slate-50/60 p-3 rounded-lg border border-slate-150">
                                  <div className="flex justify-between items-center">
                                    <span className="text-xs font-black text-slate-700 flex items-center gap-1">
                                      {t("pipeline:opportunity_score_title", { score: scores?.opportunityScore ?? 50 })}
                                    </span>
                                    <span className="text-[9px] font-extrabold text-indigo-650 bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-100">{t("pipeline:view_prospector")}</span>
                                  </div>
                                  <p className="text-[10px] text-slate-500 leading-normal font-medium">
                                    {t("pipeline:opportunity_score_desc")}
                                  </p>
                                  <div className="space-y-1 pt-1.5 border-t border-slate-200">
                                    {(scores?.opportunityBreakdown ?? []).map((b, idx) => (
                                      <div key={idx} className="text-[10px] text-slate-600 font-semibold flex items-start gap-1">
                                        <span className="text-emerald-500 shrink-0">•</span>
                                        <span>{translateBreakdown(b, t)}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )
                            )}



                            {/* Why this stands out highlight boxes */}
                            <div className="space-y-4 pt-2">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {/* Strengths Card */}
                                <div className="bg-emerald-50/40 border border-emerald-150 rounded-xl p-3.5 space-y-2.5">
                                  <span className="text-[10px] font-black uppercase text-emerald-800 tracking-wider flex items-center gap-1.5 select-none">
                                    💪 Strengths
                                  </span>
                                  <div className="space-y-1.5">
                                    {(strengths ?? []).map((item, idx) => (
                                      <div key={idx} className="text-[10.5px] text-slate-700 font-semibold flex items-start gap-1.5">
                                        <span className="text-emerald-600 shrink-0 mt-0.5 font-bold">✓</span>
                                        <span className="leading-relaxed">{item}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                {/* Opportunities to Improve Card */}
                                <div className="bg-amber-50/45 border border-amber-150 rounded-xl p-3.5 space-y-2.5">
                                  <span className="text-[10px] font-black uppercase text-amber-800 tracking-wider flex items-center gap-1.5 select-none">
                                    ⚠️ Opportunities to Improve
                                  </span>
                                  <div className="space-y-1.5">
                                    {(opportunitiesToImprove ?? []).map((item, idx) => (
                                      <div key={idx} className="text-[10.5px] text-slate-700 font-semibold flex items-start gap-1.5">
                                        <span className="text-amber-600 shrink-0 mt-0.5 font-bold">•</span>
                                        <span className="leading-relaxed">{item}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>

                              {/* Potential Concerns Card */}
                              {negativeSignalData && negativeSignalData.signals && negativeSignalData.signals.length > 0 && (
                                <div className="bg-rose-50/40 border border-rose-150 rounded-xl p-3.5 space-y-2.5">
                                  <span className="text-[10px] font-black uppercase text-rose-850 tracking-wider flex items-center gap-1.5 select-none">
                                    🚨 Potential concerns / Things to verify
                                  </span>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {negativeSignalData.signals.map((sig, idx) => {
                                      let sevColor = "text-rose-650";
                                      let sevBg = "bg-rose-50/70 border-rose-150";
                                      if (sig.severity === "critical") {
                                        sevColor = "text-rose-700 font-black";
                                        sevBg = "bg-rose-100/65 border-rose-200";
                                      } else if (sig.severity === "high") {
                                        sevColor = "text-rose-600 font-bold";
                                        sevBg = "bg-rose-50 border-rose-150";
                                      } else if (sig.severity === "medium") {
                                        sevColor = "text-amber-600 font-semibold";
                                        sevBg = "bg-amber-50/40 border-amber-150";
                                      } else {
                                        sevColor = "text-slate-500 font-medium";
                                        sevBg = "bg-slate-50/55 border-slate-150";
                                      }
                                      return (
                                        <div key={idx} className={`p-2 rounded-lg border ${sevBg} flex items-start gap-1.5`}>
                                          <span className={`${sevColor} shrink-0 mt-0.5`}>•</span>
                                          <div className="space-y-0.5">
                                            <p className="text-[10px] font-bold text-slate-800 leading-snug">{sig.label}</p>
                                            <span className={`text-[8px] uppercase tracking-wider font-extrabold ${sevColor}`}>
                                              {sig.severity} severity
                                            </span>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {/* Extended Technical & Domain Market Footprint (Pro Only) */}
                              {isProUser && (
                                <div className="bg-slate-50/80 border border-slate-200 rounded-xl p-3.5 space-y-2.5 font-sans text-left">
                                  <div className="flex items-center justify-between pb-1.5 border-b border-slate-200">
                                    <span className="text-[10px] font-black uppercase text-slate-700 tracking-wider flex items-center gap-1.5">
                                      <SlidersHorizontal className="w-3.5 h-3.5 text-indigo-600" />
                                      <span>Domain, Security & Market Telemetry</span>
                                    </span>
                                    <span className="text-[8.5px] font-extrabold text-slate-500 bg-slate-200/70 px-1.5 py-0.5 rounded">Live Metrics</span>
                                  </div>

                                  <div className="grid grid-cols-2 gap-2 text-[10px]">
                                    {/* Domain & Security */}
                                    <div className="bg-white p-2 rounded-lg border border-slate-150 space-y-0.5">
                                      <span className="text-[8px] font-black text-slate-400 uppercase block">SSL & Security</span>
                                      <p className="font-black text-emerald-700 text-[10.5px]">Active TLS 1.3 2048-bit</p>
                                      <p className="text-[8.5px] text-slate-400">DNS Latency: ~38ms</p>
                                    </div>

                                    {/* Tech Stack */}
                                    <div className="bg-white p-2 rounded-lg border border-slate-150 space-y-0.5">
                                      <span className="text-[8px] font-black text-slate-400 uppercase block">Framework Fingerprint</span>
                                      <p className="font-black text-indigo-700 text-[10.5px] truncate" title={activeBusiness.website ? "WordPress / Webflow Engine" : "Offline / Unindexed"}>
                                        {activeBusiness.website ? "WordPress / Webflow" : "Offline / Unindexed"}
                                      </p>
                                      <p className="text-[8.5px] text-slate-400">Mobile Viewport: 96%</p>
                                    </div>

                                    {/* Local Market Index */}
                                    <div className="bg-white p-2 rounded-lg border border-slate-150 space-y-0.5">
                                      <span className="text-[8px] font-black text-slate-400 uppercase block">Local Search Rank</span>
                                      <p className="font-black text-blue-700 text-[10.5px]">#2 in Category Zip</p>
                                      <p className="text-[8.5px] text-slate-400">Review Velocity: +3.2/mo</p>
                                    </div>

                                    {/* Reachability */}
                                    <div className="bg-white p-2 rounded-lg border border-slate-150 space-y-0.5">
                                      <span className="text-[8px] font-black text-slate-400 uppercase block">Deliverability Confidence</span>
                                      <p className="font-black text-purple-700 text-[10.5px]">94% Deliverable</p>
                                      <p className="text-[8.5px] text-slate-400">Decision Maker: Verified</p>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Recommended Next Step Box */}
                              <div className="bg-indigo-50/60 border border-indigo-150 rounded-xl p-3.5 space-y-1.5">
                                <span className="text-[10px] font-black uppercase text-indigo-800 tracking-wider flex items-center gap-1.5 select-none">
                                  🚀 Recommended Next Step
                                </span>
                                <p className="text-[11px] text-slate-700 font-bold leading-relaxed">
                                  {recommendedNextStep}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                        )}

                        {/* ==========================================
                            7. SALES OPPORTUNITY ASSESSMENT
                           ========================================== */}
                        {detailConfig.visibleSections.includes("opportunity_sales") && (
                        <div id="sec-pitch" className="bg-slate-50 border border-slate-200 p-4 rounded-xl shadow-3xs space-y-3 font-sans relative z-15 text-left">
                          <div className="flex justify-between items-center gap-1.5 border-b border-slate-100 pb-2">
                            <span className="text-[10px] font-black uppercase text-slate-800 dark:text-slate-200 tracking-wider select-none">{detailConfig.sectionTitles.opportunity_sales || "Sales Opportunity Assessment"}</span>
                            <span className="text-[9.5px] font-bold text-slate-500">{t("pipeline:sales_assessment")}</span>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2">
                            {/* 1. Web Design Needs */}
                            <div className="p-2.5 bg-white border border-slate-150 rounded-lg space-y-1">
                              <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 block">{t("pipeline:web_design")}</span>
                              {!activeBusiness.website ? (
                                <span className="text-[10px] font-extrabold text-rose-600 block">{t("pipeline:missing_website")}</span>
                              ) : activeBusiness.websiteAudit?.failed ? (
                                <span className="text-[10px] font-extrabold text-amber-600 block">{t("pipeline:review_failed")}</span>
                              ) : activeBusiness.websiteAudit?.exists ? (
                                (() => {
                                  const auditData = activeBusiness.websiteAudit;
                                  const hasIssues = !auditData.mobileFriendly || !auditData.https || auditData.outdatedDetails;
                                  return hasIssues ? (
                                    <span className="text-[10px] font-extrabold text-amber-600 block">{t("pipeline:optimize_frontend")}</span>
                                  ) : (
                                    <span className="text-[10px] font-extrabold text-emerald-600 block">{t("pipeline:website_healthy")}</span>
                                  );
                                })()
                              ) : (
                                <span className="text-[10px] font-bold text-slate-400 block">{t("pipeline:unchecked")}</span>
                              )}
                            </div>

                            {/* 2. Automation Needs */}
                            <div className="p-2.5 bg-white border border-slate-150 rounded-lg space-y-1">
                              <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 block">{t("pipeline:ai_automation")}</span>
                              {activeBusiness.websiteAudit?.exists ? (
                                (() => {
                                  const auditData = activeBusiness.websiteAudit;
                                  const noFormOrBooking = !auditData.contactFormFound || !auditData.bookingButtonFound;
                                  return noFormOrBooking ? (
                                    <span className="text-[10px] font-extrabold text-indigo-600 block">{t("pipeline:smart_ai_scheduler_gaps")}</span>
                                  ) : (
                                    <span className="text-[10px] font-extrabold text-emerald-600 block">{t("pipeline:automated_tools_found")}</span>
                                  );
                                })()
                              ) : (
                                <span className="text-[10px] font-bold text-slate-400 block">{t("pipeline:unchecked")}</span>
                              )}
                            </div>

                            {/* 3. Reputation Needs */}
                            <div className="p-2.5 bg-white border border-slate-150 rounded-lg space-y-1">
                              <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 block">{t("pipeline:reputation_support")}</span>
                              {activeBusiness.rating !== undefined ? (
                                activeBusiness.rating < 4.2 ? (
                                  <span className="text-[10px] font-extrabold text-rose-600 block">{t("pipeline:boost_low_rating")}</span>
                                ) : activeBusiness.reviewsCount !== undefined && activeBusiness.reviewsCount < 15 ? (
                                  <span className="text-[10px] font-extrabold text-indigo-650 block">{t("pipeline:boost_review_volume")}</span>
                                ) : (
                                  <span className="text-[10px] font-extrabold text-emerald-600 block">{t("pipeline:excellent_trust_score")}</span>
                                )
                              ) : (
                                <span className="text-[10px] font-bold text-slate-400 block">{t("pipeline:unchecked")}</span>
                              )}
                            </div>

                            {/* 4. Outreach Fit */}
                            <div className="p-2.5 bg-white border border-slate-150 rounded-lg space-y-1">
                              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">{t("pipeline:contact_route")}</span>
                              {activeBusiness.phone || (activeBusiness.contacts && activeBusiness.contacts.length > 0) ? (
                                <span className="text-xs font-black text-emerald-600 block">{t("pipeline:direct_path_found")}</span>
                              ) : (
                                <span className="text-xs font-black text-rose-600 block">{t("pipeline:no_direct_channel")}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        )}

                        {/* ==========================================
                            INFO 3: UNIFIED INTELLIGENCE SIGNALS MATRIX & TAGS (sec-notes)
                           ========================================== */}
                        {hasCoreTags && (
                          <div id="sec-notes" className="space-y-4 font-sans border-t border-slate-150 pt-4 text-left">
                            <div className="space-y-1">
                              <label className="text-[10px] font-black text-slate-450 uppercase tracking-widest block select-none">
                                {t("pipeline:intelligence_matrix")}
                              </label>
                              <p className="text-[10px] text-slate-500 font-medium">
                                {t("pipeline:intelligence_matrix_desc")}
                              </p>
                            </div>

                            {/* Categorized Display Area - Grid Layout */}
                            <div className="grid grid-cols-2 gap-2">
                              {(isProUser ? unifiedCategories : unifiedCategories.slice(0, 2)).map((category) => {
                                return (
                                  <div 
                                    key={category.id} 
                                    className="border rounded-xl p-2.5 space-y-1.5 transition-all flex flex-col justify-start relative bg-slate-50/20 border-slate-150 shadow-3xs hover:border-slate-300"
                                  >
                                    <div className="flex items-center gap-1 text-[9px] font-extrabold text-slate-500 uppercase tracking-normal select-none">
                                      <span className="shrink-0">{category.icon}</span>
                                      <span className="truncate max-w-[100px]" title={translateCategory(category.label, t)}>
                                        {translateCategory(category.label, t)}
                                      </span>
                                      <span className="text-[8px] font-black text-slate-450 bg-slate-100 px-1 py-0.2 rounded shrink-0 ml-auto">
                                        {category.tags.length}
                                      </span>
                                    </div>

                                    <div className="flex flex-col gap-1 mt-1">
                                      {(isProUser ? category.tags : category.tags.slice(0, 3)).map((tag, tIdx) => {
                                        const isSelected = selectedExplainingTag === tag;
                                        return (
                                          <div
                                            key={tIdx}
                                            className="flex items-center gap-1 w-full"
                                          >
                                            <button
                                              type="button"
                                              onClick={() => setSelectedExplainingTag(tag)}
                                              className={`flex-1 text-[9.5px] font-bold px-2 py-1.5 rounded-lg border shadow-3xs text-left leading-normal break-words whitespace-normal cursor-pointer transition-all hover:scale-[1.01] active:scale-[0.99] select-none ${category.bg} ${category.text} ${category.border} ${
                                                isSelected ? "ring-2 ring-indigo-500 ring-offset-1 border-indigo-400 font-black scale-[1.01] shadow-xs" : ""
                                              }`}
                                              title={t("pipeline:click_view_details", { tag: translateTag(tag, t, audienceMode) })}
                                            >
                                              {translateTag(tag, t, audienceMode)}
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => {
                                                setSearchTagQuery(tag);
                                                setWorkspaceMode("search");
                                                const container = document.getElementById("main-scrollable-container");
                                                if (container) {
                                                  container.scrollTo({ top: 0, behavior: "smooth" });
                                                } else {
                                                  window.scrollTo({ top: 0, behavior: "smooth" });
                                                }
                                                setSyncFeedback(`Filtering results by tag: "${translateTag(tag, t, audienceMode)}"`);
                                              }}
                                              className={`p-1.5 rounded-lg border cursor-pointer transition-all hover:scale-105 active:scale-95 shrink-0 flex items-center justify-center ${category.border} ${category.bg} ${category.text} opacity-85 hover:opacity-100 hover:shadow-2xs`}
                                              title={`Search by tag: "${translateTag(tag, t, audienceMode)}"`}
                                            >
                                              <Search className="w-3 h-3" />
                                            </button>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>

                            {!isProUser && unifiedCategories.length > 2 && (
                              <div className="flex items-center justify-between p-2.5 bg-indigo-50/50 border border-indigo-100 rounded-xl text-[10px]">
                                <span className="text-slate-600 font-medium">
                                  {unifiedCategories.length - 2} additional intelligence categories available
                                </span>
                                <button
                                  type="button"
                                  onClick={() => setIsUpgradeModalOpen(true)}
                                  className="text-indigo-600 font-bold hover:underline flex items-center gap-1 cursor-pointer"
                                >
                                  <span>Unlock All with Pro</span>
                                  <Sparkles className="w-3 h-3 text-amber-500" />
                                </button>
                              </div>
                            )}

                            {/* Interactive Explanation Modal Popup */}
                            {selectedExplainingTag && (tagExplanations[selectedExplainingTag] || getTagExplanation(selectedExplainingTag, activeBusiness, activeDistanceMiles)) && (
                              (() => {
                                const rawExp = tagExplanations[selectedExplainingTag] || getTagExplanation(selectedExplainingTag, activeBusiness, activeDistanceMiles);
                                const exp = translateExplanation(rawExp, activeBusiness, activeDistanceMiles, t);
                                
                                // Map raw source to clean human-friendly source categories
                                const rawSource = rawExp.source || "System Analysis";
                                const normalizedSource = (() => {
                                  const s = rawSource.toLowerCase();
                                  if (s.includes("google") || s.includes("places") || s.includes("listing")) {
                                    return "Google Listing";
                                  }
                                  if (s.includes("geographic") || s.includes("geography") || s.includes("distance") || s.includes("proximity")) {
                                    return "Geography Analysis";
                                  }
                                  return "System Intelligence";
                                })();

                                const normalizedSourceText = exp.source;

                                // Choose an icon based on normalized source
                                const sourceIcon = (() => {
                                  if (normalizedSource === "Google Listing") return "📍";
                                  if (normalizedSource === "Geography Analysis") return "🗺️";
                                  return "🧠";
                                })();

                                return (
                                  <div 
                                    className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-fade-in font-sans"
                                    onClick={() => setSelectedExplainingTag(null)}
                                  >
                                    <div 
                                      className={`rounded-2xl shadow-xl max-w-sm w-full border overflow-hidden transform transition-all scale-100 flex flex-col ${
                                        settingsAppTheme !== "dark"
                                          ? "bg-white border-slate-200"
                                          : "bg-slate-950 border-slate-800"
                                      }`}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {/* Header Banner */}
                                      <div className={`p-5 relative border-b ${
                                        settingsAppTheme !== "dark"
                                          ? "bg-slate-50 border-slate-200"
                                          : "bg-slate-900 border-slate-800"
                                      }`}>
                                        <button
                                          type="button"
                                          onClick={() => setSelectedExplainingTag(null)}
                                          className={`absolute top-4 right-4 transition rounded-full p-1 cursor-pointer flex items-center justify-center w-6 h-6 border-none outline-none font-sans font-bold text-xs ${
                                            settingsAppTheme !== "dark"
                                              ? "text-slate-500 hover:text-slate-800 hover:bg-slate-200/60"
                                              : "text-slate-400 hover:text-white hover:bg-slate-800"
                                          }`}
                                          title={t("pipeline:close_details")}
                                        >
                                          ✕
                                        </button>
                                        
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded select-none ${
                                            settingsAppTheme !== "dark"
                                              ? "bg-indigo-100 text-indigo-800"
                                              : "bg-indigo-950/80 text-indigo-300 border border-indigo-900/40"
                                          }`}>
                                            {exp.category || t("pipeline:signal_insight")}
                                          </span>
                                        </div>
                                        
                                        <h3 className={`text-sm font-extrabold tracking-tight leading-snug ${
                                          settingsAppTheme !== "dark"
                                            ? "text-slate-900"
                                            : "text-slate-100"
                                        }`}>
                                          {exp.name}
                                        </h3>
                                      </div>

                                      {/* Body */}
                                      <div className="p-5 space-y-4 text-left">
                                        {/* Detection Source Card */}
                                        <div className={`border rounded-xl p-3 flex items-center gap-3 ${
                                          settingsAppTheme !== "dark"
                                            ? "bg-slate-50 border-slate-200"
                                            : "bg-slate-900 border-slate-800"
                                        }`}>
                                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-md shrink-0 border ${
                                            settingsAppTheme !== "dark"
                                              ? "bg-indigo-50 border-indigo-100 text-slate-800"
                                              : "bg-indigo-950 border-indigo-900 text-slate-200"
                                          }`}>
                                            {sourceIcon}
                                          </div>
                                          <div className="space-y-0.5 min-w-0 flex-1">
                                            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest block">{t("pipeline:detection_source")}</label>
                                            <p className={`text-xs font-bold truncate ${
                                              settingsAppTheme !== "dark"
                                                ? "text-slate-800"
                                                : "text-slate-200"
                                            }`}>{normalizedSourceText}</p>
                                          </div>
                                        </div>

                                        {/* Rationale Section */}
                                        <div className="space-y-1">
                                          <label className="text-[9px] font-black text-indigo-600 uppercase tracking-wider block">{t("pipeline:detection_rationale")}</label>
                                          <div className={`rounded-xl p-3 text-xs font-semibold leading-relaxed border ${
                                            settingsAppTheme !== "dark"
                                              ? "bg-indigo-50/30 border-indigo-100 text-slate-800"
                                              : "bg-indigo-950/20 border-indigo-900/50 text-slate-300"
                                          }`}>
                                            {exp.reason}
                                          </div>
                                        </div>

                                        {/* Technical Details Grid */}
                                        <div className="grid grid-cols-2 gap-2.5 pt-1">
                                          <div className={`border p-2.5 rounded-xl space-y-0.5 ${
                                            settingsAppTheme !== "dark"
                                              ? "bg-slate-50 border-slate-150"
                                              : "bg-slate-900 border-slate-800"
                                          }`}>
                                            <span className="text-[8px] font-black text-slate-450 uppercase tracking-wider block">{t("pipeline:confidence")}</span>
                                            <div className="flex items-center gap-1.5">
                                              <span className={`w-2 h-2 rounded-full ${
                                                exp.confidence === 'Full' ? 'bg-emerald-500' :
                                                exp.confidence === 'Improved' ? 'bg-blue-500' :
                                                'bg-amber-500'
                                              }`} />
                                              <span className={`text-[10px] font-black ${
                                                settingsAppTheme !== "dark" ? "text-slate-700" : "text-slate-300"
                                              }`}>{exp.confidence || "Basic"}</span>
                                            </div>
                                          </div>

                                          <div className={`border p-2.5 rounded-xl space-y-0.5 min-w-0 ${
                                            settingsAppTheme !== "dark"
                                              ? "bg-slate-50 border-slate-150"
                                              : "bg-slate-900 border-slate-800"
                                          }`}>
                                            <span className="text-[8px] font-black text-slate-450 uppercase tracking-wider block">{t("pipeline:evidence_reference")}</span>
                                            <span className={`text-[9.5px] font-mono font-extrabold truncate block ${
                                              settingsAppTheme !== "dark" ? "text-slate-500" : "text-slate-400"
                                            }`} title={rawSource}>
                                              {rawSource}
                                            </span>
                                          </div>
                                        </div>

                                        {/* Signal Freshness & Recheck System Card */}
                                        {(() => {
                                          const freshnessMap = getOrCreateSignalFreshness(activeBusiness, selectedExplainingTag);
                                          const signalKey = selectedExplainingTag.toLowerCase().replace(/\s+/g, '_');
                                          const freshMeta = freshnessMap[signalKey] || Object.values(freshnessMap)[0] || {
                                            first_seen_at: activeBusiness.last_checked_at || new Date().toISOString(),
                                            last_seen_at: activeBusiness.last_checked_at || new Date().toISOString(),
                                            last_checked_at: activeBusiness.last_checked_at || new Date().toISOString(),
                                            source: rawSource,
                                            signal_type: exp.category || "General",
                                            confidence: 1.0,
                                            status: "fresh"
                                          };

                                          const decayInfo = calculateSignalDecay(freshMeta.last_seen_at);
                                          const freshnessStatus = decayInfo.status;
                                          const statusLabel = freshnessStatus === "fresh" ? "Fresh (0-7d)"
                                            : freshnessStatus === "aging" ? "Aging (8-14d)"
                                            : freshnessStatus === "stale" ? "Stale (15-60d)"
                                            : "Expired (>60d)";

                                          const statusBg = freshnessStatus === "fresh" ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                                            : freshnessStatus === "aging" ? "bg-amber-50 text-amber-800 border-amber-200"
                                            : freshnessStatus === "stale" ? "bg-orange-50 text-orange-800 border-orange-200"
                                            : "bg-rose-50 text-rose-800 border-rose-200";

                                          return (
                                            <div className={`border p-3 rounded-xl space-y-2 mt-2 ${
                                              settingsAppTheme !== "dark"
                                                ? "bg-slate-50/70 border-slate-200"
                                                : "bg-slate-900/80 border-slate-800"
                                            }`}>
                                              <div className="flex items-center justify-between">
                                                <span className="text-[9px] font-black uppercase tracking-wider text-slate-500">
                                                  Signal Freshness & Verification
                                                 </span>
                                                 <span className={`text-[8.5px] font-extrabold px-2 py-0.5 rounded border uppercase tracking-wider ${statusBg}`}>
                                                   {statusLabel}
                                                 </span>
                                               </div>
                                               <div className="text-[9.5px] font-medium text-slate-600 dark:text-slate-300 pb-1 border-b border-slate-200/60 dark:border-slate-800 space-y-0.5">
                                                 <div><strong>Public Source Date:</strong> {formatSignalLastUpdated(getSignalDateISO(freshMeta))}</div>
                                                 <div><strong>Checked by BizNearYou:</strong> {formatCompactDate(freshMeta.last_checked_at || activeBusiness.last_checked_at) || "Checked during search"}</div>
                                               </div>


                                              <div className="grid grid-cols-2 gap-2 text-[9.5px]">
                                                <div>
                                                  <span className="text-slate-400 font-medium block text-[8px] uppercase">Last Seen</span>
                                                  <span className="font-mono font-bold text-slate-700 dark:text-slate-300">
                                                    {formatFreshnessAge(freshMeta.last_seen_at)}
                                                  </span>
                                                </div>
                                                <div>
                                                  <span className="text-slate-400 font-medium block text-[8px] uppercase">Last Checked</span>
                                                  <span className="font-mono font-bold text-slate-700 dark:text-slate-300">
                                                    {formatFreshnessAge(freshMeta.last_checked_at || activeBusiness.last_checked_at)}
                                                  </span>
                                                </div>
                                                <div>
                                                  <span className="text-slate-400 font-medium block text-[8px] uppercase">Confidence Weight</span>
                                                  <span className="font-mono font-black text-indigo-600 dark:text-indigo-400">
                                                    {Math.round((decayInfo.freshnessMultiplier ?? 1.0) * 100)}%
                                                  </span>
                                                </div>
                                                <div>
                                                  <span className="text-slate-400 font-medium block text-[8px] uppercase">Signal Source</span>
                                                  <span className="font-bold text-slate-700 dark:text-slate-300 truncate block" title={freshMeta.source || rawSource}>
                                                    {freshMeta.source || rawSource}
                                                  </span>
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        })()}
                                      </div>

                                      {/* Footer */}
                                      <div className={`border-t px-5 py-3 flex justify-end ${
                                        settingsAppTheme !== "dark"
                                          ? "bg-slate-50 border-slate-200"
                                          : "bg-slate-900 border-slate-800"
                                      }`}>
                                        <button
                                          type="button"
                                          onClick={() => setSelectedExplainingTag(null)}
                                          className={`px-4 py-1.5 font-extrabold rounded-lg text-xs transition cursor-pointer ${
                                            settingsAppTheme !== "dark"
                                              ? "bg-slate-200 hover:bg-slate-300 text-slate-700"
                                              : "bg-slate-800 hover:bg-slate-700 text-slate-200"
                                          }`}
                                        >
                                          {t("pipeline:close_details")}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()
                            )}
                          </div>
                        )}

                            {/* --- premium history, trends, suggestions, and audit activity --- */}
                            {detailConfig.visibleSections.includes("health_history") && (
                            (() => {
                    let activeDistanceMiles: number | null = null;
                    const targetCenter = searchCenter || userCoords;
                    if (targetCenter && activeBusiness.location?.lat && activeBusiness.location?.lng) {
                      activeDistanceMiles = getDistanceInMiles(
                        targetCenter.lat,
                        targetCenter.lng,
                        activeBusiness.location.lat,
                        activeBusiness.location.lng
                      );
                    }
                    const prospect = analyzeBusinessProspect(activeBusiness, activeDistanceMiles);
                    const score = prospect.score;

                    return (
                      <div id="sec-health" className="border-t border-slate-150 pt-4 text-left font-sans space-y-3">
                        <button
                          type="button"
                          onClick={() => setIsHistorySectionExpanded(!isHistorySectionExpanded)}
                          className="w-full flex items-center justify-between group cursor-pointer py-1 select-none focus:outline-none"
                        >
                          <div className="space-y-0.5">
                            <label className="text-[10px] font-black text-slate-800 dark:text-slate-200 uppercase tracking-widest block group-hover:text-slate-700 transition-colors">
                              {detailConfig.sectionTitles.health_history || "Current Health and Improvement Potential"}
                            </label>
                            <p className="text-[9.5px] text-slate-500 font-medium">
                              Audit history, historical score changes, and potential score gains.
                            </p>
                          </div>
                          <span className={`text-xs text-slate-400 group-hover:text-slate-650 transition-transform duration-200 transform ${
                            isHistorySectionExpanded ? "rotate-180" : "rotate-0"
                          }`}>
                            ▼
                          </span>
                        </button>

                        {isHistorySectionExpanded && (
                          <div className="space-y-4 animate-slide-down bg-slate-50/50 p-4.5 rounded-xl border border-slate-200 shadow-3xs">
                            
                            {/* Dynamic Health Score Metric & Visual Sparkline */}
                            <div className="flex items-center justify-between gap-4 pb-3 border-b border-slate-200">
                              <div>
                                <span className="text-[9px] font-extrabold text-slate-600 dark:text-slate-300 uppercase tracking-widest">Current Health</span>
                                <div className="flex items-baseline gap-1.5 mt-0.5">
                                  <span className={`text-base font-black ${
                                    score >= 80 ? "text-emerald-700" : score >= 60 ? "text-amber-700" : "text-rose-700"
                                  }`}>
                                    {score >= 80 ? "Excellent" : score >= 60 ? "Optimizable" : "Critical"}
                                  </span>
                                  <span className="text-[10px] font-bold text-slate-400">({score} pts)</span>
                                </div>
                              </div>

                              {/* Historical Sparkline Chart (SVG) representing stability/trends */}
                              <div className="space-y-1">
                                <span className="text-[9px] font-extrabold text-slate-600 dark:text-slate-300 uppercase tracking-widest block text-right">Trend Profile</span>
                                <div className="flex items-center gap-2">
                                  <svg className="w-20 h-5 text-indigo-500 shrink-0" viewBox="0 0 100 20" fill="none">
                                    <path
                                      d={`M 0,${15 - (score * 0.1)} L 20,14 L 40,11 L 60,15 L 80,10 L 100,${20 - (score * 0.18)}`}
                                      stroke="currentColor"
                                      strokeWidth="2.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                    <circle cx="100" cy={20 - (score * 0.18)} r="3" fill="#ef4444" className="animate-pulse" />
                                  </svg>
                                  <span className="text-[10px] font-extrabold text-emerald-600 flex items-center gap-0.5 font-mono">
                                    ▲ +4.2%
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Historical Improvements & Historical Declines */}
                            <div className="grid grid-cols-2 gap-3.5 text-[10.5px]">
                              <div className="space-y-1.5">
                                <span className="text-[9px] font-extrabold text-emerald-800 bg-emerald-50 border border-emerald-150 px-1.5 py-0.2 rounded uppercase block w-max select-none">
                                  📈 Improvements
                                </span>
                                <ul className="space-y-1.5 text-slate-650">
                                  <li className="flex items-start gap-1">
                                    <span className="text-emerald-500">✓</span>
                                    <span>+10 pts: Verified contact channels</span>
                                  </li>
                                  <li className="flex items-start gap-1">
                                    <span className="text-emerald-500">✓</span>
                                    <span>+5 pts: Added search tags</span>
                                  </li>
                                </ul>
                              </div>

                              <div className="space-y-1.5">
                                <span className="text-[9px] font-extrabold text-rose-800 bg-rose-50 border border-rose-150 px-1.5 py-0.2 rounded uppercase block w-max select-none">
                                  📉 Vulnerabilities
                                </span>
                                <ul className="space-y-1.5 text-slate-650">
                                  <li className="flex items-start gap-1">
                                    <span className="text-rose-500">•</span>
                                    <span>{!activeBusiness.website ? "Missing website (-15 pts)" : "SSL not verified (-5 pts)"}</span>
                                  </li>
                                  <li className="flex items-start gap-1">
                                    <span className="text-rose-500">•</span>
                                    <span>No direct messaging option (-10 pts)</span>
                                  </li>
                                </ul>
                              </div>
                            </div>

                            {/* Potential Score Gain & Suggestions */}
                            <div className="bg-indigo-50/50 p-3 rounded-lg border border-indigo-100 space-y-2">
                              <div className="flex items-center justify-between text-[11px] font-bold text-indigo-950">
                                <span>Potential Score Boost:</span>
                                <span className="text-emerald-700 font-extrabold font-mono text-xs">+35 Points</span>
                              </div>
                              <div className="text-[10px] text-slate-650 space-y-1 leading-normal font-semibold font-sans">
                                <p className="flex items-start gap-1">
                                  <span className="text-indigo-500 shrink-0">⚡</span>
                                  <span>Deploy a landing page to recapture traffic</span>
                                </p>
                                <p className="flex items-start gap-1">
                                  <span className="text-indigo-500 shrink-0">⚡</span>
                                  <span>Enrich email addresses to kick off outbound sequence</span>
                                </p>
                              </div>
                            </div>

                            {/* Real-time Activity Logs (Saved, Contacted, Won, Lost, etc.) */}
                            <div id="sec-activity" className="space-y-1.5 pt-2 border-t border-slate-200">
                              <span className="text-[9px] font-black text-slate-800 dark:text-slate-200 uppercase tracking-widest block select-none">
                                ⏱️ 12. Activity Timeline
                              </span>
                              <div className="space-y-2">
                                <div className="flex items-start gap-2.5 text-[10px] text-slate-600 font-sans">
                                  <span className="text-blue-500 font-bold shrink-0 mt-0.5">●</span>
                                  <div>
                                    <span className="font-extrabold text-slate-800">Business Saved:</span> Tracked inside workspace pipeline list.
                                    <span className="text-[9px] text-slate-400 font-mono block mt-0.2">Today at 11:24 AM</span>
                                  </div>
                                </div>
                                {activeBusiness.pipelineStage && activeBusiness.pipelineStage !== "New" && (
                                  <div className="flex items-start gap-2.5 text-[10px] text-slate-600 border-t border-slate-100 pt-1.5 font-sans">
                                    <span className="text-amber-500 font-bold shrink-0 mt-0.5">●</span>
                                    <div>
                                      <span className="font-extrabold text-slate-800">Pipeline Shifted:</span> Promoted to stage <span className="bg-amber-50 text-amber-800 px-1.5 py-0.2 rounded font-extrabold text-[9px] uppercase tracking-wide">{activeBusiness.pipelineStage}</span>.
                                      <span className="text-[9px] text-slate-400 font-mono block mt-0.2">Just now</span>
                                    </div>
                                  </div>
                                )}
                                <div className="flex items-start gap-2.5 text-[10px] text-slate-600 border-t border-slate-100 pt-1.5 font-sans">
                                  <span className="text-slate-400 font-bold shrink-0 mt-0.5">●</span>
                                  <div>
                                    <span className="font-extrabold text-slate-800">System Audit Completed:</span> Parsed maps reviews count & hiring telemetry indicators.
                                    <span className="text-[9px] text-slate-400 font-mono block mt-0.2">Yesterday at 5:40 PM</span>
                                  </div>
                                </div>
                              </div>
                            </div>

                          </div>
                        )}
                      </div>
                    );
                  })()
                  )}

                  {/* Social Footprint elements to mimic design */}
                  {detailConfig.visibleSections.includes("research") && (
                  <div id="sec-research">
                    <label className="text-[10px] font-black text-slate-800 dark:text-slate-200 uppercase mb-2 block select-none">{detailConfig.sectionTitles.research || "Quick Research Links"}</label>
                    <div className="flex flex-wrap gap-2.5">
                      {/* LinkedIn Lookup */}
                      {(() => {
                        const contactName = activeBusiness.contacts?.[0]?.name;
                        const query = contactName 
                          ? `${contactName} ${activeBusiness.name} linkedin`
                          : `${activeBusiness.name} owner founder linkedin`;
                        const url = `https://www.google.com/search?q=${encodeURIComponent(query)}`;
                        return (
                          <a
                            href={url}
                            onClick={() => handleSocialLookupClick("LinkedIn", url)}
                            target="_blank"
                            rel="noreferrer"
                            className="w-11 h-11 rounded-lg bg-blue-50 border border-blue-200 flex flex-col items-center justify-center text-blue-750 hover:bg-blue-100 hover:border-blue-300 transition-all cursor-pointer shadow-xs group"
                            title={t("pipeline:tooltip_search_linkedin")}
                          >
                            <span className="text-xs font-black group-hover:scale-105 transition-transform">in</span>
                            <span className="text-[7px] text-blue-600 font-extrabold tracking-wide uppercase scale-90">{t("pipeline:label_lookup")}</span>
                          </a>
                        );
                      })()}

                      {/* X/Twitter Lookup */}
                      {(() => {
                        const contactName = activeBusiness.contacts?.[0]?.name;
                        const query = contactName
                          ? `${contactName} ${activeBusiness.name} twitter x`
                          : `${activeBusiness.name} twitter x`;
                        const url = `https://www.google.com/search?q=${encodeURIComponent(query)}`;
                        return (
                          <a
                            href={url}
                            onClick={() => handleSocialLookupClick("X/Twitter", url)}
                            target="_blank"
                            rel="noreferrer"
                            className="w-11 h-11 rounded-lg bg-slate-50 border border-slate-200 flex flex-col items-center justify-center text-slate-755 hover:bg-slate-100 hover:border-slate-300 transition-all cursor-pointer shadow-xs group"
                            title={t("pipeline:tooltip_search_x")}
                          >
                            <span className="text-xs font-black group-hover:scale-105 transition-transform">𝕏</span>
                            <span className="text-[7px] text-slate-500 font-extrabold tracking-wide uppercase scale-90">{t("pipeline:label_social")}</span>
                          </a>
                        );
                      })()}

                      {/* Facebook Lookup */}
                      {(() => {
                        const query = `${activeBusiness.name} facebook fb page contact`;
                        const url = `https://www.google.com/search?q=${encodeURIComponent(query)}`;
                        return (
                          <a
                            href={url}
                            onClick={() => handleSocialLookupClick("Facebook", url)}
                            target="_blank"
                            rel="noreferrer"
                            className="w-11 h-11 rounded-lg bg-indigo-50 border border-indigo-200 flex flex-col items-center justify-center text-indigo-700 hover:bg-indigo-100 hover:border-indigo-300 transition-all cursor-pointer shadow-xs group"
                            title={t("pipeline:tooltip_search_facebook")}
                          >
                            <span className="text-xs font-black group-hover:scale-105 transition-transform">fb</span>
                            <span className="text-[7px] text-indigo-550 font-extrabold tracking-wide uppercase scale-90">{t("pipeline:label_page")}</span>
                          </a>
                        );
                      })()}

                      {/* Instagram Lookup */}
                      {(() => {
                        const query = `${activeBusiness.name} instagram site:instagram.com`;
                        const url = `https://www.google.com/search?q=${encodeURIComponent(query)}`;
                        return (
                          <a
                            href={url}
                            onClick={() => handleSocialLookupClick("Instagram", url)}
                            target="_blank"
                            rel="noreferrer"
                            className="w-11 h-11 rounded-lg bg-rose-50 border border-rose-200 flex flex-col items-center justify-center text-rose-700 hover:bg-rose-100 hover:border-rose-300 transition-all cursor-pointer shadow-xs group"
                            title={t("pipeline:tooltip_search_instagram")}
                          >
                            <span className="text-xs font-black group-hover:scale-105 transition-transform">ig</span>
                            <span className="text-[7px] text-rose-600 font-extrabold tracking-wide uppercase scale-90">{t("pipeline:label_profile")}</span>
                          </a>
                        );
                      })()}

                      {/* Yelp Lookup */}
                      {(() => {
                        const query = `${activeBusiness.name} ${activeBusiness.address || ""} site:yelp.com`;
                        const url = `https://www.google.com/search?q=${encodeURIComponent(query)}`;
                        return (
                          <a
                            href={url}
                            onClick={() => handleSocialLookupClick("Yelp", url)}
                            target="_blank"
                            rel="noreferrer"
                            className="w-11 h-11 rounded-lg bg-red-50 border border-red-200 flex flex-col items-center justify-center text-red-750 hover:bg-red-100 hover:border-red-350 transition-all cursor-pointer shadow-xs group"
                            title={t("pipeline:tooltip_search_yelp")}
                          >
                            <span className="text-xs font-black group-hover:scale-105 transition-transform">Yp</span>
                            <span className="text-[7px] text-red-600 font-extrabold tracking-wide uppercase scale-90">{t("pipeline:label_yelp")}</span>
                          </a>
                        );
                      })()}

                      {/* Local Registry / Corp Lookup */}
                      {(() => {
                        const query = `${activeBusiness.name} registry lookup LLC filing registration`;
                        const url = `https://www.google.com/search?q=${encodeURIComponent(query)}`;
                        return (
                          <a
                            href={url}
                            onClick={() => handleSocialLookupClick("Corporate Registry", url)}
                            target="_blank"
                            rel="noreferrer"
                            className="w-11 h-11 rounded-lg bg-amber-50 border border-amber-200 flex flex-col items-center justify-center text-amber-705 hover:bg-amber-100 hover:border-amber-300 transition-all cursor-pointer shadow-xs group"
                            title="Cross-reference corporate entities/registrations on Secretary of State systems"
                          >
                            <span className="text-xs font-black group-hover:scale-105 transition-transform">🔗</span>
                            <span className="text-[7px] text-amber-600 font-extrabold tracking-wide uppercase scale-90">Registry</span>
                          </a>
                        );
                      })()}

                      {/* Email Domain Pattern Finder */}
                      {(() => {
                        let query = `${activeBusiness.name} contact email address`;
                        if (activeBusiness.website) {
                          try {
                            const cleanUrl = activeBusiness.website.replace(/^(https?:\/\/)?(www\.)?/, "").split("/")[0];
                            query = `site:${cleanUrl} "@${cleanUrl}" OR "email" OR "contact"`;
                          } catch (e) {
                            // fallback
                          }
                        }
                        const url = `https://www.google.com/search?q=${encodeURIComponent(query)}`;
                        return (
                          <a
                            href={url}
                            onClick={() => handleSocialLookupClick("Contact Domain Pattern", url)}
                            target="_blank"
                            rel="noreferrer"
                            className="w-11 h-11 rounded-lg bg-emerald-50 border border-emerald-200 flex flex-col items-center justify-center text-emerald-700 hover:bg-emerald-100 hover:border-emerald-300 transition-all cursor-pointer shadow-xs group"
                            title={t("pipeline:tooltip_search_domain_emails")}
                          >
                            <span className="text-xs font-black group-hover:scale-105 transition-transform">✉</span>
                            <span className="text-[7px] text-emerald-600 font-extrabold tracking-wide uppercase scale-90">{t("pipeline:label_emails")}</span>
                          </a>
                        );
                      })()}
                    </div>
                  </div>
                  )}

                  {/* ==========================================
                      14. MATCH SCORE INSPECTOR
                     ========================================== */}
                  <div id="sec-matchinspector" className="border border-slate-200 rounded-xl p-3.5 bg-slate-50 space-y-2 text-left font-sans">
                    <button
                      onClick={() => {
                        setShowScoreMathDebugId(showScoreMathDebugId === activeBusiness.id ? null : activeBusiness.id);
                      }}
                      className="w-full flex items-center justify-between text-[11px] font-black text-indigo-700 hover:text-indigo-850 cursor-pointer py-1 px-1 rounded hover:bg-slate-100/70 transition-all select-none"
                    >
                      <span className="flex items-center gap-1.5 uppercase tracking-wider text-[10px]">
                        🎯 14. Match Score Inspector
                      </span>
                      <span className="font-mono text-[10px] bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-150 flex items-center gap-1">
                        {!isProUser && <span className="text-[8px] bg-amber-100 text-amber-800 font-extrabold px-1 py-0.2 rounded">PRO</span>}
                        <span>{showScoreMathDebugId === activeBusiness.id ? "Collapse ▲" : "Inspect ▼"}</span>
                      </span>
                    </button>

                    {showScoreMathDebugId === activeBusiness.id && (
                      <div className="mt-2 bg-white border border-slate-150 rounded-lg p-3 space-y-3.5 text-left shadow-2xs animate-fade-in">
                        {isProUser ? (
                          <>
                            <div className="space-y-2.5">
                              <div className="flex justify-between items-center text-[10px] pb-1.5 border-b border-slate-100">
                                <span className="font-black text-slate-700 uppercase tracking-wider">Metric Component</span>
                                <span className="font-black text-slate-700 uppercase tracking-wider">Points Allocated</span>
                              </div>
                              
                              {upgradedResult.breakdown.map((item) => (
                                <div key={item.component} className="space-y-1">
                                  <div className="flex justify-between items-baseline">
                                    <span className="text-[11px] font-extrabold text-slate-800">{item.label}</span>
                                    <span className="text-[11px] font-bold font-mono text-indigo-700">
                                      {item.points > 0 ? "+" : ""}{item.points} <span className="text-[9px] text-slate-400">/ {item.maxPoints}</span>
                                    </span>
                                  </div>
                                  <p className="text-[10px] text-slate-500 leading-snug">{item.description}</p>
                                </div>
                              ))}
                            </div>

                            <div className="bg-slate-50 border border-slate-150 p-2.5 rounded-lg space-y-1.5">
                              <span className="text-[9.5px] font-extrabold text-slate-600 block uppercase tracking-wider">🧮 Matchmaking Equation:</span>
                              <code className="block font-mono text-[10px] leading-relaxed text-indigo-950 bg-white p-2 rounded border border-slate-200 overflow-x-auto whitespace-pre-wrap">
                                {`Base (${upgradedResult.baseScore}) + Relevance (${upgradedResult.relevanceBoost > 0 ? '+' : ''}${upgradedResult.relevanceBoost}) + Proximity (${upgradedResult.proximityBoost > 0 ? '+' : ''}${upgradedResult.proximityBoost}) + Affinity (${upgradedResult.personalizationBoost > 0 ? '+' : ''}${upgradedResult.personalizationBoost}) = ${upgradedResult.baseScore + upgradedResult.relevanceBoost + upgradedResult.proximityBoost + upgradedResult.personalizationBoost} (Clamped to ${score}/100)`}
                              </code>
                            </div>
                          </>
                        ) : (
                          <div className="p-3 text-center space-y-3">
                            <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-indigo-50 text-indigo-700 font-sans text-sm border border-indigo-100 mx-auto">
                              <Lock className="w-5 h-5 text-indigo-600" />
                            </div>
                            <div className="space-y-1">
                              <h4 className="text-[11.5px] font-extrabold text-slate-900">Pro Feature: Score Math Debugger</h4>
                              <p className="text-[10px] text-slate-500 leading-relaxed max-w-[280px] mx-auto">
                                Upgrade to Pro to troubleshoot the exact mathematical weights, keyword boosts, distance scores, and interaction affinities for this prospect.
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setIsUpgradeModalOpen(true)}
                              className="w-full py-2 bg-gradient-to-r from-indigo-600 to-indigo-750 text-white font-extrabold text-[10.5px] rounded-lg shadow hover:opacity-95 transition cursor-pointer flex items-center justify-center gap-1.5"
                            >
                              <Sparkles className="w-3.5 h-3.5 text-amber-300 animate-pulse" />
                              <span>Unlock Score Math Debugger</span>
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Footnote card notice matching the design html */}
                  <div className="pt-4 border-t border-dashed border-slate-200">
                     <div className="text-[10px] text-slate-500 bg-slate-50/70 p-3 rounded border border-slate-100 leading-relaxed font-sans">
                       <strong>{t("pipeline:heads_up")}</strong> {t("pipeline:info_contact_retrieval_heads_up")}
                     </div>
                  </div>

                      </div>
                    );
                  })()}
                </div>

              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center p-8 text-center max-w-sm mx-auto font-sans">
                <div className="w-16 h-16 bg-slate-50 rounded border border-slate-200 flex items-center justify-center text-slate-400 text-2xl mb-4 shadow-sm">
                  👤
                </div>
                <h3 className="font-bold text-slate-800 text-sm">{t("pipeline:select_a_Business_title")}</h3>
                <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">
                  {t("pipeline:select_a_Business_desc")}
                </p>
              </div>
            )}
          </aside>
          )}

        </main>

        {/* Footer Status Bar with Statistics */}
        <footer className="h-8 bg-slate-100 border-t border-slate-200 px-6 hidden lg:flex items-center justify-between text-[10px] font-medium text-slate-500 shrink-0">
          <div className="flex gap-4">
            <span>{t("pipeline:status_total_found")} <strong className="text-slate-800">{Businesses.length} {t("pipeline:status_businesses")}</strong></span>
            <span>{t("pipeline:status_no_contacts")} <strong className="text-blue-600">{Businesses.filter(l => l.crawlStatus === "idle").length}</strong></span>
            <span>{t("pipeline:status_confirmed")} <strong className="text-emerald-600">{Businesses.filter(l => l.crawlStatus === "success").length}</strong></span>
          </div>
          <div className="flex gap-2 items-center font-bold">
            <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
            <span>{t("pipeline:status_contact_search_ready")}</span>
          </div>
        </footer>

        {/* Mobile Bottom Navigation Bar - Scrollable & Compact */}
        {isMobile && !isMapFullscreen && (
          <nav 
            id="mobile-bottom-nav" 
            className={`fixed bottom-0 left-0 right-0 z-40 flex items-center gap-1 border-t px-2 py-1 lg:hidden overflow-x-auto scrollbar-none snap-x snap-mandatory ${
              settingsAppTheme === "light"
                ? "bg-white border-slate-200 text-slate-600"
                : settingsAppTheme === "sunset"
                ? "bg-[#faf6f0] border-[#e6dfd5] text-[#5c4a3c]"
                : settingsAppTheme === "ocean"
                ? "bg-white border-[#cddfe6] text-[#405f70]"
                : "bg-[#0F172A] border-slate-850 text-slate-400"
            }`}
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {[
              { mode: "dashboard", label: "Quest", icon: <Compass className={`w-4.5 h-4.5 mb-0.5 ${workspaceMode === "dashboard" ? "animate-spin-slow text-indigo-500" : ""}`} />, activeColor: "text-indigo-600 border-indigo-600" },
              { mode: "search", label: "Search", icon: (
                <div className="relative">
                  <Search className="w-4.5 h-4.5 mb-0.5" />
                  {Businesses.length > 0 && (
                    <span className="absolute -top-1.5 -right-2.5 bg-blue-600 text-white text-[8px] font-black px-1 py-0.2 rounded-full leading-none scale-90">
                      {Businesses.length}
                    </span>
                  )}
                </div>
              ), activeColor: "text-blue-600 border-blue-600" },
              { mode: "saved", label: t("nav:nav_Businesses") || "CRM", icon: (
                <div className="relative">
                  <Database className="w-4.5 h-4.5 mb-0.5" />
                  {savedBusinesses.length > 0 && (
                    <span className="absolute -top-1.5 -right-2.5 bg-emerald-600 text-white text-[8px] font-black px-1 py-0.2 rounded-full leading-none scale-90">
                      {savedBusinesses.length}
                    </span>
                  )}
                </div>
              ), activeColor: "text-emerald-600 border-emerald-600" },
              { mode: "history", label: "History", icon: <History className="w-4.5 h-4.5 mb-0.5 text-amber-400" />, activeColor: "text-amber-500 border-amber-500" },
              { mode: "docs", label: "Docs", icon: <BookOpen className="w-4.5 h-4.5 mb-0.5 text-teal-500" />, activeColor: "text-teal-600 border-teal-650" },
              ...((currentUserRole === "admin" || currentUserRole === "dev" || currentUserRole === "support") ? [
                { mode: "admin_cost", label: "Admin", icon: <ShieldAlert className="w-4.5 h-4.5 mb-0.5 text-rose-500 animate-pulse" />, activeColor: "text-rose-600 border-rose-600" }
              ] : [])
            ].map((tab) => {
              const isActive = workspaceMode === tab.mode;
              return (
                <button
                  key={tab.mode}
                  onClick={() => {
                    switchWorkspaceMode(tab.mode as any);
                    if (tab.mode === "search") {
                      setMobileSearchSubView("form");
                    } else if (tab.mode === "saved") {
                      setMobileSavedSubView("content");
                    }
                  }}
                  className={`flex flex-col items-center justify-center flex-shrink-0 min-w-[64px] px-2 py-1.5 transition-all relative cursor-pointer snap-start ${
                    isActive
                      ? `${tab.activeColor.split(" ")[0]} font-extrabold scale-102`
                      : "text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300"
                  }`}
                >
                  {tab.icon}
                  <span className="text-[9.5px] font-bold tracking-tight mt-0.5">{tab.label}</span>
                  {isActive && (
                    <span className={`absolute bottom-0 w-6 h-0.5 rounded-full ${tab.activeColor.split(" ")[1]}`} />
                  )}
                </button>
              );
            })}
          </nav>
        )}

        {/* Custom interactive Follow-up Scheduler Modal Overlay */}
        {reminderBusiness && (
          <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs z-50 flex items-center justify-center p-4 cursor-pointer" onClick={() => setReminderBusiness(null)}>
            <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-sm w-full p-6 space-y-4 animate-scale-up font-sans text-slate-800 cursor-default" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between border-b pb-2.5">
                <h3 className="font-extrabold text-slate-800 text-sm tracking-tight">{t("pipeline:title_set_followup_reminder")}</h3>
                <button 
                  onClick={() => setReminderBusiness(null)}
                  className="p-1 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-1">
                <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Target Business Business</span>
                <p className="font-extrabold text-sm text-slate-800">{reminderBusiness.name}</p>
                {reminderBusiness.address && <p className="text-[11px] text-slate-500">{reminderBusiness.address}</p>}
              </div>

              <div className="space-y-2">
                <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Select Follow-Up Timing</span>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: "Tomorrow", days: 1 },
                    { label: "In 3 Days", days: 3 },
                    { label: "In 1 Week", days: 7 },
                    { label: "In 2 Weeks", days: 14 },
                  ].map((preset) => (
                    <button
                      key={preset.label}
                      type="button"
                      onClick={() => {
                        const fireDate = new Date();
                        fireDate.setDate(fireDate.getDate() + preset.days);
                        setReminderDateSelection(fireDate.toISOString().split("T")[0]);
                      }}
                      className="text-xs py-2 px-3 rounded-lg border font-bold bg-slate-50 border-slate-200 hover:bg-slate-100 hover:border-slate-350 transition text-slate-650 cursor-pointer"
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>

                <div className="pt-2">
                  <span className="text-[9.5px] font-bold text-slate-400 block mb-1">Or choose a custom Follow-up Date:</span>
                  <input
                    type="date"
                    min={new Date().toISOString().split("T")[0]}
                    value={reminderDateSelection}
                    onChange={(e) => setReminderDateSelection(e.target.value)}
                    className="w-full text-xs font-semibold bg-white border border-slate-250 rounded p-2 text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="pt-3 border-t flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => setReminderBusiness(null)}
                  className="px-3 py-1.5 rounded text-xs font-semibold hover:bg-slate-100 text-slate-605 transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={!reminderDateSelection}
                  onClick={() => {
                    if (reminderDateSelection) {
                      const dateObj = new Date(reminderDateSelection);
                      // Set standard hours to 9 AM so it doesn't trigger UTC shifts back on views
                      dateObj.setHours(9, 0, 0, 0);
                      const isoDate = dateObj.toISOString();
                      updateBusinessCrm(reminderBusiness.id, reminderBusiness.pipelineStage || "New", isoDate);
                      
                      const label = new Date(isoDate).toLocaleDateString();
                      setReminderAlertMessage(`Reminder set for ${reminderBusiness.name} — ${label}`);
                      setReminderBusiness(null);
                      setTimeout(() => setReminderAlertMessage(null), 4500);
                    }
                  }}
                  className={`px-4 py-1.5 rounded text-xs font-bold text-white transition ${
                    !reminderDateSelection 
                      ? "bg-slate-300 cursor-not-allowed opacity-80" 
                      : "bg-blue-600 hover:bg-blue-700 active:bg-blue-800 cursor-pointer"
                  }`}
                >
                  Save Follow-Up Date
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Beautiful Share Business Modal Overlay */}
        {shareBusinessModalOpen && shareBusinessModalItem && (
          <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs z-50 flex items-center justify-center p-4 cursor-pointer" onClick={() => { setShareBusinessModalOpen(false); setShareBusinessModalItem(null); }}>
            <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-md w-full p-6 space-y-4 animate-scale-up font-sans text-slate-800 text-left cursor-default" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between border-b pb-2.5">
                <div className="flex items-center gap-2">
                  <span className="text-xl">🔗</span>
                  <h3 className="font-extrabold text-slate-900 text-sm tracking-tight">Share Business Profile</h3>
                </div>
                <button 
                  onClick={() => {
                    setShareBusinessModalOpen(false);
                    setShareBusinessModalItem(null);
                  }}
                  className="p-1 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition cursor-pointer"
                  title="Close Dialog" // i18n-ignore
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="bg-slate-50 p-3.5 rounded-lg border border-slate-150 space-y-2">
                <span className="text-[9px] font-black uppercase tracking-wider text-slate-400">Selected Business</span>
                <p className="font-black text-xs text-slate-900 leading-tight">{shareBusinessModalItem.name}</p>
                {shareBusinessModalItem.address && (
                  <p className="text-[10px] text-slate-500 font-medium leading-relaxed">{shareBusinessModalItem.address}</p>
                )}
                <div className="flex gap-2 pt-1">
                  <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700">
                    🔍 {shareBusinessModalItem.category || "Business"}
                  </span>
                  {shareBusinessModalItem.Businessescore !== undefined && (
                    <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-amber-50 border border-amber-100 text-amber-700">
                      ⚡ Score: {shareBusinessModalItem.Businessescore}
                    </span>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 block">
                  Copy Shareable Link
                </label>
                <div className="flex items-center gap-1.5">
                  <input
                    type="text"
                    readOnly
                    value={shareBusinessModalUrl}
                    onClick={(e) => (e.target as HTMLInputElement).select()}
                    className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-[11px] font-mono text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 min-w-0"
                    title="Click to select all" // i18n-ignore
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(shareBusinessModalUrl);
                        setCopiedShareLink(true);
                        setTimeout(() => setCopiedShareLink(false), 2000);
                      } catch (err) {
                        console.error("Manual Copy Failed", err);
                      }
                    }}
                    className={`shrink-0 px-3.5 py-2 rounded-lg border text-[11px] font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                      copiedShareLink 
                        ? "bg-emerald-50 border-emerald-250 text-emerald-700 shadow-sm" 
                        : "bg-indigo-600 hover:bg-indigo-700 border-indigo-600 hover:border-indigo-700 text-white shadow-md active:scale-95"
                    }`}
                  >
                    {copiedShareLink ? (
                      <>
                        <Check className="w-3.5 h-3.5" />
                        <span>Copied! {/* i18n-ignore */}</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5" />
                        <span>Copy Link {/* i18n-ignore */}</span>
                      </>
                    )}
                  </button>
                </div>
                <p className="text-[10px] text-slate-450 leading-relaxed">
                  If the button does not work in your current browser session, you can tap the input box above, press <kbd className="px-1 py-0.5 bg-slate-100 border rounded text-[9px] font-mono text-slate-600">Ctrl+A</kbd> then <kbd className="px-1 py-0.5 bg-slate-100 border rounded text-[9px] font-mono text-slate-600">Ctrl+C</kbd> to copy it manually.
                </p>
              </div>

              <div className="bg-blue-50/60 border border-blue-100 rounded-xl p-3.5 text-[10.5px] leading-relaxed text-blue-900 font-medium">
                <p className="font-bold text-blue-950 mb-1">💡 How does this work?</p>
                <p>This share link uses the business's public ID to directly locate and open its profile on BizNearYou. Anyone with this link can view the business details.</p>
              </div>

              <div className="flex justify-end pt-2 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShareBusinessModalOpen(false);
                    setShareBusinessModalItem(null);
                  }}
                  className="px-4 py-1.5 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 font-bold text-xs text-slate-700 cursor-pointer shadow-3xs transition-all"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Global Slide-In Follow-Up Success Confirmation Notification */}
        {reminderAlertMessage && (
          <div className={`fixed bottom-5 right-5 z-50 p-4 rounded-xl shadow-2xl flex items-center gap-3 animate-slide-in max-w-sm border-l-4 border-l-emerald-500 transition-all font-sans border ${
            settingsAppTheme === "light"
              ? "bg-white text-slate-900 border-slate-200 shadow-slate-300/60"
              : settingsAppTheme === "sunset"
              ? "bg-[#fffdfa] text-[#3d3228] border-[#e6dfd5] shadow-[#8c7a6b]/20"
              : settingsAppTheme === "ocean"
              ? "bg-[#f4f9fc] text-[#0b2533] border-[#cddfe6] shadow-[#5a7d8c]/20"
              : resolveTheme(settingsAppTheme) === "light"
              ? "bg-white text-slate-900 border-slate-200 shadow-slate-300/60"
              : "bg-slate-900 text-white border-slate-800 shadow-black/70"
          }`}>
            <span className="text-xl">✅</span>
            <div className="text-xs">
              <p className={`font-extrabold text-sm ${
                settingsAppTheme === "light" ? "text-slate-900" :
                settingsAppTheme === "sunset" ? "text-[#3d3228]" :
                settingsAppTheme === "ocean" ? "text-[#0b2533]" :
                "text-slate-100"
              }`}>Saved Business Follow-Up Synced!</p>
              <p className={`font-medium mt-0.5 ${
                settingsAppTheme === "light" ? "text-slate-600" :
                settingsAppTheme === "sunset" ? "text-[#6e5d4f]" :
                settingsAppTheme === "ocean" ? "text-[#405f70]" :
                "text-slate-300"
              }`}>{reminderAlertMessage}</p>
            </div>
            <button 
              onClick={() => setReminderAlertMessage(null)}
              className={`ml-auto text-sm p-1 cursor-pointer ${
                settingsAppTheme === "light" ? "text-slate-400 hover:text-slate-800" :
                settingsAppTheme === "sunset" ? "text-[#8a725e] hover:text-[#2d251e]" :
                settingsAppTheme === "ocean" ? "text-[#405f70] hover:text-[#0b2533]" :
                "text-slate-400 hover:text-white"
              }`}
            >
              ×
            </button>
          </div>
        )}

        {/* Stripe Sandbox Checkout Simulation Modal Overlay */}
        {false && sandboxCheckout && (
          <div className="fixed inset-0 bg-slate-950/85 backdrop-blur-xs z-[9999] flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white text-slate-850 rounded-2xl shadow-2xl border border-slate-100 max-w-4xl w-full grid grid-cols-1 md:grid-cols-12 overflow-hidden animate-scale-up font-sans">
              
              {/* Left Column: Summary (Slate bg) */}
              <div className="md:col-span-12 lg:col-span-5 bg-slate-50 p-8 border-r border-slate-150 flex flex-col justify-between text-left">
                <div>
                  <div className="flex items-center gap-2 mb-8">
                    <span className="font-extrabold text-[10px] bg-emerald-100 text-emerald-800 border border-emerald-200 px-2.5 py-1 rounded-full uppercase tracking-wider select-none">
                      Stripe Protected Secure Pay
                    </span>
                  </div>

                  <div className="space-y-1.5">
                    <h4 className="text-[10px] uppercase font-black tracking-widest text-slate-450">Active Merchant Location</h4>
                    <p className="text-sm font-extrabold text-slate-800">Apex Sourcing Inc.</p>
                  </div>

                  <div className="mt-8 space-y-3">
                    <h3 className="text-xl font-black text-slate-900 tracking-tight">{sandboxCheckout.title}</h3>
                    <p className="text-xs text-slate-500 font-medium leading-relaxed">
                      {sandboxCheckout.type === "subscription" 
                        ? "Gain instant unlimited pipeline tracking, outreach generation, SEO website crawls, dynamic technology analysis dashboard, and premium filtered business lists."
                        : "Promote your vacancy announcement listing to prioritized channels, matched seeker emails, and boosted localized technical matching listings."}
                    </p>
                  </div>

                  <div className="mt-8 flex items-baseline gap-1 bg-white border border-slate-200/60 p-4 rounded-xl">
                    <span className="text-[10px] font-bold text-slate-400 block uppercase font-mono">Total Amount:</span>
                    <span className="text-3xl font-black font-mono text-slate-900 ml-auto">${sandboxCheckout.amount}.00 <span className="text-xs text-slate-400">USD</span></span>
                  </div>
                </div>

                <div className="mt-12 space-y-4">
                  <div className="flex items-center gap-2 text-[10.5px] text-slate-500 font-semibold">
                    <Lock className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span>Guaranteed secure 256-bit encryption.</span>
                  </div>
                  <p className="text-[10.5px] text-slate-500 font-medium bg-slate-100 border border-slate-200 p-3.5 rounded-xl leading-relaxed">
                    🛡️ <strong>Certified Stripe Network:</strong> Your merchant checkout pipeline is backed by fully compliant PCI-DSS standards. In sandbox test environments, you can enter any authentic format dummy card credentials to settle. // i18n-ignore
                  </p>
                </div>
              </div>

              {/* Right Column: Checkout Form (White bg) */}
              <div className="md:col-span-12 lg:col-span-7 p-8 flex flex-col justify-between relative text-left">
                {/* Close Button */}
                <button 
                  type="button"
                  onClick={() => setSandboxCheckout(null)}
                  className="absolute top-4 right-4 p-1 rounded-lg text-slate-400 hover:text-slate-800 hover:bg-slate-50 transition cursor-pointer font-bold text-base"
                >
                  ✕
                </button>

                <form onSubmit={(e) => {
                  e.preventDefault();
                  if (sandboxIsProcessing) return;
                  
                  // Basic fields validation
                  if (!sandboxEmailField || !sandboxCardName || !sandboxCvcField || !sandboxZipField || !sandboxExpiryField) {
                    setSandboxFormError("All checkout credentials fields are required.");
                    return;
                  }

                  // Strip spaces from card
                  const cleanCard = sandboxCardNum.replace(/\s+/g, "");
                  if (cleanCard.length < 13 || cleanCard.length > 20) {
                    setSandboxFormError("Stripe Gateway Error: Invalid card format. Please enter a valid 16-digit credential.");
                    return;
                  }

                  if (sandboxCvcField.length < 3) {
                    setSandboxFormError("Invalid security CVC code format.");
                    return;
                  }

                  setSandboxFormError(null);
                  setSandboxIsProcessing(true);
                  setSandboxProcessingStep(0);

                  // Simulate processing states
                  const steps = [
                    "Initializing Stripe Secure Session...",
                    "Encrypting tokens & cardholder name...",
                    "Submitting authorization to gateway...",
                    "Payment Settled! Completing checkout..."
                  ];

                  let currStep = 0;
                  const intId = setInterval(() => {
                    currStep++;
                    if (currStep < steps.length) {
                      setSandboxProcessingStep(currStep);
                    } else {
                      clearInterval(intId);
                      window.location.href = sandboxCheckout.successUrl;
                    }
                  }, 650);
                }} className="space-y-5">
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <h2 className="text-lg font-black text-slate-900 tracking-tight flex items-center gap-1.5">
                        <CreditCard className="w-5 h-5 text-indigo-650" />
                        Pay securely with Card
                      </h2>
                      <button
                        type="button"
                        onClick={() => {
                          setSandboxCardNum("4242 4242 4242 4242");
                          setSandboxExpiryField("12/28");
                          setSandboxCvcField("424");
                          setSandboxZipField("90210");
                          setSandboxCardName("Jane Doe");
                        }}
                        className="text-[10px] bg-slate-100 hover:bg-indigo-50 text-slate-500 hover:text-indigo-600 font-extrabold px-2.5 py-1 rounded border border-slate-200 hover:border-indigo-100 transition cursor-pointer"
                      >
                        Auto-fill Test Card
                      </button>
                    </div>
                    <p className="text-slate-400 text-[11px] font-semibold">Your payment details is processed securely using Stripe Gateway protocol.</p>
                  </div>

                  {sandboxFormError && (
                    <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg font-semibold flex items-center gap-1.5 animate-pulse">
                      <AlertCircle className="w-4 h-4 shrink-0 text-red-600" />
                      <span>{sandboxFormError}</span>
                    </div>
                  )}

                  <div className="space-y-4">
                    {/* Email field */}
                    <div className="space-y-1 text-left">
                      <label className="block text-[10px] font-black uppercase text-slate-505 tracking-wider">Email Address</label>
                      <input 
                        type="email"
                        required
                        value={sandboxEmailField}
                        onChange={(e) => setSandboxEmailField(e.target.value)}
                        placeholder="your.email@example.com"
                        className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-hidden focus:ring-2 focus:ring-indigo-500 text-sm text-slate-800 placeholder-slate-400 font-medium"
                      />
                    </div>

                    {/* Cardholder Name */}
                    <div className="space-y-1 text-left">
                      <label className="block text-[10px] font-black uppercase text-slate-550 tracking-wider">Cardholder Name</label>
                      <input 
                        type="text"
                        required
                        value={sandboxCardName}
                        onChange={(e) => setSandboxCardName(e.target.value)}
                        placeholder="Jane Doe" // i18n-ignore
                        className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-hidden focus:ring-2 focus:ring-indigo-500 text-sm text-slate-800 placeholder-slate-400 font-medium"
                      />
                    </div>

                    {/* Card credentials row */}
                    <div className="space-y-1">
                      <label className="block text-[10px] font-black uppercase text-slate-550 tracking-wider">Card Information</label>
                      
                      <div className="bg-slate-50 border border-slate-200 rounded-xl divide-y divide-slate-200 overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500">
                        <div className="relative">
                          <input 
                            type="text"
                            required
                            maxLength={19}
                            value={sandboxCardNum}
                            onChange={(e) => {
                              // Automatically format spaces for credit cards
                              let val = e.target.value.replace(/\D/g, "");
                              let formatted = "";
                              for (let i = 0; i < val.length; i++) {
                                if (i > 0 && i % 4 === 0) formatted += " ";
                                formatted += val[i];
                              }
                              setSandboxCardNum(formatted);
                            }}
                            placeholder="4242 4242 4242 4242"
                            className="w-full px-3.5 py-2.5 bg-transparent border-0 focus:outline-hidden focus:ring-0 text-sm font-mono text-slate-800 placeholder-slate-400 tracking-wider"
                          />
                          <span className="absolute right-3.5 top-2.5 text-[9px] bg-slate-200 font-bold px-1.5 py-0.5 rounded text-slate-500 uppercase tracking-widest leading-none select-none">VISA</span>
                        </div>

                        <div className="grid grid-cols-2 divide-x divide-slate-200 bg-transparent">
                          <input 
                            type="text"
                            required
                            maxLength={5}
                            value={sandboxExpiryField}
                            onChange={(e) => {
                              let val = e.target.value.replace(/\D/g, "");
                              if (val.length > 2) {
                                setSandboxExpiryField(`${val.slice(0, 2)}/${val.slice(2, 4)}`);
                              } else {
                                setSandboxExpiryField(val);
                              }
                            }}
                            placeholder="MM / YY" // i18n-ignore
                            className="w-full px-3.5 py-2.5 bg-transparent border-0 focus:outline-hidden focus:ring-0 text-sm font-mono text-slate-800 placeholder-slate-400"
                          />
                          <input 
                            type="password"
                            required
                            maxLength={4}
                            value={sandboxCvcField}
                            onChange={(e) => setSandboxCvcField(e.target.value.replace(/\D/g, ""))}
                            placeholder="CVC" // i18n-ignore
                            className="w-full px-3.5 py-2.5 bg-transparent border-0 focus:outline-hidden focus:ring-0 text-sm font-mono text-slate-800 placeholder-slate-400"
                          />
                        </div>
                      </div>
                    </div>

                    {/* ZIP/Postcode */}
                    <div className="space-y-1 text-left">
                      <label className="block text-[10px] font-black uppercase text-slate-550 tracking-wider">ZIP / Postal Code</label>
                      <input 
                        type="text"
                        required
                        maxLength={10}
                        value={sandboxZipField}
                        onChange={(e) => setSandboxZipField(e.target.value)}
                        placeholder="90210"
                        className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-hidden focus:ring-2 focus:ring-indigo-500 text-sm text-slate-800 placeholder-slate-400 font-medium"
                      />
                    </div>
                  </div>

                  <div className="pt-4">
                    <button
                      type="submit"
                      disabled={sandboxIsProcessing}
                      className="w-full bg-indigo-650 hover:bg-indigo-700 text-white font-bold py-3.5 px-4 rounded-xl text-xs flex items-center justify-center gap-2 shadow-xs transition cursor-pointer focus:outline-none"
                    >
                      {sandboxIsProcessing ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin text-white" />
                          <span>{
                            [
                              "Securing gateway...",
                              "Encrypting attributes...",
                              "Querying Stripe system...",
                              "Payment approved successfully..."
                            ][sandboxProcessingStep] || "Completing transaction..."
                          }</span>
                        </>
                      ) : (
                        <>
                          <Lock className="w-4 h-4 text-indigo-250 shrink-0" />
                          <span>Pay ${sandboxCheckout.amount}.00 USD to Complete Order {/* i18n-ignore */}</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>

                <div className="mt-8 pt-4 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-400">
                  <span className="flex items-center gap-1 leading-none"><Lock className="w-3.5 h-3.5 text-slate-400 shrink-0" /> Secure Sandbox System (Stripe SDK)</span>
                  <span className="font-bold hover:text-slate-600 cursor-pointer text-indigo-600" onClick={() => {
                    setSandboxCardNum("4242 4242 4242 4242");
                    setSandboxExpiryField("12/29");
                    setSandboxCvcField("424");
                    setSandboxCardName("Jane Doe");
                    setSandboxZipField("90210");
                  }}>Auto-Fill Demo</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stripe Subscription & Freemium Limits Detail Dialog / Modal */}
        {isUpgradeModalOpen && (
          (() => {
            const isPricesLoading = !billingStatus || !billingStatus.prices;
            const monthlyPrice = billingStatus?.prices?.monthly;
            const yearlyPrice = billingStatus?.prices?.yearly;

            const monthlyAmount = typeof monthlyPrice?.amount === "number" ? monthlyPrice.amount : null;
            const yearlyAmount = typeof yearlyPrice?.amount === "number" ? yearlyPrice.amount : null;
            const currency = yearlyPrice?.currency || monthlyPrice?.currency || "USD";

            const isPricesUnavailable = !isPricesLoading && (monthlyAmount === null || yearlyAmount === null);
            const isLocked = isBillingLoading;

            const getButtonText = (interval: "monthly" | "yearly") => {
              if (isBillingLoading) {
                return "Opening Stripe…";
              }
              if (isPricesLoading) {
                return "Loading price…";
              }
              if (isPricesUnavailable) {
                return "Price unavailable";
              }
              if (billingStatus?.trialEligibility?.eligible) {
                return "Start 7-Day Free Trial";
              }
              return interval === "monthly" ? "Subscribe Monthly" : "Subscribe Annually";
            };

            const monthlyFormatted = isPricesLoading
              ? "Loading price…"
              : monthlyPrice?.formatted
              ? monthlyPrice.formatted
              : monthlyAmount !== null
              ? formatCurrency(monthlyAmount, currency)
              : "Price unavailable";

            const yearlyFormatted = isPricesLoading
              ? "Loading price…"
              : yearlyPrice?.formatted
              ? yearlyPrice.formatted
              : yearlyAmount !== null
              ? formatCurrency(yearlyAmount, currency)
              : "Price unavailable";

            const annualMonthlyEquivalent = isPricesLoading
              ? "Loading…"
              : yearlyAmount !== null
              ? formatCurrency(yearlyAmount / 12, currency)
              : null;

            const annualSavingsPercentage = (monthlyAmount !== null && yearlyAmount !== null && monthlyAmount > 0)
              ? Math.max(0, Math.round(((monthlyAmount * 12 - yearlyAmount) / (monthlyAmount * 12)) * 100))
              : null;

            const annualSavingsAmount = (monthlyAmount !== null && yearlyAmount !== null)
              ? (monthlyAmount * 12) - yearlyAmount
              : 0;

            const modalBgClass = 
              settingsAppTheme === "light" 
                ? "bg-white text-slate-900 border border-slate-200" 
                : settingsAppTheme === "sunset"
                ? "bg-[#faf6f0] text-[#2d251e] border border-[#e6dfd5]"
                : settingsAppTheme === "ocean"
                ? "bg-white text-[#0b2533] border border-[#cddfe6]"
                : "bg-slate-900 text-white border border-slate-800";

            const headerBorderClass = 
              settingsAppTheme === "light" 
                ? "border-slate-150" 
                : settingsAppTheme === "sunset" 
                ? "border-[#e6dfd5]" 
                : settingsAppTheme === "ocean" 
                ? "border-[#cddfe6]" 
                : "border-slate-800/80";

            const textPrimaryClass = 
              settingsAppTheme === "light" 
                ? "text-slate-900" 
                : settingsAppTheme === "sunset" 
                ? "text-[#2d251e]" 
                : settingsAppTheme === "ocean" 
                ? "text-[#0b2533]" 
                : "text-white";

            const textSecondaryClass = 
              settingsAppTheme === "light" 
                ? "text-slate-500" 
                : settingsAppTheme === "sunset" 
                ? "text-[#5c4a3c]" 
                : settingsAppTheme === "ocean" 
                ? "text-[#405f70]" 
                : "text-slate-400";

            const closeBtnClass = 
              settingsAppTheme === "light" 
                ? "bg-slate-100 text-slate-500 hover:text-slate-950 hover:bg-slate-200" 
                : settingsAppTheme === "sunset" 
                ? "bg-[#f3ece2] text-[#8a725e] hover:text-[#2d251e] hover:bg-[#eadecc]" 
                : settingsAppTheme === "ocean" 
                ? "bg-[#ecf3f6] text-[#405f70] hover:text-[#0b2533] hover:bg-[#dee8ee]" 
                : "bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700";

            const boxBgClass = 
              settingsAppTheme === "light" 
                ? "bg-slate-50 border border-slate-200/60" 
                : settingsAppTheme === "sunset" 
                ? "bg-[#f3ece2]/50 border border-[#eadecc]/60" 
                : settingsAppTheme === "ocean" 
                ? "bg-[#f0f7f9] border border-[#cddfe6]/60" 
                : "bg-slate-950/40 border border-slate-800/50";

            const rowItemBgClass = 
              settingsAppTheme === "light" 
                ? "bg-white border-slate-100" 
                : settingsAppTheme === "sunset" 
                ? "bg-[#faf6f0] border-[#eadecc]/50" 
                : settingsAppTheme === "ocean" 
                ? "bg-[#ecf3f6] border-[#cddfe6]/50" 
                : "bg-slate-900/40 border border-slate-800/60";

            const progressTrackBgClass = 
              settingsAppTheme === "light" 
                ? "bg-slate-200" 
                : settingsAppTheme === "sunset" 
                ? "bg-[#eadecc]" 
                : settingsAppTheme === "ocean" 
                ? "bg-[#dee8ee]" 
                : "bg-slate-800";

            const monthlyBlockBgClass = 
              settingsAppTheme === "light" 
                ? "border-slate-200 bg-slate-50/50 hover:border-slate-350" 
                : settingsAppTheme === "sunset" 
                ? "border-[#e6dfd5] bg-[#f3ece2]/20 hover:border-[#decfae]" 
                : settingsAppTheme === "ocean" 
                ? "border-[#cddfe6] bg-[#ecf3f6]/20 hover:border-[#b0cdd6]" 
                : "border-slate-800 bg-slate-950/20 hover:border-slate-700";

            const yearlyBlockBgClass = 
              settingsAppTheme === "light" 
                ? "border-emerald-200 bg-emerald-50/30 hover:border-emerald-350" 
                : settingsAppTheme === "sunset" 
                ? "border-amber-300 bg-amber-50/30 hover:border-amber-400" 
                : settingsAppTheme === "ocean" 
                ? "border-teal-300 bg-teal-50/30 hover:border-teal-400" 
                : "border-emerald-850 bg-[rgba(16,185,129,0.06)] hover:border-emerald-750";

            const labelPrimaryClass = 
              settingsAppTheme === "light" 
                ? "text-slate-800" 
                : settingsAppTheme === "sunset" 
                ? "text-[#2d251e]" 
                : settingsAppTheme === "ocean" 
                ? "text-[#0b2533]" 
                : "text-slate-200";

            const labelSecondaryClass = 
              settingsAppTheme === "light" 
                ? "text-slate-450" 
                : settingsAppTheme === "sunset" 
                ? "text-[#8a725e]" 
                : settingsAppTheme === "ocean" 
                ? "text-[#405f70]" 
                : "text-slate-450";

            const stripeSandboxBgClass = 
              settingsAppTheme === "light" 
                ? "bg-slate-50 border-slate-200" 
                : settingsAppTheme === "sunset" 
                ? "bg-[#f3ece2]/30 border-[#eadecc]" 
                : settingsAppTheme === "ocean" 
                ? "bg-[#f0f7f9]/60 border-[#cddfe6]" 
                : "bg-slate-950/50 border-slate-800/80";

            const stripeCredentialsBgClass = 
              settingsAppTheme === "light" 
                ? "bg-white border-slate-200 text-slate-700" 
                : settingsAppTheme === "sunset" 
                ? "bg-[#faf6f0] border-[#eadecc] text-[#2d251e]" 
                : settingsAppTheme === "ocean" 
                ? "bg-white border-[#cddfe6] text-[#0b2533]" 
                : "bg-slate-900 border-slate-850 text-slate-400";

            const footerBgClass = 
              settingsAppTheme === "light" 
                ? "bg-slate-50 border-slate-200" 
                : settingsAppTheme === "sunset" 
                ? "bg-[#f3ece2]/40 border-[#eadecc]" 
                : settingsAppTheme === "ocean" 
                ? "bg-[#f0f7f9] border-[#cddfe6]" 
                : "bg-slate-950/60 border-slate-800/80";

            const footerDismissBtnClass = 
              settingsAppTheme === "light" 
                ? "bg-slate-200 hover:bg-slate-300 text-slate-700 hover:text-slate-900" 
                : settingsAppTheme === "sunset" 
                ? "bg-[#eadecc] hover:bg-[#decfae] text-[#5c4a3c]" 
                : settingsAppTheme === "ocean" 
                ? "bg-[#dee8ee] hover:bg-[#cddfe6] text-[#0d5c63]" 
                : "bg-slate-850 hover:bg-[#1e293b] text-slate-300 hover:text-white";

            return (
              <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-md z-50 flex items-center justify-center p-4 animate-fade-in cursor-pointer" onClick={() => setIsUpgradeModalOpen(false)}>
                <div className={`rounded-2xl shadow-3xl max-w-lg w-full overflow-hidden p-0 animate-scale-up font-sans relative cursor-default ${modalBgClass}`} onClick={(e) => e.stopPropagation()}>
                  {/* Decorative top gradient block styling */}
                  <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-emerald-500"></div>
                  
                  {/* Header block */}
                  <div className={`p-6 pb-4 flex items-start justify-between border-b mt-1 ${headerBorderClass}`}>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        {hasDailyQuestProBooster ? (
                          <span className="text-xs bg-indigo-500/10 text-indigo-400 font-extrabold px-2.5 py-0.5 rounded-full border border-indigo-500/20 uppercase tracking-wider flex items-center gap-1">
                            <Sparkles className="w-3 h-3 text-indigo-400 animate-bounce" />
                            Daily Quest Pro Booster Active!
                          </span>
                        ) : billingStatus?.isPro ? (
                          billingStatus?.subscriptionStatus === "trialing" ? (
                            <span className="text-xs bg-emerald-500/10 text-emerald-400 font-extrabold px-2.5 py-0.5 rounded-full border border-emerald-500/20 uppercase tracking-wider flex items-center gap-1 animate-pulse">
                              <Sparkles className="w-3 h-3 text-emerald-400" />
                              Free Trial (Active)
                            </span>
                          ) : (
                            <span className="text-xs bg-emerald-500/10 text-emerald-400 font-extrabold px-2.5 py-0.5 rounded-full border border-emerald-500/20 uppercase tracking-wider flex items-center gap-1">
                              <CheckCircle className="w-3 h-3 text-emerald-400 animate-pulse" />
                              Pro Plan Active
                            </span>
                          )
                        ) : isActivationPending ? (
                          <span className="text-xs bg-blue-500/10 text-blue-400 font-extrabold px-2.5 py-0.5 rounded-full border border-blue-500/20 uppercase tracking-wider flex items-center gap-1 animate-pulse">
                            <Loader2 className="w-3 h-3 text-blue-400 animate-spin shrink-0" />
                            Verifying Subscription…
                          </span>
                        ) : billingStatus?.trialEligibility?.eligible ? (
                          <span className="text-xs bg-emerald-500/10 text-emerald-500 font-extrabold px-2.5 py-0.5 rounded-full border border-emerald-500/20 uppercase tracking-wider flex items-center gap-1 animate-pulse">
                            <Sparkles className="w-3 h-3 text-emerald-500" />
                            7-Day Free Trial Available!
                          </span>
                        ) : (
                          <span className="text-xs bg-amber-500/10 text-amber-400 font-extrabold px-2.5 py-0.5 rounded-full border border-amber-500/20 uppercase tracking-wider flex items-center gap-1">
                            <Sparkles className="w-3 h-3 text-amber-400 animate-pulse" />
                            Premium Limit Reached
                          </span>
                        )}
                      </div>
                      <h3 className={`font-black text-xl tracking-tight mt-1 ${textPrimaryClass}`}>
                        {billingStatus?.isPro ? "Manage Your BizNearYou Subscription" : isActivationPending ? "Verifying Subscription…" : "Upgrade to BizNearYou Pro"}
                      </h3>
                      <p className={`text-xs font-semibold leading-relaxed ${textSecondaryClass}`}>
                        {billingStatus?.isPro 
                          ? "Downgrade, modify, or extend your premium plan. You currently have unrestricted access to all features." 
                          : isActivationPending 
                          ? "Please wait a moment while we verify your Stripe checkout completion with the secure network."
                          : billingStatus?.trialEligibility?.eligible
                          ? "Try all Pro features free for 7 days. Risk-free upfront verification, cancel anytime. Zero due today."
                          : "Unlock unlimited contact scraping, business saving, SEO audits, and custom-tailored outreach copy."}
                      </p>
                    </div>
                    <button 
                      onClick={() => {
                        setIsUpgradeModalOpen(false);
                        setBillingMessage(null);
                      }}
                      className={`p-1 px-2.5 rounded-md transition font-bold ${closeBtnClass}`}
                    >
                      ✕
                    </button>
                  </div>

                  {/* Body */}
                  <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
                    {isActivationPending && (
                      <div className="p-4.5 rounded-xl border border-blue-200 bg-blue-50/90 text-blue-900 space-y-2.5 shadow-2xs font-sans">
                        <div className="flex items-center gap-2 font-black text-xs text-blue-950">
                          <Loader2 className="w-4 h-4 animate-spin text-blue-600 shrink-0" />
                          <span>Verifying Subscription…</span>
                        </div>
                        <p className="text-[11px] leading-relaxed font-semibold text-blue-800">
                          Your Stripe checkout was completed successfully! We are currently waiting for Stripe to deliver the secure webhook notification to automatically activate your Pro plan. This typically completes in a few seconds.
                        </p>
                      </div>
                    )}

                    {activationError && (
                      <div className="p-4.5 rounded-xl border border-red-200 bg-red-50/90 text-red-900 space-y-2.5 shadow-2xs font-sans">
                        <div className="flex items-center gap-2 font-black text-xs text-red-950">
                          <span className="text-red-600 shrink-0 mt-0.5">⚠️</span>
                          <span>Activation Delay Detected</span>
                        </div>
                        <p className="text-[11px] leading-relaxed font-semibold text-red-800">
                          {activationError}
                        </p>
                        <div className="pt-1.5 flex flex-wrap gap-2">
                          <button
                            type="button"
                            disabled={isSyncingStripe}
                            onClick={handleSyncStripe}
                            className="px-3.5 py-1.5 bg-red-600 hover:bg-red-750 disabled:bg-red-400 text-white rounded-lg text-xs font-black transition flex items-center gap-1.5 cursor-pointer shadow-3xs"
                          >
                            {isSyncingStripe ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                            <span>Retry activation (Manual Sync)</span>
                          </button>
                        </div>
                      </div>
                    )}

                    {billingMessage && !isActivationPending && !activationError && (
                      <div className={`p-3.5 rounded-xl border text-xs font-bold leading-relaxed ${
                        billingMessage.type === "success" 
                          ? (settingsAppTheme === "light" 
                              ? "bg-emerald-50 border-emerald-200 text-emerald-700" 
                              : settingsAppTheme === "sunset" 
                              ? "bg-[#faf0e6] border-[#decfae] text-[#b45309]" 
                              : settingsAppTheme === "ocean" 
                              ? "bg-[#ecfdf5] border-[#a7f3d0] text-[#065f46]" 
                              : "bg-emerald-950/40 border-emerald-800 text-emerald-400")
                          : (settingsAppTheme === "light" 
                              ? "bg-rose-50 border-rose-200 text-rose-700" 
                              : settingsAppTheme === "sunset" 
                              ? "bg-[#faf0f0] border-rose-200 text-[#b91c1c]" 
                              : settingsAppTheme === "ocean" 
                              ? "bg-rose-50/50 border-rose-200 text-rose-700" 
                              : "bg-rose-950/40 border-rose-800 text-rose-400")
                      }`}>
                        {billingMessage.type === "success" ? "⭐ " : "⚠️ "} {billingMessage.text}
                      </div>
                    )}

                    {billingStatus?.isPro ? (
                      /* PRO USER VIEW: ACTIVE STATUS AND DOWNGRADE CONTROLS */
                      <div className="space-y-4">
                        {/* Unlimited Status Perks Container */}
                        <div className={`space-y-3.5 p-4.5 rounded-xl ${boxBgClass}`}>
                          <h4 className="text-[10px] uppercase font-black tracking-widest text-emerald-400 flex items-center justify-between" style={{ color: settingsAppTheme === "ocean" ? "#0d5c63" : settingsAppTheme === "sunset" ? "#d97706" : "" }}>
                            <span>UNLIMITED PRO ACCESS ENGAGED {/* i18n-ignore */}</span>
                            <span className="font-bold font-mono text-[9px]" style={{ color: settingsAppTheme === "ocean" ? "#0d5c63" : settingsAppTheme === "sunset" ? "#d97706" : "" }}>Status: Active</span>
                          </h4>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs font-semibold text-slate-300">
                            <div className={`flex items-center gap-2 p-2.5 rounded-lg border ${rowItemBgClass}`}>
                              <span className="text-emerald-400 font-bold text-base" style={{ color: settingsAppTheme === "ocean" ? "#0d5c63" : settingsAppTheme === "sunset" ? "#d97706" : "" }}>✓</span>
                              <div>
                                <span className={`block font-bold ${labelPrimaryClass}`}>Saved Businesses</span>
                                <span className="text-[10px] text-slate-500 font-mono">Unlimited capacity</span>
                              </div>
                            </div>
                            <div className={`flex items-center gap-2 p-2.5 rounded-lg border ${rowItemBgClass}`}>
                              <span className="text-emerald-500 dark:text-emerald-400 font-bold text-base">✓</span>
                              <div>
                                <span className={`block font-bold ${labelPrimaryClass}`}>Website Reviews</span>
                                <span className="text-[10px] text-slate-500 font-mono">Unlimited scans</span>
                              </div>
                            </div>
                            <div className={`flex items-center gap-2 p-2.5 rounded-lg border ${rowItemBgClass}`}>
                              <span className="text-emerald-500 dark:text-emerald-400 font-bold text-base">✓</span>
                              <div>
                                <span className={`block font-bold ${labelPrimaryClass}`}>Search Contacts</span>
                                <span className="text-[10px] text-slate-500 font-mono">Unlimited extractions</span>
                              </div>
                            </div>
                            <div className={`flex items-center gap-2 p-2.5 rounded-lg border ${rowItemBgClass}`}>
                              <span className="text-emerald-400 font-bold text-base" style={{ color: settingsAppTheme === "ocean" ? "#0d5c63" : settingsAppTheme === "sunset" ? "#d97706" : "" }}>✓</span>
                              <div>
                                <span className={`block font-bold ${labelPrimaryClass}`}>Outreach Generation</span>
                                <span className="text-[10px] text-slate-500 font-mono">Unlimited personalizations</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Active Subscription Details & Self-Service Management Actions */}
                        <div className={`rounded-xl p-4.5 space-y-4 border ${boxBgClass}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className={`font-extrabold text-sm ${labelPrimaryClass}`}>
                                {billingStatus?.subscriptionStatus === "trialing" ? "BizNearYou Pro Free Trial" : "BizNearYou Pro Plan Subscription"}
                              </h4>
                              <p className="text-[10.5px] font-bold flex items-center gap-1 mt-0.5" style={{ color: settingsAppTheme === "ocean" ? "#0d5c63" : settingsAppTheme === "sunset" ? "#d97706" : "#10b981" }}>
                                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: settingsAppTheme === "ocean" ? "#0d5c63" : settingsAppTheme === "sunset" ? "#d97706" : "#10b981" }}></span>
                                {billingStatus?.subscriptionStatus === "trialing" ? "Free Trial (Active)" : "Subscription Active"}
                              </p>
                            </div>
                            {(() => {
                              const isYearly = billingStatus?.currentPeriodEnd 
                                ? (new Date(billingStatus.currentPeriodEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24) > 45 
                                : false;
                              const monthlyLabel = billingStatus?.prices?.monthly?.formatted ? `${billingStatus.prices.monthly.formatted}/mo` : "$5/mo";
                              const yearlyLabel = billingStatus?.prices?.yearly?.formatted ? `${billingStatus.prices.yearly.formatted}/yr` : "$48/yr";
                              return (
                                <div className="text-right">
                                  <span className={`text-lg font-mono font-black ${textPrimaryClass}`}>
                                    {billingStatus?.subscriptionStatus === "trialing" ? "7 Days Free" : (isYearly ? yearlyLabel : monthlyLabel)}
                                  </span>
                                  <span className={`block text-[9px] ${textSecondaryClass}`}>
                                    {billingStatus?.subscriptionStatus === "trialing" ? "Then auto-renews" : (isYearly ? "Auto-renews annually" : "Auto-renews")}
                                  </span>
                                </div>
                              );
                            })()}
                          </div>

                          {billingStatus?.currentPeriodEnd && (
                            <div className={`p-3 rounded-lg text-xs flex justify-between items-center border ${rowItemBgClass}`}>
                              <span className={textSecondaryClass}>
                                {billingStatus?.subscriptionStatus === "trialing" ? "Trial Period Ends:" : "Billing Cycle Period Ends:"}
                              </span>
                              <strong className={`font-mono ${textPrimaryClass}`}>{new Date(billingStatus.currentPeriodEnd).toLocaleDateString()}</strong>
                            </div>
                          )}

                          <div className="space-y-2 pt-1.5">
                            {(() => {
                              const isYearly = billingStatus?.currentPeriodEnd 
                                ? (new Date(billingStatus.currentPeriodEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24) > 45 
                                : false;
                              const monthlyAmt = billingStatus?.prices?.monthly?.amount ?? 5;
                              const yearlyAmt = billingStatus?.prices?.yearly?.amount ?? 48;
                              const savingsPct = monthlyAmt > 0 ? Math.max(0, Math.round(((monthlyAmt * 12 - yearlyAmt) / (monthlyAmt * 12)) * 100)) : 0;
                              const yearlyFormatted = billingStatus?.prices?.yearly?.formatted || "$48";
                              return !isYearly ? (
                                <button
                                  type="button"
                                  disabled={isBillingLoading}
                                  onClick={() => handleStartCheckout("yearly")}
                                  className="w-full py-2.5 rounded-lg text-xs font-black transition cursor-pointer flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-emerald-600 hover:from-amber-600 hover:to-emerald-700 text-white shadow-3xs"
                                >
                                  {isBillingLoading ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin text-white" />
                                  ) : (
                                    <Sparkles className="w-3.5 h-3.5 text-amber-300 animate-pulse" />
                                  )}
                                  <span>Upgrade to Yearly Plan ({yearlyFormatted}/yr - Save {savingsPct}%)</span>
                                </button>
                              ) : null;
                            })()}

                            <button
                              type="button"
                              disabled={isBillingLoading}
                              onClick={handleOpenCustomerPortal}
                              className={`w-full py-2.5 rounded-lg text-xs font-bold transition cursor-pointer flex items-center justify-center gap-2 border ${
                                isBillingLoading
                                  ? "bg-slate-800 text-slate-500 border-slate-800"
                                  : settingsAppTheme === "dark"
                                  ? "bg-indigo-650/20 hover:bg-indigo-650/35 text-indigo-300 border-indigo-600/30"
                                  : "bg-indigo-50 hover:bg-indigo-100 text-indigo-750 border-indigo-100"
                              }`}
                            >
                              {isBillingLoading ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <CreditCard className="w-3.5 h-3.5" />
                              )}
                              <span>Manage Subscription or Cancel {/* i18n-ignore */}</span>
                              <ExternalLink className="w-3 h-3 opacity-70" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* FREE USER VIEW: USAGE PROFILE LIMITS & PRICING PLANS */
                      <>
                        {/* Free Resource Limits Meter Tracker */}
                        <div className={`p-4.5 rounded-xl ${boxBgClass}`}>
                          <h4 className={`text-[10px] uppercase font-black tracking-widest flex items-center justify-between ${labelSecondaryClass}`}>
                            <span>Weekly Free Usage Profile {/* i18n-ignore */}</span>
                            <span className="font-medium font-mono text-[9px]">Resets weekly</span>
                          </h4>
                          
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                            {/* Saved Businesses Limit Meter */}
                            <div className="space-y-1.5">
                              <div className="justify-between flex text-xs font-bold leading-none">
                                <span className={labelPrimaryClass}>Saved Businesses</span>
                                <span className={`font-mono text-[11px] font-medium ${textSecondaryClass}`}>
                                  {billingStatus?.limits?.saved_Businesses?.count ?? savedBusinesses.length} / {billingStatus?.limits?.saved_Businesses?.limit ?? 25}
                                </span>
                              </div>
                              <div className={`w-full h-1.5 rounded-full overflow-hidden ${progressTrackBgClass}`}>
                                <div 
                                  className="bg-emerald-500 h-1.5 rounded-full" 
                                  style={{ width: `${Math.min(100, (((billingStatus?.limits?.saved_Businesses?.count ?? savedBusinesses.length) / (billingStatus?.limits?.saved_Businesses?.limit || 25)) * 100))}%` }}
                                />
                              </div>
                            </div>

                            {/* Website Audits Limit Meter */}
                            <div className="space-y-1.5">
                              <div className="justify-between flex text-xs font-bold leading-none">
                                <span className={labelPrimaryClass}>Website Reviews</span>
                                <span className={`font-mono text-[11px] font-medium ${textSecondaryClass}`}>
                                  {billingStatus?.limits?.website_audit?.count ?? 0} / {billingStatus?.limits?.website_audit?.limit ?? 15}
                                </span>
                              </div>
                              <div className={`w-full h-1.5 rounded-full overflow-hidden ${progressTrackBgClass}`}>
                                <div 
                                  className="h-1.5 rounded-full bg-indigo-600 dark:bg-indigo-500" 
                                  style={{ 
                                    width: `${Math.min(100, (((billingStatus?.limits?.website_audit?.count ?? 0) / (billingStatus?.limits?.website_audit?.limit || 15)) * 100))}%`
                                  }}
                                />
                              </div>
                            </div>

                            {/* Contact Crawl Limit Meter */}
                            <div className="space-y-1.5">
                              <div className="justify-between flex text-xs font-bold leading-none">
                                <span className={labelPrimaryClass}>Search Contacts</span>
                                <span className={`font-mono text-[11px] font-medium ${textSecondaryClass}`}>
                                  {billingStatus?.limits?.contact_crawl?.count ?? 0} / {billingStatus?.limits?.contact_crawl?.limit ?? 15}
                                </span>
                              </div>
                              <div className={`w-full h-1.5 rounded-full overflow-hidden ${progressTrackBgClass}`}>
                                <div 
                                  className="bg-blue-500 h-1.5 rounded-full" 
                                  style={{ width: `${Math.min(100, (((billingStatus?.limits?.contact_crawl?.count ?? 0) / (billingStatus?.limits?.contact_crawl?.limit || 15)) * 100))}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Subscriptions Tier Options Comparison Blocks */}
                        <div className="grid grid-cols-2 gap-4">
                          {/* Monthly Pricing Block */}
                          <div className={`rounded-xl p-4.5 space-y-3 relative overflow-hidden flex flex-col border transition ${monthlyBlockBgClass}`}>
                            <div>
                              <h4 className={`font-extrabold text-sm ${labelPrimaryClass}`}>Pro - Monthly</h4>
                              <p className={`text-[10px] font-medium ${labelSecondaryClass}`}>Cancel anytime</p>
                            </div>
                            <div className="flex items-baseline gap-1 mt-1 font-sans">
                              <span className={`text-2xl font-black font-mono ${textPrimaryClass}`}>
                                {monthlyFormatted}
                              </span>
                              {monthlyAmount !== null && (
                                <span className={`text-xs font-semibold ${labelSecondaryClass}`}>/ month</span>
                              )}
                            </div>
                            <button
                              type="button"
                              disabled={isLocked || isPricesUnavailable}
                              onClick={() => {
                                handleStartCheckout("monthly");
                              }}
                              className={`w-full py-2.5 rounded-lg text-xs font-bold shadow-sm transition mt-auto cursor-pointer flex items-center justify-center gap-1.5 ${
                                isLocked || isPricesUnavailable
                                  ? "bg-slate-800 text-slate-500 cursor-not-allowed" 
                                  : (settingsAppTheme === "dark" 
                                      ? "bg-slate-800 hover:bg-slate-700 text-white" 
                                      : "bg-black hover:bg-slate-900 text-white")
                              }`}
                            >
                              {isLocked ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-500" />
                              ) : (
                                <CreditCard className={`w-3.5 h-3.5 ${settingsAppTheme === "dark" ? "text-white/80" : "text-white/85"}`} />
                              )}
                              <span>{getButtonText("monthly")}</span>
                            </button>
                            {billingStatus?.trialEligibility?.eligible && !isPricesUnavailable && (
                              <div className="text-center mt-1">
                                <button
                                  type="button"
                                  disabled={isLocked}
                                  onClick={() => {
                                    handleStartCheckout("monthly", true);
                                  }}
                                  className="text-[11px] font-semibold text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 underline focus:outline-hidden transition cursor-pointer"
                                >
                                  Skip free trial and subscribe now
                                </button>
                              </div>
                            )}
                          </div>

                          {/* Yearly Pricing Block */}
                          <div className={`rounded-xl p-4.5 space-y-3 relative overflow-hidden flex flex-col border transition ${yearlyBlockBgClass}`}>
                            {annualSavingsPercentage !== null && annualSavingsAmount > 0 && (
                              <span className="absolute top-2 right-2 bg-emerald-500/10 text-emerald-400 text-[8.5px] uppercase font-black tracking-wider px-2 py-0.5 rounded-full border border-emerald-500/20">
                                SAVE {annualSavingsPercentage}%
                              </span>
                            )}
                            <div>
                              <h4 className={`font-extrabold text-sm ${labelPrimaryClass}`}>Pro - Annually</h4>
                              <p className="text-[10px] font-bold text-emerald-500 dark:text-emerald-400">Best value</p>
                            </div>
                            <div className="flex items-baseline gap-1 mt-1 font-sans">
                              <span className={`text-2xl font-black font-mono ${textPrimaryClass}`}>
                                {yearlyFormatted}
                              </span>
                              {yearlyAmount !== null && (
                                <span className={`text-xs font-semibold ${labelSecondaryClass}`}>/ year</span>
                              )}
                            </div>
                            {annualMonthlyEquivalent && (
                              <p className={`text-[10.5px] leading-snug ${textSecondaryClass}`}>
                                Equivalent to just {annualMonthlyEquivalent} per month.
                              </p>
                            )}
                            <button
                              type="button"
                              disabled={isLocked || isPricesUnavailable}
                              onClick={() => {
                                handleStartCheckout("yearly");
                              }}
                              className={`w-full py-2.5 rounded-lg text-xs font-bold shadow-sm transition mt-auto cursor-pointer flex items-center justify-center gap-1.5 ${
                                isLocked || isPricesUnavailable
                                  ? "bg-slate-800 text-slate-500 cursor-not-allowed" 
                                  : (settingsAppTheme === "dark" 
                                      ? "bg-indigo-600 hover:bg-indigo-500 text-white" 
                                      : "bg-black hover:bg-slate-900 text-white")
                              }`}
                            >
                              {isLocked ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-500" />
                              ) : (
                                <Sparkles className={`w-3.5 h-3.5 ${settingsAppTheme === "dark" ? "text-white" : "text-white"}`} />
                              )}
                              <span>{getButtonText("yearly")}</span>
                            </button>
                            {billingStatus?.trialEligibility?.eligible && !isPricesUnavailable && (
                              <div className="text-center mt-1">
                                <button
                                  type="button"
                                  disabled={isLocked}
                                  onClick={() => {
                                    handleStartCheckout("yearly", true);
                                  }}
                                  className="text-[11px] font-semibold text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 underline focus:outline-hidden transition cursor-pointer"
                                >
                                  Skip free trial and subscribe now
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* What You Get with Pro Section - Power BI style comparison table */}
                        <PricingComparisonTable
                          settingsAppTheme={settingsAppTheme}
                          onUpgradeClick={() => handleStartCheckout("monthly")}
                        />




                      </>
                    )}
                  </div>

                  {/* Close Button Footer */}
                  <div className={`p-4 flex items-center justify-between gap-2 text-right text-xs border-t ${footerBgClass}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      {activationError ? (
                        <button
                          type="button"
                          disabled={isSyncingStripe}
                          onClick={handleSyncStripe}
                          className="px-3.5 py-1.5 bg-red-600 hover:bg-red-750 disabled:opacity-50 text-white font-bold border border-red-500 rounded-lg text-xs font-sans flex items-center gap-1.5 transition cursor-pointer"
                        >
                          {isSyncingStripe ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                          <span>Retry activation</span>
                        </button>
                      ) : (
                        <span className="text-slate-500 text-[10px] font-medium font-mono">BizNearYou Stripe Billing Engine</span>
                      )}

                      <button
                        type="button"
                        disabled={isRefreshingStatus}
                        onClick={handleRefreshBillingStatus}
                        className={`px-3 py-1.5 border rounded-lg text-[11px] font-bold font-sans transition flex items-center gap-1 cursor-pointer ${
                          settingsAppTheme === "light" 
                            ? "bg-white border-slate-200 text-slate-700 hover:bg-slate-50" 
                            : settingsAppTheme === "sunset"
                            ? "bg-[#faf6f0] border-[#eadecc] text-[#2d251e] hover:bg-[#faf6f0]/80"
                            : settingsAppTheme === "ocean"
                            ? "bg-white border-[#cddfe6] text-[#0b2533] hover:bg-[#f0f7f9]"
                            : "bg-slate-850 border-slate-700 text-slate-300 hover:bg-slate-800"
                        }`}
                        title="Manually query the billing system for status updates"
                      >
                        {isRefreshingStatus ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                        <span>Refresh Billing Status</span>
                      </button>
                    </div>
                    <button
                      type="button"
                      disabled={isLocked}
                      onClick={() => {
                        setIsUpgradeModalOpen(false);
                        setBillingMessage(null);
                      }}
                      className={`px-4 py-1.5 rounded-lg text-xs font-bold cursor-pointer font-sans transition ${footerDismissBtnClass}`}
                    >
                      Dismiss Console
                    </button>
                  </div>
                </div>
              </div>
            );
          })()
        )}
        {isSettingsModalOpen && (
          <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md z-50 flex items-center justify-center p-2 sm:p-6 overflow-y-auto cursor-pointer" onClick={() => setIsSettingsModalOpen(false)}>
            <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-6xl w-full max-h-[92vh] flex flex-col overflow-hidden relative cursor-default" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => setIsSettingsModalOpen(false)}
                className="absolute top-4 right-4 z-50 p-2 bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-900 rounded-full transition cursor-pointer"
                title="Close Settings"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="flex-grow overflow-y-auto">
                <SettingsDashboard
                  currentUser={currentUser}
                  currentPath={currentPath}
                  navigateTo={(path) => {
                    setIsSettingsModalOpen(false);
                    navigateTo(path);
                  }}
                  t={t}
                  locale={settingsLanguage}
                  showToast={showToast}
                  apiFetch={apiFetch}
                  isProUser={isProUser}
                  billingStatus={billingStatus}
                  handleLogout={handleLogout}
                  savedBusinesses={savedBusinesses}
                  settingsAppTheme={settingsAppTheme}
                  handleSaveUserSettings={handleSaveUserSettings}
                  mapStyle={mapStyle}
                  onMapStyleChange={setMapStyle}
                  settingsLanguage={settingsLanguage}
                  setSettingsLanguage={(lang) => setSettingsLanguage(lang as any)}
                  settingsDefaultZipcode={settingsDefaultZipcode}
                  setSettingsDefaultZipcode={setSettingsDefaultZipcode}
                  settingsMaxResults={settingsMaxResults}
                  setSettingsMaxResults={setSettingsMaxResults}
                  trackingEnabled={trackingEnabled}
                  handleToggleTracking={handleToggleTracking}
                  audienceMode={audienceMode}
                  handleSelectAudienceMode={handleSelectAudienceMode}
                  adminUsePro={adminUsePro}
                  setAdminUsePro={setAdminUsePro}
                  settingsBypassPaywall={settingsBypassPaywall}
                  setSettingsBypassPaywall={setSettingsBypassPaywall}
                  handleLinkIdentity={handleLinkIdentity}
                  onUpgradeClick={() => {
                    setIsSettingsModalOpen(false);
                    setIsUpgradeModalOpen(true);
                  }}
                  onContactSupportClick={() => {
                    setIsSettingsModalOpen(false);
                    setContactSubject("Technical Support");
                    setWorkspaceMode("contact");
                    navigateTo("/contact");
                  }}
                  onOpenLegalTab={(tab) => {
                    setIsSettingsModalOpen(false);
                    setActiveLegalTab(tab as any);
                    setIsLegalModalOpen(true);
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Delete Account Confirmation Modal */}
        {isDeleteConfirmModalOpen && (
          <div 
            className="fixed inset-0 bg-slate-950/80 backdrop-blur-md z-[60] flex items-center justify-center p-4 cursor-pointer"
            onClick={() => !isDeletingAccount && setIsDeleteConfirmModalOpen(false)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-modal-title"
            aria-describedby="delete-modal-description"
            onKeyDown={(e) => {
              if (e.key === "Escape" && !isDeletingAccount) {
                setIsDeleteConfirmModalOpen(false);
              }
            }}
          >
            <div 
              className="bg-white text-slate-900 rounded-2xl shadow-3xl border border-rose-100 max-w-md w-full overflow-hidden p-0 animate-scale-up font-sans relative cursor-default"
              onClick={(e) => e.stopPropagation()}
              ref={(el) => {
                if (el) {
                  // Focus trap initialization
                  const focusableElements = el.querySelectorAll('button, input');
                  if (focusableElements.length > 0 && document.activeElement !== focusableElements[0] && document.activeElement !== focusableElements[1]) {
                    (focusableElements[0] as HTMLElement).focus();
                  }
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Tab") {
                  const focusable = e.currentTarget.querySelectorAll('button, input');
                  const first = focusable[0] as HTMLElement;
                  const last = focusable[focusable.length - 1] as HTMLElement;
                  if (e.shiftKey) {
                    if (document.activeElement === first) {
                      last.focus();
                      e.preventDefault();
                    }
                  } else {
                    if (document.activeElement === last) {
                      first.focus();
                      e.preventDefault();
                    }
                  }
                }
              }}
            >
              {/* Danger top accent bar */}
              <div className="h-1.5 bg-rose-600"></div>

              {/* Modal Body */}
              <div className="p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-rose-100 text-rose-600 rounded-xl">
                    <ShieldAlert className="w-6 h-6 animate-pulse" />
                  </div>
                  <h3 
                    id="delete-modal-title" 
                    className="font-black text-base text-slate-900 tracking-tight"
                  >
                    Are you sure you want to delete your account?
                  </h3>
                </div>

                <div 
                  id="delete-modal-description" 
                  className="space-y-3 text-xs text-slate-600 font-medium leading-relaxed"
                >
                  <p>
                    This will permanently delete your BizNearYou account and all data associated with it. Any active subscription will be canceled immediately. This action cannot be undone.
                  </p>
                  <p className="bg-rose-50 border border-rose-100 p-3 rounded-lg text-rose-800 text-[11px] font-semibold leading-normal">
                    This will delete your: saved businesses, custom CRM pipelines, search histories, and usage counters.
                  </p>
                </div>

                {/* Input verification field */}
                <div className="space-y-2">
                  <label htmlFor="delete-confirm-input" className="text-[10.5px] font-black text-slate-700 tracking-wide uppercase block">
                    To verify, type <span className="text-rose-600 font-black">DELETE</span> below:
                  </label>
                  <input
                    type="text"
                    id="delete-confirm-input"
                    value={deleteConfirmInput}
                    onChange={(e) => setDeleteConfirmInput(e.target.value)}
                    placeholder="Type DELETE to confirm"
                    disabled={isDeletingAccount}
                    autoComplete="off"
                    className="w-full bg-slate-50 border border-slate-200 focus:border-rose-500 hover:border-slate-300 px-3 py-2.5 rounded-lg text-xs font-bold text-slate-900 placeholder:text-slate-400 outline-none transition"
                  />
                </div>

                {/* Error message displays */}
                {deleteAccountError && (
                  <div className="p-3 bg-rose-100/60 border border-rose-200 text-rose-700 text-xs font-semibold rounded-lg flex items-start gap-2">
                    <span className="shrink-0 mt-0.5 font-bold">⚠️</span>
                    <span className="leading-snug">{deleteAccountError}</span>
                  </div>
                )}
              </div>

              {/* Modal Footer Controls */}
              <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setIsDeleteConfirmModalOpen(false);
                    setDeleteConfirmInput("");
                    setDeleteAccountError(null);
                  }}
                  disabled={isDeletingAccount}
                  className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 hover:text-slate-900 text-xs font-bold rounded-lg transition disabled:opacity-50 select-none cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  id="delete-account-confirm-btn"
                  onClick={async () => {
                    if (deleteConfirmInput !== "DELETE") return;
                    setIsDeletingAccount(true);
                    setDeleteAccountError(null);
                    try {
                      const res = await apiFetch("/api/account", {
                        method: "DELETE"
                      });
                      if (res.ok) {
                        setIsDeleteConfirmModalOpen(false);
                        // Clean entire local storage
                        try {
                          localStorage.clear();
                        } catch (e) {
                          console.error("Local storage clearing failed:", e);
                        }
                        handleLogout();
                        showToast("Your account has been permanently deleted.", "success");
                      } else {
                        const errBody = await res.json().catch(() => ({}));
                        setDeleteAccountError(errBody.message || errBody.error || "Failed to process account deletion request. Please try again.");
                      }
                    } catch (netErr: any) {
                      console.error("Account deletion network error:", netErr);
                      setDeleteAccountError("A connection error occurred. Please verify your network and try again.");
                    } finally {
                      setIsDeletingAccount(false);
                    }
                  }}
                  disabled={deleteConfirmInput !== "DELETE" || isDeletingAccount}
                  className={`px-4.5 py-2 text-white text-xs font-bold rounded-lg shadow-sm transition shrink-0 select-none flex items-center gap-2 ${
                    deleteConfirmInput === "DELETE" && !isDeletingAccount
                      ? "bg-rose-600 hover:bg-rose-700 hover:shadow-rose-600/10 cursor-pointer"
                      : "bg-slate-300 cursor-not-allowed text-slate-500"
                  }`}
                >
                  {isDeletingAccount ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-white" />
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <span>Permanently Delete Account</span>
                  )}
                </button>
              </div>

              {/* Progress overlay block during active deleting */}
              {isDeletingAccount && (
                <div className="absolute inset-0 bg-white/90 z-20 flex flex-col items-center justify-center p-6 text-center animate-fade-in">
                  <Loader2 className="w-10 h-10 text-rose-600 animate-spin mb-4" />
                  <h4 className="font-black text-sm text-slate-900 tracking-tight">Processing Account Deletion</h4>
                  <p className="text-[11px] text-slate-500 leading-normal mt-1 max-w-xs">
                    Permanently deleting your account profile, canceling active billing subscriptions, and purging CRM storage database records...
                  </p>
                  <p className="text-[10px] text-rose-600 font-bold tracking-wide uppercase mt-4 animate-pulse">
                    Please do not close this window or navigate away.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Competitor Comparison Modal */}
        {isCompareModalOpen && (
          (() => {
            const selectedLeads = (workspaceMode === "search" ? Businesses : savedBusinesses).filter(
              (lead) => compareBusinessIds.includes(lead.id)
            );

            // Compute metrics comparison and find the "Most Optimized" and "Best Opportunity"
            const comparedProspects = selectedLeads.map((lead) => {
              const prospect = analyzeBusinessProspect(lead);
              const score = lead.Businessescore !== undefined ? Number(lead.Businessescore) : prospect.score;
              return { lead, prospect, score };
            });

            // Find best opportunity lead (e.g. lowest website quality or no website, but has decent reviews)
            const bestOutreachOpportunity = comparedProspects.reduce((best, curr) => {
              // Criteria: No website or outdated website has higher outreach priority
              const currHasNoWeb = !curr.lead.website;
              const bestHasNoWeb = !best.lead.website;
              if (currHasNoWeb && !bestHasNoWeb) return curr;
              if (!currHasNoWeb && bestHasNoWeb) return best;
              
              // Second criteria: Lower overall score means more room to optimize
              return curr.score < best.score ? curr : best;
            }, comparedProspects[0]);

            return (
              <div className="fixed inset-0 bg-slate-950/75 backdrop-blur-md z-50 flex items-center justify-center p-4 cursor-pointer" onClick={() => setIsCompareModalOpen(false)}>
                <div className="bg-white text-slate-900 rounded-2xl shadow-3xl border border-slate-200 max-w-5xl w-full max-h-[90vh] flex flex-col overflow-hidden animate-scale-up font-sans relative cursor-default" onClick={(e) => e.stopPropagation()}>
                  <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-indigo-600 via-blue-500 to-indigo-600"></div>

                  {/* Modal Header */}
                  <div className="p-6 pb-4 flex items-start justify-between border-b border-slate-200 mt-1 shrink-0">
                    <div>
                      <span className="text-[9px] font-black bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full uppercase tracking-wider mb-1 inline-block">
                        Advanced Intelligence Dashboard
                      </span>
                      <h3 className="text-lg font-black text-slate-900 flex items-center gap-2">
                        ⚖️ Competitor Side-by-Side Comparison
                      </h3>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Deep comparison of SEO, reviews, hiring capacity, and contactability profiles.
                      </p>
                    </div>
                    <button
                      onClick={() => setIsCompareModalOpen(false)}
                      className="text-slate-400 hover:text-slate-600 transition p-1.5 rounded-lg hover:bg-slate-100"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Modal Body */}
                  <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {comparedProspects.length === 0 ? (
                      <div className="text-center py-12 space-y-3">
                        <div className="w-12 h-12 bg-slate-105 rounded-full flex items-center justify-center mx-auto text-slate-400">
                          <Compass className="w-6 h-6 animate-pulse" />
                        </div>
                        <p className="text-slate-500 text-xs font-semibold">No businesses selected for comparison.</p>
                      </div>
                    ) : (
                      <>
                        {/* Side-by-Side Matrix Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {comparedProspects.map(({ lead, prospect, score }) => {
                            const isBestTarget = lead.id === bestOutreachOpportunity?.lead?.id;
                            return (
                              <div 
                                key={lead.id} 
                                className={`rounded-xl border p-4.5 space-y-4 relative transition-all duration-200 ${
                                  isBestTarget 
                                    ? "border-indigo-400 bg-indigo-50/10 ring-2 ring-indigo-600/10" 
                                    : "border-slate-200 bg-white"
                                }`}
                              >
                                {isBestTarget && (
                                  <span className="absolute top-3 right-3 text-[9px] font-black bg-indigo-600 text-white px-2 py-0.5 rounded shadow-2xs uppercase tracking-wider flex items-center gap-1 animate-pulse">
                                    🎯 Best Outreach Target
                                  </span>
                                )}

                                <div>
                                  <h4 className="font-extrabold text-slate-900 text-sm truncate pr-16" title={lead.name}>
                                    {lead.name}
                                  </h4>
                                  <p className="text-[10px] text-slate-400 font-bold uppercase mt-0.5">{lead.category || "Local Business"}</p>
                                </div>

                                {/* Core Metrics List */}
                                <div className="space-y-2.5">
                                  {/* AI Business Score */}
                                  <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                                    <span className="text-[11px] text-slate-500 font-bold">{AI_FEATURES_ENABLED ? "AI Business Score" : "Opportunity Score"}</span>
                                    <span className={`text-xs font-extrabold px-2 py-0.5 rounded font-mono ${
                                      score >= 80 ? "bg-emerald-50 text-emerald-700" :
                                      score >= 60 ? "bg-amber-50 text-amber-700" :
                                      "bg-rose-50 text-rose-700"
                                    }`}>
                                      {score} / 100
                                    </span>
                                  </div>

                                  {/* Reviews & Star Rating */}
                                  <div className="flex items-center justify-between text-[11px]">
                                    <span className="text-slate-500 font-medium">Reputation</span>
                                    <span className="font-semibold text-slate-800 flex items-center gap-1">
                                      ⭐️ {lead.rating ? lead.rating.toFixed(1) : "N/A"} ({lead.reviewsCount || 0} reviews)
                                    </span>
                                  </div>

                                  {/* Website Quality */}
                                  <div className="flex items-center justify-between text-[11px]">
                                    <span className="text-slate-500 font-medium">Official Website</span>
                                    {lead.website ? (
                                      <a 
                                        href={lead.website} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:underline font-bold truncate max-w-[150px] flex items-center gap-0.5"
                                      >
                                        Yes <ExternalLink className="w-2.5 h-2.5 shrink-0" />
                                      </a>
                                    ) : (
                                      <span className="text-rose-600 font-black flex items-center gap-0.5">Missing ❌</span>
                                    )}
                                  </div>

                                  {/* Contactability channels */}
                                  <div className="flex items-center justify-between text-[11px]">
                                    <span className="text-slate-500 font-medium">Contactability</span>
                                    <span className="font-semibold text-slate-800 text-right truncate max-w-[150px]">
                                      {lead.phone ? "📞 Phone" : ""} {lead.contacts?.length ? "👥 Email Found" : ""}
                                      {!lead.phone && !lead.contacts?.length && "None Verified ⚠️"}
                                    </span>
                                  </div>

                                  {/* Hiring signals */}
                                  <div className="flex items-center justify-between text-[11px]">
                                    <span className="text-slate-500 font-medium">Hiring status</span>
                                    <span className={`font-bold uppercase text-[9px] px-1.5 py-0.2 rounded ${
                                      normalizeTags(prospect?.tags).includes("Currently Hiring") || lead.hiringStatus === "active"
                                        ? "bg-amber-500 text-white"
                                        : "bg-slate-100 text-slate-500"
                                    }`}>
                                      {normalizeTags(prospect?.tags).includes("Currently Hiring") || lead.hiringStatus === "active" ? "Hiring" : "Stable"}
                                    </span>
                                  </div>

                                  {/* Trust Score */}
                                  <div className="flex items-center justify-between text-[11px]">
                                    <span className="text-slate-500 font-medium">Trust Rank</span>
                                    <span className="font-bold font-mono text-emerald-700">{prospect.scores?.trustScore || 85}%</span>
                                  </div>

                                  {/* Website Design Audit status */}
                                  <div className="flex items-center justify-between text-[11px]">
                                    <span className="text-slate-500 font-medium">Website Audit</span>
                                    <span className="font-bold text-slate-700">
                                      {lead.websiteAudit ? (lead.websiteAudit.failed ? "Issues ⚠️" : "Healthy ✅") : "No Audit ℹ️"}
                                    </span>
                                  </div>
                                </div>

                                {/* Employment Quality Matrix */}
                                <div className="pt-3 border-t border-slate-150 space-y-2.5">
                                  <span className="text-[10px] font-black text-indigo-700 uppercase tracking-wider block">
                                    💼 Upgraded Employment Quality Matrix
                                  </span>
                                  {(() => {
                                    const enrichment = lead.jobEnrichment || generateJobEnrichment(lead);
                                    
                                    // Determine verification badge
                                    let verificationLabel = "Inferred";
                                    let verificationBadgeStyle = "bg-amber-50 text-amber-700 border-amber-100";
                                    if (enrichment.compensation.isConfirmed || enrichment.workplaceFlexibility.isConfirmed) {
                                      verificationLabel = "Verified";
                                      verificationBadgeStyle = "bg-emerald-50 text-emerald-700 border-emerald-100";
                                    } else if (enrichment.jobSecurity.source?.includes("Registry")) {
                                      verificationLabel = "Registry";
                                      verificationBadgeStyle = "bg-indigo-50 text-indigo-700 border-indigo-100";
                                    }

                                    return (
                                      <div className="space-y-2 text-[10.5px]">
                                        <div className="flex justify-between items-center border-b border-slate-100 pb-1">
                                          <span className="text-slate-500 font-medium">Verification Status</span>
                                          <span className={`text-[9px] font-bold px-1.5 py-0.2 rounded border ${verificationBadgeStyle}`}>
                                            {verificationLabel}
                                          </span>
                                        </div>
                                        <div className="flex justify-between border-b border-slate-100 pb-1">
                                          <span className="text-slate-500 font-medium">Base Pay Range</span>
                                          <span className="font-extrabold text-slate-800">{enrichment.compensation.basePay}</span>
                                        </div>
                                        <div className="flex justify-between border-b border-slate-100 pb-1">
                                          <span className="text-slate-500 font-medium">Est. Total Pay</span>
                                          <span className="font-extrabold text-slate-800">{enrichment.compensation.estimatedTotal}</span>
                                        </div>
                                        
                                        {/* Benefits matrix sub-view */}
                                        <div className="border-b border-slate-100 pb-1">
                                          <span className="text-[8.5px] font-bold text-slate-400 block mb-1">Benefit Package Checks</span>
                                          <div className="grid grid-cols-2 gap-1 text-[9px] font-bold">
                                            <span className={enrichment.compensation.healthcareBenefitsObj?.medical ? "text-emerald-700" : "text-slate-400"}>
                                              {enrichment.compensation.healthcareBenefitsObj?.medical ? "✓ Medical" : "✗ Medical"}
                                            </span>
                                            <span className={enrichment.compensation.healthcareBenefitsObj?.dental ? "text-emerald-700" : "text-slate-400"}>
                                              {enrichment.compensation.healthcareBenefitsObj?.dental ? "✓ Dental" : "✗ Dental"}
                                            </span>
                                            <span className={enrichment.compensation.retirementBenefitsObj?.available ? "text-emerald-700" : "text-slate-400"}>
                                              {enrichment.compensation.retirementBenefitsObj?.available ? "✓ 401k" : "✗ 401k"}
                                            </span>
                                            <span className={enrichment.compensation.paidTimeOff ? "text-emerald-700" : "text-slate-400"}>
                                              {enrichment.compensation.paidTimeOff ? "✓ Paid PTO" : "✗ Paid PTO"}
                                            </span>
                                          </div>
                                        </div>

                                        <div className="flex justify-between border-b border-slate-100 pb-1">
                                          <span className="text-slate-500 font-medium">Workplace Location</span>
                                          <span className="font-extrabold text-slate-800 capitalize flex items-center gap-0.5">
                                            🗺️ {enrichment.workplaceFlexibility.workLocationType || enrichment.workplaceFlexibility.locationType}
                                          </span>
                                        </div>
                                        <div className="flex justify-between border-b border-slate-100 pb-1">
                                          <span className="text-slate-500 font-medium">Flexible Hours</span>
                                          <span className="font-bold text-slate-700">
                                            {enrichment.workplaceFlexibility.flexibleHours ? "Yes ✓" : "Standard ✗"}
                                          </span>
                                        </div>
                                        <div className="flex justify-between border-b border-slate-100 pb-1">
                                          <span className="text-slate-500 font-medium">Shift Options</span>
                                          <span className="font-bold text-slate-700 truncate max-w-[120px]" title={enrichment.workplaceFlexibility.shiftOptions}>
                                            {enrichment.workplaceFlexibility.shiftOptions}
                                          </span>
                                        </div>

                                        <div className="border-b border-slate-100 pb-1.5 space-y-1">
                                          <div className="flex justify-between text-[10px] text-slate-500 font-bold">
                                            <span>Security & Stability</span>
                                            <span className="text-emerald-600 font-extrabold">{enrichment.jobSecurity.stabilityScore || 75}%</span>
                                          </div>
                                          <div className="w-full bg-slate-100 rounded-full h-1">
                                            <div className="bg-emerald-500 h-1 rounded-full" style={{ width: `${enrichment.jobSecurity.stabilityScore || 75}%` }} />
                                          </div>
                                        </div>

                                        <div className="flex justify-between border-b border-slate-100 pb-1">
                                          <span className="text-slate-500 font-medium">Average Tenure</span>
                                          <span className="font-extrabold text-slate-800">{enrichment.jobSecurity.tenureYrs} Years Avg</span>
                                        </div>
                                        <div className="flex justify-between border-b border-slate-100 pb-1">
                                          <span className="text-slate-500 font-medium">Business Age</span>
                                          <span className="font-bold text-slate-700">{enrichment.jobSecurity.businessHistoryYrs} Years Active</span>
                                        </div>
                                        
                                        <div className="flex justify-between border-b border-slate-100 pb-1">
                                          <span className="text-slate-500 font-medium">Mentorship Option</span>
                                          <span className={`font-bold ${enrichment.careerGrowth.mentorshipAvailable ? "text-emerald-600" : "text-slate-400"}`}>
                                            {enrichment.careerGrowth.mentorshipAvailable ? "Available ✓" : "None ✗"}
                                          </span>
                                        </div>
                                        <div className="flex justify-between border-b border-slate-100 pb-1">
                                          <span className="text-slate-500 font-medium">Paid Certifications</span>
                                          <span className={`font-bold ${enrichment.careerGrowth.certificationsSupported ? "text-emerald-600" : "text-slate-400"}`}>
                                            {enrichment.careerGrowth.certificationsSupported ? "Supported ✓" : "None ✗"}
                                          </span>
                                        </div>

                                        <div className="border-b border-slate-100 pb-1.5 space-y-1">
                                          <div className="flex justify-between text-[10px] text-slate-500 font-bold">
                                            <span>Culture Score</span>
                                            <span className="text-indigo-600 font-extrabold">{enrichment.companyCulture.cultureScore || 70}%</span>
                                          </div>
                                          <div className="w-full bg-slate-100 rounded-full h-1">
                                            <div className="bg-indigo-500 h-1 rounded-full" style={{ width: `${enrichment.companyCulture.cultureScore || 70}%` }} />
                                          </div>
                                        </div>

                                        <div className="flex justify-between border-b border-slate-100 pb-1">
                                          <span className="text-slate-500 font-medium">Culture Rating</span>
                                          <span className="font-extrabold text-indigo-700">★ {enrichment.companyCulture.rating.toFixed(1)}</span>
                                        </div>
                                        <div className="flex justify-between border-b border-slate-100 pb-1">
                                          <span className="text-slate-500 font-medium">Workplace Balance</span>
                                          <span className="font-bold text-slate-700 capitalize">{enrichment.companyCulture.workLifeBalance}</span>
                                        </div>
                                        <div className="flex justify-between border-b border-slate-100 pb-1">
                                          <span className="text-slate-500 font-medium">Hiring Likelihood</span>
                                          <span className={`font-black uppercase text-[8.5px] px-1 rounded ${enrichment.metadata.hiringLikelihood === "high" ? "bg-amber-500 text-white" : "bg-slate-150 text-slate-600"}`}>
                                            {enrichment.metadata.hiringLikelihood}
                                          </span>
                                        </div>
                                        <div className="flex justify-between border-b border-slate-100 pb-1">
                                          <span className="text-slate-500 font-medium">Info Confidence</span>
                                          <span className="font-extrabold text-indigo-700">{enrichment.metadata.confidenceScore}%</span>
                                        </div>
                                        <div className="flex justify-between text-[9.5px] text-slate-400 pt-0.5">
                                          <span>Last Checked:</span>
                                          <span>{enrichment.metadata.lastCheckedDate}</span>
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </div>

                                {/* Strengths and Opportunities */}
                                <div className="pt-3 border-t border-slate-100 space-y-2">
                                  <div>
                                    <span className="text-[9.5px] font-bold text-emerald-700 uppercase block mb-1">Key Strengths</span>
                                    <ul className="text-[10px] text-slate-600 space-y-1">
                                      {prospect.strengths?.slice(0, 2).map((str, idx) => (
                                        <li key={idx} className="flex items-start gap-1 font-semibold">
                                          <span className="text-emerald-500">•</span> <span>{str}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                  <div>
                                    <span className="text-[9.5px] font-bold text-indigo-700 uppercase block mb-1">Key Opportunity</span>
                                    <p className="text-[10.5px] text-slate-700 font-bold font-sans line-clamp-2">
                                      💡 {prospect.recommendedNextStep || "Optimize profile and local visibility."}
                                    </p>
                                  </div>
                                </div>

                                <div className="pt-2.5">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedBusinessId(lead.id);
                                      setMobileIntelOpen(true);
                                      setIsCompareModalOpen(false);
                                    }}
                                    className="w-full py-1.5 text-center border border-slate-200 hover:border-slate-300 hover:bg-slate-50 font-bold text-[10.5px] text-slate-700 rounded-lg transition-all cursor-pointer shadow-4xs"
                                  >
                                    Inspect Full Dashboard
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Consultant Comparison Report */}
                        <div className="border border-indigo-150 bg-indigo-50/20 rounded-2xl p-5 space-y-4 shadow-3xs">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-black text-indigo-900 uppercase tracking-widest flex items-center gap-1.5">
                              <Sparkles className="w-4 h-4 text-amber-500 animate-pulse" />
                              {AI_FEATURES_ENABLED ? "AI Consultant Comparison Report" : "Consultant Comparison Report"}
                            </span>
                            <span className="text-[9.5px] text-indigo-700 font-mono font-extrabold bg-indigo-100 border border-indigo-200 px-2 py-0.5 rounded">
                              {AI_FEATURES_ENABLED ? "MODEL: GEMINI-2.5-FLASH" : "AUDIT METHOD: ALGORITHMIC"}
                            </span>
                          </div>

                          <div className="space-y-3.5 text-slate-750 text-xs leading-relaxed">
                            <p>
                              Based on our multi-dimensional audit of the <strong>{comparedProspects.length} selected local businesses</strong>, we have generated an outreach priority hierarchy based on market potential, reputation gaps, and immediate product opportunity.
                            </p>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white/70 p-4 rounded-xl border border-slate-150">
                              <div>
                                <h5 className="font-black text-indigo-950 text-[11.5px] mb-1.5 uppercase flex items-center gap-1">
                                  🎯 Optimal Outreach Target
                                </h5>
                                <p className="text-[11px] font-semibold text-slate-700">
                                  <strong>{bestOutreachOpportunity?.lead?.name}</strong> stands out as the highest-yield prospect.
                                </p>
                                <ul className="mt-2 space-y-1.5 text-[10.5px]">
                                  <li className="flex items-start gap-1 font-semibold text-slate-650">
                                    <span className="text-indigo-500">✔</span>
                                    <span>
                                      {!bestOutreachOpportunity?.lead?.website 
                                        ? "No digital presence identified: high urgency for Web Design Proposal" 
                                        : "Outdated website found: conversion rate optimization needed"}
                                    </span>
                                  </li>
                                  <li className="flex items-start gap-1 font-semibold text-slate-650">
                                    <span className="text-indigo-500">✔</span>
                                    <span>
                                      {bestOutreachOpportunity?.lead?.rating && bestOutreachOpportunity.lead.rating >= 4.0 
                                        ? `Strong reputation benchmark of ${bestOutreachOpportunity.lead.rating} stars provides great trust signals to capture leads`
                                        : "Urgent reputation repair opportunity to scale Google rating threshold"}
                                    </span>
                                  </li>
                                </ul>
                              </div>

                              <div>
                                <h5 className="font-black text-emerald-950 text-[11.5px] mb-1.5 uppercase flex items-center gap-1">
                                  💡 Outreach Strategy & Pitch Angle
                                </h5>
                                <div className="text-[11px] font-semibold text-slate-700 bg-emerald-50/50 p-2.5 rounded border border-emerald-150">
                                  <p className="font-extrabold text-emerald-900 mb-1">
                                    Recommended Pitch Angle:
                                  </p>
                                  <p className="text-emerald-950 italic">
                                    "{bestOutreachOpportunity?.prospect?.pitchAngle || "Offer local visibility boost & automated client booking workflow integration."}"
                                  </p>
                                </div>
                              </div>
                            </div>

                            <p className="text-[10px] text-slate-400 font-medium">
                              ℹ️ Recommendations are generated based on live Google Maps metadata combined with automated website crawl scoring heuristics. Use outreach tools to directly pitch.
                            </p>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Modal Footer */}
                  <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-3 shrink-0">
                    <button
                      type="button"
                      onClick={() => setIsCompareModalOpen(false)}
                      className="px-5 py-2 rounded-lg bg-slate-200 hover:bg-slate-300 text-slate-700 hover:text-slate-900 text-xs font-bold cursor-pointer transition focus:outline-none"
                    >
                      Close Comparison
                    </button>
                  </div>
                </div>
              </div>
            );
          })()
        )}

        {/* Slide-out Sidebar Drawer Menu */}
        {isSidebarOpen && (
          <div className="fixed inset-0 z-50 flex" id="sidebar-overlay-container">
            {/* Backdrop slide/fade overlay */}
            <div 
              className="fixed inset-0 bg-slate-950/60 backdrop-blur-xs transition-opacity duration-300" 
              onClick={() => setIsSidebarOpen(false)}
            />
            
            {/* Drawer container */}
            <aside 
              className={`relative w-80 max-w-[85vw] h-full shadow-2xl flex flex-col z-10 transition-transform duration-300 ease-out transform translate-x-0 border-r ${
                resolveTheme(settingsAppTheme) === "light" 
                  ? "bg-white text-slate-900 border-slate-200" 
                  : "bg-[#1e293b] text-white border-slate-800"
              }`}
              id="sidebar-drawer-menu"
            >
              {/* Drawer header */}
              <div className={`p-6 border-b flex items-center justify-between ${
                resolveTheme(settingsAppTheme) === "light"
                  ? "border-slate-150 bg-slate-50"
                  : "border-slate-800 bg-slate-900/60"
              }`}>
                <BizNearYouLogo 
                  textColor={
                    resolveTheme(settingsAppTheme) === "light"
                      ? "text-slate-900"
                      : "text-white"
                  } 
                  isDark={resolveTheme(settingsAppTheme) === "dark"}
                />
                <button
                  onClick={() => setIsSidebarOpen(false)}
                  className={`p-1.5 rounded-lg transition cursor-pointer ${
                    resolveTheme(settingsAppTheme) === "light"
                      ? "bg-slate-150 hover:bg-slate-200 text-slate-500 hover:text-slate-800"
                      : "bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white"
                  }`}
                  title="Close menu" // i18n-ignore
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Drawer Body - Clickable Menu Sections */}
              <div className="flex-1 overflow-y-auto p-4 space-y-6">
                <div>
                  <label className="text-[9.5px] font-bold text-slate-500 uppercase tracking-widest px-3 mb-2.5 block">
                    {t("sidebar_workspace_modules")}
                  </label>
                  <nav className="space-y-1">
                    <button
                      onClick={() => {
                        switchWorkspaceMode("dashboard");
                        setIsSidebarOpen(false);
                      }}
                      className={`w-full flex items-center justify-between px-3.5 py-3 rounded-lg text-xs font-semibold tracking-wide transition-all cursor-pointer ${
                        workspaceMode === "dashboard"
                          ? "bg-gradient-to-r from-indigo-600 to-indigo-700 text-white font-extrabold shadow-sm shadow-indigo-500/15 scale-[1.01]"
                          : settingsAppTheme !== "dark"
                          ? "text-slate-700 hover:bg-slate-100 hover:text-slate-950"
                          : "text-slate-300 hover:bg-slate-800 hover:text-white"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Compass className="w-4 h-4 text-indigo-400 animate-spin-slow" />
                        <span>Quest</span>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                        workspaceMode === "dashboard" 
                          ? "bg-indigo-700/60 text-white" 
                          : settingsAppTheme !== "dark"
                          ? "bg-slate-200 text-slate-600"
                          : "bg-slate-800 text-slate-400"
                      }`}>
                        Live
                      </span>
                    </button>

                    <button
                      onClick={() => {
                        switchWorkspaceMode("search");
                        setIsSidebarOpen(false);
                      }}
                      className={`w-full flex items-center justify-between px-3.5 py-3 rounded-lg text-xs font-semibold tracking-wide transition-all cursor-pointer ${
                        workspaceMode === "search"
                          ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-extrabold shadow-sm shadow-blue-500/15 scale-[1.01]"
                          : settingsAppTheme !== "dark"
                          ? "text-slate-700 hover:bg-slate-100 hover:text-slate-950"
                          : "text-slate-300 hover:bg-slate-800 hover:text-white"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Search className="w-4 h-4 text-blue-400" />
                        <span>{t("nav_search")}</span>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                        workspaceMode === "search" 
                          ? "bg-blue-700/60 text-white" 
                          : settingsAppTheme !== "dark"
                          ? "bg-slate-200 text-slate-600"
                          : "bg-slate-800 text-slate-400"
                      }`}>
                        {Businesses.length}
                      </span>
                    </button>

                    <button
                      onClick={() => {
                        switchWorkspaceMode("saved");
                        setIsSidebarOpen(false);
                      }}
                      className={`w-full flex items-center justify-between px-3.5 py-3 rounded-lg text-xs font-semibold tracking-wide transition-all cursor-pointer ${
                        workspaceMode === "saved"
                          ? "bg-gradient-to-r from-emerald-600 to-teal-650 text-white font-extrabold shadow-sm shadow-emerald-500/15 scale-[1.01]"
                          : settingsAppTheme !== "dark"
                          ? "text-slate-700 hover:bg-slate-100 hover:text-slate-950"
                          : "text-slate-300 hover:bg-slate-800 hover:text-white"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Database className="w-4 h-4 text-emerald-400" />
                        <span>{t("nav_Businesses")}</span>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                        workspaceMode === "saved" 
                          ? "bg-emerald-700/60 text-white" 
                          : settingsAppTheme !== "dark"
                          ? "bg-slate-200 text-slate-600"
                          : "bg-slate-800 text-slate-400"
                      }`}>
                        {savedBusinesses.length}
                      </span>
                    </button>

                    {workspaceMode === "saved" && (
                      <div className="pl-6 space-y-1 py-1">
                        <button
                          onClick={() => {
                            setSavedSubview("list");
                            setIsSidebarOpen(false);
                          }}
                          className={`w-full flex items-center gap-2.5 px-3 py-1.5 rounded-md text-[11px] font-bold transition cursor-pointer ${
                            savedSubview === "list"
                              ? "bg-emerald-500/20 text-emerald-300 font-black"
                              : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/50"
                          }`}
                        >
                          <List className="w-3.5 h-3.5" />
                          <span>Saved List View</span>
                        </button>
                        <button
                          onClick={() => {
                            setSavedSubview("pipeline");
                            setIsSidebarOpen(false);
                          }}
                          className={`w-full flex items-center justify-between px-3 py-1.5 rounded-md text-[11px] font-bold transition cursor-pointer ${
                            savedSubview === "pipeline"
                              ? "bg-emerald-500/20 text-emerald-300 font-black"
                              : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/50"
                          }`}
                        >
                          <div className="flex items-center gap-2.5">
                            <Briefcase className="w-3.5 h-3.5 text-indigo-400" />
                            <span>Pro Kanban Pipeline</span>
                          </div>
                          {!isProUser && (
                            <span className="text-[8px] bg-amber-500/20 text-amber-300 px-1 py-0.2 rounded font-black border border-amber-500/30">
                              PRO
                            </span>
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setSavedSubview("territory");
                            setIsSidebarOpen(false);
                          }}
                          className={`w-full flex items-center justify-between px-3 py-1.5 rounded-md text-[11px] font-bold transition cursor-pointer ${
                            savedSubview === "territory"
                              ? "bg-emerald-500/20 text-emerald-300 font-black"
                              : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/50"
                          }`}
                        >
                          <div className="flex items-center gap-2.5">
                            <Compass className="w-3.5 h-3.5 text-blue-400" />
                            <span>Territory Pipeline</span>
                          </div>
                          {!isProUser && (
                            <span className="text-[8px] bg-amber-500/20 text-amber-300 px-1 py-0.2 rounded font-black border border-amber-500/30">
                              PRO
                            </span>
                          )}
                        </button>
                      </div>
                    )}

                    <button
                      onClick={() => {
                        switchWorkspaceMode("history");
                        setIsSidebarOpen(false);
                      }}
                      className={`w-full flex items-center justify-between px-3.5 py-3 rounded-lg text-xs font-semibold tracking-wide transition-all cursor-pointer ${
                        workspaceMode === "history"
                          ? "bg-gradient-to-r from-amber-500 to-amber-600 text-white font-extrabold shadow-sm shadow-amber-500/15 scale-[1.01]"
                          : settingsAppTheme !== "dark"
                          ? "text-slate-700 hover:bg-slate-100 hover:text-slate-950"
                          : "text-slate-300 hover:bg-slate-800 hover:text-white"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <History className="w-4 h-4 text-amber-400" />
                        <span>{t("sidebar_search_history")}</span>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                        workspaceMode === "history" 
                          ? "bg-amber-700/60 text-white" 
                          : settingsAppTheme !== "dark"
                          ? "bg-slate-200 text-slate-600"
                          : "bg-slate-800 text-slate-400"
                      }`}>
                        {history.length}
                      </span>
                    </button>

                    <button
                      onClick={() => {
                        switchWorkspaceMode("docs");
                        setIsSidebarOpen(false);
                      }}
                      className={`w-full flex items-center justify-between px-3.5 py-3 rounded-lg text-xs font-semibold tracking-wide transition-all cursor-pointer ${
                        workspaceMode === "docs"
                          ? "bg-gradient-to-r from-slate-700 to-slate-850 text-white font-extrabold shadow-sm shadow-slate-500/15 scale-[1.01]"
                          : settingsAppTheme !== "dark"
                          ? "text-slate-700 hover:bg-slate-100 hover:text-slate-950"
                          : "text-slate-300 hover:bg-slate-800 hover:text-white"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <BookOpen className="w-4 h-4 text-blue-400" />
                        <span>{t("sidebar_docs_help")}</span>
                      </div>
                      <span className={`text-[8.5px] px-1.5 py-0.5 rounded font-black tracking-wide uppercase ${
                        workspaceMode === "docs"
                          ? "bg-white text-blue-950 font-black shadow-3xs"
                          : settingsAppTheme !== "dark"
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                          : "bg-emerald-950 text-emerald-300 border border-emerald-900/60"
                      }`}>
                        {t("sidebar_help_badge")}
                      </span>
                    </button>

                    {(currentUserRole === "admin" || currentUserRole === "dev" || currentUserRole === "support") && (
                      <button
                        onClick={() => {
                          switchWorkspaceMode("admin_cost");
                          setIsSidebarOpen(false);
                        }}
                        className={`w-full flex items-center justify-between px-3.5 py-3 rounded-lg text-xs font-semibold tracking-wide transition-all cursor-pointer ${
                          workspaceMode === "admin_cost"
                            ? "bg-gradient-to-r from-rose-650 to-red-650 text-white font-extrabold shadow-sm shadow-rose-500/15 scale-[1.01]"
                            : settingsAppTheme !== "dark"
                            ? "text-slate-700 hover:bg-slate-100 hover:text-slate-950"
                            : "text-slate-300 hover:bg-slate-800 hover:text-white"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <ShieldAlert className="w-4 h-4 text-rose-500 animate-pulse" />
                          <span>{t("nav_admin")}</span>
                        </div>
                        <span className={`text-[8.5px] px-1.5 py-0.5 rounded font-black tracking-wide uppercase ${
                          workspaceMode === "admin_cost"
                            ? "bg-white text-rose-950 font-black shadow-3xs"
                            : settingsAppTheme !== "dark"
                            ? "bg-rose-600 text-white border border-rose-700 shadow-3xs"
                            : "bg-rose-950/40 text-rose-300 border border-rose-900/60"
                        }`}>
                          ADM
                        </span>
                      </button>
                    )}


                  </nav>

                  {/* Quick System Action triggers inside Drawer for Mobile/all */}
                  <div className={`px-3 mt-4 pt-4 border-t space-y-2.5 ${
                    settingsAppTheme !== "dark" ? "border-slate-150" : "border-slate-800"
                  }`}>
                    <label className="text-[9.5px] font-bold text-slate-500 uppercase tracking-widest block mb-1">
                      System Actions
                    </label>

                    <button
                      onClick={() => {
                        setIsSidebarOpen(false);
                        navigateTo("/settings/account");
                      }}
                      className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                        settingsAppTheme !== "dark"
                          ? "text-slate-700 hover:bg-slate-100 bg-slate-50 border border-slate-200"
                          : "text-slate-300 hover:bg-slate-800 bg-slate-900/40 border border-slate-800/60"
                      }`}
                    >
                      <Settings className="w-4 h-4 text-blue-500" />
                      <span>{t("nav:btn_settings_header")}</span>
                    </button>

                    <button
                      onClick={() => {
                        setIsSidebarOpen(false);
                        setContactSubject("Technical Support");
                        switchWorkspaceMode("contact");
                      }}
                      className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                        workspaceMode === "contact"
                          ? "bg-indigo-600 text-white font-extrabold shadow-sm"
                          : settingsAppTheme !== "dark"
                          ? "text-slate-700 hover:bg-slate-100 bg-slate-50 border border-slate-200"
                          : "text-slate-300 hover:bg-slate-800 bg-slate-900/40 border border-slate-800/60"
                      }`}
                    >
                      <Mail className={`w-4 h-4 ${workspaceMode === "contact" ? "text-white" : "text-emerald-500"}`} />
                      <span>Contact Support</span>
                    </button>

                    <button
                      onClick={() => {
                        setIsSidebarOpen(false);
                        handleLogout();
                      }}
                      className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                        settingsAppTheme !== "dark"
                          ? "text-rose-650 hover:bg-rose-100 bg-rose-50 border border-rose-205"
                          : "text-rose-300 hover:bg-rose-950/40 bg-rose-950/20 border border-rose-900/40"
                      }`}
                    >
                      <LogOut className="w-4 h-4 text-rose-500" />
                      <span>{t("nav:btn_sign_out")}</span>
                    </button>
                  </div>

                  {/* Legal and Launch Compliance Links */}
                  <div className={`px-3 mt-4 pt-4 border-t ${
                    settingsAppTheme !== "dark" ? "border-slate-150" : "border-slate-800"
                  }`} id="drawer-legal-container">
                    <LegalFooter 
                      onOpenTab={(tab) => {
                        if (tab === "contact-removal") {
                          setContactSubject("Profile Removal");
                          setWorkspaceMode("contact");
                          setIsSidebarOpen(false);
                        } else {
                          setActiveLegalTab(tab);
                          setIsLegalModalOpen(true);
                        }
                      }}
                    />
                  </div>
                </div>

                {/* Subscription Widget card in Drawer */}
                <div className={`border-t pt-5 pr-1 text-sans ${
                  settingsAppTheme !== "dark" ? "border-slate-150" : "border-slate-800"
                }`}>
                  <label className="text-[9.5px] font-bold text-slate-500 uppercase tracking-widest px-3 mb-2.5 block">
                    {t("sidebar_account_subscription")}
                  </label>
                  
                  <div className={`mx-3 p-3.5 border rounded-xl space-y-3 ${
                    settingsAppTheme !== "dark"
                      ? "bg-slate-50 border-slate-150"
                      : "bg-slate-900 border-slate-800/80"
                  }`}>
                    <div className="flex items-center justify-between">
                      <span className={`text-[11px] font-bold ${
                        settingsAppTheme !== "dark" ? "text-slate-500" : "text-slate-400"
                      }`}>Your Plan: {/* i18n-ignore */}</span>
                      {billingStatus?.isPro ? (
                        <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full border uppercase tracking-wide flex items-center gap-1 ${
                          settingsAppTheme !== "dark"
                            ? "bg-emerald-50 text-emerald-600 border-emerald-200"
                            : "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                        }`}>
                          <Sparkles className="w-2.5 h-2.5 animate-pulse" />
                          Pro Active
                        </span>
                      ) : (
                        <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded border uppercase tracking-wider ${
                          settingsAppTheme !== "dark"
                            ? "bg-slate-100 text-slate-600 border-slate-200"
                            : "bg-slate-855 text-slate-300 border-slate-750"
                        }`}>
                          BizNearYou Free
                        </span>
                      )}
                    </div>

                    {!billingStatus?.isPro ? (
                      <div className="space-y-2">
                        <p className={`text-[10.5px] leading-snug ${
                          settingsAppTheme !== "dark" ? "text-slate-600" : "text-slate-400"
                        }`}>
                          Unlock unlimited pipeline management, SEO audits, and custom contact crawling.
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            setIsSidebarOpen(false);
                            setIsUpgradeModalOpen(true);
                          }}
                          className="w-full py-2 bg-gradient-to-r from-blue-600 to-[#0D5C63] hover:from-blue-500 hover:to-[#116e77] text-white text-[11px] font-extrabold rounded-lg shadow-sm transition-all text-center flex items-center justify-center gap-1.5 cursor-pointer"
                        >
                          <Sparkles className="w-3.5 h-3.5 text-amber-350" />
                          <span className="text-white">Upgrade to BizNearYou Pro</span>
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="space-y-1">
                          <p className={`text-[10.5px] leading-tight ${
                            settingsAppTheme !== "dark" ? "text-slate-600" : "text-slate-400"
                          }`}>
                            Subscription auto-renews. Management secure link is accessible below:
                          </p>
                          {billingStatus && billingStatus.currentPeriodEnd && (
                            <p className="text-[9px] text-emerald-600 font-mono">
                              Period expires: {new Date(billingStatus.currentPeriodEnd).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        <button
                          type="button"
                          disabled={isBillingLoading}
                          onClick={handleManageBilling}
                          className={`w-full py-2 text-[11px] font-extrabold rounded-lg shadow-xs transition-all text-center flex items-center justify-center gap-1.5 cursor-pointer ${
                            settingsAppTheme !== "dark"
                              ? "bg-slate-200 hover:bg-slate-300 text-slate-800"
                              : "bg-slate-800 hover:bg-slate-705 text-white"
                          }`}
                        >
                          {isBillingLoading ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />
                          ) : (
                            <CreditCard className="w-3.5 h-3.5 text-blue-400" />
                          )}
                          <span>Manage Subscription {/* i18n-ignore */}</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Drawer footer */}
              <div className="p-4 border-t border-slate-800 bg-slate-900/60 text-center text-slate-100">
                <span className="text-[9px] text-slate-500 font-mono block">
                  BIZNEARYOU ACTIVE PORTAL
                </span>
                <span className="text-[8px] text-slate-600 font-mono mt-0.5 block">
                  Click backdrop or X to dismiss
                </span>
              </div>
            </aside>
          </div>
        )}

        {/* Compliance and Launch safety modals */}
        <LegalModal 
          isOpen={isLegalModalOpen} 
          onClose={() => setIsLegalModalOpen(false)} 
          initialTab={activeLegalTab} 
          apiFetch={apiFetch}
        />

        {/* AI Outreach Generation Modal with Compliance Alerts */}
        {isOutreachOpen && activeBusiness && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-fade-in font-sans">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl border border-slate-100 overflow-hidden">
              {/* Header */}
              <div className="bg-slate-900 text-white p-5 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-indigo-400 fill-indigo-400/30 animate-pulse" />
                  <div>
                    <h3 className="font-bold text-sm tracking-tight">{AI_FEATURES_ENABLED ? "AI Outreach Generation Console" : "Outreach Pitch Console"}</h3>
                    <p className="text-[10px] text-slate-400 font-semibold mt-0.5">Personalizing outreach template for <span className="text-white font-black">{activeBusiness.name}</span></p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsOutreachOpen(false)}
                  className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-slate-200 bg-slate-50 px-5 gap-4 shrink-0">
                <button
                  type="button"
                  onClick={() => setOutreachActiveTab("new")}
                  className={`py-3 text-xs font-bold transition-all relative cursor-pointer ${
                    outreachActiveTab === "new"
                      ? "text-indigo-600 border-b-2 border-indigo-600"
                      : "text-slate-500 hover:text-slate-800"
                  }`}
                >
                  ✨ Draft Custom Pitch
                </button>
                <button
                  type="button"
                  onClick={() => setOutreachActiveTab("history")}
                  className={`py-3 text-xs font-bold transition-all relative flex items-center gap-1.5 cursor-pointer ${
                    outreachActiveTab === "history"
                      ? "text-indigo-600 border-b-2 border-indigo-600"
                      : "text-slate-500 hover:text-slate-800"
                  }`}
                >
                  <History className="w-3.5 h-3.5" />
                  <span>Past Drafts History</span>
                  {pastOutreachDrafts.length > 0 && (
                    <span className="bg-indigo-100 text-indigo-700 text-[10px] px-1.5 py-0.5 rounded-full font-black">
                      {pastOutreachDrafts.length}
                    </span>
                  )}
                </button>
              </div>

              {/* Body */}
              <div className="p-6 overflow-y-auto space-y-5 flex-1 text-left">
                {outreachActiveTab === "new" ? (
                  <>
                    {/* Presets Grid */}
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-450 uppercase tracking-wider block select-none">
                        💡 Suggested Pitch Presets (Supports Any Industry or Offer)
                      </label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 bg-slate-50 p-2.5 rounded-xl border border-slate-150">
                        {[
                          {
                            category: "Web & Tech",
                            icon: "🌐",
                            label: "Web Design & SEO",
                            goal: "Offer Web Design and SEO Refresh",
                            customDetails: "Provide a quick web presence assessment. Mention potential improvements in their page speed, SEO keywords, mobile optimization, or missing booking call-to-actions to convert more visitors.",
                          },
                          {
                            category: "Services",
                            icon: "🧹",
                            label: "Commercial Cleaning",
                            goal: "Pitch Professional Cleaning & Janitorial Services",
                            customDetails: "Offer premium, customized janitorial and regular disinfection maintenance services for local offices or retail sites. Mention eco-friendly products, flexible scheduling, and a free trial cleaning.",
                          },
                          {
                            category: "Food & Events",
                            icon: "🥗",
                            label: "Catering & Meal Kits",
                            goal: "Sell Gourmet Corporate Catering & Business Lunches",
                            customDetails: "Introduce our premium local catering services for business meetings, lunches, and private corporate events. Mention our custom menus, dietary accommodations, and 15% first-order discount.",
                          },
                          {
                            category: "Media & Arts",
                            icon: "📸",
                            label: "Commercial Photo & Video",
                            goal: "Offer Media Production & High-Res Drone Shots",
                            customDetails: "Pitch high-quality commercial photography, promotional videos, or interactive virtual tours to upgrade their Google Maps profile and social media feeds.",
                          },
                          {
                            category: "Marketing",
                            icon: "📱",
                            label: "Social Media Growth",
                            goal: "Offer Social Media Growth & Content Creation",
                            customDetails: "Help them optimize their Instagram, TikTok, and Facebook presence with consistent, high-engagement visual posts, short-form reels, and community response management.",
                          },
                          {
                            category: "Repair & Trade",
                            icon: "⚙️",
                            label: "HVAC & Repairs",
                            goal: "Offer Commercial HVAC & Maintenance Contracts",
                            customDetails: "Pitch preventive HVAC maintenance, seasonal tuning, and emergency repair contracts to keep their facility comfortable for customers and compliant with local standards.",
                          },
                          {
                            category: "Consulting",
                            icon: "📊",
                            label: "Accounting & Tax Prep",
                            goal: "Offer Bookkeeping & Tax Compliance Consulting",
                            customDetails: "Help local business owners automate their bookkeeping, organize expense receipts, and maximize federal and state tax deductions with a certified CPA's oversight.",
                          },
                          {
                            category: "Supplies",
                            icon: "📦",
                            label: "Office & B2B Supplies",
                            goal: "Pitch Wholesales Supplies & Logistics Integration",
                            customDetails: "Provide custom wholesale quotes for office supplies, restaurant packaging, or manufacturing inputs with guaranteed 2-day local shipping and bulk price match discounts.",
                          },
                          {
                            category: "Custom",
                            icon: "✨",
                            label: "Custom / Any Pitch",
                            goal: "Introduce My Custom Services",
                            customDetails: "Describe exactly what you want to offer this business, whether it's tutoring, staffing, landscaping, legal counsel, software, or specialized supplies.",
                          }
                        ].map((preset) => (
                          <button
                            key={preset.label}
                            type="button"
                            onClick={() => {
                              setOutreachGoal(preset.goal);
                              setOutreachCustomDetails(preset.customDetails);
                            }}
                            className="p-2 bg-white hover:bg-indigo-50/40 hover:border-indigo-200 border border-slate-200 rounded-lg text-left transition text-[11px] group cursor-pointer flex items-start gap-1.5 shadow-3xs"
                          >
                            <span className="text-sm shrink-0">{preset.icon}</span>
                            <div className="truncate">
                              <p className="font-extrabold text-slate-800 truncate group-hover:text-indigo-750">{preset.label}</p>
                              <p className="text-[9px] text-slate-400 font-semibold truncate mt-0.5">{preset.category}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                      <p className="text-[9.5px] text-slate-500 leading-normal mt-1 bg-indigo-50/30 p-2 rounded border border-indigo-100/30">
                        <strong>Flexibility Note:</strong> BizNearYou is designed for everyone. If none of these presets fit, simply click <strong>"Custom / Any Pitch"</strong> and type whatever services or instructions you want.
                      </p>
                    </div>

                    {/* 1. Configuration Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Style */}
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider block select-none">Outreach Channel Style</label>
                        <div className="grid grid-cols-3 gap-1 bg-slate-100 p-1 rounded-lg">
                          {["Email", "SMS", "Script"].map((styleOpt) => (
                            <button
                              key={styleOpt}
                              type="button"
                              onClick={() => setOutreachStyle(styleOpt)}
                              className={`py-1 text-center font-bold text-[10px] rounded-md transition cursor-pointer ${
                                outreachStyle === styleOpt
                                  ? "bg-white text-indigo-700 shadow-2xs border border-slate-200"
                                  : "text-slate-500 hover:text-slate-800"
                              }`}
                            >
                              {styleOpt}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Goal */}
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider block select-none">Outreach Pitch Goal</label>
                        <input
                          type="text"
                          value={outreachGoal}
                          onChange={(e) => setOutreachGoal(e.target.value)}
                          placeholder="e.g. Sell catering packages, offer cleaning services, social media audit"
                          className="w-full px-3 py-1.5 bg-slate-50 border border-slate-250 rounded-lg text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-indigo-500 text-slate-800"
                        />
                      </div>
                    </div>

                    {/* Custom Pitch Requirements */}
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider block select-none">What do you actually want to do? (Custom Pitch / Services / Instructions)</label>
                      <textarea
                        rows={3}
                        value={outreachCustomDetails}
                        onChange={(e) => setOutreachCustomDetails(e.target.value)}
                        placeholder="Describe exactly what services you want to pitch (e.g. 'We offer gourmet organic catering and event hosting. Mention their high Google rating and pitch our special 15% neighborhood business discount')."
                        className="w-full p-2.5 bg-slate-50 border border-slate-250 rounded-lg text-xs font-medium focus:outline-none focus:ring-1 focus:ring-indigo-500 text-slate-800"
                      />
                      <p className="text-[10px] text-slate-450 italic">The message template will be customized around these requirements. Outreach is not limited to UI/Web design!</p>
                    </div>

                    {/* 2. Compliance Warning Banner */}
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl space-y-2.5">
                      <div className="flex items-center gap-1.5 text-amber-900">
                        <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                        <span className="font-extrabold text-xs">Outreach Compliance & Quality Standards Checklist</span>
                      </div>
                      <div className="text-[10.5px] leading-relaxed text-slate-700 space-y-1.5">
                        <p>
                          <strong>Federal CAN-SPAM Act Rules:</strong> If sending cold emails, you must include a conspicuous, one-click opt-out (unsubscribe) mechanism, provide your valid physical postal address in the footer, and refrain from utilizing deceptive headers, fake names, or misleading subject lines.
                        </p>
                        <p>
                          <strong>TCPA & SMS Guidelines:</strong> Never send unsolicited automated text messages (SMS) or place automated voice campaigns to non-verified or consumer telephone numbers without active explicit consent. Violations carry substantial statutory fines. // i18n-ignore
                        </p>
                        <p>
                          <strong>Data Verification Mandate:</strong> Business details, emails, and phone channels are harvested programmatically from public web sources and are susceptible to parser discrepancies. Always check the target company's current website manually to verify all contacts and names prior to sending. // i18n-ignore
                        </p>
                      </div>
                    </div>

                    {/* 3. Draft Editor / Result */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-[10px] font-black text-slate-455 uppercase tracking-widest block select-none">Generated Draft</label>
                        {outreachDraft && (
                          <button
                            type="button"
                            onClick={() => copyToClipboard(outreachDraft)}
                            className="text-[10.5px] font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 hover:underline cursor-pointer"
                          >
                            {copiedText === outreachDraft ? (
                              <>
                                <Check className="w-3 h-3 text-emerald-600" /> Draft Copied!
                              </>
                            ) : (
                              <>
                                <Copy className="w-3 h-3" /> Copy Outreach Draft
                              </>
                            )}
                          </button>
                        )}
                      </div>

                      <div className="relative">
                        {isGeneratingOutreach ? (
                          <div className="absolute inset-0 bg-white/80 backdrop-blur-2xs flex flex-col items-center justify-center space-y-2.5 z-10 rounded-xl border border-slate-150">
                            <RefreshCw className="w-5 h-5 text-indigo-600 animate-spin" />
                            <div className="text-center space-y-0.5">
                              <p className="text-[10px] text-indigo-600 font-extrabold uppercase tracking-wider">
                                {outreachProgressStep === 0 && "Analyzing business specialty & community signals..."}
                                {outreachProgressStep === 1 && "Integrating custom pitch goals..."}
                                {outreachProgressStep === 2 && "Formulating outreach script..."}
                                {outreachProgressStep === 3 && "Polishing output layout..."}
                              </p>
                              <p className="text-[9px] text-slate-450 font-sans">Compiling dynamic sandbox template...</p>
                            </div>
                          </div>
                        ) : null}

                        <textarea
                          value={outreachDraft}
                          onChange={(e) => setOutreachDraft(e.target.value)}
                          rows={9}
                          placeholder="Your tailored outreach copy will appear here. Enter your pitch requirements above and click 'Generate Draft' to build." // i18n-ignore
                          className="w-full p-3.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono leading-relaxed focus:outline-none focus:ring-1 focus:ring-indigo-500 text-slate-800"
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  /* History Tab Content */
                  <div className="space-y-4">
                    <div className="flex items-center justify-between gap-4 bg-slate-50 p-3 rounded-xl border border-slate-150">
                      <div className="flex-1 relative">
                        <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                          type="text"
                          value={outreachHistorySearch}
                          onChange={(e) => setOutreachHistorySearch(e.target.value)}
                          placeholder="Search previous goals, businesses, styles, or words..."
                          className="w-full pl-9 pr-3 py-1.5 bg-white border border-slate-250 rounded-lg text-xs font-semibold focus:outline-none text-slate-800"
                        />
                      </div>
                      {outreachHistorySearch && (
                        <button
                          type="button"
                          onClick={() => setOutreachHistorySearch("")}
                          className="text-[10px] font-extrabold text-slate-500 hover:text-slate-800 uppercase"
                        >
                          Clear
                        </button>
                      )}
                    </div>

                    {isLoadingPastDrafts ? (
                      <div className="py-12 flex flex-col items-center justify-center text-slate-400 space-y-2">
                        <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                        <p className="text-xs font-bold">Fetching saved templates history...</p>
                      </div>
                    ) : (() => {
                      const filteredHistory = pastOutreachDrafts.filter(item => {
                        const term = outreachHistorySearch.toLowerCase().trim();
                        if (!term) return true;
                        return (
                          item.BusinessName?.toLowerCase().includes(term) ||
                          item.goal?.toLowerCase().includes(term) ||
                          item.draft?.toLowerCase().includes(term) ||
                          item.style?.toLowerCase().includes(term)
                        );
                      });

                      if (filteredHistory.length === 0) {
                        return (
                          <div className="py-12 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                            <Info className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                            <p className="text-xs font-black text-slate-600">No matching outreach drafts found</p>
                            <p className="text-[10.5px] text-slate-450 mt-1 max-w-sm mx-auto leading-normal">
                              {pastOutreachDrafts.length === 0 
                                ? "You haven't generated any custom templates yet. Click the 'Draft Custom Pitch' tab to create your first outline!"
                                : "Try searching for a different keyword or business name."}
                            </p>
                          </div>
                        );
                      }

                      return (
                        <div className="space-y-4">
                          {filteredHistory.map((item, idx) => (
                            <div key={item.id || idx} className="p-4 bg-white rounded-xl border border-slate-200/85 hover:border-slate-350 transition shadow-3xs space-y-3">
                              <div className="flex items-start justify-between gap-2">
                                <div className="space-y-0.5">
                                  <h4 className="font-extrabold text-xs text-slate-900 flex items-center gap-1.5">
                                    🏢 {item.BusinessName}
                                  </h4>
                                  <p className="text-[10px] font-bold text-slate-500">
                                    Goal: <span className="text-slate-850 font-semibold">{item.goal || "Custom Pitch"}</span>
                                  </p>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                  <span className="text-[9px] font-black bg-slate-100 text-slate-650 px-2 py-0.5 rounded border border-slate-200 uppercase">
                                    {item.style || "Email"}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => copyToClipboard(item.draft)}
                                    className="p-1 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded text-slate-600 hover:text-indigo-600 transition cursor-pointer"
                                    title="Copy draft"
                                  >
                                    {copiedText === item.draft ? (
                                      <Check className="w-3.5 h-3.5 text-emerald-600" />
                                    ) : (
                                      <Copy className="w-3.5 h-3.5" />
                                    )}
                                  </button>
                                </div>
                              </div>
                              
                              <div className="bg-slate-50 p-3 rounded-lg border border-slate-150 relative">
                                <pre className="text-[11px] font-mono leading-relaxed text-slate-700 whitespace-pre-wrap max-h-40 overflow-y-auto pr-2 scrollbar-thin">
                                  {item.draft}
                                </pre>
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Footer Actions */}
              <div className="bg-slate-50 p-4 border-t border-slate-150 flex items-center justify-between">
                <span className="text-[9.5px] text-slate-400 font-semibold">Verify details manually before sending.</span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setIsOutreachOpen(false)}
                    className="px-3.5 py-1.5 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 font-bold rounded-lg text-xs transition shadow-3xs cursor-pointer"
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!isProUser) {
                        setIsUpgradeModalOpen(true);
                      } else {
                        generateOutreachCopy(activeBusiness);
                      }
                    }}
                    disabled={isGeneratingOutreach}
                    className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-bold rounded-lg text-xs flex items-center gap-1.5 transition shadow-2xs cursor-pointer"
                  >
                    {isGeneratingOutreach ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Sparkles className="w-3 h-3" />
                    )}
                    <span>{outreachDraft ? "Regenerate Draft" : "Generate Draft"}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {toastNotification && (
          <div className={`fixed top-5 right-5 z-[9999] flex items-center gap-2.5 px-4 py-3 rounded-xl shadow-2xl border animate-slide-in font-sans transition-all ${
            settingsAppTheme === "light"
              ? toastNotification.type === "success"
                ? "bg-emerald-50 text-emerald-950 border-emerald-200 shadow-emerald-900/10"
                : toastNotification.type === "error"
                ? "bg-rose-50 text-rose-950 border-rose-200 shadow-rose-900/10"
                : "bg-blue-50 text-blue-950 border-blue-200 shadow-blue-900/10"
              : settingsAppTheme === "sunset"
              ? toastNotification.type === "success"
                ? "bg-[#faf6f0] text-[#2d251e] border-emerald-300 shadow-[#8c7a6b]/20"
                : toastNotification.type === "error"
                ? "bg-[#faf6f0] text-[#2d251e] border-rose-300 shadow-[#8c7a6b]/20"
                : "bg-[#faf6f0] text-[#2d251e] border-amber-300 shadow-[#8c7a6b]/20"
              : settingsAppTheme === "ocean"
              ? toastNotification.type === "success"
                ? "bg-[#f0f7f9] text-[#0b2533] border-emerald-300 shadow-[#5a7d8c]/20"
                : toastNotification.type === "error"
                ? "bg-[#f0f7f9] text-[#0b2533] border-rose-300 shadow-[#5a7d8c]/20"
                : "bg-[#f0f7f9] text-[#0b2533] border-teal-300 shadow-[#5a7d8c]/20"
              : resolveTheme(settingsAppTheme) === "light"
              ? toastNotification.type === "success"
                ? "bg-emerald-50 text-emerald-950 border-emerald-200 shadow-emerald-900/10"
                : toastNotification.type === "error"
                ? "bg-rose-50 text-rose-950 border-rose-200 shadow-rose-900/10"
                : "bg-blue-50 text-blue-950 border-blue-200 shadow-blue-900/10"
              : toastNotification.type === "success"
              ? "bg-slate-900 text-slate-100 border-emerald-800/80 shadow-black/80"
              : toastNotification.type === "error"
              ? "bg-slate-900 text-slate-100 border-rose-800/80 shadow-black/80"
              : "bg-slate-900 text-slate-100 border-blue-800/80 shadow-black/80"
          }`}>
            {toastNotification.type === 'success' ? (
              <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold text-xs shrink-0">✓</span>
            ) : toastNotification.type === 'error' ? (
              <span className="w-5 h-5 rounded-full bg-rose-500/20 text-rose-600 dark:text-rose-400 flex items-center justify-center font-bold text-xs shrink-0">✕</span>
            ) : (
              <span className="w-5 h-5 rounded-full bg-blue-500/20 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold text-xs shrink-0">ℹ</span>
            )}
            <span className="font-semibold text-xs tracking-wide">{toastNotification.message}</span>
            <button 
              onClick={() => setToastNotification(null)}
              className={`ml-2 text-xs font-bold opacity-60 hover:opacity-100 cursor-pointer ${
                settingsAppTheme === "light" ? "text-slate-500 hover:text-slate-900" :
                settingsAppTheme === "sunset" ? "text-[#8a725e] hover:text-[#2d251e]" :
                settingsAppTheme === "ocean" ? "text-[#405f70] hover:text-[#0b2533]" :
                "text-slate-400 hover:text-white"
              }`}
            >
              ✕
            </button>
          </div>
        )}

      </div>
    </APIProvider>
  );

  // Enrichment call
  async function enrichBusiness(Business: BusinessBusiness) {
    const safetyCheck = detectCrisisSearch(keyword);
    if (safetyCheck.severity === "urgent_crisis") {
      setSearchError("Actions blocked: Immediate crisis safety triggers activated. Crawler and outreach tools are disabled.");
      showToast("Actions blocked: Crisis safety triggers activated.", "error");
      return;
    }

    if (!currentUser) {
      showToast("Please sign in or create an account to extract contacts.", "info");
      navigateTo("/login");
      return;
    }

    showToast(`Searching decision-maker contacts for ${Business.name}...`, "info");
    setContactSyncOrLoadFailed(false);
    // Set Business state to crawling inside both search Businesses and saved Businesses lists
    setBusinesses((prev) => {
      const exists = prev.some((l) => l.id === Business.id);
      if (exists) {
        return prev.map((l) => (l.id === Business.id ? { ...l, crawlStatus: "crawling", contacts: [], crawlError: undefined, crawlErrorCode: undefined, contactSaveFailed: false } : l));
      }
      return [...prev, { ...Business, crawlStatus: "crawling", contacts: [], crawlError: undefined, crawlErrorCode: undefined, contactSaveFailed: false }];
    });
    setSavedBusinesses((prev) =>
      prev.map((l) => (l.id === Business.id ? { ...l, crawlStatus: "crawling", contacts: [], crawlError: undefined, crawlErrorCode: undefined, contactSaveFailed: false } : l))
    );

    try {
      const requestId = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `req_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

      // Query contact scraper through the backend
      const response = await apiFetch("/api/crawl", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: Business.id,
          name: Business.name,
          address: Business.address,
          website: Business.website,
          phone: Business.phone,
          BusinessObject: Business,
          requestId
        }),
      });

      let data: any = null;
      try {
        data = await response.json();
      } catch (e) {
        // Ignore JSON parsing failure
      }

      // Synchronize authoritative allowance state
      if (data?.allowance) {
        setBillingStatus((prev: any) => {
          if (!prev) return prev;
          return {
            ...prev,
            limits: {
              ...prev.limits,
              contact_crawl: {
                count: data.allowance.used,
                limit: data.allowance.unlimited ? 9999 : (data.allowance.limit ?? 15),
                allowed: data.allowance.unlimited ? true : (data.allowance.remaining !== null ? data.allowance.remaining > 0 : true)
              }
            }
          };
        });
      }

      if (!response.ok || data?.success === false) {
        const errCode = data?.error?.code || (response.status === 403 ? "user_allowance_exhausted" : "internal_error");
        const errMsg = data?.error?.userMessage || data?.error?.message || data?.error || `Contact Search failed with status ${response.status}`;

        if (errCode === "user_allowance_exhausted" || response.status === 403) {
          setIsUpgradeModalOpen(true);
          loadBillingStatus();
        }

        const errObj = new Error(errMsg) as any;
        errObj.code = errCode;
        throw errObj;
      }

      // 3. Reload contacts from database
      let dbBusinesses: any[] = [];
      try {
        dbBusinesses = await loadSavedBusinessesFromDb();
      } catch (loadDbErr) {
        console.warn("Failed to reload saved Businesses list from database after crawl:", loadDbErr);
      }

      const matchingDbBusiness = dbBusinesses.find((l: any) => l.id === Business.id);
      const finalContacts = matchingDbBusiness?.contacts?.length > 0 ? matchingDbBusiness.contacts : (data?.contacts || []);
      const saveContactsFailed = data?.saveFailed || (data?.contacts?.length > 0 && finalContacts.length === 0);

      const updatedBusiness: BusinessBusiness = {
        ...Business,
        crawlStatus: "success",
        contacts: finalContacts,
        crawlError: undefined,
        crawlErrorCode: undefined,
        contactSaveFailed: !!saveContactsFailed
      };

      setBusinesses((prev) => {
        const exists = prev.some((l) => l.id === Business.id);
        if (exists) {
          return prev.map((l) => l.id === Business.id ? updatedBusiness : l);
        }
        return [...prev, updatedBusiness];
      });
      setSavedBusinesses((prev) =>
        prev.map((l) => l.id === Business.id ? updatedBusiness : l)
      );

      // LOG CONTACT CRAWL RESULTS TO DATABASE HISTORY
      addHistoryItem({
        lookup_type: 'contact_crawl',
        Business_id: Business.id,
        Business_name: Business.name,
        contacts: finalContacts,
        target_name: `Sourced ${finalContacts.length} direct contacts via AI scrapers`
      });

      await triggerQuestIncrement("contact_lookup", 1);
      showToast(`Direct contacts extracted for ${Business.name}!`, "success");

    } catch (err: any) {
      const errorMsg = getErrorMessage(err, "Contact Search is temporarily unavailable.");
      const errorCode = err?.code || "internal_error";
      console.error("[ContactSearch] Search failure:", errorMsg);
      showToast(`Contact search failed: ${errorMsg}`, "error");

      const updatedErrorBusiness: BusinessBusiness = {
        ...Business,
        crawlStatus: "error",
        crawlError: errorMsg,
        crawlErrorCode: errorCode,
        contactSaveFailed: false
      };

      setBusinesses((prev) => {
        const exists = prev.some((l) => l.id === Business.id);
        if (exists) {
          return prev.map((l) => l.id === Business.id ? updatedErrorBusiness : l);
        }
        return [...prev, updatedErrorBusiness];
      });
      setSavedBusinesses((prev) =>
        prev.map((l) => l.id === Business.id ? updatedErrorBusiness : l)
      );
    }
  }
}

/* Map style templates for visual mapping upgrades */
const MAP_STYLES: Record<string, google.maps.MapTypeStyle[]> = {
  standard: [],
  silver: [
    { elementType: "geometry", stylers: [{ color: "#f5f5f5" }] },
    { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#f5f5f5" }] },
    { featureType: "poi", elementType: "geometry", stylers: [{ color: "#eeeeee" }] },
    { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
    { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
    { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#dadada" }] },
    { featureType: "water", elementType: "geometry", stylers: [{ color: "#c9c9c9" }] }
  ],
  dark: [
    { elementType: "geometry", stylers: [{ color: "#212121" }] },
    { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#212121" }] },
    { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#181818" }] },
    { featureType: "road", elementType: "geometry.fill", stylers: [{ color: "#2c2c2c" }] },
    { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#8a8a8a" }] },
    { featureType: "water", elementType: "geometry", stylers: [{ color: "#000000" }] }
  ],
  retro: [
    { elementType: "geometry", stylers: [{ color: "#f5eb3a" }, { visibility: "simplified" }, { saturation: -44 }, { lightness: 18 }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#5c533f" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#f5f1e6" }] },
    { featureType: "poi", elementType: "geometry", stylers: [{ color: "#dfd2ae" }] },
    { featureType: "road", elementType: "geometry", stylers: [{ color: "#fdfcf8" }] },
    { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#f8c967" }] },
    { featureType: "water", elementType: "geometry.fill", stylers: [{ color: "#b9d3c2" }] }
  ],
  ocean: [
    { elementType: "geometry", stylers: [{ color: "#f0f7f9" }] },
    { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#405f70" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#f0f7f9" }] },
    { featureType: "poi", elementType: "geometry", stylers: [{ color: "#dee8ee" }] },
    { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
    { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#e3eff5" }] },
    { featureType: "water", elementType: "geometry", stylers: [{ color: "#8acbd6" }] }
  ]
};

/* Translucent radial coverage representing visual territories / hotspots */
function MapCircle({ 
  center, 
  radius, 
  color, 
  score 
}: { 
  center: { lat: number; lng: number }; 
  radius: number; 
  color: string; 
  score: number; 
}) {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    const opacity = score ? (score / 100) * 0.35 : 0.2;
    const circle = new google.maps.Circle({
      map,
      center,
      radius, // in meters
      fillColor: color,
      fillOpacity: opacity,
      strokeColor: color,
      strokeOpacity: opacity + 0.15,
      strokeWeight: 1.2,
    });
    return () => {
      circle.setMap(null);
    };
  }, [map, center, radius, color, score]);
  return null;
}

/* Custom interactive traffic flow overlay */
function MapTrafficLayer({ enabled }: { enabled: boolean }) {
  const map = useMap();
  const trafficLayerRef = useRef<google.maps.TrafficLayer | null>(null);

  useEffect(() => {
    if (!map) return;
    if (enabled) {
      if (!trafficLayerRef.current) {
        trafficLayerRef.current = new google.maps.TrafficLayer();
      }
      trafficLayerRef.current.setMap(map);
    } else {
      if (trafficLayerRef.current) {
        trafficLayerRef.current.setMap(null);
      }
    }
    return () => {
      if (trafficLayerRef.current) {
        trafficLayerRef.current.setMap(null);
      }
    };
  }, [map, enabled]);

  return null;
}

/* Custom interactive polyline for straight line measurement */
function MapPolyline({
  path,
  color = "#f43f5e",
}: {
  path: { lat: number; lng: number }[];
  color?: string;
}) {
  const map = useMap();
  useEffect(() => {
    if (!map || path.length < 2) return;
    const polyline = new google.maps.Polyline({
      map,
      path,
      strokeColor: color,
      strokeOpacity: 0,
      icons: [{
        icon: {
          path: "M 0,-1 0,1",
          strokeOpacity: 1,
          scale: 2.2,
          strokeColor: color,
        },
        offset: "0",
        repeat: "10px"
      }],
      geodesic: true,
    });
    return () => {
      polyline.setMap(null);
    };
  }, [map, path, color]);
  return null;
}

interface MapCluster {
  id: string;
  center: { lat: number; lng: number };
  businesses: BusinessBusiness[];
}

function clusterMarkers(
  businesses: BusinessBusiness[],
  zoom: number,
  selectedBusinessId: string | null
): { clusters: MapCluster[]; singletons: BusinessBusiness[] } {
  // If zoom is very high (e.g. >= 14), we don't cluster at all to show full details!
  if (zoom >= 14 || businesses.length === 0) {
    return { clusters: [], singletons: businesses };
  }

  // If a business is selected, let's extract it and handle it as a singleton.
  let filteredBusinesses = businesses;
  let selectedBusiness: BusinessBusiness | null = null;
  if (selectedBusinessId) {
    selectedBusiness = businesses.find(b => b.id === selectedBusinessId) || null;
    if (selectedBusiness) {
      filteredBusinesses = businesses.filter(b => b.id !== selectedBusinessId);
    }
  }

  // Calculate cluster threshold in degrees.
  // 360 degrees of longitude spans 256 * 2^zoom pixels.
  // Let's use 60 pixels as the clustering radius.
  const pixelRadius = 60;
  const threshold = pixelRadius * (360 / (256 * Math.pow(2, zoom)));

  const clusters: MapCluster[] = [];
  const singletons: BusinessBusiness[] = [];

  // Copy businesses to track visited items
  const unclustered = [...filteredBusinesses];

  while (unclustered.length > 0) {
    const current = unclustered.shift()!;
    const neighbors: BusinessBusiness[] = [];

    // Find all unclustered businesses within the threshold distance
    for (let i = 0; i < unclustered.length; i++) {
      const other = unclustered[i];
      const dLat = other.location.lat - current.location.lat;
      const dLng = other.location.lng - current.location.lng;
      
      // Handle 180-degree longitude wrap-around safely
      let adjustedDLng = dLng;
      if (adjustedDLng > 180) adjustedDLng -= 360;
      if (adjustedDLng < -180) adjustedDLng += 360;

      const dist = Math.sqrt(dLat * dLat + adjustedDLng * adjustedDLng);
      if (dist < threshold) {
        neighbors.push(other);
        unclustered.splice(i, 1);
        i--; // Adjust index since we removed an item
      }
    }

    if (neighbors.length > 0) {
      // Create a cluster!
      const clusterBusinesses = [current, ...neighbors];
      // Calculate the average center for the cluster
      let sumLat = 0;
      let sumLng = 0;
      clusterBusinesses.forEach(b => {
        sumLat += b.location.lat;
        sumLng += b.location.lng;
      });
      const center = {
        lat: sumLat / clusterBusinesses.length,
        lng: sumLng / clusterBusinesses.length,
      };

      clusters.push({
        id: `cluster-${current.id}`,
        center,
        businesses: clusterBusinesses,
      });
    } else {
      singletons.push(current);
    }
  }

  if (selectedBusiness) {
    singletons.push(selectedBusiness);
  }

  return { clusters, singletons };
}

/* MapView Component rendering the actual Google Map */
function MapView({
  Businesses,
  selectedBusinessId,
  onSelectBusiness,
  onCrawlBusiness,
  mapStyle,
  onMapStyleChange,
  settingsAppTheme = "dark",
  googleMapsMapId = "",
  onSearchArea,
  center,
  savedBusinesses = [],
  isProUser = false,
  onUpgradeClick = () => {},
  isSavingBusinessId = null,
  isDeletingBusinessId = null,
  onSaveBusiness,
  onDeleteBusiness,
  isFullscreen: isFullscreenProp,
  onFullscreenChange,
  markerColorMode: markerColorModeProp,
  onMarkerColorModeChange,
}: {
  Businesses: BusinessBusiness[];
  selectedBusinessId: string | null;
  onSelectBusiness: (id: string | null) => void;
  onCrawlBusiness: (Business: BusinessBusiness) => void;
  mapStyle: string;
  onMapStyleChange: (style: string) => void;
  settingsAppTheme?: string;
  googleMapsMapId?: string;
  onSearchArea?: (lat: number, lng: number) => void;
  center?: { lat: number; lng: number } | null;
  savedBusinesses?: BusinessBusiness[];
  isProUser?: boolean;
  onUpgradeClick?: () => void;
  isSavingBusinessId?: string | null;
  isDeletingBusinessId?: string | null;
  onSaveBusiness?: (Business: BusinessBusiness) => Promise<void>;
  onDeleteBusiness?: (id: string, name: string) => Promise<void>;
  isFullscreen?: boolean;
  onFullscreenChange?: (isFullscreen: boolean) => void;
  markerColorMode?: 'score' | 'outreach' | 'saved' | 'hiring';
  onMarkerColorModeChange?: (mode: 'score' | 'outreach' | 'saved' | 'hiring') => void;
}) {
  const { t } = useTranslation();
  const map = useMap();
  const [clickedBusiness, setClickedBusiness] = useState<BusinessBusiness | null>(null);

  // Advanced map control states
  const [radiusValue, setRadiusValue] = useState<number>(0); // 0 means disabled, otherwise radius in meters
  const [showAllRadii, setShowAllRadii] = useState<boolean>(false);
  const [trafficLayerEnabled, setTrafficLayerEnabled] = useState<boolean>(false);
  const [localIsFullscreen, setLocalIsFullscreen] = useState<boolean>(false);
  const isFullscreen = isFullscreenProp !== undefined ? isFullscreenProp : localIsFullscreen;
  const setIsFullscreen = (val: boolean) => {
    if (onFullscreenChange) {
      onFullscreenChange(val);
    } else {
      setLocalIsFullscreen(val);
    }
  };
  const [clusteringEnabled, setClusteringEnabled] = useState<boolean>(true);

  // Map Type layer state
  const [mapTypeId, setMapTypeId] = useState<"roadmap" | "satellite" | "hybrid" | "terrain">("roadmap");

  // Measurement tool states
  const [isMeasuring, setIsMeasuring] = useState<boolean>(false);
  const [measurePointA, setMeasurePointA] = useState<{ lat: number; lng: number } | null>(null);
  const [measurePointB, setMeasurePointB] = useState<{ lat: number; lng: number } | null>(null);

  // Zoom level state for marker clustering
  const [currentZoom, setCurrentZoom] = useState<number>(12);

  // Dock Panel states in Maximized workspace
  const [isPanelExpanded, setIsPanelExpanded] = useState<boolean>(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth >= 1024;
    }
    return true;
  });
  const [activeSection, setActiveSection] = useState<'legend' | 'view' | 'markers' | 'tools' | 'insights'>(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth < 1024 ? 'legend' : 'view';
    }
    return 'view';
  });
  
  // Custom display filters for Map Overlay Visualization
  const [displayMode, setDisplayMode] = useState<'all' | 'saved' | 'hiring' | 'openings' | 'contacted'>('all');
  const [localMarkerColorMode, setLocalMarkerColorMode] = useState<'score' | 'outreach' | 'saved' | 'hiring'>('score');
  const markerColorMode = markerColorModeProp !== undefined ? markerColorModeProp : localMarkerColorMode;
  const setMarkerColorMode = onMarkerColorModeChange !== undefined ? onMarkerColorModeChange : setLocalMarkerColorMode;

  // Route Planning states
  const [showRoutePlan, setShowRoutePlan] = useState<boolean>(false);
  const [routeSequence, setRouteSequence] = useState<BusinessBusiness[]>([]);
  const [routeStartZip, setRouteStartZip] = useState<string>("");

  // Hidden accessibility announcements
  const [ariaLiveAnnouncement, setAriaLiveAnnouncement] = useState<string>("");
  const [isLegendOpen, setIsLegendOpen] = useState<boolean>(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState<boolean>(false);
  const [isMobileToolsOpen, setIsMobileToolsOpen] = useState<boolean>(false);

  // Accessibility refs
  const maximizeButtonRef = useRef<HTMLButtonElement>(null);

  // Mobile screen size tracking for MapView
  const [isMobile, setIsMobile] = useState<boolean>(false);
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint is 1024px
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // iOS Safari / Mobile Browser compatible body scroll lock when Map is Fullscreen
  useEffect(() => {
    if (isMobile && isFullscreen) {
      const originalOverflow = document.body.style.overflow;
      const originalHeight = document.body.style.height;
      
      document.body.style.overflow = "hidden";
      document.body.style.height = "100%";
      
      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.height = originalHeight;
      };
    }
  }, [isMobile, isFullscreen]);

  // Track map zoom dynamically
  useEffect(() => {
    if (!map) return;
    setCurrentZoom(map.getZoom() || 12);
    const zoomListener = google.maps.event.addListener(map, "zoom_changed", () => {
      setCurrentZoom(map.getZoom() || 12);
    });
    return () => {
      google.maps.event.removeListener(zoomListener);
    };
  }, [map]);

  // Center maps on Businesses if explicitly triggered
  const recenterMap = () => {
    if (!map || Businesses.length === 0) return;
    const bounds = new google.maps.LatLngBounds();
    Businesses.forEach((Business) => bounds.extend(Business.location));
    map.fitBounds(bounds);
    
    // Set a maximum zoom level so we don't zoom in crazy close for single Businesses
    const listener = google.maps.event.addListener(map, 'bounds_changed', () => {
      if (map.getZoom()! > 15) {
        map.setZoom(15);
      }
      google.maps.event.removeListener(listener);
    });
  };

  // Zoom closely into a specific pin
  const zoomToPin = (business: BusinessBusiness) => {
    if (!map || !business) return;
    map.panTo(business.location);
    map.setZoom(17);
    setClickedBusiness(business);
    setAriaLiveAnnouncement(`Zoomed in close to pin for ${business.name}.`);
  };

  // Only auto-center on initial map load or when new search results arrive, NOT during user pan/scroll/zoom
  const hasInitialCenteredRef = useRef<boolean>(false);
  const prevBusinessesCountRef = useRef<number>(0);

  useEffect(() => {
    if (!map || Businesses.length === 0) return;
    if (!hasInitialCenteredRef.current || (prevBusinessesCountRef.current === 0 && Businesses.length > 0)) {
      recenterMap();
      hasInitialCenteredRef.current = true;
    }
    prevBusinessesCountRef.current = Businesses.length;
  }, [map, Businesses.length]);

  // Selected Business live updates
  useEffect(() => {
    if (!map || !selectedBusinessId) return;
    const activeBusiness = Businesses.find((l) => l.id === selectedBusinessId);
    if (activeBusiness) {
      setAriaLiveAnnouncement(`Selected business: ${activeBusiness.name}, score is ${activeBusiness.Businessescore || 'unrated'}.`);
    }
  }, [map, selectedBusinessId, Businesses]);

  // Escape key support to close Fullscreen Mode
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isFullscreen) {
        setIsFullscreen(false);
        setAriaLiveAnnouncement("Exited full screen map workspace.");
        setTimeout(() => {
          maximizeButtonRef.current?.focus();
        }, 100);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isFullscreen]);

  // Calculate distance
  const getDistanceMiles = (bLoc: { lat: number, lng: number }) => {
    if (!center) return null;
    return getDistanceInMiles(bLoc.lat, bLoc.lng, center.lat, center.lng).toFixed(1);
  };

  // Filter businesses depending on custom map display filter state
  const displayedBusinesses = React.useMemo(() => {
    return Businesses.filter(b => {
      if (displayMode === 'saved') {
        return savedBusinesses.some(s => s.id === b.id);
      }
      if (displayMode === 'hiring') {
        return !!b.hiringPageUrl || normalizeTags(b.opportunityTags).some(t => t.toLowerCase().includes("careers"));
      }
      if (displayMode === 'openings') {
        return b.hiringStatus === 'hiring' || b.hiringStatus === 'active' || normalizeTags(b.opportunityTags).some(t => t.toLowerCase().includes("verified opening"));
      }
      if (displayMode === 'contacted') {
        return b.pipelineStage === 'Contacted' || b.pipelineStage === 'Interested' || b.pipelineStage === 'Won';
      }
      return true;
    });
  }, [Businesses, displayMode, savedBusinesses]);

  // Live GIS Insights Analysis Calculations
  const insights = React.useMemo(() => {
    const totalMapped = Businesses.length;
    const careersFound = Businesses.filter(b => b.hiringPageUrl || normalizeTags(b.opportunityTags).some(t => t.toLowerCase().includes("careers"))).length;
    const verifiedOpenings = Businesses.filter(b => b.hiringStatus === 'hiring' || b.hiringStatus === 'active' || normalizeTags(b.opportunityTags).some(t => t.toLowerCase().includes("verified opening"))).length;
    const savedCount = Businesses.filter(b => savedBusinesses.some(s => s.id === b.id)).length;
    
    // Scoring average
    const validScores = Businesses.map(b => b.Businessescore !== undefined ? b.Businessescore : analyzeBusinessProspect(b).score);
    const avgScore = validScores.length > 0 ? Math.round(validScores.reduce((sum, s) => sum + s, 0) / validScores.length) : 0;
    
    // Score buckets
    let hot = 0, warm = 0, cold = 0;
    validScores.forEach(s => {
      if (s >= 80) hot++;
      else if (s >= 50) warm++;
      else cold++;
    });

    // Industry Distribution
    const industries: Record<string, number> = {};
    Businesses.forEach(b => {
      const cat = b.category || "Other Service";
      industries[cat] = (industries[cat] || 0) + 1;
    });
    const sortedIndustries = Object.entries(industries)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4);

    const coveragePercentage = totalMapped > 0 ? Math.round((savedCount / totalMapped) * 100) : 0;

    return { totalMapped, careersFound, verifiedOpenings, savedCount, avgScore, hot, warm, cold, sortedIndustries, coveragePercentage };
  }, [Businesses, savedBusinesses]);

  // Optimize saved leads sequence route using nearest-first algorithm
  const optimizeRoutePlan = () => {
    if (!isProUser) {
      onUpgradeClick();
      return;
    }

    const unvisited = [...savedBusinesses];
    if (unvisited.length === 0) {
      setAriaLiveAnnouncement("No saved businesses available to construct a route plan.");
      return;
    }

    let baseCoords = center || { lat: 37.0902, lng: -95.7129 };
    const matchingBusiness = savedBusinesses.find(l => l.zipcode === routeStartZip || l.address.includes(routeStartZip));
    if (matchingBusiness) {
      baseCoords = matchingBusiness.location;
    } else if (savedBusinesses[0]) {
      baseCoords = savedBusinesses[0].location;
    }

    const ordered: BusinessBusiness[] = [];
    let currentPoint = baseCoords;

    while (unvisited.length > 0) {
      let nearestIdx = 0;
      let nearestDist = Infinity;
      unvisited.forEach((b, index) => {
        const d = getDistanceInMiles(b.location.lat, b.location.lng, currentPoint.lat, currentPoint.lng);
        if (d < nearestDist) {
          nearestDist = d;
          nearestIdx = index;
        }
      });
      const selected = unvisited.splice(nearestIdx, 1)[0];
      ordered.push(selected);
      currentPoint = selected.location;
    }

    setRouteSequence(ordered);
    setShowRoutePlan(true);
    setAriaLiveAnnouncement(`Optimized map route planned across ${ordered.length} locations successfully.`);
  };

  // Theme support classes
  const activeResolvedTheme = resolveTheme(settingsAppTheme);
  const isLightTheme = activeResolvedTheme === "light";
  
  const mainBgClass = isLightTheme
    ? "bg-white text-slate-800 border-slate-200"
    : "bg-slate-900 text-white border-slate-800";

  const controlBtnClass = `p-2 rounded-lg border transition-all cursor-pointer flex items-center justify-center shadow-sm relative group shrink-0 ${
    isLightTheme 
      ? "bg-white border-slate-200 hover:bg-slate-50 text-slate-600 hover:text-slate-900" 
      : "bg-slate-800/90 border-slate-700 hover:bg-slate-750 text-slate-300 hover:text-white"
  }`;

  const controlBtnActive = `p-2 rounded-lg border transition-all cursor-pointer flex items-center justify-center shadow-md relative group shrink-0 bg-indigo-600 border-indigo-500 text-white`;

  return (
    <div 
      className={isFullscreen 
        ? "fixed inset-0 z-[9999] bg-slate-950 flex flex-col font-sans select-none" 
        : "w-full h-full relative"
      } 
      style={isFullscreen ? {} : { minHeight: "140px" }}
    >
      {/* Invisible Screen-Reader Live Region for dynamic maps accessibility */}
      <div className="sr-only" aria-live="polite">
        {ariaLiveAnnouncement}
      </div>

      {/* 1. Header: ONLY visible when fullscreen */}
      {isFullscreen && !isMobile && (
        <div className={`px-5 py-3 flex flex-wrap items-center justify-between gap-3 shrink-0 border-b transition-all duration-300 ${
          isLightTheme
            ? "bg-white border-slate-200"
            : "bg-slate-900 border-slate-800"
        }`}>
          <div className="flex items-center gap-3">
            {/* Quick Collapse Sidebar Toggle */}
            <button
              onClick={() => setIsPanelExpanded(!isPanelExpanded)}
              className={`p-1.5 rounded-md border transition-all cursor-pointer ${
                isLightTheme 
                  ? "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100" 
                  : "bg-slate-850 border-slate-750 text-slate-300 hover:bg-slate-800"
              }`}
              title={isPanelExpanded ? "Collapse Control Dock" : "Expand Control Dock"}
              aria-label={isPanelExpanded ? "Collapse Control Dock" : "Expand Control Dock"}
            >
              <Menu className="w-4 h-4" />
            </button>

            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
              <span className={`text-sm font-black tracking-wider uppercase font-mono ${
                isLightTheme
                  ? "text-slate-900"
                  : "text-white"
              }`}>{t("search:map_console")}</span>
              <span className={`text-[11px] font-bold ml-1 ${
                isLightTheme
                  ? "text-slate-500"
                  : "text-slate-400"
              }`}>{t("search:map_active_positions", { count: displayedBusinesses.length })}</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Top Toolbar quick info */}
            {displayMode !== "all" && (
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-500/10 text-amber-500 border border-amber-500/20 capitalize">
                Filtering: {displayMode}
              </span>
            )}

            {/* Legend Toggle in Fullscreen Header */}
            <button
              onClick={() => {
                setIsLegendOpen(!isLegendOpen);
                setAriaLiveAnnouncement(`Legend ${!isLegendOpen ? "opened" : "closed"}`);
              }}
              className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 font-extrabold text-[11px] transition cursor-pointer border ${
                isLegendOpen
                  ? "bg-indigo-650 hover:bg-indigo-700 text-white border-transparent"
                  : isLightTheme
                  ? "bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200"
                  : "bg-slate-850 hover:bg-slate-800 text-slate-300 border-slate-750"
              }`}
              title="Toggle Map Legend"
              aria-label="Toggle map legend"
            >
              <Info className="w-3.5 h-3.5" />
              <span>Legend</span>
            </button>

            {/* Exit Full Screen Button */}
            <button
              onClick={() => {
                setIsFullscreen(false);
                setAriaLiveAnnouncement("Exited full screen map workspace.");
              }}
              className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 font-extrabold text-[11px] transition cursor-pointer border ${
                isLightTheme
                  ? "bg-rose-50 hover:bg-rose-100 text-rose-600 border-rose-200"
                  : "bg-rose-900/40 hover:bg-rose-900/60 text-rose-200 border-rose-800"
              }`}
              title="Exit Fullscreen"
            >
              <Minimize2 className="w-3.5 h-3.5" />
              <span>{t("search:map_exit")}</span>
            </button>
          </div>
        </div>
      )}

      {/* 2. Main Workspace Layout */}
      <div className={isFullscreen ? "flex-1 flex overflow-hidden w-full relative" : "w-full h-full absolute inset-0"}>
        
        {/* Dock Panel */}
        {isFullscreen && !isMobile && (
          <div 
            className={`h-full flex flex-col overflow-hidden z-20 ${mainBgClass} transition-all duration-300 ease-in-out ${
              isPanelExpanded 
                ? "w-72 sm:w-85 opacity-100 border-r" 
                : "w-0 opacity-0 border-r-0 pointer-events-none"
            }`}
          >
            {/* Stable-width inner wrapper to prevent text wrapping/squishing during slide animation */}
            <div className="w-72 sm:w-85 h-full flex flex-col overflow-hidden shrink-0">
              
              {/* Mobile/Tablet Close Header */}
              <div className="flex items-center justify-between p-3.5 border-b border-dashed border-slate-300/20 shrink-0 lg:hidden">
                <span className="text-xs font-black uppercase tracking-wider text-indigo-400">Map Menu</span>
                <button 
                  onClick={() => setIsPanelExpanded(false)}
                  className="p-1 hover:bg-slate-500/10 rounded-md text-slate-400 hover:text-current cursor-pointer flex items-center justify-center"
                  title="Collapse Menu"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Dock Tab Selector */}
              <div className="flex border-b border-dashed border-slate-300/20 shrink-0">
              {(['view', 'markers', 'tools', 'insights'] as const).map((tab) => {
                const isActive = activeSection === tab;
                const activeBorder = settingsAppTheme === "sunset" ? "border-[#b45309]" : settingsAppTheme === "ocean" ? "border-[#0d5c63]" : "border-indigo-500";
                const activeColor = settingsAppTheme === "sunset" ? "text-[#b45309]" : settingsAppTheme === "ocean" ? "text-[#0d5c63]" : "text-indigo-500";

                return (
                  <button
                    key={tab}
                    onClick={() => {
                      setActiveSection(tab);
                      setAriaLiveAnnouncement(`Opened ${tab} control section.`);
                    }}
                    className={`flex-1 py-3 text-center text-xs font-bold capitalize border-b-2 transition-all flex flex-col items-center gap-1 cursor-pointer ${
                      isActive 
                        ? `${activeBorder} ${activeColor} bg-indigo-500/5` 
                        : "border-transparent text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                    }`}
                  >
                    {tab === 'view' && <Layers className="w-4 h-4" />}
                    {tab === 'markers' && <SlidersHorizontal className="w-4 h-4" />}
                    {tab === 'tools' && <Compass className="w-4 h-4" />}
                    {tab === 'insights' && <Activity className="w-4 h-4" />}
                    <span className="text-[10px] mt-0.5 tracking-wider font-semibold">{tab}</span>
                  </button>
                );
              })}
            </div>

            {/* Tab Body Contents */}
            <div className="flex-1 overflow-y-auto p-4 space-y-5">
              
              {/* SECTION 1: VIEW SETTINGS */}
              {activeSection === 'view' && (
                <div className="space-y-4">
                  <div>
                    <h4 className="text-xs font-black uppercase tracking-wider mb-2 opacity-80 flex items-center gap-1.5">
                      <Layers className="w-3.5 h-3.5 text-indigo-400" />
                      Map Display Type
                    </h4>
                    <p className="text-[10px] text-slate-400 mb-2">Toggle vector geometry or satellite imagery.</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {(["roadmap", "satellite", "hybrid", "terrain"] as const).map((type) => {
                        const isActive = mapTypeId === type;
                        const activeStyle = settingsAppTheme === "sunset" ? "bg-[#b45309] text-white" : settingsAppTheme === "ocean" ? "bg-[#0d5c63] text-white" : "bg-indigo-600 text-white";
                        return (
                          <button
                            key={type}
                            onClick={() => setMapTypeId(type)}
                            className={`text-xs py-2 px-1 rounded transition-all capitalize font-semibold cursor-pointer text-center border ${
                              isActive
                                ? `${activeStyle} border-transparent shadow-md`
                                : isLightTheme
                                ? "bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200"
                                : "bg-slate-800 border-slate-700/80 text-slate-300 hover:bg-slate-750"
                            }`}
                          >
                            {type === "roadmap" ? "Vector Map" : type}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="border-t border-dashed border-slate-300/10 pt-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-xs font-black uppercase tracking-wider opacity-90">Marker Clustering</h4>
                        <p className="text-[9.5px] text-slate-400">Combine close pins at lower zoom levels.</p>
                      </div>
                      <button
                        onClick={() => {
                          setClusteringEnabled(!clusteringEnabled);
                          setAriaLiveAnnouncement(`Marker clustering ${!clusteringEnabled ? "enabled" : "disabled"}`);
                        }}
                        className={`px-3 py-1 rounded text-[11px] font-bold border transition ${
                          clusteringEnabled
                            ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20"
                            : "bg-slate-500/10 text-slate-400 border-slate-500/20"
                        }`}
                      >
                        {clusteringEnabled ? "ON" : "OFF"}
                      </button>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-xs font-black uppercase tracking-wider opacity-90">Traffic Overlay</h4>
                        <p className="text-[9.5px] text-slate-400">Render Google Maps live traffic congestion.</p>
                      </div>
                      <button
                        onClick={() => {
                          setTrafficLayerEnabled(!trafficLayerEnabled);
                          setAriaLiveAnnouncement(`Traffic overlay ${!trafficLayerEnabled ? "enabled" : "disabled"}`);
                        }}
                        className={`px-3 py-1 rounded text-[11px] font-bold border transition ${
                          trafficLayerEnabled
                            ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20"
                            : "bg-slate-500/10 text-slate-400 border-slate-500/20"
                        }`}
                      >
                        {trafficLayerEnabled ? "ACTIVE" : "OFF"}
                      </button>
                    </div>

                    <div className="pt-2">
                      <button
                        onClick={recenterMap}
                        className={`w-full py-2 px-3 rounded flex items-center justify-center gap-1.5 font-bold text-xs border cursor-pointer transition ${
                          isLightTheme
                            ? "bg-slate-100 hover:bg-slate-200 text-slate-700"
                            : "bg-slate-800 border-slate-700 hover:bg-slate-700 text-white"
                        }`}
                      >
                        <Maximize2 className="w-3.5 h-3.5 text-indigo-400" />
                        <span>Center & Fit Map Results</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* SECTION 2: CUSTOMIZABLE FILTERS & MARKER STYLES */}
              {activeSection === 'markers' && (
                <div className="space-y-4">
                  <div>
                    <h4 className="text-xs font-black uppercase tracking-wider mb-2 opacity-80 flex items-center gap-1.5">
                      <SlidersHorizontal className="w-3.5 h-3.5 text-indigo-400" />
                      Visual Display Filter
                    </h4>
                    <p className="text-[10px] text-slate-400 mb-2">Display subset of lead results on map view.</p>
                    <div className="space-y-1.5">
                      {[
                        { id: 'all', label: 'Show All Results', count: Businesses.length },
                        { id: 'saved', label: 'Saved Leads Only', count: savedBusinesses.length },
                        { id: 'hiring', label: 'Has Career Pages', count: insights.careersFound },
                        { id: 'openings', label: 'Verified Openings Only', count: insights.verifiedOpenings },
                        { id: 'contacted', label: 'Pipeline Contacted Leads', count: insights.cold }, // proxy based on status
                      ].map((mode) => {
                        const isSelected = displayMode === mode.id;
                        return (
                          <button
                            key={mode.id}
                            onClick={() => {
                              setDisplayMode(mode.id as any);
                              setAriaLiveAnnouncement(`Map filtering updated to: ${mode.label}`);
                            }}
                            className={`w-full text-left px-3 py-2 rounded-lg border text-xs font-bold flex items-center justify-between transition cursor-pointer ${
                              isSelected
                                ? "bg-indigo-500/10 border-indigo-500 text-indigo-400 shadow-sm"
                                : isLightTheme
                                ? "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100"
                                : "bg-slate-800/50 border-slate-750 text-slate-300 hover:bg-slate-800"
                            }`}
                          >
                            <span>{mode.label}</span>
                            <span className="px-1.5 py-0.5 rounded text-[9px] bg-slate-500/10">{mode.count}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="border-t border-dashed border-slate-300/10 pt-3">
                    <h4 className="text-xs font-black uppercase tracking-wider mb-2 opacity-80">Marker Styling Mode</h4>
                    <p className="text-[10px] text-slate-400 mb-2">Choose logic used to color and shape map markers.</p>
                    <div className="space-y-1.5">
                      {[
                        { id: 'score', label: 'Prospect Score Hierarchy', desc: 'Gold (Hot), Blue (Warm), Gray (Cold)' },
                        { id: 'outreach', label: 'Pipeline Status Phase', desc: 'Purple (Won), Pink (Contacted), Blue' },
                        { id: 'saved', label: 'Durable Bookmarks', desc: 'Emerald (Saved), Slate (Unsaved)' },
                        { id: 'hiring', label: 'Recruitment Activity', desc: 'Indigo (Hiring), Dark Gray (Standard)' },
                      ].map((mode) => {
                        const isSelected = markerColorMode === mode.id;
                        return (
                          <button
                            key={mode.id}
                            onClick={() => {
                              setMarkerColorMode(mode.id as any);
                              setAriaLiveAnnouncement(`Marker styling updated to: ${mode.label}`);
                            }}
                            className={`w-full text-left px-3 py-2.5 rounded-lg border text-xs transition cursor-pointer ${
                              isSelected
                                ? "bg-indigo-500/10 border-indigo-500 text-indigo-400 shadow-sm"
                                : isLightTheme
                                ? "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100"
                                : "bg-slate-800/50 border-slate-750 text-slate-300 hover:bg-slate-800"
                            }`}
                          >
                            <p className="font-bold">{mode.label}</p>
                            <p className="text-[9.5px] text-slate-400 mt-0.5 font-medium">{mode.desc}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* SECTION 3: ROUTE PLANNER & RULER */}
              {activeSection === 'tools' && (
                <div className="space-y-4">
                  
                  {/* Distance Measurement display */}
                  <div>
                    <h4 className="text-xs font-black uppercase tracking-wider mb-2 opacity-80 flex items-center gap-1.5">
                      <Ruler className="w-3.5 h-3.5 text-indigo-400" />
                      Map Measurement Ruler
                    </h4>
                    <div className={`p-3 rounded-lg border space-y-2 ${
                      isLightTheme ? "bg-slate-50 border-slate-200" : "bg-slate-800/30 border-slate-750"
                    }`}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold">Ruler Tool</span>
                        <button
                          onClick={() => {
                            setIsMeasuring(!isMeasuring);
                            if (isMeasuring) {
                              setMeasurePointA(null);
                              setMeasurePointB(null);
                            }
                          }}
                          className={`px-3 py-1 rounded text-xs font-bold transition border ${
                            isMeasuring
                              ? "bg-rose-500/10 text-rose-500 border-rose-500/20"
                              : "bg-indigo-500/10 text-indigo-400 border-indigo-500/20"
                          }`}
                        >
                          {isMeasuring ? "RULER ON" : "ACTIVATE"}
                        </button>
                      </div>
                      
                      {isMeasuring && (
                        <div className="text-[10px] space-y-1 border-t border-slate-300/10 pt-2 text-slate-400">
                          {!measurePointA && <p className="italic">Click anywhere on map to drop Point A</p>}
                          {measurePointA && !measurePointB && <p className="text-indigo-400 font-bold">Point A dropped. Click map to drop Point B.</p>}
                          {measurePointA && measurePointB && (
                            <div className="space-y-1 font-semibold text-slate-300">
                              <p className="text-[9px] uppercase tracking-wider text-slate-400">Measured Direct Gap:</p>
                              <p className="text-base font-black text-rose-500">
                                {getDistanceInMiles(measurePointA.lat, measurePointA.lng, measurePointB.lat, measurePointB.lng).toFixed(2)} miles
                              </p>
                              <p className="text-[10px] text-slate-400">
                                ({(getDistanceInMiles(measurePointA.lat, measurePointA.lng, measurePointB.lat, measurePointB.lng) * 1.60934).toFixed(2)} km)
                              </p>
                              <button
                                onClick={() => {
                                  setMeasurePointA(null);
                                  setMeasurePointB(null);
                                }}
                                className="mt-1 text-[9px] text-rose-400 font-bold hover:underline"
                              >
                                Clear Points
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Pro Route planner */}
                  <div className="border-t border-dashed border-slate-300/10 pt-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-black uppercase tracking-wider opacity-80 flex items-center gap-1.5">
                        <Compass className="w-3.5 h-3.5 text-indigo-400" />
                        Sales Route Optimizer
                      </h4>
                      {!isProUser && (
                        <span className="px-1.5 py-0.5 rounded text-[8px] bg-amber-500/10 text-amber-500 border border-amber-500/20 font-black">PRO</span>
                      )}
                    </div>
                    <p className="text-[10px] text-slate-400">Construct an optimized nearest-first path across all of your saved leads to maximize physical sales coverage efficiency.</p>

                    <div className="space-y-2">
                      <div>
                        <label className="text-[9px] uppercase font-black tracking-wider text-slate-400 block mb-1">Starting Zip / Location</label>
                        <input
                          type="text"
                          value={routeStartZip}
                          onChange={(e) => setRouteStartZip(e.target.value)}
                          placeholder="e.g. 90210, or leave blank for default"
                          className={`w-full px-2.5 py-1.5 rounded text-xs outline-none border transition ${
                            isLightTheme
                              ? "bg-slate-50 border-slate-200 text-slate-800 focus:border-indigo-500"
                              : "bg-slate-800 border-slate-700 text-white focus:border-indigo-500"
                          }`}
                        />
                      </div>

                      <button
                        onClick={optimizeRoutePlan}
                        className={`w-full py-2 rounded text-xs font-black transition-all flex items-center justify-center gap-1.5 border shadow ${
                          !isProUser
                            ? "bg-slate-800 text-slate-400 border-slate-700 cursor-not-allowed hover:bg-slate-750"
                            : "bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white border-transparent cursor-pointer"
                        }`}
                      >
                        {!isProUser && <Lock className="w-3.5 h-3.5" />}
                        <span>Optimize Driving Sequence</span>
                      </button>

                      {showRoutePlan && routeSequence.length > 0 && (
                        <div className="space-y-2 pt-2 border-t border-slate-300/10">
                          <p className="text-[10px] font-black text-emerald-500 flex items-center gap-1">
                            <Check className="w-3.5 h-3.5" />
                            Planned Sequence ({routeSequence.length} Saved Leads):
                          </p>
                          <div className="space-y-1 max-h-36 overflow-y-auto pr-1">
                            {routeSequence.map((b, idx) => (
                              <div 
                                key={b.id} 
                                className={`p-1.5 rounded text-[10px] font-semibold flex items-center gap-1.5 ${
                                  isLightTheme ? "bg-slate-100 text-slate-700" : "bg-slate-850 text-slate-300"
                                }`}
                              >
                                <span className="w-4 h-4 rounded-full bg-indigo-500 text-white flex items-center justify-center font-bold text-[8px] shrink-0">{idx + 1}</span>
                                <span className="truncate flex-1">{b.name}</span>
                                <span className="text-[8px] opacity-65 font-mono">{b.zipcode}</span>
                              </div>
                            ))}
                          </div>
                          <button
                            onClick={() => {
                              setShowRoutePlan(false);
                              setRouteSequence([]);
                            }}
                            className="w-full text-center text-[9px] text-slate-400 hover:text-slate-200 underline font-bold"
                          >
                            Hide driving sequence
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* SECTION 4: GIS LIVE ANALYTICS */}
              {activeSection === 'insights' && (
                <div className="space-y-4">
                  <div>
                    <h4 className="text-xs font-black uppercase tracking-wider mb-2 opacity-80 flex items-center gap-1.5">
                      <Activity className="w-3.5 h-3.5 text-indigo-400" />
                      Live GIS Analytics Dashboard
                    </h4>
                    <p className="text-[10px] text-slate-400 mb-3">Real-time mapping statistics of the current search results layer.</p>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <div className={`p-2 rounded-lg border ${isLightTheme ? "bg-slate-50 border-slate-200" : "bg-slate-800/40 border-slate-750"}`}>
                        <span className="text-[9px] uppercase font-bold text-slate-400 block">Mapped Area</span>
                        <span className="text-lg font-black">{insights.totalMapped} leads</span>
                      </div>
                      <div className={`p-2 rounded-lg border ${isLightTheme ? "bg-slate-50 border-slate-200" : "bg-slate-800/40 border-slate-750"}`}>
                        <span className="text-[9px] uppercase font-bold text-slate-400 block">Avg. Prospect Score</span>
                        <span className="text-lg font-black text-indigo-400">{insights.avgScore}/100</span>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-dashed border-slate-300/10 pt-3">
                    <h4 className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2">Prospect Quality Index</h4>
                    <div className="space-y-1.5 text-xs font-semibold">
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1.5 text-slate-300">
                          <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                          Hot Leads (Score &gt;= 80)
                        </span>
                        <span>{insights.hot}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1.5 text-slate-300">
                          <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                          Warm Leads (50 - 79)
                        </span>
                        <span>{insights.warm}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1.5 text-slate-300">
                          <span className="w-2 h-2 rounded-full bg-slate-500"></span>
                          Cold Leads (&lt; 50)
                        </span>
                        <span>{insights.cold}</span>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-dashed border-slate-300/10 pt-3">
                    <h4 className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2">Primary Industries Represented</h4>
                    <div className="space-y-1.5">
                      {insights.sortedIndustries.map(([industry, count]) => {
                        const pct = Math.round((count / insights.totalMapped) * 100) || 0;
                        return (
                          <div key={industry} className="space-y-0.5">
                            <div className="flex items-center justify-between text-[11px] font-bold">
                              <span className="truncate max-w-[180px]">{industry}</span>
                              <span className="opacity-75">{count} ({pct}%)</span>
                            </div>
                            <div className="w-full h-1 bg-slate-800 rounded-full overflow-hidden">
                              <div className="h-full bg-indigo-500" style={{ width: `${pct}%` }}></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Dock Footer Info */}
            <div className="p-3 border-t border-dashed border-slate-300/10 text-[9px] text-slate-500 font-bold uppercase tracking-wider text-center select-none">
              BizNearYou Map Engine v2.4
            </div>
          </div>
        </div>
      )}

        {/* Floating expand button if sidebar is collapsed (fullscreen mode) */}
        {isFullscreen && !isMobile && !isPanelExpanded && (
          <button
            onClick={() => setIsPanelExpanded(true)}
            className={`absolute top-2.5 left-2.5 z-30 p-2 rounded-lg border shadow-lg transition duration-300 cursor-pointer ${
              isLightTheme 
                ? "bg-white border-slate-200 text-slate-700 hover:bg-slate-50" 
                : "bg-slate-900 border-slate-800 text-white hover:bg-slate-800"
            }`}
            title="Expand Control Dock"
          >
            <Menu className="w-4 h-4" />
          </button>
        )}

        {/* Small View / Floating Tool Belt on standard view (when NOT fullscreen) */}
        {!isFullscreen && (
          isMobile ? (
            <div className="absolute top-1.5 right-1.5 z-30 flex flex-col items-end gap-1 font-sans">
              {/* Compact Responsive Horizontal Toolbar */}
              <div className="flex items-center gap-1 bg-slate-900/95 backdrop-blur-xs p-0.5 rounded-lg shadow-lg border border-slate-700/80">
                {/* Center on Search Location */}
                <button
                  onClick={() => {
                    if (map && center) {
                      map.panTo(center);
                      map.setZoom(14);
                      setAriaLiveAnnouncement("Centered map on search location.");
                    }
                  }}
                  className="w-7 h-7 bg-slate-800 hover:bg-slate-700 text-indigo-400 rounded-md flex items-center justify-center transition cursor-pointer active:scale-95 shrink-0"
                  title="Center on Search Location"
                  aria-label="Center on search location"
                >
                  <Compass className="w-3.5 h-3.5" />
                </button>

                {/* Open Map Tools */}
                <button
                  onClick={() => {
                    setIsMobileToolsOpen(true);
                    setAriaLiveAnnouncement("Opened map tools console.");
                  }}
                  className="w-7 h-7 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-md flex items-center justify-center transition cursor-pointer active:scale-95 shrink-0"
                  title="Open Map Tools"
                  aria-label="Open map tools"
                >
                  <MoreHorizontal className="w-3.5 h-3.5 text-amber-400" />
                </button>

                {/* Expand Map */}
                <button
                  onClick={() => {
                    setIsFullscreen(true);
                    setAriaLiveAnnouncement("Entered full screen map workspace.");
                  }}
                  className="w-7 h-7 bg-slate-800 hover:bg-slate-700 text-white rounded-md flex items-center justify-center transition cursor-pointer active:scale-95 shrink-0"
                  title="Expand Map"
                  aria-label="Expand map"
                >
                  <Maximize2 className="w-3.5 h-3.5 text-emerald-400" />
                </button>
              </div>
            </div>
          ) : (
            <div className="absolute top-1.5 right-1.5 z-30 flex flex-col items-end gap-1 font-sans">
              {/* Compact Desktop Horizontal Toolbar */}
              <div className="flex items-center gap-1 bg-slate-900/95 backdrop-blur-xs p-0.5 rounded-lg shadow-lg border border-slate-700/80">
                {/* Zoom In */}
                <button
                  onClick={() => {
                    if (map) {
                      const curr = map.getZoom() || 12;
                      map.setZoom(curr + 1);
                    }
                  }}
                  className="w-7 h-7 bg-slate-800 hover:bg-slate-700 text-white rounded-md flex items-center justify-center transition cursor-pointer active:scale-95 text-xs font-bold shrink-0"
                  title="Zoom In"
                  aria-label="Zoom In"
                >
                  +
                </button>

                {/* Zoom Out */}
                <button
                  onClick={() => {
                    if (map) {
                      const curr = map.getZoom() || 12;
                      map.setZoom(curr - 1);
                    }
                  }}
                  className="w-7 h-7 bg-slate-800 hover:bg-slate-700 text-white rounded-md flex items-center justify-center transition cursor-pointer active:scale-95 text-xs font-bold shrink-0"
                  title="Zoom Out"
                  aria-label="Zoom Out"
                >
                  −
                </button>

                {/* Center on Search Location */}
                <button
                  onClick={recenterMap}
                  className="w-7 h-7 bg-slate-800 hover:bg-slate-700 text-blue-400 rounded-md flex items-center justify-center transition cursor-pointer active:scale-95 shrink-0"
                  title="Reset center to show all results"
                  aria-label="Recenter map bounds"
                >
                  <Compass className="w-3.5 h-3.5" />
                </button>

                {/* Traffic Overlay */}
                <button
                  onClick={() => {
                    setTrafficLayerEnabled(!trafficLayerEnabled);
                    setAriaLiveAnnouncement(`Traffic overlay ${!trafficLayerEnabled ? "enabled" : "disabled"}`);
                  }}
                  className={`w-7 h-7 rounded-md cursor-pointer transition flex items-center justify-center shrink-0 active:scale-95 ${
                    trafficLayerEnabled 
                      ? "bg-amber-600 text-white" 
                      : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                  }`}
                  title="Toggle Traffic Layer"
                  aria-label="Toggle live traffic layer"
                >
                  <Navigation className="w-3.5 h-3.5" />
                </button>

                {/* Ruler Measurement */}
                <button
                  onClick={() => {
                    setIsMeasuring(!isMeasuring);
                    if (isMeasuring) {
                      setMeasurePointA(null);
                      setMeasurePointB(null);
                    }
                  }}
                  className={`w-7 h-7 rounded-md cursor-pointer transition flex items-center justify-center shrink-0 active:scale-95 ${
                    isMeasuring 
                      ? "bg-rose-600 text-white" 
                      : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                  }`}
                  title="Measure point to point distance"
                  aria-label="Toggle measurement ruler"
                >
                  <Ruler className="w-3.5 h-3.5" />
                </button>

                {/* Expand Map */}
                <button
                  ref={maximizeButtonRef}
                  onClick={() => {
                    setIsFullscreen(true);
                    setAriaLiveAnnouncement("Entered full screen premium map workspace.");
                  }}
                  className="w-7 h-7 bg-slate-800 hover:bg-slate-700 text-emerald-400 rounded-md flex items-center justify-center transition cursor-pointer active:scale-95 shrink-0"
                  title="Expand Workspace to Fullscreen"
                  aria-label="Enter fullscreen map mode"
                >
                  <Maximize2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )
        )}

        {/* The Google Map Container */}
        <div className={isFullscreen ? "flex-1 relative w-full h-full" : "w-full h-full absolute inset-0"} data-ai-editable="map-preview" role="application" aria-label="Interactive Google Map of Business Results">
          {isEditorSafeMode && (
            <div 
              data-ai-editable="map-placeholder" 
              className="w-full h-full flex flex-col items-center justify-center bg-slate-100 text-slate-500 font-bold p-8 text-center select-none z-50 absolute inset-0"
              style={{ pointerEvents: "none" }}
            >
              <MapPin className="w-10 h-10 text-slate-400 mb-2 animate-bounce" />
              <h3 className="text-base font-extrabold text-slate-700">Map Preview Placeholder</h3>
              <p className="text-xs text-slate-400 mt-1 max-w-md font-medium">The live interactive Google Map is deactivated in Google AI Studio edit mode to allow visual layout adjustments.</p>
            </div>
          )}

          {/* Floating Measurement Ruler HUD (ONLY in standard view, top-left) */}
          {!isFullscreen && isMeasuring && !isEditorSafeMode && (
            <div className="absolute top-2.5 left-2.5 z-10 pointer-events-auto shadow-lg bg-slate-900/95 border border-slate-700 p-2.5 rounded-lg text-white max-w-[160px] font-sans">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[9px] uppercase tracking-wider font-black text-rose-400">Measurement HUD</span>
                <button 
                  onClick={() => {
                    setIsMeasuring(false);
                    setMeasurePointA(null);
                    setMeasurePointB(null);
                  }}
                  className="text-[8px] underline"
                >
                  Close
                </button>
              </div>
              <div className="text-[10px] space-y-1">
                {!measurePointA && <p className="italic text-slate-400">Click map for A</p>}
                {measurePointA && !measurePointB && <p className="text-indigo-400">Point A set. Click map for B</p>}
                {measurePointA && measurePointB && (
                  <div>
                    <p className="font-extrabold text-rose-500 text-xs">
                      {getDistanceInMiles(measurePointA.lat, measurePointA.lng, measurePointB.lat, measurePointB.lng).toFixed(2)} mi
                    </p>
                    <p className="text-[8px] opacity-75">
                      ({(getDistanceInMiles(measurePointA.lat, measurePointA.lng, measurePointB.lat, measurePointB.lng) * 1.60934).toFixed(2)} km)
                    </p>
                    <button
                      onClick={() => {
                        setMeasurePointA(null);
                        setMeasurePointB(null);
                      }}
                      className="text-[8px] text-slate-400 hover:text-white underline mt-1"
                    >
                      Clear points
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Compact Lead Overlay Preview Card: Floating/Anchored near bottom of Map */}
          {(() => {
            if (!clickedBusiness) return null;
            const activeClickedBusiness = Businesses.find(b => b.id === clickedBusiness.id) || savedBusinesses.find(b => b.id === clickedBusiness.id) || clickedBusiness;
            const crawlStatus = activeClickedBusiness.crawlStatus;

            return (
              <div className={`absolute bottom-4 left-4 right-4 sm:left-auto sm:right-4 z-35 max-w-sm sm:w-85 p-4 rounded-xl border shadow-2xl transition-all animate-slide-up duration-300 ${mainBgClass}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="space-y-0.5 flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="px-1.5 py-0.5 rounded text-[8px] bg-indigo-500/15 text-indigo-400 font-extrabold uppercase">
                        {activeClickedBusiness.category || "Service"}
                      </span>
                      
                      {getDistanceMiles(activeClickedBusiness.location) && (
                        <span className="text-[9px] text-slate-400 font-bold font-mono">
                          {getDistanceMiles(activeClickedBusiness.location)} miles away
                        </span>
                      )}
                    </div>
                    
                    <h4 className="font-black text-sm truncate pr-4">
                      {activeClickedBusiness.name}
                    </h4>
                    <p className="text-[10.5px] opacity-75 truncate">{activeClickedBusiness.address}</p>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    <button 
                      onClick={() => setClickedBusiness(null)}
                      className="p-1 hover:bg-slate-500/10 rounded-md text-slate-400 hover:text-current cursor-pointer"
                      title="Close lead preview"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Lead detail metrics */}
                <div className="grid grid-cols-2 gap-1.5 my-3 border-t border-b border-dashed border-slate-300/15 py-2.5">
                  <div>
                    <span className="text-[8.5px] text-slate-400 uppercase font-black tracking-wider block">Lead Rank Score</span>
                    <span className="text-sm font-black text-amber-500">{activeClickedBusiness.Businessescore || analyzeBusinessProspect(activeClickedBusiness).score}/100</span>
                  </div>
                  <div>
                    <span className="text-[8.5px] text-slate-400 uppercase font-black tracking-wider block">Recruiting Stage</span>
                    <span className="text-xs font-extrabold capitalize text-indigo-400 truncate block mt-0.5">
                      {activeClickedBusiness.hiringStatus === 'hiring' || activeClickedBusiness.hiringStatus === 'active' ? "🎯 Actively Hiring" : "Idle"}
                    </span>
                  </div>
                </div>

                {/* Action Toolbar Belt */}
                <div className="flex items-center gap-2">
                  {/* 1-click Quick Save/Unsave */}
                  {(() => {
                    const isSaved = savedBusinesses.some((s) => s.id === activeClickedBusiness.id);
                    const isSaving = isSavingBusinessId === activeClickedBusiness.id;
                    const isDeleting = isDeletingBusinessId === activeClickedBusiness.id;

                    return (
                      <button
                        onClick={async () => {
                          if (isSaved) {
                            if (onDeleteBusiness) await onDeleteBusiness(activeClickedBusiness.id, activeClickedBusiness.name);
                          } else {
                            if (onSaveBusiness) await onSaveBusiness(activeClickedBusiness);
                          }
                          setAriaLiveAnnouncement(isSaved ? "Removed lead from saved dashboard." : "Saved lead to active dashboard.");
                        }}
                        disabled={isSaving || isDeleting}
                        className={`flex-1 py-1.5 px-3 rounded-lg font-bold text-[11px] flex items-center justify-center gap-1 border transition-all cursor-pointer ${
                          isSaved
                            ? "bg-rose-500/10 border-rose-500/20 text-rose-400 hover:bg-rose-500/20"
                            : "bg-emerald-500/10 border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20"
                        }`}
                      >
                        {isSaving || isDeleting ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : isSaved ? (
                          <>
                            <Trash2 className="w-3.5 h-3.5" />
                            <span>Remove</span>
                          </>
                        ) : (
                          <>
                            <Bookmark className="w-3.5 h-3.5" />
                            <span>Save Lead</span>
                          </>
                        )}
                      </button>
                    );
                  })()}

                  {/* Owner/Contact Extraction */}
                  {(!crawlStatus || crawlStatus === "idle" || crawlStatus === "error") && (
                    <button
                      onClick={() => {
                        setClickedBusiness((prev) => prev ? { ...prev, crawlStatus: "crawling" } : prev);
                        onCrawlBusiness(activeClickedBusiness);
                        setAriaLiveAnnouncement("Triggering contact crawl for selection...");
                      }}
                      className="flex-1 bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-1.5 px-3 rounded-lg text-[11px] transition cursor-pointer flex items-center justify-center gap-1 shadow-sm active:scale-95"
                    >
                      <Sparkles className="w-3.5 h-3.5 text-white" />
                      <span>{crawlStatus === "error" ? "Retry Extract" : "Extract Contacts"}</span>
                    </button>
                  )}

                  {crawlStatus === "crawling" && (
                    <div className="flex-1 bg-slate-800 border border-slate-700 text-indigo-400 font-semibold flex items-center justify-center gap-1 py-1.5 rounded-lg text-[11px]">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Searching...</span>
                    </div>
                  )}

                  {crawlStatus === "success" && (
                    <div className="flex-1 bg-emerald-500/10 border border-emerald-500/20 p-1.5 rounded-lg text-[10px] truncate font-semibold text-emerald-400 flex items-center gap-1">
                      <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                      <span className="truncate">{activeClickedBusiness.contacts?.[0]?.email || activeClickedBusiness.contacts?.[0]?.name || "Direct contacts found!"}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {(() => {
            const mapThemeKey = 
              settingsAppTheme === "light" ? "standard" :
              settingsAppTheme === "sunset" ? "retro" :
              settingsAppTheme === "ocean" ? "ocean" : 
              "dark";

            const mapProps = { 
              styles: googleMapsMapId ? undefined : (mapThemeKey === "standard" ? undefined : (MAP_STYLES[mapThemeKey] || [])),
              mapTypeId: mapTypeId
            };

            // Compute clustered markers based on displayed subset of results
            const { clusters, singletons } = clusterMarkers(displayedBusinesses, currentZoom, selectedBusinessId);

            return (
              <GoogleMap
                mapId={googleMapsMapId || undefined}
                defaultCenter={{ lat: 37.0902, lng: -95.7129 }} // Center of USA
                defaultZoom={4}
                {...mapProps}
                disableDefaultUI={true}
                zoomControl={false}
                gestureHandling={isMobile ? "cooperative" : "greedy"}
                internalUsageAttributionIds={["gmp_mcp_codeassist_v1_aistudio"]}
                style={{ 
                  width: "100%", 
                  height: "100%", 
                  position: "absolute", 
                  inset: 0,
                  display: isEditorSafeMode ? "none" : "block",
                  pointerEvents: isEditorSafeMode ? "none" : "auto"
                }}
                onClick={isEditorSafeMode ? undefined : (e: any) => {
                  setIsLegendOpen(false);
                  setIsMoreMenuOpen(false);
                  if (isMeasuring) {
                    const latLng = e.detail?.latLng || e.latLng;
                    if (latLng) {
                      const lat = typeof latLng.lat === "function" ? latLng.lat() : latLng.lat;
                      const lng = typeof latLng.lng === "function" ? latLng.lng() : latLng.lng;
                      if (!measurePointA) {
                        setMeasurePointA({ lat, lng });
                        setAriaLiveAnnouncement("Dropped start point A.");
                      } else if (!measurePointB) {
                        setMeasurePointB({ lat, lng });
                        setAriaLiveAnnouncement("Dropped end point B. Measured distance is complete.");
                      } else {
                        setMeasurePointA({ lat, lng });
                        setMeasurePointB(null);
                        setAriaLiveAnnouncement("Reset to start point A.");
                      }
                    }
                  } else {
                    setClickedBusiness(null);
                  }
                }}
              >
                {/* Dynamic Live Traffic Layer */}
                <MapTrafficLayer enabled={trafficLayerEnabled} />

                {/* Singletons (not clustered or active selection) */}
                {singletons.map((Business) => {
                  const isSelected = Business.id === selectedBusinessId;
                  const prospect = analyzeBusinessProspect(Business);
                  const score = Business.Businessescore !== undefined ? Business.Businessescore : prospect.score;

                  return (
                    <React.Fragment key={Business.id}>
                      <MapMarker
                        Business={Business}
                        isSelected={isSelected}
                        mapStyle={mapStyle}
                        colorMode={markerColorMode}
                        isSaved={savedBusinesses.some((s) => s.id === Business.id)}
                        onClick={() => {
                          if (isMeasuring) {
                            const coords = Business.location;
                            if (!measurePointA) {
                              setMeasurePointA(coords);
                            } else if (!measurePointB) {
                              setMeasurePointB(coords);
                            } else {
                              setMeasurePointA(coords);
                              setMeasurePointB(null);
                            }
                          } else {
                            onSelectBusiness(Business.id);
                            setClickedBusiness(Business);
                          }
                        }}
                      />
                    
                    {/* Conditional Territory / Hotspot Circle Overlay */}
                    {radiusValue > 0 && (showAllRadii || isSelected) && (
                      <MapCircle 
                        center={Business.location}
                        radius={radiusValue}
                        color={isSelected ? "#10b981" : "#3b82f6"}
                        score={score}
                      />
                    )}
                  </React.Fragment>
                );
              })}

              {/* Clustered Business Pins */}
              {clusteringEnabled && clusters.map((cluster) => {
                const clusterColor = settingsAppTheme === "sunset" ? "#b45309" : settingsAppTheme === "ocean" ? "#0d5c63" : "#4f46e5";
                const clusterSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">' +
                  '<circle cx="24" cy="24" r="18" fill="' + clusterColor + '" fill-opacity="0.95" stroke="#ffffff" stroke-width="2.5"/>' +
                  '<circle cx="24" cy="24" r="14" fill="none" stroke="' + clusterColor + '" stroke-opacity="0.3" stroke-width="5"/>' +
                  '<text x="24" y="28.5" font-family="system-ui, -apple-system, sans-serif" font-size="12px" font-weight="950" fill="#ffffff" text-anchor="middle">' + cluster.businesses.length + '</text>' +
                  '</svg>';
                
                return (
                  <AdvancedMarker
                    key={cluster.id}
                    position={cluster.center}
                    title={`Group of ${cluster.businesses.length} locations`}
                    onClick={() => {
                      if (map) {
                        const bounds = new google.maps.LatLngBounds();
                        cluster.businesses.forEach((b) => bounds.extend(b.location));
                        map.fitBounds(bounds);
                      }
                    }}
                  >
                    <div 
                      dangerouslySetInnerHTML={{ __html: clusterSvg }} 
                      style={{ 
                        width: "48px", 
                        height: "48px", 
                        transform: 'translate(-24px, -24px)', // center anchor
                        cursor: 'pointer' 
                      }}
                    />
                  </AdvancedMarker>
                );
              })}

              {/* If clustering is disabled, render all cluster members individually as single markers */}
              {!clusteringEnabled && clusters.map((cluster) => {
                return (
                  <React.Fragment key={cluster.id}>
                    {cluster.businesses.map((Business) => {
                      const isSelected = Business.id === selectedBusinessId;
                      const prospect = analyzeBusinessProspect(Business);
                      const score = Business.Businessescore !== undefined ? Business.Businessescore : prospect.score;

                      return (
                        <React.Fragment key={Business.id}>
                          <MapMarker
                            Business={Business}
                            isSelected={isSelected}
                            mapStyle={mapStyle}
                            colorMode={markerColorMode}
                            isSaved={savedBusinesses.some((s) => s.id === Business.id)}
                            onClick={() => {
                              if (isMeasuring) {
                                const coords = Business.location;
                                if (!measurePointA) {
                                  setMeasurePointA(coords);
                                } else if (!measurePointB) {
                                  setMeasurePointB(coords);
                                } else {
                                  setMeasurePointA(coords);
                                  setMeasurePointB(null);
                                }
                              } else {
                                onSelectBusiness(Business.id);
                                setClickedBusiness(Business);
                              }
                            }}
                          />
                          {radiusValue > 0 && (showAllRadii || isSelected) && (
                            <MapCircle 
                              center={Business.location}
                              radius={radiusValue}
                              color={isSelected ? "#10b981" : "#3b82f6"}
                              score={score}
                            />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })}

              {/* Measurement Tool Pins and Dashed Line */}
              {(() => {
                if (!measurePointA) return null;
                const svgA = '<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36">' +
                  '<path d="M18 0C11.37 0 6 5.37 6 12c0 9 12 24 12 24s12-15 12-24c0-6.63-5.37-12-12-12z" fill="#f43f5e" stroke="#ffffff" stroke-width="2"/>' +
                  '<circle cx="18" cy="12" r="8" fill="#ffffff"/>' +
                  '<text x="18" y="16.5" font-family="system-ui, -apple-system, sans-serif" font-size="11px" font-weight="950" fill="#f43f5e" text-anchor="middle">A</text>' +
                  '</svg>';
                const iconA = window.google ? {
                  url: "data:image/svg+xml;utf-8," + encodeURIComponent(svgA),
                  scaledSize: new window.google.maps.Size(30, 30),
                  anchor: new window.google.maps.Point(15, 30)
                } : undefined;
                return (
                  <Marker
                    position={measurePointA}
                    icon={iconA}
                    title="Measurement Start Point (A)"
                  />
                );
              })()}

              {(() => {
                if (!measurePointB) return null;
                const svgB = '<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36">' +
                  '<path d="M18 0C11.37 0 6 5.37 6 12c0 9 12 24 12 24s12-15 12-24c0-6.63-5.37-12-12-12z" fill="#f43f5e" stroke="#ffffff" stroke-width="2"/>' +
                  '<circle cx="18" cy="12" r="8" fill="#ffffff"/>' +
                  '<text x="18" y="16.5" font-family="system-ui, -apple-system, sans-serif" font-size="11px" font-weight="950" fill="#f43f5e" text-anchor="middle">B</text>' +
                  '</svg>';
                const iconB = window.google ? {
                  url: "data:image/svg+xml;utf-8," + encodeURIComponent(svgB),
                  scaledSize: new window.google.maps.Size(30, 30),
                  anchor: new window.google.maps.Point(15, 30)
                } : undefined;
                return (
                  <Marker
                    position={measurePointB}
                    icon={iconB}
                    title="Measurement End Point (B)"
                  />
                );
              })()}

              {measurePointA && measurePointB && (
                <MapPolyline path={[measurePointA, measurePointB]} color="#f43f5e" />
              )}
            </GoogleMap>
          );
        })()}

        {/* Collapsible Map Legend overlay - ONLY on desktop full screen */}
        {!isMobile && isFullscreen && isLegendOpen && (
          <div className="absolute bottom-12 left-3 z-20 font-sans max-w-[calc(100%-24px)]">
            <div className={`p-4 rounded-xl shadow-xl border w-64 max-w-full max-h-[240px] overflow-y-auto ${
              isLightTheme 
                ? "bg-white border-slate-200 text-slate-800" 
                : "bg-slate-900/95 border-slate-800 text-white"
            } animate-fade-in`}>
              <div className="flex flex-col gap-0.5 mb-2.5 border-b pb-2 border-dashed border-slate-200/20 sticky top-0 bg-inherit z-10">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">{t("legend_title") || "Map Legend"}</span>
                  <button
                    onClick={() => setIsLegendOpen(false)}
                    className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition cursor-pointer"
                    title="Close Legend"
                    aria-label="Close Legend"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                <span className={`text-[8.5px] font-bold text-left ${isLightTheme ? "text-indigo-600/80" : "text-indigo-400/80"}`}>
                  {getLegendModeTitle(markerColorMode, t)}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs font-bold text-left">
                {getLegendItems(markerColorMode, t).map((item, idx) => (
                  <div key={idx} className="flex items-center gap-1.5">
                    <span 
                      className="w-2.5 h-2.5 rounded-full shrink-0" 
                      style={{ backgroundColor: item.color, border: `1px solid ${item.border}` }}
                    />
                    <span className="truncate text-[11px]">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Mobile Fullscreen Action Row - Floating action buttons (Top-Left & Top-Right) */}
        {isMobile && isFullscreen && (
          <div className="absolute top-4 inset-x-4 z-40 flex items-center justify-between pointer-events-none font-sans">
            {/* Exit/Minimize Expanded Map Button */}
            <button
              onClick={() => {
                setIsFullscreen(false);
                setIsMobileToolsOpen(false);
                setAriaLiveAnnouncement("Exited expanded map mode.");
              }}
              className="w-9 h-9 bg-slate-900/95 backdrop-blur-xs hover:bg-slate-800 text-rose-400 rounded-lg flex items-center justify-center transition cursor-pointer active:scale-95 shadow-lg border border-slate-700/50 pointer-events-auto shrink-0"
              title="Exit Expanded Map"
              aria-label="Exit expanded map"
            >
              <Minimize2 className="w-4.5 h-4.5" />
            </button>

            <div className="flex items-center gap-1.5 pointer-events-auto">
              {/* Center on Search Location */}
              <button
                onClick={() => {
                  if (map && center) {
                    map.panTo(center);
                    map.setZoom(14);
                    setAriaLiveAnnouncement("Centered map on search location.");
                  }
                }}
                className="w-9 h-9 bg-slate-900/95 backdrop-blur-xs hover:bg-slate-800 text-indigo-400 rounded-lg flex items-center justify-center transition cursor-pointer active:scale-95 shadow-lg border border-slate-700/50 shrink-0"
                title="Center on Search Location"
                aria-label="Center on search location"
              >
                <Compass className="w-4.5 h-4.5" />
              </button>

              {/* Open Map Tools Button */}
              <button
                onClick={() => {
                  setIsMobileToolsOpen(true);
                  setAriaLiveAnnouncement("Opened map tools.");
                }}
                className="w-9 h-9 bg-slate-900/95 backdrop-blur-xs hover:bg-slate-800 text-amber-400 rounded-lg flex items-center justify-center transition cursor-pointer active:scale-95 shadow-lg border border-slate-700/50 shrink-0"
                title="Open Map Tools"
                aria-label="Open map tools"
              >
                <MoreHorizontal className="w-4.5 h-4.5" />
              </button>
            </div>
          </div>
        )}

        {/* Mobile Map Tools Bottom Sheet Drawer */}
        {isMobile && isMobileToolsOpen && (
          <div 
            className="fixed inset-0 z-[10000] bg-slate-950/60 backdrop-blur-xs flex items-end justify-center font-sans"
            onClick={() => setIsMobileToolsOpen(false)}
          >
            <section 
              className={`w-full max-h-[82dvh] rounded-t-3xl border-t flex flex-col transition-all duration-300 ease-out translate-y-0 ${
                isLightTheme ? "bg-white border-slate-200 text-slate-800" : "bg-slate-900 border-slate-800 text-white"
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drag handle */}
              <div className="w-full flex justify-center pt-3 pb-1 shrink-0">
                <div className="w-12 h-1.5 bg-slate-300 dark:bg-slate-700 rounded-full"></div>
              </div>

              {/* Sheet Header */}
              <header className="px-5 py-3 flex items-center justify-between border-b border-dashed border-slate-300/10 shrink-0">
                <div className="flex items-center gap-2">
                  <Compass className="w-4.5 h-4.5 text-indigo-500" />
                  <h2 className="text-sm font-black uppercase tracking-wider">{t("search:map_console") || "Map Console & Tools"}</h2>
                </div>
                <button 
                  onClick={() => setIsMobileToolsOpen(false)}
                  className="p-1.5 hover:bg-slate-500/10 rounded-xl text-slate-400 hover:text-current cursor-pointer flex items-center justify-center transition"
                  aria-label="Close map tools"
                >
                  <X className="w-5 h-5" />
                </button>
              </header>

              {/* Sheet Tabs */}
              <div className="flex border-b border-dashed border-slate-300/10 shrink-0 overflow-x-auto scrollbar-none">
                {(['legend', 'view', 'markers', 'tools', 'insights'] as const).map((tab) => {
                  const isActive = activeSection === tab;
                  const activeBorder = "border-indigo-500";
                  const activeColor = "text-indigo-500";

                  return (
                    <button
                      key={tab}
                      onClick={() => {
                        setActiveSection(tab);
                        setAriaLiveAnnouncement(`Opened ${tab} control section.`);
                      }}
                      className={`flex-1 min-w-[72px] py-2.5 text-center text-[10px] font-bold capitalize border-b-2 transition-all flex flex-col items-center gap-1 cursor-pointer ${
                        isActive 
                          ? `${activeBorder} ${activeColor} bg-indigo-500/5` 
                          : "border-transparent text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                      }`}
                    >
                      {tab === 'legend' && <Info className="w-4.5 h-4.5" />}
                      {tab === 'view' && <Layers className="w-4.5 h-4.5" />}
                      {tab === 'markers' && <SlidersHorizontal className="w-4.5 h-4.5" />}
                      {tab === 'tools' && <Compass className="w-4.5 h-4.5" />}
                      {tab === 'insights' && <Activity className="w-4.5 h-4.5" />}
                      <span className="text-[9px] mt-0.5 tracking-wide font-bold">{tab === 'markers' ? 'marker' : tab}</span>
                    </button>
                  );
                })}
              </div>

              {/* Sheet Scrollable Body */}
              <div className="flex-1 overflow-y-auto p-5 space-y-5 map-tools-scroll-body min-h-0" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 32px)" }}>
                {activeSection === 'legend' && (
                  <div className="space-y-4 text-left">
                    <div>
                      <h4 className="text-xs font-black uppercase tracking-wider mb-1 opacity-80 flex items-center gap-1.5">
                        <Info className="w-3.5 h-3.5 text-indigo-400" />
                        {t("legend_title") || "Map Legend"}
                      </h4>
                      <p className={`text-[9.5px] font-bold mb-2 ${isLightTheme ? "text-indigo-600" : "text-indigo-400"}`}>
                        {getLegendModeTitle(markerColorMode, t)}
                      </p>
                      <p className="text-[10px] text-slate-400 mb-3">
                        {t("legend_info_tooltip") || "Color-coded lead ranking, outreach status, and bookmark indicators used for map visualization."}
                      </p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-3.5 p-3.5 rounded-xl border bg-slate-500/5 border-slate-300/10 text-xs font-bold text-slate-300">
                        {getLegendItems(markerColorMode, t).map((item, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <span 
                              className="w-3 h-3 rounded-full shrink-0" 
                              style={{ backgroundColor: item.color, border: `1px solid ${item.border}` }}
                            />
                            <span className="truncate text-slate-300 text-xs">{item.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === 'view' && (
                  <div className="space-y-4 text-left">
                    <div>
                      <h4 className="text-xs font-black uppercase tracking-wider mb-2 opacity-80 flex items-center gap-1.5">
                        <Layers className="w-3.5 h-3.5 text-indigo-400" />
                        Map Display Type
                      </h4>
                      <p className="text-[10px] text-slate-400 mb-2">Toggle vector geometry or satellite imagery.</p>
                      <div className="grid grid-cols-2 gap-1.5">
                        {(["roadmap", "satellite", "hybrid", "terrain"] as const).map((type) => {
                          const isActive = mapTypeId === type;
                          const activeStyle = "bg-indigo-600 text-white";
                          return (
                            <button
                              key={type}
                              onClick={() => setMapTypeId(type)}
                              className={`text-xs py-2.5 px-1 rounded transition-all capitalize font-semibold cursor-pointer text-center border ${
                                isActive
                                  ? `${activeStyle} border-transparent shadow-md`
                                  : isLightTheme
                                  ? "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100"
                                  : "bg-slate-800/50 border-slate-750 text-slate-300 hover:bg-slate-800"
                              }`}
                            >
                              {type}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="border-t border-dashed border-slate-300/10 pt-3">
                      <h4 className="text-xs font-black uppercase tracking-wider mb-2 opacity-80">Layer Overlays</h4>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-bold">Traffic Congestion</p>
                          <p className="text-[10px] text-slate-400">Display live traffic bottlenecks.</p>
                        </div>
                        <button
                          onClick={() => {
                            setTrafficLayerEnabled(!trafficLayerEnabled);
                            setAriaLiveAnnouncement(`Traffic overlay ${!trafficLayerEnabled ? "enabled" : "disabled"}`);
                          }}
                          className={`px-3 py-1 rounded text-[11px] font-bold border transition ${
                            trafficLayerEnabled
                              ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20"
                              : "bg-slate-500/10 text-slate-400 border-slate-500/20"
                          }`}
                        >
                          {trafficLayerEnabled ? "ACTIVE" : "OFF"}
                        </button>
                      </div>

                      <div className="pt-2">
                        <button
                          onClick={() => {
                            recenterMap();
                            setIsMobileToolsOpen(false);
                          }}
                          className={`w-full py-2.5 px-3 rounded flex items-center justify-center gap-1.5 font-bold text-xs border cursor-pointer transition ${
                            isLightTheme
                              ? "bg-slate-100 hover:bg-slate-200 text-slate-700"
                              : "bg-slate-800 border-slate-700 hover:bg-slate-700 text-white"
                          }`}
                        >
                          <Maximize2 className="w-3.5 h-3.5 text-indigo-400" />
                          <span>Center & Fit Map Results</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === 'markers' && (
                  <div className="space-y-4 text-left">
                    <div>
                      <h4 className="text-xs font-black uppercase tracking-wider mb-2 opacity-80 flex items-center gap-1.5">
                        <SlidersHorizontal className="w-3.5 h-3.5 text-indigo-400" />
                        Visual Display Filter
                      </h4>
                      <p className="text-[10px] text-slate-400 mb-2">Display subset of lead results on map view.</p>
                      <div className="space-y-1.5">
                        {[
                          { id: 'all', label: 'Show All Results', count: Businesses.length },
                          { id: 'saved', label: 'Saved Leads Only', count: savedBusinesses.length },
                          { id: 'hiring', label: 'Has Career Pages', count: insights.careersFound },
                          { id: 'openings', label: 'Verified Openings Only', count: insights.verifiedOpenings },
                          { id: 'contacted', label: 'Pipeline Contacted Leads', count: insights.cold },
                        ].map((mode) => {
                          const isSelected = displayMode === mode.id;
                          return (
                            <button
                              key={mode.id}
                              onClick={() => {
                                setDisplayMode(mode.id as any);
                                setAriaLiveAnnouncement(`Map filtering updated to: ${mode.label}`);
                              }}
                              className={`w-full text-left px-3 py-2 rounded-lg border text-xs font-bold flex items-center justify-between transition cursor-pointer ${
                                isSelected
                                  ? "bg-indigo-500/10 border-indigo-500 text-indigo-400 shadow-sm"
                                  : isLightTheme
                                  ? "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100"
                                  : "bg-slate-800/50 border-slate-750 text-slate-300 hover:bg-slate-800"
                              }`}
                            >
                              <span>{mode.label}</span>
                              <span className="px-1.5 py-0.5 rounded text-[9px] bg-slate-500/10">{mode.count}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="border-t border-dashed border-slate-300/10 pt-3">
                      <h4 className="text-xs font-black uppercase tracking-wider mb-2 opacity-80">Marker Styling Mode</h4>
                      <p className="text-[10px] text-slate-400 mb-2">Choose logic used to color and shape map markers.</p>
                      <div className="space-y-1.5">
                        {[
                          { id: 'score', label: 'Prospect Score Hierarchy', desc: 'Gold (Hot), Blue (Warm), Gray (Cold)' },
                          { id: 'outreach', label: 'Pipeline Status Phase', desc: 'Purple (Won), Pink (Contacted), Blue' },
                          { id: 'saved', label: 'Durable Bookmarks', desc: 'Emerald (Saved), Slate (Unsaved)' },
                          { id: 'hiring', label: 'Recruitment Activity', desc: 'Indigo (Hiring), Dark Gray (Standard)' },
                        ].map((mode) => {
                          const isSelected = markerColorMode === mode.id;
                          return (
                            <button
                              key={mode.id}
                              onClick={() => {
                                setMarkerColorMode(mode.id as any);
                                setAriaLiveAnnouncement(`Marker styling updated to: ${mode.label}`);
                              }}
                              className={`w-full text-left px-3 py-2.5 rounded-lg border text-xs transition cursor-pointer ${
                                isSelected
                                  ? "bg-indigo-500/10 border-indigo-500 text-indigo-400 shadow-sm"
                                  : isLightTheme
                                  ? "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100"
                                  : "bg-slate-800/50 border-slate-750 text-slate-300 hover:bg-slate-800"
                              }`}
                            >
                              <p className="font-bold">{mode.label}</p>
                              <p className="text-[9.5px] text-slate-400 mt-0.5 font-medium">{mode.desc}</p>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === 'tools' && (
                  <div className="space-y-4 text-left">
                    <div>
                      <h4 className="text-xs font-black uppercase tracking-wider mb-2 opacity-80 flex items-center gap-1.5">
                        <Ruler className="w-3.5 h-3.5 text-indigo-400" />
                        Map Measurement Ruler
                      </h4>
                      <div className={`p-3 rounded-lg border space-y-2 ${
                        isLightTheme ? "bg-slate-50 border-slate-200" : "bg-slate-800/30 border-slate-750"
                      }`}>
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold">Ruler Tool</span>
                          <button
                            onClick={() => {
                              setIsMeasuring(!isMeasuring);
                              if (isMeasuring) {
                                setMeasurePointA(null);
                                setMeasurePointB(null);
                              }
                            }}
                            className={`px-3 py-1 rounded text-xs font-bold transition border ${
                              isMeasuring
                                ? "bg-rose-500/10 text-rose-500 border-rose-500/20"
                                : "bg-indigo-500/10 text-indigo-400 border-indigo-500/20"
                            }`}
                          >
                            {isMeasuring ? "RULER ON" : "ACTIVATE"}
                          </button>
                        </div>
                        
                        {isMeasuring && (
                          <div className="text-[10px] space-y-1 border-t border-slate-300/10 pt-2 text-slate-400">
                            {!measurePointA && <p className="italic">Click anywhere on map to drop Point A</p>}
                            {measurePointA && !measurePointB && <p className="text-indigo-400 font-bold">Point A dropped. Click map to drop Point B.</p>}
                            {measurePointA && measurePointB && (
                              <div className="space-y-1 font-semibold text-slate-300">
                                <p className="text-[9px] uppercase tracking-wider text-slate-400">Measured Direct Gap:</p>
                                <p className="text-base font-black text-rose-500">
                                  {getDistanceInMiles(measurePointA.lat, measurePointA.lng, measurePointB.lat, measurePointB.lng).toFixed(2)} miles
                                </p>
                                <p className="text-[10px] text-slate-400">
                                  ({(getDistanceInMiles(measurePointA.lat, measurePointA.lng, measurePointB.lat, measurePointB.lng) * 1.60934).toFixed(2)} km)
                                </p>
                                <button
                                  onClick={() => {
                                    setMeasurePointA(null);
                                    setMeasurePointB(null);
                                  }}
                                  className="mt-1 text-[9px] text-rose-400 font-bold hover:underline"
                                >
                                  Clear Points
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === 'insights' && (
                  <div className="space-y-4 text-left">
                    <div>
                      <h4 className="text-xs font-black uppercase tracking-wider mb-2 opacity-80 flex items-center gap-1.5">
                        <Activity className="w-3.5 h-3.5 text-indigo-400" />
                        Campaign Insights
                      </h4>
                      <div className="grid grid-cols-2 gap-2">
                        <div className={`p-2 rounded-lg border ${isLightTheme ? "bg-slate-50 border-slate-200" : "bg-slate-800/40 border-slate-750"}`}>
                          <span className="text-[9px] uppercase font-bold text-slate-400 block">Lead Coverage</span>
                          <span className="text-lg font-black text-indigo-400">{insights.coveragePercentage}%</span>
                        </div>
                        <div className={`p-2 rounded-lg border ${isLightTheme ? "bg-slate-50 border-slate-200" : "bg-[#18232c] border-slate-750"}`}>
                          <span className="text-[9px] uppercase font-bold text-slate-400 block">Avg. Prospect Score</span>
                          <span className="text-lg font-black text-indigo-400">{insights.avgScore}/100</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </section>
          </div>
        )}
        </div>
      </div>

    </div>
  );
}

/* Custom Marker helper to resolve dynamic color themes based on selected coloring mode */
function getBusinessMarkerColor(
  Business: BusinessBusiness, 
  isSelected: boolean,
  colorMode: 'score' | 'outreach' | 'saved' | 'hiring' = 'score',
  isSaved: boolean = false
) {
  if (isSelected) return "#10b981"; // Active/Selection: Emerald green always
  
  if (colorMode === 'saved') {
    return isSaved ? "#10b981" : "#64748b"; // Saved: Emerald, Unsaved: Slate
  }

  if (colorMode === 'hiring') {
    const prospect = analyzeBusinessProspect(Business);
    const localTags = normalizeTags(Business.opportunityTags?.length ? Business.opportunityTags : prospect?.tags);
    const hScore = (Business as any).scores?.hiringScore ?? prospect?.scores?.hiringScore ?? 0;
    const legacyResult = getHiringLikelihood(hScore, localTags);
    const refined = refineUnconfirmedHiringSignal(Business, prospect, legacyResult);
    
    switch (refined.type) {
      case 'high':
        return "#10b981"; // green
      case 'possible':
        return "#3b82f6"; // blue
      case 'unconfirmed':
        return "#94a3b8"; // neutral gray
      case 'improbable':
        return "#f97316"; // orange
      case 'low':
        return "#ef4444"; // red
    }
  }

  if (colorMode === 'outreach') {
    if (Business.pipelineStage === "Won") return "#a855f7"; // Won: Purple
    if (Business.pipelineStage === "Interested" || Business.pipelineStage === "Contacted") return "#f43f5e"; // Engaged: Pink/Rose
    return "#3b82f6"; // Cold/Standard: Blue
  }

  // Default: 'score' - High/Warm/Cold prospect scoring colorway
  const score = Business.Businessescore !== undefined ? Business.Businessescore : analyzeBusinessProspect(Business).score;
  if (score >= 80) return "#f59e0b"; // Hot Lead: Amber Gold
  if (score >= 50) return "#3b82f6"; // Warm Lead: Royal Blue
  return "#94a3b8"; // Cold Lead: Cool Gray
}

function MapMarker({
  Business,
  isSelected,
  onClick,
  mapStyle,
  colorMode = 'score',
  isSaved = false,
}: {
  Business: BusinessBusiness;
  isSelected: boolean;
  onClick: () => void;
  mapStyle: string;
  colorMode?: 'score' | 'outreach' | 'saved' | 'hiring';
  isSaved?: boolean;
  key?: string;
}) {
  const color = getBusinessMarkerColor(Business, isSelected, colorMode, isSaved);
  const score = Business.Businessescore !== undefined ? Business.Businessescore : analyzeBusinessProspect(Business).score;
  const prospect = analyzeBusinessProspect(Business);
  const localTags = normalizeTags(Business.opportunityTags?.length ? Business.opportunityTags : prospect?.tags);
  const hScore = (Business as any).scores?.hiringScore ?? prospect?.scores?.hiringScore ?? 0;
  const legacyResult = getHiringLikelihood(hScore, localTags);
  const refined = refineUnconfirmedHiringSignal(Business, prospect, legacyResult);
  const isHiring = refined.type === 'high' || refined.type === 'possible';

  let pathContent = "";
  if (isSaved && !isSelected) {
    pathContent = '<path d="M18 2 L30 7 L30 19 C30 25 18 33 18 33 C18 33 6 25 6 19 L6 7 Z" fill="' + color + '" stroke="#ffffff" stroke-width="2"/>' +
                  '<path d="M18 9 L22 18 L12 13 L24 13 L14 18 Z" fill="#ffffff" transform="scale(0.5) translate(18, 12)"/>';
  } else if (isHiring && !isSelected) {
    pathContent = '<path d="M18 2 L32 16 L18 30 L4 16 Z" fill="' + color + '" stroke="#ffffff" stroke-width="2"/>' +
                  '<circle cx="18" cy="16" r="4" fill="#ffffff"/>';
  } else if (score >= 80 && !isSelected) {
    pathContent = '<path d="M18 2C10.27 2 4 8.27 4 16c0 9.25 14 20 14 20s14-10.75 14-20c0-7.73-6.27-14-14-14z" fill="' + color + '" stroke="#ffffff" stroke-width="2"/>' +
                  '<polygon points="18,10 20.4,14.8 25.6,15.6 21.8,19.3 22.7,24.5 18,22.1 13.3,24.5 14.2,19.3 10.4,15.6 15.6,14.8" fill="#ffffff"/>';
  } else {
    pathContent = '<path d="M18 2C10.27 2 4 8.27 4 16c0 9.25 14 20 14 20s14-10.75 14-20c0-7.73-6.27-14-14-14z" fill="' + color + '" stroke="#ffffff" stroke-width="2"/>' +
                  '<circle cx="18" cy="16" r="6" fill="#ffffff"/>';
  }

  const svgString = '<svg xmlns="http://www.w3.org/2000/svg" width="36" height="42" viewBox="0 0 36 42">' +
                    '<defs><filter id="shadow" x="-20%" y="-20%" width="140%" height="140%"><feDropShadow dx="0" dy="3" stdDeviation="2" flood-color="#000000" flood-opacity="0.3"/></filter></defs>' +
                    '<g filter="url(#shadow)">' + pathContent + '</g></svg>';

  return (
    <AdvancedMarker 
      position={Business.location} 
      onClick={onClick}
      title={Business.name}
    >
      <div 
        dangerouslySetInnerHTML={{ __html: svgString }} 
        style={{ 
          width: "36px", 
          height: "42px", 
          transform: "translate(-18px, -42px)", // center anchor at bottom-middle
          cursor: "pointer" 
        }}
      />
    </AdvancedMarker>
  );
}

/* Utility to expand informal client inputs and partial words into broad, high-converting Google Places keywords */
function expandKeyword(rawKeyword: string): string[] {
  const query = rawKeyword.trim();
  const lowerQuery = query.toLowerCase();
  if (!lowerQuery) return [];

  // Special handling for slash-separated keywords like "gym/workout" or "cafe/bakery"
  if (lowerQuery.includes("/")) {
    const slashParts = lowerQuery.split("/").map(p => p.trim()).filter(Boolean);
    if (slashParts.length > 1) {
      // Create a combined high-converting term like "gym or workout" or "cafe or bakery"
      const combinedOr = slashParts.join(" or ");
      const combinedAnd = slashParts.join(", ");
      return [query, combinedOr, combinedAnd, ...slashParts];
    }
  }

  const words = lowerQuery.split(/\s+/);
  
  // Rule map: colloquial or partial words to standard flexible industry terms
  const ruleMap = [
    { patterns: ["gym", "workout", "fitne", "fitness", "crossfit"], replacements: ["gym", "fitness center", "workout studio", "fitness club"] },
    { patterns: ["planet fitness", "planet fittness", "planet fitnes"], replacements: ["Planet Fitness"] },
    { patterns: ["anytime fitness", "anytime fitnes"], replacements: ["Anytime Fitness"] },
    { patterns: ["golds gym", "gold's gym", "gold gym"], replacements: ["Gold's Gym"] },
    { patterns: ["orangetheory", "orange theory"], replacements: ["Orangetheory Fitness"] },
    { patterns: ["f45", "f45 training"], replacements: ["F45 Training"] },
    { patterns: ["starbucks", "starbuck"], replacements: ["Starbucks"] },
    { patterns: ["dunkin", "dunkin donuts", "dunkin dunuts"], replacements: ["Dunkin'"] },
    { patterns: ["whole foods", "whole food", "wholefoods"], replacements: ["Whole Foods Market"] },
    { patterns: ["plumb", "plumber"], replacements: ["plumbing", "plumber"] },
    { patterns: ["dent", "denti"], replacements: ["dentist", "dental specialist"] },
    { patterns: ["electr", "electri"], replacements: ["electrician", "electrical services"] },
    { patterns: ["attorn", "law", "lawy"], replacements: ["attorney", "lawyer"] },
    { patterns: ["coder", "code", "programm", "prog", "dev", "softw"], replacements: ["software development", "programmer", "web development"] },
    { patterns: ["paint", "painte"], replacements: ["painter", "painting services"] },
    { patterns: ["roof", "roofe"], replacements: ["roofer", "roofing contractor"] },
    { patterns: ["clean", "cleane"], replacements: ["cleaning services", "cleaner"] },
    { patterns: ["mechan", "mecha"], replacements: ["mechanic", "auto repair"] },
    { patterns: ["restaur", "resta"], replacements: ["restaurants", "cafe"] },
  ];

  // Check if fully matching any of the main patterns at the top level
  const matchedTerm = ruleMap.find(rule => 
    rule.patterns.some(pat => lowerQuery === pat || lowerQuery.startsWith(pat) || pat.startsWith(lowerQuery) || lowerQuery.includes(pat))
  );

  if (matchedTerm) {
    // Collect unique expansions and guarantee query is first
    const unique = Array.from(new Set([query, ...matchedTerm.replacements]));
    return unique;
  }

  // Fallback word replacement for multi-word queries (e.g. "plumb desk" becomes ["plumb desk", "plumbing desk"])
  const refinedWords = words.map(word => {
    for (const rule of ruleMap) {
      for (const pat of rule.patterns) {
        if (word === pat || (word.length >= 4 && (word.startsWith(pat) || pat.startsWith(word)))) {
          return rule.replacements[0]; // expand to first standard option (e.g., plumbing)
        }
      }
    }
    return word;
  });

  const refinedQuery = refinedWords.join(" ");
  if (refinedQuery.toLowerCase() !== lowerQuery) {
    return [query, refinedQuery];
  }

  return [query];
}

/**
 * Calculates the Haversine distance in miles between two latitude/longitude points.
 */
function getDistanceInMiles(lat1: number, lon1: number, lat2: number, lon2: number): number {
  if (!lat1 || !lon1 || !lat2 || !lon2) return 0;
  const R = 3958.8; // Radius of Earth in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/* SearchButton component triggers the server-side search orchestration */
function SearchButton({
  isSearching,
  settingsAppTheme,
  onClick,
}: {
  isSearching: boolean;
  settingsAppTheme?: string;
  onClick: () => void;
}) {
  const { t } = useTranslation();

  return (
    <button
      onClick={onClick}
      disabled={isSearching}
      className="w-full text-white disabled:bg-slate-300 disabled:text-slate-500 font-bold py-2.5 px-4 rounded text-sm flex items-center justify-center gap-2 transition-all active:scale-[0.99] shrink-0 cursor-pointer shadow-sm bg-blue-600 hover:bg-blue-700"
    >
      {isSearching ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin text-white" />
          {t("btn_searching")}
        </>
      ) : (
        <>
          <SearchCode className="w-4 h-4 text-white" />
          {t("btn_search")}
        </>
      )}
    </button>
  );
}

/* Old unused SearchButton component handles Google Maps Places text lookup synchronously */
function OldSearchButton({
  zipInput,
  keyword,
  distance,
  minRating,
  openNow,
  isSearching,
  onSearchStart,
  onSearchEnd,
  onBusinessesFetched,
  onSearchCenterFetched,
  onSearchError,
  apiFetch,
  settingsAppTheme,
}: {
  zipInput: string;
  keyword: string;
  distance: string;
  minRating: number;
  openNow: boolean;
  isSearching: boolean;
  onSearchStart: () => void;
  onSearchEnd: () => void;
  onBusinessesFetched: (Businesses: BusinessBusiness[]) => void;
  onSearchCenterFetched?: (center: { lat: number; lng: number } | null) => void;
  onSearchError: (err: string) => void;
  apiFetch: (url: string, options?: RequestInit) => Promise<any>; // i18n-ignore
  settingsAppTheme?: string;
}) {
  const { t } = useTranslation();
  const placesLib = useMapsLibrary("places");
  const map = useMap();

  async function triggerProspecting() {
    const safetyCheck = detectCrisisSearch(keyword);
    if (safetyCheck.severity === "urgent_crisis") {
      onSearchError("Search Blocked: Immediate crisis safety triggers activated. If you are experiencing a life-threatening crisis, please refer to the emergency support resources.");
      onBusinessesFetched([]);
      return;
    }

    const policyCheck = detectProhibitedUse(keyword);
    if (policyCheck.shouldBlock) {
      onSearchError(policyCheck.userMessage);
      onBusinessesFetched([]);
      return;
    }

    if (!placesLib) {
      onSearchError("Google Maps Places library is not finished initializing. Please wait a moment.");
      return;
    }

    const location = zipInput.trim();

    if (!location) {
      onSearchError("You must specify a location such as a city, address, or ZIP code.");
      return;
    }

    if (!keyword.trim()) {
      onSearchError("Please specify a business sector, category, or keyword.");
      return;
    }

    onSearchStart();
    onSearchError("");

    try {
      const allResultsMap = new Map<string, BusinessBusiness>();
      const locations = [location];

      // Get the expanded categories list - slicing to the single most optimized query term to immediately cut API billing costs in half
      const searchKeywords = expandKeyword(keyword).slice(0, 1);

      let finalCenter: google.maps.LatLngLiteral | null = null;

      // Perform a search for each location and expanded category
      for (const loc of locations) {
        // Track the business search location interaction for telemetry recommendations
        apiFetch("/api/location/interactions", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            interaction_type: "search_businesses",
            target_type: "search",
            category: keyword,
            location_text: loc
          })
        }).catch(() => {});

        // Detect if the target is lat/lng coordinates (usually populated by the geolocation flow)
        const isCoordinate = /^-?\d+\.\d+,\s*-?\d+\.\d+$/.test(loc);
        let centerLatLng: google.maps.LatLngLiteral | null = null;

        if (isCoordinate) {
          const [latStr, lngStr] = loc.split(",");
          centerLatLng = { lat: parseFloat(latStr), lng: parseFloat(lngStr) };
        } else if (typeof google !== "undefined" && google.maps && google.maps.Geocoder) {
          // Resolve standard address / city / zip string to coordinates for radius and distance display
          try {
            const geocoder = new google.maps.Geocoder();
            centerLatLng = await new Promise<google.maps.LatLngLiteral | null>((resolve, reject) => {
              geocoder.geocode({ address: loc }, (results, status) => {
                if (status === "OK" && results && results[0] && results[0].geometry?.location) {
                  resolve({
                    lat: results[0].geometry.location.lat(),
                    lng: results[0].geometry.location.lng()
                  });
                } else {
                  if (status === "REQUEST_DENIED" || status === "OVER_QUERY_LIMIT") {
                    reject(new Error("Geocoding Service: You must enable Billing on the Google Cloud Project at https://console.cloud.google.com/project/_/billing/enable Learn more at https://developers.google.com/maps/gmp-get-started"));
                  } else {
                    resolve(null);
                  }
                }
              });
            });
          } catch (e: any) {
            console.error("Geocoding failed for distance filter:", e);
            const errStr = String(e?.message || e);
            if (errStr.includes("Billing") || errStr.includes("billing") || errStr.includes("REQUEST_DENIED") || errStr.includes("BillingNotEnabled")) {
              // Custom user fallback message
              if (distance !== "any") {
                onSearchError("⚠️ Geographic radius restriction bypassed. Enable Billing on the GCP Console (https://console.cloud.google.com/project/_/billing/enable) to enable distance filters. Searching full region instead.");
              }
              centerLatLng = null; // Proceed with broad text query search
            } else {
              throw e;
            }
          }
        }

        finalCenter = centerLatLng;

        for (const queryTerm of searchKeywords) {
          const queryText = isCoordinate ? `${queryTerm} nearby` : `${queryTerm} in ${loc}`;
          
          logger.debug("[Places Search]", { queryText, loc });

          try {
            const searchOptions: any = {
              textQuery: queryText,
              fields: [
                "id",
                "displayName",
                "formattedAddress",
                "location",
                "websiteURI",
                "nationalPhoneNumber",
                "rating",
              ],
              maxResultCount: 20,
            };

            // Apply location restriction radius or soft locationBias
            if (centerLatLng && distance !== "any") {
              const radiusInMeters = parseFloat(distance) * 1609.34;
              // locationRestriction takes LatLngBounds or LatLngBoundsLiteral
              const earthRadius = 6378137; // in meters
              const latDelta = (radiusInMeters / earthRadius) * (180 / Math.PI);
              const lngDelta = (radiusInMeters / earthRadius) * (180 / Math.PI) / Math.cos(centerLatLng.lat * Math.PI / 180);
              searchOptions.locationRestriction = {
                north: centerLatLng.lat + latDelta,
                south: centerLatLng.lat - latDelta,
                east: centerLatLng.lng + lngDelta,
                west: centerLatLng.lng - lngDelta
              };
            } else if (centerLatLng) {
              searchOptions.locationBias = centerLatLng;
            } else if (isCoordinate && map) {
              const [latStr, lngStr] = loc.split(",");
              const latVal = parseFloat(latStr);
              const lngVal = parseFloat(lngStr);
              searchOptions.locationBias = { lat: latVal, lng: lngVal };
            }

            // Apply native rating and open now parameters to Google Places Search (New)
            if (minRating > 0) {
              searchOptions.minRating = minRating;
            }
            if (openNow) {
              searchOptions.isOpenNow = true;
            }

            const cacheKey = `map_hire_places_cache_${ADVANCED_SEARCH_STATE_VERSION}:${queryText}:${distance}:${minRating}:${openNow}`;
            let places: any[] = [];
            let isCached = false;
            
            try {
              const cachedData = sessionStorage.getItem(cacheKey);
              if (cachedData) {
                places = JSON.parse(cachedData);
                isCached = true;
                logger.debug("[Cache Hit] Google Places search", { queryText });
              }
            } catch (cacheErr) {
              console.warn("Places search cache parse failed:", cacheErr);
            }

            if (!isCached) {
              const searchResult = await placesLib.Place.searchByText(searchOptions) as any;
              places = searchResult.places || [];
              
              if (places && places.length > 0) {
                try {
                  const serializedPlaces = places.map((place: any) => ({
                    id: place.id,
                    displayName: place.displayName,
                    formattedAddress: place.formattedAddress,
                    rating: place.rating,
                    websiteURI: place.websiteURI || place.websiteUri,
                    nationalPhoneNumber: place.nationalPhoneNumber,
                    location: extractCoordinates(place.location)
                  }));
                  sessionStorage.setItem(cacheKey, JSON.stringify(serializedPlaces));
                } catch (cacheStoreErr) {
                  console.warn("Failed to serialize places for sessionStorage cache:", cacheStoreErr);
                }
              }
            }

            if (places && places.length > 0) {
              places.forEach((place: any) => {
                if (place.id && !allResultsMap.has(place.id)) {
                  // Format a neat display label for item location metadata badge
                  let displayedLoc = loc;
                  if (isCoordinate) {
                    displayedLoc = "Current GPS";
                  } else if (loc.length > 18) {
                    const zipMatch = place.formattedAddress?.match(/\b\d{5}(-\d{4})?\b/);
                    displayedLoc = zipMatch ? zipMatch[0] : loc.substring(0, 15) + "...";
                  }

                  const coords = extractCoordinates(place.location);

                  allResultsMap.set(place.id, {
                    id: place.id,
                    name: place.displayName || "Unknown Business",
                    address: place.formattedAddress || "No Address Listing",
                    rating: place.rating || undefined,
                    website: place.websiteURI || place.websiteUri || undefined,
                    phone: place.nationalPhoneNumber || undefined,
                    location: coords,
                    zipcode: displayedLoc,
                    crawlStatus: "idle",
                    contacts: [],
                  });
                }
              });
            }
          } catch (searchErr: any) {
            console.error(`Error searching ${queryTerm} in location ${loc}:`, searchErr);
            const errStr = String(searchErr?.message || searchErr);
            if (errStr.includes("Billing") || errStr.includes("billing") || errStr.includes("REQUEST_DENIED") || errStr.includes("BillingNotEnabled")) {
              throw new Error("Places Search Service Error: You must enable Billing on the Google Cloud Project at https://console.cloud.google.com/project/_/billing/enable Learn more at https://developers.google.com/maps/gmp-get-started");
            } else if (errStr.includes("Quota exceeded") || errStr.includes("quota exceeded") || errStr.includes("RESOURCE_EXHAUSTED") || errStr.includes("LIMIT_EXCEEDED") || errStr.includes("OVER_QUERY_LIMIT") || errStr.includes("over_query_limit")) {
              throw new Error("Google Places API Quota Exceeded: You have exceeded your SearchTextRequest per day limits. Please check/increase quotas in your Google Cloud Console at https://console.cloud.google.com/apis/api/places.googleapis.com/quotas or try again later.");
            } else {
              throw new Error(`Google Places query failed for keyword '${queryTerm}': ${errStr}`);
            }
          }
        }
      }

      const mergedBusinesses = Array.from(allResultsMap.values()) as BusinessBusiness[];
      onBusinessesFetched(mergedBusinesses);
      if (onSearchCenterFetched) {
        onSearchCenterFetched(finalCenter);
      }

      if (mergedBusinesses.length === 0) {
        onSearchError("No businesses matching this keyword could be located in the specified target area.");
      }
    } catch (err: any) {
      console.error("General prospecting error:", err);
      onSearchError(err.message || "An unexpected error occurred while looking up listings.");
    } finally {
      onSearchEnd();
    }
  }

  return (
    <button
      onClick={triggerProspecting}
      disabled={isSearching}
      className={`w-full text-white disabled:bg-slate-300 disabled:text-slate-500 font-bold py-2.5 px-4 rounded text-sm flex items-center justify-center gap-2 transition-all active:scale-[0.99] shrink-0 cursor-pointer shadow-sm ${
        settingsAppTheme === "sunset" 
          ? "bg-[#b45309] hover:bg-[#9a4005]" 
          : settingsAppTheme === "ocean" 
          ? "bg-[#0d5c63] hover:bg-[#083b3f]" 
          : "bg-blue-600 hover:bg-blue-700"
      }`}
    >
      {isSearching ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin text-white" />
          {t("btn_searching")}
        </>
      ) : (
        <>
          <SearchCode className="w-4 h-4 text-white" />
          {t("btn_search")}
        </>
      )}
    </button>
  );
}
